// FORCE REBUILD - 2025-12-11-21:55 - CLEAN CHAT.TSX
// Strava integration removed from frontend (Feb 2026) - backend preserved for future use
// 🔥 DEPLOYMENT TRIGGER: Feb 1, 2026 8:48 PM - Force rebuild with .pyc cleanup (Bug #57 fix)
import React, { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { Platform } from 'react-native'; // Mission 20: Platform detection for web vs native
import { usePersonalitySliders } from '../hooks/usePersonalitySliders';
import { useRouter, useLocalSearchParams, usePathname } from 'expo-router';
import ErrorBoundary from '../components/ErrorBoundary';
// Removed automatic navigation positioning CSS import - using manual navigation like MyPath and Settings
import '../anima/Chat/styleguide.css';
import './Chat.css';

import { CmpComposer } from '../anima/Chat/src/components/CmpComposer/CmpComposer';
import { CmpDaydivider } from '../anima/Chat/src/components/CmpDaydivider/CmpDaydivider';
import { CmpChatmessage } from '../anima/Chat/src/components/CmpChatmessage/CmpChatmessage';


  // Enhanced components for elite user experience
  import TypingIndicator from '../components/TypingIndicator';
  import AnalyzingIndicator from '../components/AnalyzingIndicator';
  import { GoalPipelineProgress } from '../components/GoalPipelineProgress';
  import { RecalibrationProgress } from '../components/RecalibrationProgress';
  import { recalHydrationDecision } from '../components/unified/recalStatusContract';
  import { GoalCreationSuccessBadge } from '../components/GoalCreationSuccessBadge';
  import { DeviceSyncProgressIndicator } from '../components/DeviceSyncProgressIndicator';
  // Tier D-6 (2026-05-15) — HRZoneEducationModal retired. First-Garmin-sync
  // education now flows through the existing notification surface + the
  // drawer's "What are heart rate zones?" primer. See
  // ~/Desktop/hr-zones-drawer-redesign.md (RETIRE section).
// 🚫 TEMPORARILY DISABLED: Goal summarizing UI component
// import GoalSummarizingIndicator from '../components/GoalSummarizingIndicator';
  import { addParagraphBreaks } from '../utils/textFormatting';
  import { getApiBaseUrl } from '../utils/api';
  import { AsyncProcessNotificationPill, AsyncProcessNotification } from '../components/notifications/AsyncProcessNotificationPill';
  import { PlanReadyMessage } from '../components/notifications/PlanReadyMessage';  // 🎉 JAN 5, 2026: Option A plan notification
  import InlinePersonalitySliders from '../components/InlinePersonalitySliders';
import CoachingStyleSaved from '../components/CoachingStyleSaved';
import FileAnalysisComponent from '../components/FileAnalysisComponent';
import ChatHistory from '../components/chat/ChatHistory';
import { DayDivider } from '../components/chat/DayDivider';
import { NewMessagesDivider } from '../components/chat/NewMessagesDivider';
import { ContextualItemBadge } from '../components/ContextualItemBadge';
import { ContextualLaunchPill } from '../components/ContextualLaunchPill';
import { ChatHistoryProcessor } from '../services/chatHistoryProcessor';
import { ChatHistory as ChatHistoryType } from '../types/ChatHistory';

import { NotificationPermissionModal } from '../components/NotificationPermissionModal';
import { PersonalitySliderModal } from '../components/PersonalitySliderModal';
import PlanNotificationComponent from '../components/PlanNotificationComponent';  // 🎯 SPRINT 2: Plan notifications
import { ServiceConnector, ServiceType, PlatformType } from '../components/ServiceConnector';
import NotificationService from '../utils/notifications';
import { getUserStorage, setUserStorage, getCurrentUserId, removeUserStorage } from '../utils/userStorage';
import { storage } from '../services/storage'; // Mission 20: Unified storage service
import { markChatAsRead } from '../services/notificationReadState';
import { useExtractionHealthMonitor } from '../hooks/useExtractionHealthMonitor'; // 🩺 MISSION 24: Real-time extraction health monitoring
import { API_BASE_URL } from '../utils/api'; // Environment-aware API URL
import {
  type MessageReaction,
  type MessageContext,
  analyzeMessageContext,
  shouldCedarReact,
  getContextualEmoji
} from '../utils/messageReactionHelpers';
import { 
  getNotificationBehavior, 
  getNotificationIcon, 
  getNotificationTitle, 
  getBannerMessage,
  shouldShowBanner,
  shouldAutoNavigate
} from '../utils/notificationHelpers'; // 📥 JAN 31, 2026: Notification banner helpers
import { ExternalLink, ArrowDown, ArrowUp } from 'lucide-react'; // 🔬 JAN 25, 2026: For research event deep linking
import { SubscriptionWarningBanner } from '../components/subscription/SubscriptionWarningBanner'; // 💰 Sprint 7: Chat limit warning
import { SubscriptionBlockOverlay } from '../components/subscription/SubscriptionBlockOverlay'; // 💰 Sprint 7: Hard block overlay
import { PaywallModal } from '../components/PaywallModal'; // 💰 Trial gate after Step 6
import { useSubscription } from '../hooks/useSubscription'; // 💰 Trial gate + refresh after purchase
import {
  INTAKE_ACCEPT_ATTRIBUTE,
  INTAKE_ACCEPTED_SUMMARY,
  fetchIntakeFormats,
  isIntakeExtensionAllowed,
  intakeCapMb,
} from '../constants/intakeFormats'; // 📎 Universal Intake — mirrors services/intake_format_registry.py
import { trackIntakeRefusal } from '../services/analyticsEvents';

// 🌊 STREAMING CONFIGURATION - ENABLED FOR REAL-TIME LLM RESPONSES
const ENABLE_STREAMING = true; // Toggle for streaming vs traditional chat
const STREAMING_FALLBACK_TIMEOUT = 30000; // 30 seconds timeout for streaming (with immediate feedback)
const CHAT_REQUEST_TIMEOUT = 150000; // 150 seconds for text messages (increased Mar 2026 — discovery pipeline can take 60-70s)
const FILE_UPLOAD_TIMEOUT = 180000; // 🔧 NEW (Oct 2025) - 180 seconds (3 minutes) for file uploads with generous buffer (backend: 120s + 60s safety margin)

// 🎯 STATE MACHINE: Consolidated Chat State Management
// =====================================================

type ChatPhase = 
  | 'initializing'     // Loading user data and determining entry point
  | 'tour'            // Frontend tour in progress  
  | 'onboarding'      // LLM-driven onboarding conversation
  | 'active'          // Normal chat functionality

interface ChatUser {
  id: string;
  name: string;
  tourCompleted: boolean;
  onboardingCompleted: boolean;
}

interface ScrollState {
  isUserAtBottom: boolean;
  shouldAutoScroll: boolean;
  isInitialLoad: boolean;
}

interface ChatState {
  phase: ChatPhase;
  messages: ChatMessage[];
  user: ChatUser | null;
  ui: {
    isTyping: boolean;
    typingMessageId: string | null;
    chatPaused: boolean;
    scrollState: ScrollState;
    hasStartedInitialMessage: boolean;
    onboardingTriggered: boolean;
  };
  // 🚨 NEW: Backend State Synchronization
  backendState?: {
    onboardingStep: number;
    onboardingCompleted: boolean;
    chatName?: string;
    detectedScenario?: string;
    lastSyncedAt: string;
    // 💰 Mission 33 Sprint 7: Subscription enforcement metadata
    subscription?: {
      tier: string;         // 'trial' | 'basic' | 'full' | 'extended'
      remaining: number;    // messages remaining this week
      limit: number;        // total weekly limit
      warning_level: string | null;  // 'approaching' | 'critical' | null
      degraded_mode?: boolean;       // true when paid user at limit (gpt-5-mini)
      post_trial_phase?: 'selling' | 'lite' | 'blocked' | null;
      week_end_date?: string;        // ISO date string for limit reset
      is_debrief?: boolean;          // true when current message is debrief-exempt
      // 📋 Mission 33: Touchpoint state (Essentials composer + all tiers during touchpoints)
      active_touchpoint_id?: string | null;
      touchpoint_type?: string | null;       // 'morning_checkin' | 'debrief' | 'weekly_checkin' | 'recalibration'
      touchpoint_remaining?: number | null;  // Cedar responses remaining
      composer_deactivated?: boolean;         // true when Essentials user outside active touchpoint
    };
    subscription_blocked?: boolean;  // true when hard-blocked by enforcement
    block_type?: string;             // 'hard_limit' | 'post_trial_block' etc.
  };
  contextualEntry?: {
    from: string;
    contextData: any;
    initialMessage: string;
  };
  // 🚀 AUTO-DISCOVERY TRIGGER: Prevent multiple discovery triggers
  autoDiscoveryTriggered?: boolean;
}

interface StateTransition {
  type: 'INITIALIZE' | 'START_TOUR' | 'COMPLETE_TOUR' | 'START_ONBOARDING' | 'COMPLETE_ONBOARDING' | 'ACTIVATE_CHAT' | 'ADD_MESSAGE' | 'UPDATE_UI' | 'HANDLE_CONTEXTUAL_ENTRY' | 'SYNC_BACKEND_STATE' | 'REMOVE_MESSAGE' | 'ADD_REACTION' | 'CLEAR_PERSONALITY_SLIDERS_SUBMITTED' | 'SET_TYPING';
  payload?: any;
}

// 🚀 AUTO-MESSAGE POLLING: Poll for auto-follow-up messages
async function pollAutoMessages(userId: string, since?: string): Promise<any[]> {
  try {
    const jwtToken = await storage.getItem('jwt_token');
    const headers: Record<string, string> = {};
    
    if (jwtToken) {
      headers['Authorization'] = `Bearer ${jwtToken}`;
    }
    
    const url = since 
      ? `${API_BASE_URL}/api/auto-messages/${userId}?since=${encodeURIComponent(since)}`
      : `${API_BASE_URL}/api/auto-messages/${userId}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      console.error('❌ Auto-message poll failed:', response.status);
      return [];
    }
    
    const data = await response.json();
    return data.messages || [];
  } catch (error) {
    console.error('❌ Auto-message polling error:', error);
    return [];
  }
}

// 🚨 SYNCHRONOUS PROCESSING: Direct API call with immediate response
async function getCedarResponse(userMessage: string, attachments: any[], options: { 
  allowBackground?: boolean, 
  messageId?: string,
  abortSignal?: AbortSignal 
} = {}): Promise<{ text: string }> {
  const jwtToken = await storage.getItem('jwt_token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };
  
  if (jwtToken) {
    headers['Authorization'] = `Bearer ${jwtToken}`;
  }

  // 🚀 BACKGROUND PROCESSING: Enable only after onboarding completion
  // During onboarding, we need immediate chunking and state management
  let isOnboardingCompleted = false;
  const userId = getCurrentUserId();
  if (userId) {
    const chatStateRaw = await storage.getItem(`chatState_${userId}`);
    if (chatStateRaw) {
      try {
        const parsed = JSON.parse(chatStateRaw);
        isOnboardingCompleted = parsed.backendState?.onboardingCompleted || false;
      } catch {
        // ignore parse errors
      }
    }
  }
  const requestBody = { 
    message: userMessage,
    allow_background_processing: options.allowBackground !== false && isOnboardingCompleted, // Only enable after onboarding
    frontend_message_id: options.messageId || `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  };

  // 🚨 CORS DEBUG: Enhanced logging
  console.log('🔍 CORS DEBUG: Making request to:', `${API_BASE_URL}/api/chat`);
  console.log('🔍 CORS DEBUG: Current origin:', Platform.OS === 'web' ? window.location.origin : 'mobile-app');
  console.log('🔍 CORS DEBUG: Headers:', headers);
  console.log('🔍 CORS DEBUG: Request body size:', JSON.stringify(requestBody).length);
  
  const response = await fetch(`${API_BASE_URL}/api/chat`, {
    method: 'POST',
    headers,
    body: JSON.stringify(requestBody),
    signal: options.abortSignal
  }).catch(error => {
    console.error('🚨 CORS DEBUG: Fetch failed:', error);
    console.error('🚨 CORS DEBUG: Error type:', error.constructor.name);
    console.error('🚨 CORS DEBUG: Error message:', error.message);
    console.error('🚨 CORS DEBUG: Network online:', navigator.onLine);
    console.error('🚨 CORS DEBUG: Timestamp:', new Date().toISOString());
    throw error;
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  return { text: data.text || data.response || 'I apologize, but I received an empty response. Could you please try again?' };
}

// 🌊 SSE EVENT PARSER: Parse Server-Sent Events from streaming response
function parseSSEEvents(buffer: string): { 
  parsed: Array<{type: string, data: string}>, 
  remaining: string 
} {
  const events: Array<{type: string, data: string}> = [];
  const lines = buffer.split('\n');
  
  let currentEvent = { type: 'message', data: '' };
  let completeLines: number[] = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    if (line.startsWith('event: ')) {
      currentEvent.type = line.substring(7).trim();
    } else if (line.startsWith('data: ')) {
      currentEvent.data = line.substring(6);
    } else if (line === '' && currentEvent.data) {
      events.push({...currentEvent});
      currentEvent = { type: 'message', data: '' };
      completeLines.push(i);
    }
  }
  
  // Return unparsed remainder
  const lastComplete = completeLines.length > 0 ? completeLines[completeLines.length - 1] + 1 : 0;
  const remaining = lines.slice(lastComplete).join('\n');
  
  return { parsed: events, remaining };
}

// 🔧 FEB 11, 2026: Module-level parseMessageActions (must be accessible from sendStreamingMessage)
// This is a pure function (regex parsing only, no hooks/state) so it's safe at module level.
// The component-level wrapper at line ~7415 delegates to this.
//
// Apr 2026 — LOAD_AWARE_ADAPTIVE_COACHING P0: action strings may carry
// query-string metadata like `action:revert_modality_4?ref=abc&style=subtle`.
// We parse those into the action object's `ref` and `style` fields so the
// renderer can apply the subtle variant (white bg, green text) called out
// in sprint §8.29.
function parseMessageActionsModule(text: string): { cleanText: string; actions?: Array<{type: string; label: string; id: string; sourceId?: string; ref?: string; style?: string}> } {
  const actionRegex = /\[([^\]]+)\]\(action:([^)]+)\)/g;
  const sourceRegex = /\[Source\]\(source:([^)]+)\)/g;
  const actions: Array<{type: string; label: string; id: string; sourceId?: string; ref?: string; style?: string}> = [];
  let cleanText = text;
  let match;

  // Parse action buttons (with optional ?ref=...&style=... query string)
  while ((match = actionRegex.exec(text)) !== null) {
    const [fullMatch, label, actionBlob] = match;
    const [actionType, queryStr] = actionBlob.split('?', 2);
    const params = new Map<string, string>();
    if (queryStr) {
      for (const pair of queryStr.split('&')) {
        const [k, v] = pair.split('=', 2);
        if (k) params.set(k, decodeURIComponent(v || ''));
      }
    }
    actions.push({
      type: actionType,
      label,
      id: `action-${Date.now()}-${Math.random()}`,
      ref: params.get('ref'),
      style: params.get('style'),
    });
    cleanText = cleanText.replace(fullMatch, '');
  }

  // Parse source buttons
  while ((match = sourceRegex.exec(text)) !== null) {
    const [fullMatch, sourceId] = match;
    actions.push({
      type: 'view_source',
      label: 'Source',
      id: `source-${Date.now()}-${Math.random()}`,
      sourceId
    });
    cleanText = cleanText.replace(fullMatch, '');
  }

  // Only remove punctuation immediately after action links
  let finalCleanText = cleanText
    .replace(/(\]\([^)]+\))[\.!?]+/g, '$1')
    .trim();

  return { cleanText: finalCleanText, actions: actions.length > 0 ? actions : undefined };
}

// 🌊 STREAMING RESPONSE: Stream LLM response in real-time
async function sendStreamingMessage(
  userMessage: string,
  attachments: any[],
  transition: any,
  setBackendState: any,
  setIsStreaming: any,
  cedarMessageId: string,  // 🎯 FIX: Accept cedar ID from caller (generated early for thinking dots)
  messages: any[],  // 🚀 ISSUE #2: For goal detection
  processedMessageIdsRef: React.MutableRefObject<Set<string>>,  // 🔒 FIX: Deduplication tracker
  setShowPipelineProgress?: (show: boolean) => void,  // 🚀 ISSUE #2: Pipeline progress state
  setPipelineGoalText?: (text: string) => void,
  setPipelineGoalDetails?: (details: string) => void,
  setPipelineGoalId?: (id: string) => void,  // 🚀 ISSUE #2 FIX: Missing parameter for goal ID
  chatName?: string,  // 🐛 FIX (Jan 31, 2026): Pass chatName for progress message metadata
  logExtraction?: (messageId: string) => Promise<any>  // 🩺 MISSION 24: Extraction health monitoring
): Promise<void> {
  // 🔧 BUG #76 FIX (Feb 4, 2026): Use static import (line 48), not dynamic import
  // PROBLEM: Dynamic import failed in production - storage.js returned HTML instead of module
  // SOLUTION: Storage already imported at top of file, just use it directly
  const jwtToken = await storage.getItem('jwt_token');
  
  // STEP 1: Cedar message ID already generated by caller (for thinking dots placeholder)
  console.log(`🎯 STREAMING: Reusing Cedar message ID from thinking dots placeholder: ${cedarMessageId}`);
  
  // STEP 2: Prepare request
  let isOnboardingCompleted = false;
  const currentUserId = getCurrentUserId();
  if (currentUserId) {
    const chatStateRaw = await storage.getItem(`chatState_${currentUserId}`);
    if (chatStateRaw) {
      try {
        const parsed = JSON.parse(chatStateRaw);
        isOnboardingCompleted = parsed.backendState?.onboardingCompleted || false;
      } catch {
        // ignore
      }
    }
  }
  
  // 🔍 RETRIEVE CONTEXTUAL SESSION (Feb 4, 2026): For BOTH FormData and JSON paths
  console.log('🔍 CONTEXTUAL SESSION: Checking for active session...');
  const contextualSessionJson = await storage.getItem('contextual_session');
  console.log('🔍 CONTEXTUAL SESSION: Retrieved from storage:', contextualSessionJson ? 'YES' : 'NO');
  
  let contextualSession = null;
  if (contextualSessionJson) {
    try {
      contextualSession = JSON.parse(contextualSessionJson);
      console.log('✅ CONTEXTUAL SESSION: Parsed successfully');
      console.log('   Type:', contextualSession.type);
      console.log('   Title:', contextualSession.title);
      console.log('   Conversation ID:', contextualSession.conversation_id);
    } catch (parseError) {
      console.error('❌ CONTEXTUAL SESSION: Failed to parse JSON:', parseError);
    }
  }
  
  const requestBody = attachments.length > 0
    ? (() => {
        const formData = new FormData();
        formData.append('message', userMessage);
        formData.append('cedar_message_id', cedarMessageId);
        formData.append('allow_background_processing', String(isOnboardingCompleted));
        attachments.forEach((file, index) => {
          formData.append(`file_${index}`, file);
        });
        
        // Include contextual session if available
        if (contextualSession) {
          formData.append('contextual_launch', JSON.stringify(contextualSession));
          if (contextualSession.conversation_id) {
            formData.append('conversation_id', contextualSession.conversation_id);
          }
          console.log('✅ CONTEXTUAL SESSION: Appended to FormData');
        }
        
        return formData;
      })()
    : JSON.stringify({
        message: userMessage,
        cedar_message_id: cedarMessageId,
        allow_background_processing: isOnboardingCompleted,
        ...(contextualSession ? {
          contextual_launch: contextualSession,
          conversation_id: contextualSession.conversation_id
        } : {})
      });
  
  // 🔍 DEBUG REQUEST BODY (Feb 4, 2026)
  if (typeof requestBody === 'string') {
    const bodyObj = JSON.parse(requestBody);
    console.log('📦 REQUEST BODY (JSON):');
    console.log('   message:', bodyObj.message?.substring(0, 50));
    console.log('   contextual_launch present:', !!bodyObj.contextual_launch);
    console.log('   conversation_id present:', !!bodyObj.conversation_id);
    if (bodyObj.contextual_launch) {
      console.log('   contextual_launch.type:', bodyObj.contextual_launch.type);
      console.log('   contextual_launch.title:', bodyObj.contextual_launch.title);
    }
  } else {
    console.log('📦 REQUEST BODY (FormData): attachments =', attachments.length);
  }
  
  const headers: Record<string, string> = {
    'Accept': 'text/event-stream'
  };
  
  if (jwtToken) {
    headers['Authorization'] = `Bearer ${jwtToken}`;
  }
  
  // Only add Content-Type for JSON (FormData sets its own)
  if (typeof requestBody === 'string') {
    headers['Content-Type'] = 'application/json';
  }
  
  // 📡 FRONTEND REQUEST MONITORING
  const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
  console.log(`📡 STREAM REQUEST [${requestId}]:`);
  console.log(`   Message: "${userMessage.substring(0, 50)}..."`);
  console.log(`   Cedar msg ID: ${cedarMessageId}`);
  console.log(`   Time: ${new Date().toISOString()}`);
  console.log(`🌊 STREAMING: Making streaming request to /api/chat`);
  
  // STEP 3: Make streaming request
  // 🔧 M35B FIX: Pass AbortSignal so navigating away aborts the fetch,
  // which lets the backend detect the closed connection immediately and
  // release the per-user streaming lock instead of holding it until timeout.
  const response = await fetch(`${API_BASE_URL}/api/chat`, {
    method: 'POST',
    headers,
    body: requestBody,
    signal: abortControllerRef.current?.signal
  });
  
  console.log(`📡 RESPONSE [${requestId}]: Status ${response.status}`);
  
  const contentType = response.headers.get('content-type');
  console.log(`🌊 STREAMING DEBUG: Response status=${response.status}, content-type=${contentType}`);
  
  // 🚨 CRITICAL CHECK: Verify we actually got a streaming response
  if (contentType && !contentType.includes('text/event-stream')) {
    console.error(`🚨 STREAMING BUG: Backend returned ${contentType} instead of text/event-stream!`);
    console.error(`   This means streaming failed on backend and returned JSON instead`);
    throw new Error(`Expected text/event-stream but got ${contentType}`);
  }
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  if (!response.body) {
    console.error(`🚨 STREAMING BUG: Response body is null despite status ${response.status}`);
    throw new Error('Response body is null');
  }
  
  console.log(`✅ STREAMING: Valid SSE response received, creating reader...`);
  
  // STEP 4: Process stream
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  
  // 🔧 FIX: Get existing message from state to preserve 'thinking' status
  // The placeholder was created with 'thinking' status - we need to preserve that
  const existingMessage = messages.find((msg: any) => msg.id === cedarMessageId);
  
  let currentMessage: any = existingMessage ? {
    ...existingMessage,  // Preserve all existing properties (including 'thinking' status)
    isStreaming: true,   // Mark as streaming
    isAnimated: true    // Enable animation when content arrives
  } : {
    id: cedarMessageId,
    sender: 'cedar',
    text: '',
    timestamp: '',  // Will be set when first chunk arrives
    status: 'thinking',  // Keep as 'thinking' until first text chunk arrives
    isStreaming: true,
    isAnimated: true,
    metadata: null
  };
  
  // 🎯 FIX: DON'T update the message yet - keep the thinking dots visible!
  // The placeholder already exists with 'thinking' status - don't touch it until first chunk arrives
  // The flushTextBuffer function will:
  //   1. Add the first text chunk
  //   2. Transition from 'thinking' to 'typing'
  //   3. Add timestamp when content actually arrives
  // This ensures thinking dots are visible until Cedar actually starts responding
  console.log(`🎯 STREAMING: Keeping placeholder bubble ${cedarMessageId} in 'thinking' status - will update when first chunk arrives`);
  
  let eventBuffer = '';
  let hasReceivedMetadata = false;
  let hasReceivedFirstChunk = false; // 🔧 FIX Issue #1: Track first chunk to clear typing indicator
  let pendingChunkNumber: number | null = null; // 🎯 NEW: Track when we're waiting for next chunk content
  let metadataReceivedAt = 0; // 🔧 FIX: Track when metadata arrives to measure pause
  let currentChunkIndex = 0; // 🎬 Track chunk index for staggered animation
  
  // 🎬 ANIMATION FIX (Nov 25, 2025): Periodic flush interval for smooth animation
  // 
  // PROBLEM: Previous approach (flush on chunk arrival) doesn't work because:
  // - When chunks arrive in bursts after a pause, React batches all state updates
  // - Result: "Perfect" animates, pause, then entire rest appears at once
  //
  // SOLUTION: Use a PERIODIC INTERVAL that flushes the buffer every 50ms
  // - Runs independently of chunk arrival timing
  // - Ensures UI updates at regular intervals even during burst arrivals
  // - Each flush gives TypingText ~2-3 chars to animate (50ms at typical LLM speed)
  //
  let textChunkBuffer = '';  // Accumulates text chunks before updating state
  let flushIntervalId: ReturnType<typeof setInterval> | null = null;  // Periodic flush interval
  
  // 🔧 JAN 23, 2026: Paragraph breaks during streaming — now shared via textFormatting.ts
  // Import: addParagraphBreaks from '../utils/textFormatting'
  
  // Helper: Flush buffered text to state
  const flushTextBuffer = (reason?: string) => {
    if (textChunkBuffer.length === 0) return;
    
    // 🎬 FIX (Dec 4, 2025): Transition from 'thinking' to 'typing' when first content arrives
    const wasThinking = currentMessage.status === 'thinking';
    const isFirstContent = currentMessage.text === '';
    
    // 🔧 JAN 23, 2026: Add paragraph breaks progressively during streaming
    currentMessage.text += textChunkBuffer;
    currentMessage.text = addParagraphBreaks(currentMessage.text);
    
    // Update status if this is the first content for a thinking bubble
    if (wasThinking && isFirstContent && String(currentMessage.text || '').length > 0) {
      currentMessage.status = 'typing';
      currentMessage.isStreaming = true;
      currentMessage.isAnimated = true;
      // 🎯 ADD TIMESTAMP NOW: When Cedar actually starts responding (first chunk arrives)
      if (!currentMessage.timestamp || currentMessage.timestamp === '') {
        currentMessage.timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
      console.log(`🎬 FLUSH: Transitioned message from 'thinking' to 'typing' with first ${String(currentMessage.text || '').length} chars`);
    }
    
    const flushReason = reason || 'manual';
    if (textChunkBuffer.length >= 4) {
      console.log(`🎬 FLUSH [${flushReason}]: ${textChunkBuffer.length} chars (total: ${String(currentMessage.text || '').length})`);
    }
    textChunkBuffer = '';
    transition({ type: 'ADD_MESSAGE', payload: { message: currentMessage } });
  };
  
  // 🎬 START periodic flush interval (50ms = smooth animation, not too chatty)
  flushIntervalId = setInterval(() => {
    if (textChunkBuffer.length > 0) {
      flushTextBuffer('interval');
    }
  }, 50);
  
  // STEP 5: Read and process stream
  console.log(`🌊 STREAMING: Starting reader.read() loop for message ${cedarMessageId}`);
  let readCount = 0;
  while (true) {
    const { done, value } = await reader.read();
    readCount++;
    console.log(`🌊 STREAMING: reader.read() iteration ${readCount} - done=${done}, value=${value ? value.length : 0} bytes`);
    if (done) {
      console.log(`🌊 STREAMING: Stream ended after ${readCount} reads`);
      if (readCount === 1) {
        console.error(`🚨 STREAMING BUG: Stream ended on first read - no data received!`);
      }
      // 🎬 ANIMATION FIX: Stop interval and flush remaining text
      if (flushIntervalId) {
        clearInterval(flushIntervalId);
        flushIntervalId = null;
      }
      flushTextBuffer('stream_end');
      console.log(`🌊 STREAMING: Stream complete`);
      
      // 🔧 SAFEGUARD FIX (Dec 2024): Ensure typing state is cleared and message is finalized even if done event is missing
      // This prevents send button from staying disabled after streaming completes
      if (currentMessage.status !== 'delivered') {
        currentMessage.status = 'delivered';
        currentMessage.isStreaming = false;
        currentMessage.text = String(currentMessage.text || '').trim();
        transition({ type: 'ADD_MESSAGE', payload: { message: currentMessage } });
        console.log(`🔧 SAFEGUARD: Finalized message as delivered`);
      }
      setIsStreaming(false);
      transition({ type: 'UPDATE_UI', payload: { isTyping: false, typingMessageId: null } });
      console.log(`🔧 SAFEGUARD: Cleared typing state after stream complete`);
      
      break;
    }
    
    const chunk = decoder.decode(value, { stream: true });
    console.log(`🌊 STREAMING DEBUG: Received ${chunk.length} bytes: ${chunk.substring(0, 200)}`);
    eventBuffer += chunk;
    
    const events = parseSSEEvents(eventBuffer);
    console.log(`🌊 STREAMING DEBUG: Parsed ${events.parsed.length} events, remaining buffer: ${events.remaining.length} bytes`);
    eventBuffer = events.remaining;
    
    // 🎬 ANIMATION FIX (Nov 24, 2025): Buffer chunks for smooth typing animation
    // 
    // PROBLEM: SSE sends single characters ("P", "e", "r", "f", "e", "c", "t"...)
    //          Updating React state on EVERY char means TypingText only gets 1 char to animate
    //          Result: Text appears instantly (no visible typing effect)
    //
    // SOLUTION: Buffer characters and only update state every 80ms OR when buffer has 10+ chars
    //           This gives TypingText larger chunks to animate smoothly
    //
    // Example: Instead of 10 state updates with 1 char each,
    //          we get 1 state update with "Perfect - t" (10 chars)
    //          TypingText animates 10 chars at 30ms/char = 300ms visible animation
    
    for (let i = 0; i < events.parsed.length; i++) {
      const event = events.parsed[i];
      
      if (event.type === 'metadata') {
        // 🔧 FIX: Track metadata arrival time to measure pause to first chunk
        metadataReceivedAt = Date.now();
        console.log(`⏱️ TIMING: Metadata received at ${metadataReceivedAt} (timestamp will appear now)`);
        
        // 🌊 MISSION 18 FIX: DON'T clear typing state here!
        // Stream of consciousness should remain visible until first chunk arrives
        // The typing state will be cleared when first chunk arrives (lines 375-379)
        // This allows stream thoughts to display alongside thinking dots until message starts
        // transition({ type: 'UPDATE_UI', payload: { isTyping: false, typingMessageId: null } }); // REMOVED - too early!
        // console.log('🔧 FIX Issue #1: Cleared typing indicator (both flags) on metadata'); // REMOVED
        
        // Store metadata
        const metadata = JSON.parse(event.data);
        currentMessage.metadata = metadata;
        // 💰 HARD BLOCK: Backend sends `blocked: true` at the TOP LEVEL of the metadata
        // event (not inside metadata.backend_state). Merge it into backend_state so that
        // `chatState.backendState.subscription_blocked` becomes true and
        // SubscriptionBlockOverlay renders instead of the chat UI.
        const enrichedBackendState = metadata.backend_state
          ? {
              ...metadata.backend_state,
              subscription_blocked:
                metadata.blocked === true ? true : (metadata.backend_state.subscription_blocked ?? false),
            }
          : metadata.blocked === true
          ? { subscription_blocked: true }
          : undefined;
        setBackendState(enrichedBackendState);
        
        // 📡 METADATA TRACKING
        const backendStep = metadata.backend_state?.onboardingStep;
        console.log(`📡 METADATA [${requestId}]: Backend Step ${backendStep}`);
        if (backendStep === 1 || backendStep === 0) {
          console.warn(`   ⚠️ Backend at Step ${backendStep} - check if this is expected`);
        }
        
        // 🎛️ CHECK FOR PERSONALITY SLIDERS in streaming path
        if (metadata.backend_state?.personality_slider_action === true) {
          console.log('🎛️ STREAMING: personality_slider_action detected - will trigger sliders after message completes');
          // Store flag to trigger sliders after stream completes
          currentMessage.triggerPersonalitySliders = true;
        }
        
        // 📱 CHECK FOR SERVICE ACTION (Dec 6, 2025) - Device sync connector
        if (metadata.serviceAction) {
          console.log('📱 STREAMING: serviceAction detected in metadata:', metadata.serviceAction);
          // Store service action to attach to final message
          currentMessage.serviceAction = {
            type: 'service_connection',
            service: metadata.serviceAction.service as ServiceType,
            platform: (metadata.serviceAction.platform || 'auto') as PlatformType
          };
          console.log(`📱 STREAMING: Will show ${metadata.serviceAction.service} connector after message`);
        }
        
        hasReceivedMetadata = true;
        
      } else if (event.type === 'chunk') {
        const chunkData = JSON.parse(event.data);
        
        let justCreatedNewBubble = false; // 🔧 FIX: Track if we just created a new bubble
        
        // 🎯 FIX: If we have a pending chunk number, UPDATE the thinking bubble with content
        // 🔧 FIX (Dec 4, 2025): Only transition if currentMessage matches the expected chunk
        // This prevents the bug where CHUNK_2 boundary arrives before CHUNK_1 content,
        // causing CHUNK_1 content to incorrectly go to CHUNK_2's bubble
        const expectedChunkId = pendingChunkNumber !== null ? `${cedarMessageId}_chunk${pendingChunkNumber}` : null;
        const isCorrectChunk = expectedChunkId && currentMessage.id === expectedChunkId;
        
        if (pendingChunkNumber !== null && isCorrectChunk && currentMessage.text === '') {
          // 🔧 FIX: Measure pause from metadata to first chunk
          if (metadataReceivedAt > 0) {
            const pauseDuration = Date.now() - metadataReceivedAt;
            console.log(`⏱️ TIMING: Pause from metadata to first chunk: ${pauseDuration}ms (${(pauseDuration/1000).toFixed(2)}s)`);
            metadataReceivedAt = 0; // Reset after first measurement
          }
          
          console.log(`🎯 CHUNK FIX: First content arrived for CHUNK_${pendingChunkNumber} - transitioning thinking → typing`);
          
          // 🎬 ANIMATION FIX: Two-step transition to make animation visible
          // Problem: Text arrives faster (100-200ms) than animation speed (30ms/char * 183 chars = 5.5s)
          // Solution: Let TypingText mount with initial text, THEN let subsequent chunks append normally
          
          // Update the existing thinking bubble with first chunk
          currentMessage.text = chunkData.text; // Add first chunk content
          currentMessage.status = 'typing'; // Change from 'thinking' to 'typing'
          currentMessage.isStreaming = true;
          currentMessage.isAnimated = true; // 🎬 FIX (Dec 4, 2025): Ensure animation flag is set
          transition({ type: 'ADD_MESSAGE', payload: { message: currentMessage } });
          console.log(`✅ CHUNK FIX: Transitioned CHUNK_${pendingChunkNumber} bubble from thinking → typing (${chunkData.text.length} chars)`);
          
          pendingChunkNumber = null; // Reset
          
        } else if (chunkData.chunk_id) {
          // Multi-bubble: Create separate messages for CHUNK_2+
          if (chunkData.chunk_id > 1) {
            // 🎬 FIX (Dec 4, 2025): Use 'typing' status for animation, not 'delivered'
            // Previous bug: status='delivered' caused instant text reveal (no animation)
            currentMessage = {
              id: `${cedarMessageId}_chunk${chunkData.chunk_id}`,
              sender: 'cedar',
              text: chunkData.text,
              timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              status: 'typing',  // ✅ FIX: Enable animation
              isStreaming: true,  // ✅ FIX: Mark as streaming for proper animation
              isAnimated: true  // ✅ FIX: Enable shouldAnimate calculation
            };
            transition({ type: 'ADD_MESSAGE', payload: { message: currentMessage } });
            console.log(`🌊 STREAMING: Created new chunk message ${chunkData.chunk_id} (with typing animation)`);
          } else {
            currentMessage.text = chunkData.text;
          }
        } else {
          // 🎬 ANIMATION FIX: Buffer text instead of direct append
          // This allows chunks to accumulate before updating state
          textChunkBuffer += chunkData.text;
        }
        
        // 🔧 FIX Issue #4: BACKUP clear of typing dots on first chunk of text
        // Ensures dots are cleared even if chunk_boundary event has timing issues
        const totalTextLength = String(currentMessage.text || '').length + textChunkBuffer.length;
        if (!hasReceivedFirstChunk && totalTextLength > 0) {
          // 🔧 FIX: Measure pause from metadata to first chunk (for regular streaming, not multi-bubble)
          if (metadataReceivedAt > 0) {
            const pauseDuration = Date.now() - metadataReceivedAt;
            console.log(`⏱️ TIMING: Pause from metadata to first chunk: ${pauseDuration}ms (${(pauseDuration/1000).toFixed(2)}s)`);
            metadataReceivedAt = 0; // Reset after first measurement
          }
          
          hasReceivedFirstChunk = true;
          transition({ type: 'UPDATE_UI', payload: { isTyping: false, typingMessageId: null } });
          console.log(`🔧 FIX Issue #4: BACKUP typing dots clear on first text chunk (${totalTextLength} chars)`);
        }
        
        // 🎬 ANIMATION FIX (Nov 25, 2025): No synchronous flush here!
        // The periodic interval (50ms) handles flushing independently
        // This ensures smooth animation even when chunks arrive in bursts
        
      } else if (event.type === 'chunk_boundary') {
        // 🎬 ANIMATION FIX: Flush any remaining buffered text before handling boundary
        flushTextBuffer('boundary');
        
        // Handle chunk boundary (prepare for next chunk)
        const boundaryData = JSON.parse(event.data);
        console.log(`🎯 STREAMING: Chunk boundary detected - CHUNK_${boundaryData.chunk_number} boundary`);
        
        // 🔧 FIX ISSUES #1 & #2: When CHUNK_1 arrives, REMOVE the initial placeholder message entirely
        if (boundaryData.chunk_number === 1) {
          // The initial message (id=cedarMessageId) was created with status='typing' and empty text
          // Instead of finalizing it (which creates an empty visible bubble), DELETE it from state
          // This prevents the empty bubble flash that users see before chunks start
          transition({ type: 'REMOVE_MESSAGE', payload: { messageId: cedarMessageId } });
          console.log(`🔧 FIX Issues #1 & #2: Removed initial placeholder message (${cedarMessageId}) - no empty bubble`);
          
          // Clear typing indicator
          hasReceivedFirstChunk = true;
          transition({ type: 'UPDATE_UI', payload: { isTyping: false, typingMessageId: null } });
          console.log(`🔧 FIX Issue #1: Cleared typing dots on CHUNK_1 boundary`);
        } else {
          // For CHUNK_2+: Finalize previous chunk and prepare for next
          // 
          // 🎬 SEQUENTIAL ANIMATION (Dec 5, 2025): Backend now handles the delay!
          // The backend sleeps 2 seconds after sending CHUNK_2 boundary BEFORE sending
          // CHUNK_2 content. This means by the time we receive CHUNK_2 boundary, 
          // CHUNK_1 content is already done streaming and animating.
          //
          // Frontend just needs to finalize CHUNK_1 and create CHUNK_2 bubble.
          
          console.log(`🎬 SEQUENTIAL: CHUNK_${boundaryData.chunk_number} boundary arrived`);
          console.log(`   Previous chunk (CHUNK_${boundaryData.chunk_number - 1}) has ${String(currentMessage.text || '').length} chars`);
          
          const trimmedText = String(currentMessage.text || '').trim();
          
          if (trimmedText) {
            // 🔧 FEB 4, 2026: Strip chunk markers from multi-bubble responses
            const cleanText = trimmedText
              .replace(/CHUNK_\d+:\s*/gi, '')
              .replace(/\[CHUNK\s+\d+\]\s*/gi, '')
              .replace(/---\s*CHUNK\s+\d+\s*---\s*/gi, '')
              .trim();
            
            currentMessage.text = cleanText;
            currentMessage.status = 'delivered';
            currentMessage.isStreaming = false;
            transition({ type: 'ADD_MESSAGE', payload: { message: currentMessage } });
            
            // 🔒 FIX (Dec 1, 2025): Add chunk message to persistent tracker
            processedMessageIdsRef.current.add(currentMessage.id);
            console.log(`✅ STREAMING: Finalized CHUNK_${boundaryData.chunk_number - 1} (${cleanText.length} chars)`);
          } else {
            // Previous chunk has no content - remove empty bubble
            console.log(`⚠️ STREAMING: CHUNK_${boundaryData.chunk_number - 1} had no content - removing empty bubble`);
            transition({ type: 'REMOVE_MESSAGE', payload: { messageId: currentMessage.id } });
          }
        }
        
        // 🎯 FIX: Create thinking bubble for next chunk (shows dots while waiting)
        pendingChunkNumber = boundaryData.chunk_number;
        currentChunkIndex = boundaryData.chunk_number;
        currentMessage = {
          id: `${cedarMessageId}_chunk${pendingChunkNumber}`,
          sender: 'cedar',
          text: '',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          status: 'thinking',
          isStreaming: false,
          isAnimated: true,
          chunkIndex: currentChunkIndex
        };
        transition({ type: 'ADD_MESSAGE', payload: { message: currentMessage } });
        console.log(`🎯 CHUNK FIX: Created thinking bubble for CHUNK_${boundaryData.chunk_number}`)
        
      } else if (event.type === 'pause') {
        // Pause between chunks
        const pauseData = JSON.parse(event.data);
        console.log(`🌊 STREAMING: Pausing for ${pauseData.duration}ms`);
        await new Promise(resolve => setTimeout(resolve, pauseData.duration));
        
      } else if (event.type === 'done') {
        // 🎬 ANIMATION FIX: Stop interval and flush remaining text
        if (flushIntervalId) {
          clearInterval(flushIntervalId);
          flushIntervalId = null;
        }
        flushTextBuffer('done_event');
        
        // 📝 STREAMING FORMATTING FIX (Jan 30, 2025): Update message with formatted text
        // 🔧 JAN 23, 2026: Paragraph breaks now added DURING streaming (addParagraphBreaks)
        // So we DON'T need to replace text here - just trim and finalize
        try {
          const doneData = JSON.parse(event.data);
          // Check if this is a chunk (ID contains "_chunk")
          const isChunkMessage = currentMessage.id.includes('_chunk');
          
          if (isChunkMessage) {
            // Multi-chunk response: Keep streamed text, strip chunk markers, then trim
            // 🔧 FEB 4, 2026: Strip chunk markers if LLM included them
            currentMessage.text = currentMessage.text
              .replace(/CHUNK_\d+:\s*/gi, '')
              .replace(/\[CHUNK\s+\d+\]\s*/gi, '')
              .replace(/---\s*CHUNK\s+\d+\s*---\s*/gi, '')
              .trim();
            console.log(`🔒 MULTI-CHUNK FIX: Keeping streamed text for chunk ${currentMessage.id} (${String(currentMessage.text || '').length} chars)`);
            console.log(`   NOT replacing with formatted_text (would duplicate chunks)`);
          } else {
            // 🔧 JAN 23, 2026: Keep progressively formatted text (no replacement)
            // Paragraph breaks were already added during streaming via addParagraphBreaks()
            // 🔧 FEB 4, 2026: Strip chunk markers if LLM included them
            currentMessage.text = currentMessage.text
              .replace(/CHUNK_\d+:\s*/gi, '')
              .replace(/\[CHUNK\s+\d+\]\s*/gi, '')
              .replace(/---\s*CHUNK\s+\d+\s*---\s*/gi, '')
              .trim();
            console.log(`📝 STREAMING: Keeping progressively formatted text (${String(currentMessage.text || '').length} chars)`);
            console.log(`   Line breaks were added during streaming - no snap to final view`);
          }
        } catch (e) {
          // Fallback if done event doesn't have formatted_text
          currentMessage.text = String(currentMessage.text || '').trim();
          console.log(`⚠️ STREAMING: Could not parse done event data, using trimmed text`);
        }
        
        // 🔧 FIX (Mar 24, 2026): __START_ONBOARDING__ PATH A intentionally
        // yields no text (only advances state). If streaming produced empty
        // text for this trigger, use the standard Step 1 question so the user
        // sees the name prompt instead of a blank bubble.
        if (!String(currentMessage.text || '').trim() && userMessage === '__START_ONBOARDING__') {
          currentMessage.text = "What name would you like me to call you when we chat?";
          console.log('🔧 STREAMING: __START_ONBOARDING__ returned empty — using Step 1 fallback text');
        }

        currentMessage.status = 'delivered';

        // 🔧 FEB 10/11, 2026: Parse action buttons from finalized text
        // LLM generates [Connect Garmin](action:connect_garmin) markdown which Streamdown
        // renders as <a href="action:connect_garmin"> - browsers block non-standard protocols
        // Uses module-level parseMessageActionsModule (sendStreamingMessage is module-level, not component-level)
        const { cleanText: parsedCleanText, actions: parsedActions } = parseMessageActionsModule(currentMessage.text);
        if (parsedActions && parsedActions.length > 0) {
          currentMessage.text = parsedCleanText;
          
          // 🔧 FEB 11, 2026: Auto-fire service connectors instead of rendering as clickable buttons.
          // When LLM outputs [Connect Garmin](action:connect_garmin), the ServiceConnector should
          // render inline immediately (same as metadata.serviceAction path) - NOT as a button the user clicks.
          // Strava removed from frontend (Feb 2026) - only Garmin service actions supported
          const serviceAction = parsedActions.find(a => a.type === 'connect_garmin');
          if (serviceAction) {
            currentMessage.serviceAction = {
              type: 'service_connection',
              service: 'garmin',
              platform: 'auto'
            };
            // Remove service actions from the actions array (they render via ServiceConnector, not buttons)
            const remainingActions = parsedActions.filter(a => a.type !== 'connect_garmin');
            currentMessage.actions = remainingActions.length > 0 ? remainingActions : undefined;
            console.log(`📱 STREAMING DONE: Auto-attached serviceAction for garmin (ServiceConnector will render inline)`);
          } else {
            currentMessage.actions = parsedActions;
          }
          console.log(`🎯 STREAMING DONE: Parsed ${parsedActions.length} action(s) from message text`);
        }
        
        // 🔧 FIX TYPING ANIMATION (Nov 22, 2025): Let TypingText finish naturally
        // TypingText component now handles animation completion gracefully
        // It will continue animating until displayedText === text, even after isStreaming becomes false
        // This prevents the "skip to end" bug while maintaining smooth animation
        
        // Immediately mark as not streaming (TypingText will finish animation)
        currentMessage.isStreaming = false;
        transition({ type: 'ADD_MESSAGE', payload: { message: currentMessage } });
        setIsStreaming(false);
        
        // 🔒 FIX (Dec 1, 2025): Add streaming message to persistent tracker
        // This prevents background poller from picking it up 0.6s later and displaying it AGAIN
        // Root cause: Streaming messages were never added to tracker, so background poller thought they were new
        processedMessageIdsRef.current.add(currentMessage.id);
        console.log(`🔒 STREAMING: Added ${currentMessage.id} to persistent tracker (prevents duplicate from background poller)`);
        
        console.log(`✅ DONE: Message marked complete, animation finishing naturally`);
        console.log(`   Text length: ${String(currentMessage.text || '').length} chars`);
        
        // 🔧 FIX Issue #3: Clear typing indicator when stream completes
        transition({ type: 'UPDATE_UI', payload: { isTyping: false, typingMessageId: null } });
        
        // 🩺 MISSION 24: Log extraction completion for health monitoring
        // This triggers the frontend health indicator after backend extraction finishes
        // 🛡️ FIX (Dec 25, 2025): Wrapped in try-catch to prevent breaking slider flow
        try {
          console.log('🩺 MISSION 24: Logging extraction for message:', currentMessage.id);
          if (typeof logExtraction === 'function') {
            await logExtraction(currentMessage.id);
          } else {
            console.warn('🩺 MISSION 24: logExtraction not available - skipping health monitoring');
          }
        } catch (healthError) {
          console.warn('🩺 MISSION 24: Health monitoring failed (non-blocking):', healthError);
        }
        
        // 🚀 ISSUE #2: Detect goal confirmation and show pipeline progress
        // 🚀 ISSUE #2 FIX: Comprehensive detection for all goal confirmation message variants
        const isGoalConfirmation = 
          currentMessage.text.includes("Perfect! I'm creating your") ||
          currentMessage.text.includes("Perfect! I'm creating a profile") ||
          currentMessage.text.includes("creating your comprehensive goal profile") ||
          currentMessage.text.includes("creating your goal profile") ||
          currentMessage.text.includes("creating a profile for your") ||
          currentMessage.text.includes("goal profile now") ||
          currentMessage.text.includes("I'm building:") ||
          currentMessage.text.includes("I'm building your plan") || // 🚀 FIX (Jan 21): Add Step 2.45 text pattern
          // 🆕 STREAMING FIX: Also check metadata for goal creation flags
          currentMessage.metadata?.goalCreated === true ||
          currentMessage.metadata?.goalCreationTriggered === true;
        
        // 🚀 PIPELINE PROGRESS: Insert goal progress card into chat on goal confirmation.
        // The guard was previously tied to optional legacy setter functions (setShowPipelineProgress etc.)
        // which are no longer needed — the transition() call is the only required action.
        // Fixed (Mar 2026): removed setter guard so the card always fires when isGoalConfirmation is true.
        if (isGoalConfirmation) {
          try {
            console.log('🚀 PIPELINE PROGRESS: Goal confirmation detected - inserting progress card');
            console.log('   Metadata:', currentMessage.metadata);

            // Check both camelCase (goalId) and snake_case (goal_id)
            const goalId = currentMessage.metadata?.goalId || currentMessage.metadata?.goal_id;

            if (goalId) {
              console.log(`🚀 PIPELINE PROGRESS: Goal ID found in metadata: ${goalId}`);

              // 🎯 MAR 11, 2026 FIX: Set pipelineGoalId state to render progress
              // as a persistent fixed strip above the composer instead of inline message.
              // Inline messages scroll out of view and can be lost during state refreshes.
              if (setPipelineGoalId) {
                setPipelineGoalId(goalId);
                console.log('🚀 PIPELINE PROGRESS: Set pipelineGoalId for fixed bottom strip');
              }

              // 🎯 MAR 15, 2026: Populate goal text for the pipeline strip title
              // ("Creating your plan for [goal]"). Extract from last user message.
              if (setPipelineGoalText) {
                const userMsgs = messages.filter((m: any) => m.sender === 'user');
                const lastUserMsg = userMsgs.length > 0 ? userMsgs[userMsgs.length - 1] : null;
                const extractedGoalText = lastUserMsg?.text || '';
                setPipelineGoalText(extractedGoalText);
                console.log('🚀 PIPELINE PROGRESS: Set pipelineGoalText:', extractedGoalText.substring(0, 50));
              }

              // Also insert a lightweight system message as a historical marker
              // (no progress component rendered — that's handled by the fixed strip)
              const userMessages = messages.filter((m: any) => m.sender === 'user');
              const lastUserMessage = userMessages.length > 0 ? userMessages[userMessages.length - 1] : null;
              const goalText = lastUserMessage?.text || 'your goal';

              const progressMessage = {
                id: `progress_${goalId}_${Date.now()}`,
                text: '',
                sender: 'system' as const,
                status: 'delivered' as const,
                timestamp: new Date().toISOString(),
                metadata: {
                  progressIndicator: true,
                  goalId: goalId,
                  goalText: goalText,
                  chatName: chatName
                }
              };

              transition({
                type: 'ADD_MESSAGE',
                payload: { message: progressMessage }
              });

              console.log('🚀 PIPELINE PROGRESS: Inserted progress marker + set fixed strip state');
            } else {
              console.warn('⚠️ PIPELINE PROGRESS: Goal confirmation detected but no goalId/goal_id in metadata');
              console.warn('   Available metadata keys:', Object.keys(currentMessage.metadata || {}));
            }
          } catch (pipelineError: any) {
            // Progress card failure is non-critical — streaming already succeeded
            console.error('⚠️ Pipeline progress card failed (non-critical):', pipelineError.message);
          }
        }
        
        // 🟢 RECALIBRATION PROGRESS STRIP (Mar 2026 / Mission 28)
        // Detect recalibrationTriggered in streaming metadata and inject a system message that
        // renders the RecalibrationProgress component (web equivalent of RecalibrationProgressNative).
        const recalTriggered = currentMessage.metadata?.recalibrationTriggered === true;
        const recalTrackingId = currentMessage.metadata?.recalibrationTrackingId;
        const recalType = currentMessage.metadata?.recalibrationType || 'Updating your plan…';

        if (recalTriggered && recalTrackingId) {
          try {
            // Set state to render the fixed strip above the composer
            setRecalTrackingId(recalTrackingId);
            setRecalType(recalType);
            console.log('🟢 RECALIBRATION: Set recalTrackingId for fixed bottom strip');

            // Also insert a lightweight system message as a historical marker
            const recalProgressMessage = {
              id: `recal_${recalTrackingId}_${Date.now()}`,
              text: '',
              sender: 'system' as const,
              status: 'delivered' as const,
              timestamp: new Date().toISOString(),
              metadata: {
                recalibrationIndicator: true,
                recalibrationTrackingId: recalTrackingId,
                recalibrationType: recalType,
              },
            };
            transition({
              type: 'ADD_MESSAGE',
              payload: { message: recalProgressMessage },
            });
            console.log('🟢 RECALIBRATION: Inserted marker + set fixed strip state');
          } catch (recalErr: any) {
            console.error('⚠️ Recalibration strip failed (non-critical):', recalErr.message);
          }
        }

        // 🔧 FALLBACK: If SSE metadata didn't trigger the pipeline indicator,
        // check if a pipeline started anyway (handles edge cases where metadata
        // event was lost or didn't include goalCreated flag)
        if (!isGoalConfirmation && setPipelineGoalId) {
          try {
            const jwtToken = await storage.getItem('jwt_token');
            const headers: Record<string, string> = {};
            if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
            const pipelineCheck = await fetch(`${API_BASE_URL}/api/pipeline/active`, { headers });
            if (pipelineCheck.ok) {
              const pipelineData = await pipelineCheck.json();
              if (pipelineData.active && pipelineData.goal_id) {
                console.log(`🔧 PIPELINE FALLBACK: Detected active pipeline via polling — goal_id=${pipelineData.goal_id}`);
                setPipelineGoalId(pipelineData.goal_id);
                if (setPipelineGoalText && pipelineData.goal_text) {
                  setPipelineGoalText(pipelineData.goal_text);
                }
              }
            }
          } catch (fallbackErr) {
            // Non-critical — just means indicator won't show
            console.warn('⚠️ PIPELINE FALLBACK: Check failed (non-critical):', fallbackErr);
          }
        }

        console.log(`✅ STREAMING COMPLETE:`, {
          messageId: currentMessage.id.slice(-12),
          status: currentMessage.status,
          textLength: currentMessage.text.length,
          isStreaming: currentMessage.isStreaming,
          isGoalConfirmation
        });
        
        // 🎛️ TRIGGER PERSONALITY SLIDERS if flagged during metadata
        if (currentMessage.triggerPersonalitySliders) {
          console.log('🎛️ STREAMING: Triggering inline personality sliders after stream completion');
          
          // 🔒 DISABLE AUTOSCROLL: Turn off autoscroll when Step 6 sliders appear
          transition({
            type: 'UPDATE_UI',
            payload: {
              scrollState: {
                shouldAutoScroll: false,
                isUserAtBottom: true
              }
            }
          });
          console.log('🔒 SCROLL: Autoscroll disabled for Step 6 personality sliders');
          
          setTimeout(() => {
            // 🔧 FIX: Create slider message instead of setting UI state
            const sliderMessage: ChatMessage = {
              id: `slider_${Date.now()}`,
              sender: 'system',
              text: '', // Empty text - sliders render instead
              timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              status: 'delivered',
              sliderAction: {
                type: 'personality_sliders',
                sliders: [
                  { id: 'energy', label: 'Energy & Warmth', value: 3, min: 1, max: 5 },
                  { id: 'data_depth', label: 'Data Depth', value: 3, min: 1, max: 5 },
                  { id: 'challenge', label: 'Challenge', value: 3, min: 1, max: 5 },
                  { id: 'humor', label: 'Humor', value: 3, min: 1, max: 5 }
                ]
              }
            };
            transition({ type: 'ADD_MESSAGE', payload: { message: sliderMessage } });
            console.log('✅ STREAMING: Created slider message with sliderAction');
          }, 300); // Small delay to let message render
        }
        
      } else if (event.type === 'error') {
        // 🎬 ANIMATION FIX: Stop interval on error
        if (flushIntervalId) {
          clearInterval(flushIntervalId);
          flushIntervalId = null;
        }
        
        const errorData = JSON.parse(event.data);
        
        if (errorData.retry) {
          // Show friendly retry message
          currentMessage.text = errorData.message;
          transition({ type: 'ADD_MESSAGE', payload: { message: currentMessage } });
          console.log(`🌊 STREAMING: Retry ${errorData.retry_delay}ms - ${errorData.message}`);
          // Auto-retry will continue stream
        } else {
          // Manual retry needed
          currentMessage.text = errorData.message;
          currentMessage.status = 'error';
          currentMessage.retryButton = errorData.manual_retry || false;
          transition({ type: 'ADD_MESSAGE', payload: { message: currentMessage } });
          setIsStreaming(false);
          console.log(`🌊 STREAMING: Error - manual retry needed`);
        }
      }
    }
  }
}

// 🔄 BACKGROUND RESPONSE CHECKING: Check for completed responses when user returns
async function checkForCompletedBackgroundResponses(userId: string): Promise<{ responses: any[]; subscription: any }> {
  try {
    const jwtToken = await storage.getItem('jwt_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (jwtToken) {
      headers['Authorization'] = `Bearer ${jwtToken}`;
    }

    const response = await fetch(`${API_BASE_URL}/api/background-responses/${userId}`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      console.warn('⚠️ BACKGROUND: Failed to check for completed responses');
      return { responses: [], subscription: null };
    }

    const data = await response.json();
    const completedResponses = data.responses || [];

    console.log(`🔄 BACKGROUND: Found ${completedResponses.length} completed responses`);
    return { responses: completedResponses, subscription: data.subscription || null };

  } catch (error) {
    console.error('❌ BACKGROUND: Error checking for completed responses:', error);
    return { responses: [], subscription: null };
  }
}

// State machine hook
const useChatStateMachine = (router: any, searchParams: any) => {
  const [state, setState] = useState<ChatState>({
    phase: 'initializing',
    messages: [],
    user: null,
    ui: {
      isTyping: false,
      typingMessageId: null,
      chatPaused: false,
      scrollState: {
        isUserAtBottom: true,
        shouldAutoScroll: true,
        isInitialLoad: true
      },
      hasStartedInitialMessage: false,
      onboardingTriggered: false
    }
  });

  // Single transition function - all state changes go through here
  const transition = useCallback((action: StateTransition) => {
    setState(prevState => {
      console.log(`🎯 STATE MACHINE: ${action.type}`, action.payload);
      
      switch (action.type) {
        case 'INITIALIZE':
          console.log(`🔍 REDUCER: INITIALIZE action received`);
          console.log(`   Previous phase: "${prevState.phase}"`);
          console.log(`   Setting phase to: "${action.payload.phase}"`);
          console.log(`   Messages count from payload: ${action.payload.messages?.length || 0}`);
          console.log(`   Messages count in prevState: ${prevState.messages.length}`);
          
          // 🔧 CRITICAL FIX (Jan 28, 2026): Merge newer messages with old chat history
          // Race condition: initializeChatSystem is async (1-2s). During that time,
          // contextual launch useEffect adds proactive message. When init finishes,
          // it dispatches INITIALIZE with old persistedMessages, wiping out proactive message.
          // Solution: MERGE old messages from payload WITH newer messages from prevState
          const payloadMessageIds = new Set((action.payload.messages || []).map((m: ChatMessage) => m.id));
          const newerMessages = prevState.messages.filter(msg => !payloadMessageIds.has(msg.id));
          
          const finalMessages = newerMessages.length > 0 
            ? [...(action.payload.messages || []), ...newerMessages] // Merge old history + new contextual messages
            : (action.payload.messages || []); // No new messages, just use payload
          
          if (newerMessages.length > 0) {
            const newerIds = newerMessages.map(msg => msg.id);
            console.log(`🔒 INIT MERGE: Merging ${action.payload.messages?.length || 0} old messages + ${newerMessages.length} newer messages (IDs: ${newerIds.join(', ')})`);
            console.log(`   Total messages after merge: ${finalMessages.length}`);
          } else {
            console.log(`✅ INIT: Using ${action.payload.messages?.length || 0} messages from initialization (no newer messages to merge)`);
          }
          
          const newState = {
            ...prevState,
            user: action.payload.user,
            messages: finalMessages,  // Use fresher messages
            phase: action.payload.phase,
            backendState: action.payload.backendState || undefined,
            ui: {
              ...prevState.ui,
              hasStartedInitialMessage: action.payload.hasStartedInitialMessage || false,
              // 🔧 FIX NAVIGATION BUG #3 (Nov 7, 2025): Don't auto-scroll on initialization
              // This prevents rapid scrolling animation when returning to chat
              scrollState: {
                isUserAtBottom: false,  // Don't assume user was at bottom
                shouldAutoScroll: false,  // Don't auto-scroll during init
                isInitialLoad: true
              }
            }
          };
          console.log(`✅ REDUCER: INITIALIZE returning new state with phase: "${newState.phase}", messages: ${newState.messages.length}`);
          return newState;

        case 'START_TOUR':
          return {
            ...prevState,
            phase: 'tour',
            ui: {
              ...prevState.ui,
              chatPaused: true
            }
          };

        case 'COMPLETE_TOUR':
          return {
            ...prevState,
            phase: 'onboarding',
            user: prevState.user ? { ...prevState.user, tourCompleted: true } : null,
            ui: {
              ...prevState.ui,
              chatPaused: false,
              isTyping: true,       // Show thinking dots while waiting for first message
              typingMessageId: 'thinking'
            }
          };

        case 'START_ONBOARDING':
          return {
            ...prevState,
            phase: 'onboarding',
            ui: {
              ...prevState.ui,
              onboardingTriggered: true,
              isTyping: true,  // 🔧 FIX Issue #2: Show typing dots immediately after tour
              typingMessageId: 'thinking'  // Show typing indicator
            }
          };

        case 'COMPLETE_ONBOARDING':
          return {
            ...prevState,
            phase: 'active',
            user: prevState.user ? { ...prevState.user, onboardingCompleted: true } : null
          };

        case 'ACTIVATE_CHAT':
          return {
            ...prevState,
            phase: 'active'
          };

        case 'ADD_MESSAGE':
          // 🎛️ ENHANCED DEDUPLICATION: Handle streaming updates vs new messages
          const existingMessageIndex = prevState.messages.findIndex(msg => msg.id === action.payload.message.id);
          
          if (existingMessageIndex !== -1) {
            // 🚀 PERFORMANCE FIX: Preserve object references for unchanged messages
            // This prevents ALL messages from re-rendering when one message updates
            const existingMessage = prevState.messages[existingMessageIndex];
            const updatedMessage = {
              ...existingMessage,
              ...action.payload.message,
              // Preserve important streaming state
              wasAnimated: existingMessage.wasAnimated || action.payload.message.wasAnimated
            };
            
            // 🚀 CRITICAL: Only create new array if message actually changed
            // Deep comparison to avoid unnecessary re-renders
            const messageChanged = 
              existingMessage.text !== updatedMessage.text ||
              existingMessage.status !== updatedMessage.status ||
              existingMessage.isStreaming !== updatedMessage.isStreaming ||
              existingMessage.timestamp !== updatedMessage.timestamp;
            
            if (!messageChanged) {
              // No changes - return previous state (preserves all object references)
              console.log(`⏭️ SKIP: Message ${action.payload.message.id} unchanged, preserving references`);
              return prevState;
            }
            
            // Message changed - create new array with preserved references for other messages
            const updatedMessages = prevState.messages.map((msg, index) => 
              index === existingMessageIndex ? updatedMessage : msg
            );
            
            console.log(`🔄 UPDATE: Updating existing message ${action.payload.message.id} (changed: ${messageChanged})`);
            return {
              ...prevState,
              messages: updatedMessages
            };
          } else {
            // Add new message (ensure createdAt for day-divider boundaries - Mission 30)
            const messageToAdd = {
              ...action.payload.message,
              createdAt: action.payload.message.createdAt ?? new Date().toISOString()
            };
            const newMessages = [...prevState.messages, messageToAdd];
            console.log(`➕ ADD: Adding new message ${action.payload.message.id}`);
            
            // 🎯 STEP TRANSITION AUTO-SCROLL: Trigger scroll for step transitions
            if (action.payload.message.metadata?.stepTransition) {
              console.log('🎯 STEP TRANSITION: Detected step transition, will trigger auto-scroll');
            }
            
            return {
              ...prevState,
              messages: newMessages
            };
          }

        case 'REMOVE_MESSAGE':
          // 🎛️ NEW: Remove message by ID (for slider cleanup)
          const filteredMessages = prevState.messages.filter(msg => msg.id !== action.payload.messageId);
          console.log(`🗑️ REMOVE_MESSAGE: Removed message ${action.payload.messageId}, ${prevState.messages.length} → ${filteredMessages.length}`);
          return {
            ...prevState,
            messages: filteredMessages
          };

        case 'ADD_REACTION':
          // 🎭 PROGRESSIVE EMOJI REACTION: Add reaction to existing message
          const updatedMessages = prevState.messages.map(msg => {
            if (msg.id === action.payload.messageId) {
              const existingReactions = msg.reactions || [];
              const newReaction = action.payload.reaction;
              console.log(`🎭 ADD_REACTION: Adding ${newReaction.emoji} to message ${msg.id}`);
              return {
                ...msg,
                reactions: [...existingReactions, newReaction]
              };
            }
            return msg;
          });
          return {
            ...prevState,
            messages: updatedMessages
          };

        case 'UPDATE_UI':
          return {
            ...prevState,
            ui: {
              ...prevState.ui,
              ...action.payload
            }
          };

        case 'HANDLE_CONTEXTUAL_ENTRY':
          return {
            ...prevState,
            contextualEntry: action.payload,
            phase: 'active'
          };

        case 'SYNC_BACKEND_STATE':
          // 🚨 CRITICAL: Sync frontend state with backend state progression
          // 🎯 FIXED: Backend state is wrapped in payload.backendState
          const backendState = action.payload.backendState;
          console.log('🔄 SYNC: Backend state update received:', JSON.stringify(action.payload, null, 2));
          console.log('🔄 SYNC: Current frontend phase:', prevState.phase);
          console.log('🔄 SYNC: Current frontend backendState:', JSON.stringify(prevState.backendState, null, 2));
          
          // Update phase based on backend onboarding completion
          let newPhase = prevState.phase;
          if (backendState && backendState.onboardingCompleted && prevState.phase === 'onboarding') {
            newPhase = 'active';
            console.log('🎯 SYNC: Onboarding completed, moving to active phase');
          }
          
          // 🚨 STATE PROGRESSION GUARD: Prevent regression to lower steps
          const currentStep = prevState.backendState?.onboardingStep ?? 0;
          const newStep = backendState?.onboardingStep ?? 0;
          
          // 🚨 CRITICAL FIX: Prevent ANY backward step progression during onboarding
          // Not just when completed - prevent mid-onboarding regressions too
          const shouldPreventRegression = currentStep > newStep && currentStep > 0 && newStep >= 0;
          
          if (shouldPreventRegression) {
            console.log(`🛡️ STATE GUARD: Preventing regression from Step ${currentStep} to Step ${newStep}`);
            console.log(`🛡️ STATE GUARD: This prevents the 1,2,1,2 loop issue`);
            console.log(`🛡️ STATE GUARD: Keeping current state and ignoring backend regression`);
            return prevState; // Keep current state, don't regress
          }
          
          // 🚨 ADDITIONAL GUARD: Prevent Step 4+ from regressing to name collection (Step 1)
          if (currentStep >= 4 && newStep <= 1) {
            console.log(`🛡️ CRITICAL GUARD: Preventing Step ${currentStep} → Step ${newStep} regression (name collection)`);
            console.log(`🛡️ This is the specific issue causing the loop - maintaining Step ${currentStep}`);
            return prevState; // Keep current state
          }
          
          const newBackendState = {
            onboardingStep: backendState?.onboardingStep ?? 0,
            onboardingCompleted: backendState?.onboardingCompleted ?? false,
            chatName: backendState?.chatName ?? '',
            detectedScenario: backendState?.detectedScenario ?? '',
            lastSyncedAt: new Date().toISOString(),
            // 💰 Mission 33 Sprint 7: Preserve subscription enforcement metadata
            subscription: backendState?.subscription ?? prevState.backendState?.subscription,
            subscription_blocked: backendState?.subscription_blocked ?? false,
            block_type: backendState?.block_type,
          };
          
          console.log('🔄 SYNC: Updated frontend state:', JSON.stringify({
            phase: newPhase,
            backendState: newBackendState
          }, null, 2));
          
          // 🚀 AUTO-DISCOVERY: DISABLED - Backend handles discovery transition naturally
          // The backend will automatically ask age/injuries question after personality sliders
          if (newBackendState.onboardingStep === 7 && newBackendState.onboardingCompleted && newBackendState.detectedScenario === 'discovery_phase') {
            console.log('🚀 AUTO-DISCOVERY: Step 7 detected, backend will handle discovery questions');
            // Just mark as triggered to prevent any other discovery triggers
            if (prevState.autoDiscoveryTriggered !== true) {
              setState(currentState => ({
                ...currentState,
                autoDiscoveryTriggered: true
              }));
            }
          }
          
          // 🚨 CRITICAL FIX: Persist backendState to localStorage for refresh recovery
          const currentUser = getCurrentUserId();
          if (currentUser) {
            setUserStorage('backend_state', JSON.stringify(newBackendState), currentUser);
            console.log('💾 SYNC: Saved backend state to localStorage');
          }
          
          return {
            ...prevState,
            phase: newPhase,
            backendState: newBackendState
          };

        case 'CLEAR_PERSONALITY_SLIDERS_SUBMITTED':
          // 🎛️ UNIFIED: Personality slider clearing is now handled by unified hook
          console.log('🎛️ CLEARING: Personality slider state cleared by unified hook');
          return prevState;

        case 'SET_TYPING':
          return {
            ...prevState,
            ui: {
              ...prevState.ui,
              isTyping: action.payload?.isTyping || false,
              typingMessageId: action.payload?.typingMessageId || null
            }
          };

        default:
          return prevState;
      }
    });
  }, []);

  return { state, transition };
};

// Helper function for personality preferences
const savePersonalityPreferences = async (sliderValues: any) => {
  try {
    // This would normally save to backend
    console.log('Saving personality preferences:', sliderValues);
    await storage.setItem('personality_preferences', JSON.stringify(sliderValues));
  } catch (error) {
    console.error('Error saving personality preferences:', error);
  }
};

interface FileAttachment {
  id: string;
  file: File;
  url: string;
  type: 'image' | 'document' | 'other';
  preview?: string;
  filename: string;
  size_bytes: number;
  mime_type: string;
  // 🚨 PHASE 4: Immediate upload functionality
  uploadStatus: 'pending' | 'uploading' | 'uploaded' | 'error';
  uploadProgress: number; // 0-100
  uploadError?: string;
  uploadId?: string; // For tracking the upload
}

export interface ChatMessage {
  id: string;
  sender: 'cedar' | 'user' | 'system';  // 🎯 SPRINT 2: Added 'system' for notifications
  text: string;
  timestamp: string;
  /** ISO 8601 date string for day-divider boundaries (Mission 30). Set on all new messages; from API when loading. */
  createdAt?: string;
  attachments?: FileAttachment[];
  reactions?: MessageReaction[];
  isEdited?: boolean;
  actions?: Array<{
    type: 'connect_garmin' | 'personality_sliders' | 'view_source' | 'request_notification_permission';
    label: string;
    id: string;
    sliders?: Array<{
      id: string;
      label: string;
      value: number;
      min: number;
      max: number;
    }>;
    sourceId?: string;
  }>;
  isAnimated?: boolean;
  wasAnimated?: boolean; // 🚨 NEW: Track if message was already animated to prevent re-animation
  isStreaming?: boolean; // 🔧 FIX Issue #7: Track if message is currently streaming (prevents fallback flash)
  status: 'delivered' | 'sending' | 'sent' | 'failed' | 'error' | 'thinking' | 'typing' | 'processing' | 'streaming';
  // 🎯 SPRINT 2: Plan notification metadata
  planNotification?: {
    notificationType: 'plan_created' | 'plan_modified' | 'plan_recalibrated' | 'context_saved' | 'goal_created_aha';
    planId?: string;
    actions?: Array<{
      type: string;
      text: string;
      url: string;
    }>;
    metadata?: {
      goal_title?: string;
      change_count?: number;
      change_types?: string[];
      aha_moment?: boolean;
      timestamp?: string;
    };
  };
  // 🎯 SPRINT 3: Conversational chunk metadata
  metadata?: {
    chunk_info?: {
      sequence: number;
      total_parts: number;
      type: string;
    };
    // 🚀 AUTO-MESSAGE METADATA: For Option C auto-follow-up system
    isAutoGenerated?: boolean;
    autoFollowUp?: boolean;
    stepTransition?: string;
    optionC?: boolean;
    discoveryStart?: boolean;
    // 🔄 BACKGROUND PROCESSING METADATA: For background processing responses
    background_processing?: boolean;
    task_id?: string;
    frontend_message_id?: string;
    estimated_completion?: string;
    // 🎯 CONTEXTUAL ITEM METADATA: For contextual chat launches
    contextualItem?: {
      type: 'goal' | 'plan' | 'workout' | 'mobility' | 'progress' | 'schedule' | 'general' | 'research';
      title: string;
      description?: string;
    };
    // 🎉 JAN 5, 2026: SPECIAL HEADER METADATA - Option A plan notification
    special_header?: {
      title: string;
      icon?: string;      // e.g., 'sparkles', 'lightning'
      color?: string;     // e.g., 'green', 'blue'
    };
    plan_id?: string;      // Plan ID for navigation
    actions?: Array<{      // Action buttons for special headers
      type: string;
      text: string;
      url: string;
      icon?: string;
    }>;
    // 🎯 FEB 4, 2026: PROGRESS INDICATOR METADATA - For inline progress display
    progressIndicator?: boolean;
    goalId?: string;
    goal_id?: string;      // Backend compatibility
    goalDetails?: string;
    goalText?: string;     // Alternative field name
    // 🔔 FEB 4, 2026: NOTIFICATION PILL METADATA - For inline notification display
    notificationPill?: boolean;
    notificationId?: string;
    notificationData?: AsyncProcessNotification;
    // 🎉 FEB 4, 2026: GOAL COMPLETION SUCCESS METADATA
    goalCreationSuccess?: boolean;
    goalTitle?: string;
    // 💰 Sprint 7: Subscription degraded mode flag - used to style Cedar bubbles
    // differently when paid user is past their soft limit (lite/degraded mode)
    subscriptionDegraded?: boolean;
    // 🔬 Source attribution metadata - for "View Research Event" button
    attribution_query?: boolean;
    research_event_id?: string;
    source_count?: number;
    triggerPersonalitySliders?: boolean;
    onboardingStep?: number;
    interrupted?: boolean;
    completeMessage?: boolean;
    conversation_type?: string;
    goalCreated?: boolean;
    goalCreationTriggered?: boolean;
    contextual_launch?: any;
    intelligence_context?: any;
    // 🚨 FIX #5: File upload interim response metadata
    type?: string;
    content_type?: string;
    filename?: string;
  };
  sliderAction?: {
    type: string;
    sliders?: Array<{
      id: string;
      label: string;
      value: number;
      min: number;
      max: number;
    }>;
  };
  serviceAction?: {
    type: 'service_connection';
    service: ServiceType;
    platform: PlatformType;
  };
  coachingStyleSaved?: {
    sliderValues: Record<string, number>;
    sliderLabels: Record<string, string>;
  };
  sliderData?: any;
  file_analysis?: {
    file_insight_id: string;
    confidence: number;
    suggested_actions: Array<{
      type: string;
      label: string;
      description?: string;
      icon?: string;
    }>;
  };
    // 🎯 CONTEXTUAL LAUNCH PILL: Green pill showing what user clicked from
  contextualPill?: {
    type: 'plan' | 'goal' | 'workout' | 'mobility' | 'research' | 'health' | 'sleep';
    title: string;
    subtitle?: string;
    metadata?: {
      from?: string;
      weekDates?: string;
      progress?: string;
      goalTitle?: string;
      eventDate?: string;
      status?: string;
      goals?: string[];
      [key: string]: any;
    };
  };
}

// L2 FIX (Mission 35B): Replaced truncated inline Base64 WAV with a properly
// generated short notification beep. Previous string was truncated ('...') and
// may not have produced audible output.
// This generates a minimal valid WAV file: 44-byte header + 1000 samples of
// a 440Hz sine wave at 8kHz sample rate (~125ms beep).
const _notifAudioUrl = (() => {
  if (typeof window === 'undefined') return '';
  try {
    const sampleRate = 8000;
    const numSamples = 1000;
    const buf = new ArrayBuffer(44 + numSamples * 2);
    const view = new DataView(buf);
    // RIFF header
    const writeStr = (off: number, s: string) => { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); };
    writeStr(0, 'RIFF'); view.setUint32(4, 36 + numSamples * 2, true); writeStr(8, 'WAVE');
    writeStr(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true);
    view.setUint16(22, 1, true); view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
    writeStr(36, 'data'); view.setUint32(40, numSamples * 2, true);
    for (let i = 0; i < numSamples; i++) {
      const fade = 1 - i / numSamples; // linear fade-out
      view.setInt16(44 + i * 2, Math.round(Math.sin(2 * Math.PI * 440 * i / sampleRate) * 8000 * fade), true);
    }
    return URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
  } catch { return ''; }
})();

const playNotificationSound = () => {
  if (Platform.OS === 'web' && typeof document !== 'undefined' && document.hidden && _notifAudioUrl) {
    const audio = new Audio(_notifAudioUrl);
    audio.volume = 0.3;
    audio.play().catch(() => {});
  }
};

// 🎯 GOAL DETECTION: Detect when user message indicates goal creation intent
const detectGoalCreationIntent = (message: string): boolean => {
  const lowerMessage = message.toLowerCase();
  
  // Explicit goal creation phrases
  const goalCreationPhrases = [
    'create a goal',
    'make a goal',
    'set a goal',
    'goal card',
    'new goal',
    'add a goal',
    'create goal',
    'make goal',
    'set goal',
    'lets create',
    'let\'s create',
    'ready to create',
    'want to create',
    'create my goal',
    'build a goal',
    'establish a goal'
  ];
  
  // Check for explicit phrases
  for (const phrase of goalCreationPhrases) {
    if (lowerMessage.includes(phrase)) {
      return true;
    }
  }
  
  // Check for goal-related keywords with action words
  const goalKeywords = ['goal', 'target', 'objective', 'aim'];
  const actionWords = ['create', 'make', 'set', 'build', 'establish', 'start', 'begin'];
  
  const hasGoalKeyword = goalKeywords.some(keyword => lowerMessage.includes(keyword));
  const hasActionWord = actionWords.some(action => lowerMessage.includes(action));
  
  return hasGoalKeyword && hasActionWord;
};

// Using proper Anima CmpBootomnavCoach component with built-in state handling

const Chat: React.FC = () => {
  // 🎯 STATE MACHINE: Single source of truth for all chat state
  const router = useRouter();
  
  // 🎯 GOAL PIPELINE PROGRESS INDICATOR STATE (Re-added Jan 31, 2026)
  // 🚨 CRITICAL FIX: This was removed on Jan 28, but sendStreamingMessage still expects it!
  // Removal caused: "setShowPipelineProgress is not defined" → streaming fails → fallback to POST /api/chat
  const [showPipelineProgress, setShowPipelineProgress] = useState(false);
  const [pipelineGoalText, setPipelineGoalText] = useState('');
  const [pipelineGoalDetails, setPipelineGoalDetails] = useState<any>(null);
  const [pipelineGoalId, setPipelineGoalId] = useState<string | null>(null);

  // 🟢 RECALIBRATION PROGRESS STRIP STATE
  const [recalTrackingId, setRecalTrackingId] = useState<string | null>(null);
  const [recalType, setRecalType] = useState<string | null>(null);
  // 2026-05-05 (durable progress indicators, Tasks 2 + 4): tracking IDs we've
  // already mounted/handled this session. Hydrated lazily via the watcher
  // below — once a tid is in the set, the strip won't re-mount for it
  // (prevents "yesterday's recal pops back" on reload).
  const displayedRecalIdsRef = useRef<Set<string>>(new Set());
  // 🧭 2026-09-16 (Consent Seams T2): carrier messages parked on a question (by message id).
  const parkedRecalCarrierIdsRef = useRef<Set<string>>(new Set());

  // 🔄 DEVICE SYNC PROGRESS INDICATOR STATE
  const [showSyncProgress, setShowSyncProgress] = useState(false);
  const [syncService, setSyncService] = useState<'garmin' | null>(null);
  const [syncFromSettings, setSyncFromSettings] = useState(false);  // Track if from Settings
  
  // Tier D-6 (2026-05-15) — HRZoneEducationModal state retired. First-
  // Garmin-sync education path is now: backend banner → user taps →
  // drawer opens → user discovers the "What are heart rate zones?" link
  // for deeper learning.

  const searchParams = useLocalSearchParams();
  const pathname = usePathname();
  const { state: chatState, transition } = useChatStateMachine(router, searchParams);
  
  // 🔍 DEBUG (Jan 22, 2026): Track phase changes to diagnose stuck initializing state
  // Refresh what Cedar accepts from the server, once, at mount.
  //
  // The constants in ../constants/intakeFormats are a FALLBACK, not the source of truth —
  // the registry is (services/intake_format_registry.py). Hand-written client copy is what
  // produced the 2026-08-12 incident and what still told users Cedar could only read
  // "text out of PDFs" weeks after pages began being rendered and read by sight.
  //
  // Everything here degrades to the constants: a null response, a 404, an offline client
  // all leave the current behaviour untouched. This can only improve the copy — it is
  // never a precondition for uploading.
  const [intakeSummary, setIntakeSummary] = useState<string>(INTAKE_ACCEPTED_SUMMARY);
  useEffect(() => {
    let cancelled = false;
    void fetchIntakeFormats().then(formats => {
      if (!cancelled && formats?.summary) setIntakeSummary(formats.summary);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    console.log(`🔄 PHASE CHANGE: chatState.phase changed to "${chatState.phase}"`);
    console.log(`   Messages count: ${chatState.messages.length}`);
    console.log(`   Backend state:`, chatState.backendState);
  }, [chatState.phase, chatState.messages.length]);

  // 2026-05-05 (durable progress indicators, Task 2): hydrate the recal strip
  // from durable assistant message metadata. Mirror of the native
  // ChatScreen.native.tsx watcher. Two carriers:
  //   1. Synthetic system messages (`sender='system'` + recalibrationIndicator)
  //      from useChatLogic — live SSE path, ephemeral.
  //   2. Persisted assistant messages (`sender='cedar'` + recalibrationTrackingId
  //      in metadata) from /api/chat/messages/recent — survives reload, force
  //      quit, and backgrounding.
  // 24h completion guard prevents re-mounting historical recals on app open.
  useEffect(() => {
    if (recalTrackingId) return; // already mounted
    for (const message of chatState.messages) {
      const meta: any = (message as any).metadata || {};
      const tid: string | undefined = meta.recalibrationTrackingId;
      if (!tid) continue;
      const isStripCarrier =
        ((message as any).sender === 'system' && meta.recalibrationIndicator) ||
        // 2026-05-28 (BUG 2 sprint) — [recalibration_progress] system
        // marker. Emitted by services/recalibration_progress_marker.py
        // (default OFF behind FF_RECAL_PROGRESS_MARKER). The marker
        // fires independently of the live SSE recalibrationTriggered
        // metadata path. Discriminated from [pipeline_progress] via
        // metadata.kind === 'recalibration'. Shares the same dedup
        // state (displayedRecalIdsRef) as the other carrier shapes.
        ((message as any).sender === 'system' &&
          meta.progressIndicator &&
          meta.kind === 'recalibration') ||
        (message as any).sender === 'cedar';
      if (!isStripCarrier) continue;
      if (displayedRecalIdsRef.current.has(tid)) continue;
      if (parkedRecalCarrierIdsRef.current.has(String((message as any).id))) continue;

      const tryMount = async () => {
        const mountNow = () => {
          setRecalTrackingId(tid);
          setRecalType(meta.recalibrationType || null);
        };
        try {
          const jwtToken = await storage.getItem('jwt_token');
          const headers: Record<string, string> = {};
          if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
          const res = await fetch(`${API_BASE_URL}/api/recalibration/status/${tid}`, { headers });
          if (!res.ok) {
            mountNow();
            return;
          }
          const resp = await res.json();
          // 🧭 2026-09-16 (Consent Seams T2): one decision for every status (see recalStatusContract).
          const _hydration = recalHydrationDecision(resp?.status);
          if (_hydration === 'skip_park') {
            parkedRecalCarrierIdsRef.current.add(String((message as any).id));
            return;
          }
          if (_hydration === 'skip_seed') {
            displayedRecalIdsRef.current.add(tid);
            return;
          }
          if (resp?.status === 'completed' && resp?.completed_at) {
            const completedMs = resp.completed_at * 1000;
            if (Date.now() - completedMs > 24 * 60 * 60 * 1000) {
              displayedRecalIdsRef.current.add(tid);
              return;
            }
          }
          mountNow();
        } catch {
          mountNow();
        }
      };
      void tryMount();
      break;
    }
  }, [chatState.messages, recalTrackingId]);

  // Toast durability (Task 4): mark already-shown toasts so reload doesn't
  // re-fire them. The live useChatLogic flow handles initial firing when the
  // SSE metadata event arrives; this watcher just records "already shown"
  // for tids whose toast metadata is now durable on the chat_messages row.
  useEffect(() => {
    for (const message of chatState.messages) {
      const meta: any = (message as any).metadata || {};
      if (!meta.recalibrationToast) continue;
      const tid: string | undefined = meta.recalibrationTrackingId;
      if (!tid) continue;
      displayedRecalIdsRef.current.add(tid);
    }
  }, [chatState.messages]);
  
  // UI-only state (not part of the core chat state machine)
  const [input, setInput] = useState<string>(''); // Initialize with empty string (draft loaded in useEffect)
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  // 🔇 REMOVED (Jan 5, 2026): Processing pill not needed with interim message system
  // const [processingFiles, setProcessingFiles] = useState<boolean>(false);
  // const [processingStartTime, setProcessingStartTime] = useState<number | null>(null);
  const [currentUserMessage, setCurrentUserMessage] = useState<string>(''); // Initialize with empty string (draft loaded in useEffect)
  // 🚫 TEMPORARILY DISABLED: Goal processing state
  // const [processingGoals, setProcessingGoals] = useState<boolean>(false);
  const [showNotificationPermission, setShowNotificationPermission] = useState(false);
  const [monthlyDepleted, setMonthlyDepleted] = useState(false);
  // 🔔 NOTIFICATION SYSTEM STATE (FEB 4, 2026: Notifications now stored as messages)
  // const [notifications, setNotifications] = useState<AsyncProcessNotification[]>([]); // REMOVED: Now part of messages array
  const [lastNotificationCheck, setLastNotificationCheck] = useState<number>(Date.now());
  
  // 📅 CHAT HISTORY STATE
  const [chatHistory, setChatHistory] = useState<ChatHistoryType | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());
  const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set());
  const [showChatHistory, setShowChatHistory] = useState(false);
  // 💰 TRIAL GATE: After Step 6 (personality sliders), before Step 7 (discovery)
  const [showTrialGate, setShowTrialGate] = useState(false);
  const [pendingStep7BackendState, setPendingStep7BackendState] = useState<{
    onboardingStep: number;
    onboardingCompleted: boolean;
    chatName?: string;
    detectedScenario?: string;
    lastSyncedAt?: string;
  } | null>(null);
  const { refreshSubscription, isBetaTester, expiresAt, status: subscriptionStatus } = useSubscription();
  // 🔄 BACKGROUND RESPONSE STATE
  // 🚨 CRITICAL FIX (Jan 2025): Use ref instead of state to prevent unnecessary re-renders
  // Background polling runs every 5 seconds - state updates cause all messages to re-render
  // Using a ref prevents re-renders while still allowing us to track the checking state
  const checkingBackgroundResponsesRef = useRef<boolean>(false);
  // 🎛️ UNIFIED PERSONALITY SLIDER STATE: Replace 4 separate variables with clean hook
  const { state: personalitySliderState, actions: personalitySliderActions } = usePersonalitySliders();
  const [userName, setUserName] = useState<string>('');
  
  // 🩺 MISSION 24: EXTRACTION HEALTH MONITORING - Real-time pipeline health indicator
  const { logExtraction } = useExtractionHealthMonitor();
  
  // 🚀 SERVICE CONNECTION STATE - Inline embedded like personality sliders
  const [userPlatform, setUserPlatform] = useState<PlatformType>('desktop');
  
  // 🚨 BACKGROUND PROCESSING REMOVED - Using synchronous processing only
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // 🔒 DEDUPLICATION: Persistent tracker for processed messages (prevents duplicate display)
  const processedMessageIdsRef = useRef<Set<string>>(new Set());
  
  // 🏋️ POST-WORKOUT DEBRIEF PENDING (Mission 29): In-app flow – pill + thinking → poll by task_id → typing → delivered
  const pendingDebriefTaskIdRef = useRef<string | null>(null);
  const [pendingDebriefTaskId, setPendingDebriefTaskId] = useState<string | null>(null);
  const setPendingDebriefTaskIdRef = useRef<(id: string | null) => void>(() => {});
  useEffect(() => { setPendingDebriefTaskIdRef.current = setPendingDebriefTaskId; }, []);
  
  // 🛡️ RESILIENT POLLING: Track consecutive notification failures (Jan 30, 2026)
  // Purpose: Gracefully handle intermittent 502 errors during deployment without spamming console
  const consecutiveNotificationFailures = useRef<number>(0);

  // 💰 Sprint 7 q4/q6: One-time subscription bubble tracking
  // These refs prevent showing duplicate Cedar bubbles when subscription state changes.
  // Reset weekly when week_end_date changes (new billing week).
  const shownThreeLeftBubbleRef = useRef<boolean>(false);
  const shownDegradedModeBubbleRef = useRef<boolean>(false);
  const lastKnownWeekEndRef = useRef<string | null>(null);

  // 🆕 ISSUE 3b: NEW MESSAGES DIVIDER - Track first new message and divider visibility
  // Divider appears above the first new message when user was NOT at bottom.
  // Disappears when user scrolls past it or reaches the bottom.
  const firstNewMessageIdRef = useRef<string | null>(null);
  const [showNewMessagesDivider, setShowNewMessagesDivider] = useState(false);
  const showNewMessagesDividerRef = useRef(false);
  // Sync ref with state so handleScroll (no deps) can read current value
  useEffect(() => { showNewMessagesDividerRef.current = showNewMessagesDivider; }, [showNewMessagesDivider]);

  // 🔒 STEP 2: PERSISTENT DEDUPLICATION TRACKER (JAN 30, 2026)
  // Purpose: Load dedup tracker from unified storage on mount to prevent duplicates after navigation
  useEffect(() => {
    let cancelled = false;
    const loadDedup = async () => {
      const currentUser = getCurrentUserId();
      if (!currentUser) return;
      try {
        const savedDedupIds = await storage.getItem(`${currentUser}_processedMessageIds`);
        if (cancelled) return;
        if (savedDedupIds) {
          try {
            const parsedIds = JSON.parse(savedDedupIds);
            processedMessageIdsRef.current = new Set(parsedIds);
            console.log(`🔄 DEDUP TRACKER: Loaded ${parsedIds.length} IDs from storage`);
          } catch (error) {
            console.error('❌ DEDUP TRACKER: Failed to parse saved IDs', error);
          }
        } else {
          console.log('🔄 DEDUP TRACKER: No saved IDs found (fresh session)');
        }
      } catch (e) {
        if (!cancelled) console.error('❌ DEDUP TRACKER: Load failed', e);
      }
    };
    loadDedup();
    return () => { cancelled = true; };
  }, []); // Only run once on mount
  
  // 🔍 PLATFORM DETECTION - Detect user's platform for optimal service suggestions
  useEffect(() => {
    if (Platform.OS !== 'web') {
      setUserPlatform(Platform.OS === 'ios' ? 'ios' : Platform.OS === 'android' ? 'android' : 'desktop');
      return;
    }
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent.toLowerCase() : '';
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setUserPlatform('ios');
    } else if (/android/.test(userAgent)) {
      setUserPlatform('android');
    } else {
      setUserPlatform('desktop');
    }
  }, []);

  // 🔧 COMPOSER PERSISTENCE: Load draft asynchronously after mount
  useEffect(() => {
    const loadComposerDraft = async () => {
      const currentUser = getCurrentUserId();
      if (currentUser) {
        const draft = await getUserStorage('composer_draft', currentUser);
        if (draft) {
          setInput(draft);
          setCurrentUserMessage(draft);
        }
      }
    };
    loadComposerDraft();
  }, []); // Only run once on mount

  // 🎯 CONTEXTUAL MODAL: Handle pre-filled message from modal
  useEffect(() => {
    async function loadPreFilledMessage() {
      const preFilledMessage = await storage.getItem('pre_filled_message');
      if (preFilledMessage) {
        console.log('🎯 CONTEXTUAL MODAL: Pre-filling input with message from modal');
        setInput(preFilledMessage);
        await storage.removeItem('pre_filled_message');
        
        // Auto-focus the composer (user already hit send in modal)
        // They should see their message ready to go
      }
    }
    loadPreFilledMessage();
  }, []);

  // 💰 Sprint 7 q4/q6: One-time subscription Cedar speech bubbles
  // Watches subscription state and injects contextual Cedar messages when
  // paid users approach or reach their weekly limits. Refs reset each week.
  useEffect(() => {
    const sub = chatState.backendState?.subscription;
    if (!sub) return;

    const { tier, remaining, warning_level, degraded_mode, week_end_date } = sub;

    // Skip for trial/onboarding users (they have their own flows)
    if (!tier || tier === 'trial' || tier === 'onboarding') return;

    // Weekly reset: clear one-time flags when a new billing week starts
    if (week_end_date && week_end_date !== lastKnownWeekEndRef.current) {
      lastKnownWeekEndRef.current = week_end_date;
      shownThreeLeftBubbleRef.current = false;
      shownDegradedModeBubbleRef.current = false;
    }

    // Helper: format ISO date for user-facing messages
    const formatResetDate = (isoDate: string | undefined): string => {
      if (!isoDate) return 'next week';
      try {
        const d = new Date(isoDate);
        return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
      } catch {
        return 'next week';
      }
    };

    // 📢 BUBBLE 1: "3 messages left" — one-time heads-up for paid subscribers
    if (
      warning_level === 'critical' &&
      remaining !== undefined && remaining <= 3 && remaining > 0 &&
      !shownThreeLeftBubbleRef.current
    ) {
      shownThreeLeftBubbleRef.current = true;

      const resetDate = formatResetDate(week_end_date);
      const upgradeNote = tier === 'full'
        ? ' You can also upgrade to Extended for more weekly messages.'
        : '';

      transition({
        type: 'ADD_MESSAGE',
        payload: {
          message: {
            id: `cedar-sub-3left-${Date.now()}`,
            sender: 'cedar' as const,
            text: `🌲 Quick heads-up — you have ${remaining} message${remaining === 1 ? '' : 's'} left this week. Post-workout debriefs are always available, but research features and "Chat with Cedar" buttons will pause until ${resetDate}.${upgradeNote}`,
            timestamp: new Date().toISOString(),
            status: 'delivered' as const,
            metadata: {
              isAutoGenerated: true,
            },
          },
        },
      });
    }

    // 📢 BUBBLE 2: "Lighter mode" — one-time notice when degraded mode activates
    if (
      degraded_mode === true &&
      !shownDegradedModeBubbleRef.current
    ) {
      shownDegradedModeBubbleRef.current = true;

      const resetDate = formatResetDate(week_end_date);
      const upgradeNote = tier === 'full'
        ? ' Want the full experience back sooner? You can upgrade to Extended.'
        : '';

      transition({
        type: 'ADD_MESSAGE',
        payload: {
          message: {
            id: `cedar-sub-degraded-${Date.now()}`,
            sender: 'cedar' as const,
            text: `🌲 I'm still here for you — just running in a lighter mode until your messages reset on ${resetDate}. I'll have all your context, but I won't be pulling from research or running deep analysis until then.${upgradeNote}`,
            timestamp: new Date().toISOString(),
            status: 'delivered' as const,
            metadata: {
              isAutoGenerated: true,
              subscriptionDegraded: true,
            },
          },
        },
      });
    }
  }, [
    chatState.backendState?.subscription?.remaining,
    chatState.backendState?.subscription?.warning_level,
    chatState.backendState?.subscription?.degraded_mode,
    chatState.backendState?.subscription?.week_end_date,
    chatState.backendState?.subscription?.tier,
    transition,
  ]);

  // 💰 Sprint 7 q8: Persist chat buttons disabled state for ContextualChatButton
  // Writes to localStorage so "Chat with Cedar" buttons on other pages (Move, Insights, etc.)
  // can read the flag and grey themselves out when user is past their weekly limit.
  useEffect(() => {
    const sub = chatState.backendState?.subscription;
    if (!sub) return;

    const shouldDisable =
      sub.degraded_mode === true ||
      (sub.remaining !== undefined && sub.remaining <= 0) ||
      sub.post_trial_phase === 'lite' ||
      sub.post_trial_phase === 'blocked';

    const userId = getCurrentUserId();
    if (userId && userId !== 'default_user') {
      setUserStorage('cedar_chat_buttons_disabled', shouldDisable ? 'true' : 'false', userId);
      // Dispatch custom event for same-tab listeners (ContextualChatButton reads this)
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('cedar-subscription-change'));
      }
    }
  }, [
    chatState.backendState?.subscription?.degraded_mode,
    chatState.backendState?.subscription?.remaining,
    chatState.backendState?.subscription?.post_trial_phase,
  ]);

  // 💰 Reset monthlyDepleted when backend signals pool has been refreshed
  useEffect(() => {
    if (chatState.backendState?.subscription?.degraded_mode === false && monthlyDepleted) {
      setMonthlyDepleted(false);
    }
  }, [chatState.backendState?.subscription?.degraded_mode, monthlyDepleted]);

  // 🚨 FIX (Jan 22, 2026): Helper function for text similarity calculation (used in background poll)
  // Uses Jaccard similarity (intersection over union of word sets)
  const calculateTextSimilarity = (text1: string, text2: string): number => {
    const words1 = new Set(text1.split(/\s+/).filter(w => w.length > 0));
    const words2 = new Set(text2.split(/\s+/).filter(w => w.length > 0));
    
    if (words1.size === 0 && words2.size === 0) return 1.0;
    if (words1.size === 0 || words2.size === 0) return 0.0;
    
    const intersection = new Set([...words1].filter(w => words2.has(w)));
    const union = new Set([...words1, ...words2]);
    
    return intersection.size / union.size;
  };
  
  // 🔄 BACKGROUND RESPONSE CHECKING: Standalone function accessible from anywhere in component
  const checkBackgroundResponses = async () => {
    console.log('🔄 BACKGROUND POLL DEBUG: checkBackgroundResponses called');
    console.log('🔄 BACKGROUND POLL DEBUG: checkingBackgroundResponses =', checkingBackgroundResponsesRef.current);
      
      if (checkingBackgroundResponsesRef.current) {
        console.log('🔄 BACKGROUND POLL DEBUG: Already checking, returning early');
        return; // Prevent multiple simultaneous checks
      }
      
      const currentUser = getCurrentUserId();
      console.log('🔄 BACKGROUND POLL DEBUG: currentUser =', currentUser);
      console.log('🔄 BACKGROUND POLL DEBUG: chatState.phase =', chatState.phase);
      console.log('🔄 BACKGROUND POLL DEBUG: isProcessingChunks =', isProcessingChunksRef.current);
      
      // 🔒 RACE CONDITION PROTECTION: Skip if chunk processing is active
      if (isProcessingChunksRef.current) {
        console.log('🔒 BACKGROUND: Skipping poll - chunk processing active');
        return;
      }
      
      if (!currentUser) {
        console.log('🔄 BACKGROUND POLL DEBUG: No currentUser, returning early');
        return;
      }
      
      // 🚀 BACKGROUND PROCESSING: Poll during active phases, but NOT during tour
      // Users can navigate away during any phase and need their responses when they return
      // 🔧 FIX (Jan 27, 2026): Allow poll during 'initializing' to catch early messages
      // 🚨 FIX (Jan 28, 2026): BLOCK polling during 'tour' phase
      // The prewarmed "what name?" message should NOT display until tour completes
      // Tour completion will trigger an immediate poll to fetch waiting messages
      const allowedPhases = ['onboarding', 'active', 'initializing'];
      if (!allowedPhases.includes(chatState.phase)) {
        console.log('🔄 BACKGROUND POLL DEBUG: Invalid phase, returning early. Phase:', chatState.phase, 'Messages:', chatState.messages.length);
        return;
      }
      
      // Log when polling during initialization
      if (chatState.phase === 'initializing') {
        console.log('🔄 BACKGROUND POLL DEBUG: Polling during', chatState.phase, 'phase - checking for initial/onboarding messages');
      }
      
      // 🚀 BACKGROUND PROCESSING: Check for completed responses at ALL times
      // Users can navigate away during any phase - onboarding, discovery, or post-onboarding
      const onboardingStep = chatState.backendState?.onboardingStep || 0;
      const isOnboardingCompleted = chatState.backendState?.onboardingCompleted || false;
      console.log(`🔄 BACKGROUND: Checking for responses - step: ${onboardingStep}, completed: ${isOnboardingCompleted}`);
      
      
      checkingBackgroundResponsesRef.current = true;
      
      try {
        const bgResult = await checkForCompletedBackgroundResponses(currentUser);
        const completedResponses = bgResult.responses;

        // Mission 35B: Sync enforcement state on EVERY poll so debrief/nudge
        // touchpoint unlocks the composer for Essentials users.
        if (bgResult.subscription) {
          setChatState(prev => ({
            ...prev,
            backendState: {
              ...prev.backendState,
              subscription: bgResult.subscription,
            },
          }));
        }

      if (completedResponses.length > 0) {
        console.log(`🔄 BACKGROUND: Processing ${completedResponses.length} completed responses`);
        
        // 🚨 BUG #75 FIX (Feb 4, 2026): MARK AS RETRIEVED FIRST
        // PROBLEM: mark-retrieved was called AFTER deduplication/age filtering
        // RESULT: Filtered responses never marked → retrieved_at stays NULL → infinite loop
        // SOLUTION: Mark ALL responses as retrieved IMMEDIATELY, before any filtering
        try {
          const taskIds = completedResponses.map(r => r.task_id).filter(Boolean);
          if (taskIds.length > 0) {
            const jwtToken = await storage.getItem('jwt_token');
            
            const markResponse = await fetch(`${API_BASE_URL}/api/background-responses/mark-retrieved`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${jwtToken}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ task_ids: taskIds })
            });
            
            if (markResponse.ok) {
              const data = await markResponse.json();
              console.log(`✅ BUG #75 FIX: Marked ${data.marked} responses BEFORE filtering (prevents infinite loop)`);
            } else {
              console.warn(`⚠️ Failed to mark retrieved: ${markResponse.status}`);
            }
          }
        } catch (markError) {
          console.error(`❌ Mark retrieved failed (non-critical):`, markError);
          // Continue with processing even if marking fails
        }
        
        // 🔒 PERSISTENT DEDUPLICATION: Use ref-based tracking that survives re-renders
        // This prevents the same message from being processed multiple times during animation/streaming
        console.log(`🔍 DEDUP: Have ${processedMessageIdsRef.current.size} messages in persistent tracker`);
        
        // 🏋️ MISSION 29 IN-APP PENDING DEBRIEF: When user is on Chat waiting for debrief (pill + thinking),
        // find the response by task_id, remove thinking placeholder, show pill (if needed), add message as typing then delivered
        const pendingTaskId = pendingDebriefTaskIdRef.current;
        if (pendingTaskId) {
          const pendingResponse = completedResponses.find(r => r.task_id === pendingTaskId);
          if (pendingResponse) {
            const responseMessageId = pendingResponse.message_id || `cedar-bg-${pendingResponse.task_id}`;
            const rawText = (pendingResponse.text || pendingResponse.response?.text || '').trim();
            
            transition({ type: 'REMOVE_MESSAGE', payload: { messageId: `pending-debrief-${pendingResponse.task_id}` } });
            
            const pillId = `debrief-pill-${pendingResponse.task_id}`;
            if (!processedMessageIdsRef.current.has(pillId)) {
              const workoutTitle = pendingResponse.context?.workout_title || 'Workout';
              const debriefPill: ChatMessage = {
                id: pillId,
                sender: 'system',
                text: '',
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                status: 'delivered',
                contextualPill: {
                  type: 'workout',
                  title: `✅ ${workoutTitle}`,
                  subtitle: pendingResponse.context?.duration_minutes ? `${pendingResponse.context.duration_minutes} min • completed` : 'completed',
                  metadata: { from: 'Workout Complete', status: 'completed' }
                }
              };
              processedMessageIdsRef.current.add(pillId);
              transition({ type: 'ADD_MESSAGE', payload: { message: debriefPill } });
            }
            
            let finalText = rawText;
            let parsedActions: any[] | undefined;
            let parsedServiceAction: ChatMessage['serviceAction'];
            if (rawText && /\[([^\]]+)\]\(action:([^)]+)\)/.test(rawText)) {
              const parsed = parseMessageActions(rawText);
              finalText = parsed.cleanText;
              parsedActions = parsed.actions;
              parsedServiceAction = parsed.serviceAction;
            }
            
            const cedarMessage: ChatMessage = {
              id: responseMessageId,
              sender: 'cedar',
              text: finalText,
              timestamp: new Date(pendingResponse.timestamp || pendingResponse.completed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              status: 'typing',
              isStreaming: true,
              isAnimated: true,
              wasAnimated: false,
              ...(parsedActions?.length ? { actions: parsedActions } : {}),
              ...(parsedServiceAction ? { serviceAction: parsedServiceAction } : {}),
              metadata: {
                chunk_info: { sequence: 1, total_parts: 1, type: 'background_processed' },
                isAutoGenerated: true,
                stepTransition: `background_task_${pendingResponse.task_id}`,
                optionC: false,
                discoveryStart: false
              } as any
            };
            
            processedMessageIdsRef.current.add(responseMessageId);
            transition({ type: 'ADD_MESSAGE', payload: { message: cedarMessage } });
            
            const typingDuration = Math.min(Math.max(finalText.length * 25, 800), 3500);
            setTimeout(() => {
              transition({
                type: 'ADD_MESSAGE',
                payload: {
                  message: {
                    ...cedarMessage,
                    status: 'delivered',
                    isStreaming: false,
                    wasAnimated: true
                  }
                }
              });
              transition({ type: 'UPDATE_UI', payload: { isTyping: false, typingMessageId: null } });
            }, typingDuration);
            
            pendingDebriefTaskIdRef.current = null;
            setPendingDebriefTaskId(null);
            console.log(`🏋️ PENDING DEBRIEF: Replaced thinking with message ${responseMessageId}, typing ${typingDuration}ms then delivered`);
          }
        }
        
        // 🚨 CRITICAL FIX (Jan 2025): Early exit if all messages are already processed
        // This prevents unnecessary state reads that could trigger re-renders
        const allProcessed = completedResponses.every(response => {
          const messageId = response.message_id || `cedar-bg-${response.task_id}`;
          return processedMessageIdsRef.current.has(messageId);
        });
        
        if (allProcessed) {
          console.log(`⏭️  BACKGROUND: All ${completedResponses.length} responses already processed - skipping display (but already marked retrieved)`);
          
          // 🔧 FIX (Dec 2025): Clear isTyping even when all responses already processed
          // This prevents stuck typing states from previous operations
          if (chatState.ui.isTyping) {
            console.log(`🔧 STUCK STATE FIX: Clearing isTyping=true that was stuck (all responses already processed)`);
            transition({
              type: 'UPDATE_UI',
              payload: { isTyping: false, typingMessageId: null }
            });
          }
          return; // Early exit - no other state updates needed
        }
        
        const newResponses = completedResponses.filter(response => {
          const messageId = response.message_id || `cedar-bg-${response.task_id}`;
          
          // 🆕 STEP 6: AGE FILTER (JAN 30, 2026)
          // Purpose: Prevent very old responses (>15 minutes) from displaying
          // This catches responses that were never retrieved but are now too stale to be relevant
          const maxAgeMinutes = 15;
          const completedAt = new Date(response.completed_at).getTime();
          const ageMinutes = (Date.now() - completedAt) / (1000 * 60);
          
          if (ageMinutes > maxAgeMinutes) {
            console.log(`⏰ AGE FILTER: Response too old, skipping`, {
              task_id: response.task_id,
              ageMinutes: ageMinutes.toFixed(1),
              completed_at: response.completed_at
            });
            // Add to tracker to prevent future checks
            processedMessageIdsRef.current.add(messageId);
            return false;
          }
          
          // 🚨 FIX #5: Skip file upload interim responses - they're handled by immediate response path
          // These are stored in chat_messages but should NOT be processed by background poller
          if (response.metadata?.type === 'file_upload_interim_response' || 
              response.context?.type === 'file_upload_interim_response') {
            console.log(`⏭️  SKIP: File upload interim response ${messageId} - handled by immediate path`);
            // Add to tracker to prevent future checks
            processedMessageIdsRef.current.add(messageId);
            return false;
          }
          
          // 🔒 Check persistent tracker first (most reliable)
          if (processedMessageIdsRef.current.has(messageId)) {
            console.log(`⏭️  SKIP: Message ${messageId} already processed (persistent tracker)`);
            return false;
          }
          
          // 🔧 FIX (Jan 5, 2026): Check for duplicate text content (catches fallback IDs)
          // Issue: Same message can have cedar_xxx and cedar_fallback_xxx IDs
          // 🚨 FIX (Jan 22, 2026): Use normalized text for better matching (whitespace, case)
          const normalizeText = (text: string): string => {
            return text.trim().replace(/\s+/g, ' ').toLowerCase();
          };
          
          const responseText = (response.text || response.response?.text || '').trim();
          const normalizedResponseText = normalizeText(responseText);
          
          // Check against current chatState.messages with normalized comparison
          const textExistsInChat = chatState.messages.some(msg => {
            if (msg.sender !== 'cedar') return false;
            const normalizedMsgText = normalizeText(msg.text);
            // Check full text first (most reliable)
            if (normalizedMsgText === normalizedResponseText) return true;
            // Also check first 200 chars as fingerprint
            if (normalizedMsgText.substring(0, 200) === normalizedResponseText.substring(0, 200)) return true;
            // Similarity check for near-duplicates (>95% match)
            const similarity = calculateTextSimilarity(normalizedMsgText, normalizedResponseText);
            return similarity > 0.95;
          });
          
          if (textExistsInChat) {
            console.log(`⏭️  SKIP: Message ${messageId} - duplicate text content detected (normalized match)`);
            // Add to persistent tracker to prevent future checks
            processedMessageIdsRef.current.add(messageId);
            return false;
          }
          
          // 🔒 Also check current chat state by ID as backup
          const idExistsInChat = chatState.messages.some(msg => msg.id === messageId);
          if (idExistsInChat) {
            console.log(`⏭️  SKIP: Message ${messageId} already in chat (chatState ID check)`);
            // Add to persistent tracker so we don't check again
            processedMessageIdsRef.current.add(messageId);
            return false;
          }
          
          console.log(`✨ NEW: Message ${messageId} will be added to chat`);
          return true;
        });
        
        // 🏋️ POST-WORKOUT DEBRIEF FIX: Ensure ContextualLaunchPill is shown even when message was filtered as duplicate
        // When the debrief message was already loaded (e.g. from GET /api/chat/messages/recent or localStorage),
        // the response is excluded from newResponses — so we never run the branch that adds the pill.
        // Add the pill here for any debrief response that was filtered out, so user always sees pill + message.
        const newResponseIds = new Set(newResponses.map(r => r.message_id || `cedar-bg-${r.task_id}`));
        const filteredOutDebriefs = completedResponses.filter(
          r => r.context?.type === 'post_workout_debrief' && !newResponseIds.has(r.message_id || `cedar-bg-${r.task_id}`)
        );
        for (const response of filteredOutDebriefs) {
          const responseMessageId = response.message_id || `cedar-bg-${response.task_id}`;
          const pillId = `debrief-pill-${responseMessageId}`;
          const pillAlreadyInChat = chatState.messages.some(m => m.id === pillId);
          if (pillAlreadyInChat) continue;
          if (processedMessageIdsRef.current.has(pillId)) continue;
          const workoutTitle = response.context?.workout_title || 'Workout';
          const durationMin = response.context?.duration_minutes;
          const completionType = response.context?.completion_type || 'completed';
          const debriefPill: ChatMessage = {
            id: pillId,
            sender: 'system',
            text: '',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            status: 'delivered',
            contextualPill: {
              type: 'workout',
              title: `✅ ${workoutTitle}`,
              subtitle: durationMin ? `${durationMin} min • ${completionType}` : completionType,
              metadata: { from: 'Workout Complete', status: 'completed' }
            }
          };
          processedMessageIdsRef.current.add(pillId);
          console.log(`🏋️ DEBRIEF PILL (duplicate path): Injecting ContextualLaunchPill for "${workoutTitle}" (message already in chat)`);
          transition({ type: 'ADD_MESSAGE', payload: { message: debriefPill } });
        }
        
        // 🚀 INSTANT MESSAGE DISPLAY (Jan 22, 2026): Replace incomplete messages with complete versions
        // Check for incomplete messages in current state (thinking, typing, streaming, or empty text)
        // 🔧 FIX (Feb 2, 2026): Include 'thinking' status for navigation persistence
        const incompleteMessages = chatState.messages.filter(msg =>
          msg.sender === 'cedar' &&
          (msg.status === 'thinking' || msg.status === 'streaming' || msg.status === 'typing' || msg.status === 'error' || !msg.text || msg.text.trim().length === 0)
        );
        
        console.log(`🔄 BACKGROUND: Found ${incompleteMessages.length} incomplete messages (including thinking dots), checking for complete versions`);
        
        // For each incomplete message, check if complete version exists in completedResponses
        for (const incomplete of incompleteMessages) {
          const completeVersion = completedResponses.find(resp => {
            const respId = resp.message_id || `cedar-bg-${resp.task_id}`;
            const respText = (resp.text || resp.response?.text || '').trim();
            const incompleteText = (incomplete.text || '').trim();
            
            // Match by ID (most reliable)
            if (respId === incomplete.id) return true;
            
            // Match by text similarity (catches cases where ID changed)
            if (incompleteText.length > 0) {
              const normalizeText = (text: string): string => text.trim().replace(/\s+/g, ' ').toLowerCase();
              const similarity = calculateTextSimilarity(normalizeText(incompleteText), normalizeText(respText));
              if (similarity > 0.5) return true; // Partial match (incomplete vs complete)
            }
            
            return false;
          });
          
          if (completeVersion) {
            console.log(`✅ BACKGROUND: Replacing incomplete message ${incomplete.id} with complete version`);
            const completeText = completeVersion.text || completeVersion.response?.text || '';
            
            // Replace incomplete with complete, mark as already animated (user wasn't watching)
            const completeMessage: ChatMessage = {
              ...incomplete,
              text: completeText,
              status: 'delivered',
              wasAnimated: true, // User wasn't watching, show instantly
              isAnimated: false,
              isStreaming: false
            };
            
            // Use ADD_MESSAGE - reducer handles updates if message ID exists
            transition({ type: 'ADD_MESSAGE', payload: { message: completeMessage } });
            
            // Add to persistent tracker
            const messageId = completeVersion.message_id || `cedar-bg-${completeVersion.task_id}`;
            processedMessageIdsRef.current.add(messageId);
            
            // Remove from newResponses to avoid duplicate addition
            const index = newResponses.findIndex(r => {
              const rId = r.message_id || `cedar-bg-${r.task_id}`;
              return rId === messageId;
            });
            if (index !== -1) {
              newResponses.splice(index, 1);
              console.log(`⏭️  BACKGROUND: Removed ${messageId} from newResponses (already replaced incomplete)`);
            }
          }
        }
        
        if (newResponses.length === 0) {
          console.log(`⏭️  BACKGROUND: All responses already in chat or replaced incomplete messages - skipping duplicate additions`);
          
          // 🔧 FIX (Dec 2025): Clear isTyping even when no new responses to add
          // This prevents stuck typing states from previous operations
          if (chatState.ui.isTyping) {
            console.log(`🔧 STUCK STATE FIX: Clearing isTyping=true that was stuck (no new responses to add)`);
            transition({
              type: 'UPDATE_UI',
              payload: { isTyping: false, typingMessageId: null }
            });
          }
          return; // Early exit - no other state updates needed
        }
        
        // Add completed responses to chat
        // 🔧 STRATEGIC FIX (Dec 6, 2025): Handle chunk splitting properly
        // Background responses may contain CHUNK_1:, CHUNK_2: markers that need to be split
        // into separate message bubbles with 1-second delays between them
        const processResponsesSequentially = async () => {
          for (const response of newResponses) {
            // 🎯 SMART ANIMATION: Only animate fresh responses (< 30 seconds old)
            // Old responses (user was away for minutes/hours) appear instantly
            const completedAt = new Date(response.completed_at || response.timestamp);
            const ageInSeconds = (Date.now() - completedAt.getTime()) / 1000;
            const isFreshResponse = ageInSeconds < 30;
            const responseMessageId = response.message_id || `cedar-bg-${response.task_id}`;
            
            // 🔧 FIX (Jan 5, 2026): Skip responses older than 24 hours (likely from chat history)
            if (ageInSeconds > 86400) {
              console.log(`⏭️ BACKGROUND: Skipping old response ${responseMessageId} (age: ${(ageInSeconds/3600).toFixed(1)}h)`);
              continue;
            }
            
            console.log(`🎯 ANIMATION LOGIC: Response ID ${responseMessageId}, age: ${ageInSeconds.toFixed(1)}s, will ${isFreshResponse ? 'ANIMATE' : 'SHOW INSTANTLY'}`);
            
            const rawText = response.text || response.response?.text || 'Response completed';
            
            // 🔍 CHUNK DETECTION: Check if response contains chunk markers
            const chunkRegex = /CHUNK_(\d+):\s*/gi;
            const hasChunks = chunkRegex.test(rawText);
            
            if (hasChunks) {
              // 🎯 MULTI-CHUNK RESPONSE: Split into separate bubbles
              console.log(`🎯 CHUNK SPLIT: Detected multi-chunk response, splitting into separate bubbles`);
              
              // ✅ FIX (Jan 7, 2026): Extract chunk number AND content, then sort by actual chunk number
              // Previous: split() removed chunk markers, causing wrong order if LLM output CHUNK_2, CHUNK_3, CHUNK_1
              // Now: Parse chunk numbers explicitly and sort before display
              const chunkMatches = Array.from(rawText.matchAll(/CHUNK_(\d+):\s*([^]*?)(?=CHUNK_\d+:|$)/gi));
              const chunkSections = chunkMatches
                .map((match) => ({
                  number: parseInt((match as RegExpMatchArray)[1], 10), // Actual chunk number from CHUNK_N:
                  text: (match as RegExpMatchArray)[2].trim()
                }))
                .sort((a, b) => a.number - b.number); // Sort by actual chunk number
              
              console.log(`🎯 CHUNK SPLIT: Found ${chunkSections.length} chunks:`, chunkSections.map(c => `CHUNK_${c.number}`).join(', '));
              
              // Create separate message bubble for each chunk (now in correct order)
              for (let i = 0; i < chunkSections.length; i++) {
                const chunk = chunkSections[i];
                const chunkText = chunk.text;
                const chunkNumber = chunk.number; // Use actual chunk number from LLM
                const chunkId = `${responseMessageId}_chunk${chunkNumber}`;
                
                console.log(`🎯 CHUNK ${chunkNumber}: Creating bubble (${chunkText.length} chars)`);
                
                // 🔧 FEB 10/11, 2026: Parse action buttons from chunk text (+ auto-fire service connectors)
                let finalChunkText = chunkText;
                let chunkActions: any[] | undefined;
                let chunkServiceAction: ChatMessage['serviceAction'] | undefined;
                if (/\[([^\]]+)\]\(action:([^)]+)\)/.test(chunkText)) {
                  const parsed = parseMessageActions(chunkText);
                  if (parsed.actions && parsed.actions.length > 0) {
                    finalChunkText = parsed.cleanText;
                    chunkActions = parsed.actions;
                    console.log(`🔧 ACTION PARSE: Parsed ${parsed.actions.length} action(s) from chunk ${chunkNumber}`);
                  }
                  if (parsed.serviceAction) {
                    chunkServiceAction = parsed.serviceAction;
                    console.log(`📱 ACTION PARSE: Auto-attached serviceAction from chunk ${chunkNumber}`);
                  }
                }

                const chunkMessage: ChatMessage = {
                  id: chunkId,
                  sender: 'cedar',
                  text: finalChunkText,
                  timestamp: new Date(response.timestamp || response.completed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                  status: 'delivered',
                  isAnimated: isFreshResponse,
                  wasAnimated: !isFreshResponse,
                  actions: chunkActions,
                  ...(chunkServiceAction ? { serviceAction: chunkServiceAction } : {}),
                  metadata: {
                    // 🎉 FIX: Spread backend metadata so special_header, plan_id, actions, etc. flow through
                    ...(response.metadata || {}),
                    chunk_info: {
                      sequence: chunkNumber,
                      total_parts: chunkSections.length,
                      type: 'background_processed_chunk'
                    },
                    isAutoGenerated: true,
                    stepTransition: `background_task_${response.task_id}_chunk${chunkNumber}`,
                    optionC: false,
                    discoveryStart: false,
                    responseAge: ageInSeconds
                  } as any
                };
                
                // 🔒 Add to persistent tracker BEFORE dispatching
                processedMessageIdsRef.current.add(chunkId);
                console.log(`🔒 PERSISTENT TRACKER: Added ${chunkId} (total: ${processedMessageIdsRef.current.size})`);
                
                transition({
                  type: 'ADD_MESSAGE',
                  payload: { message: chunkMessage }
                });
                
                // ⏱️ DELAY: Wait 1 second between chunks (except after last chunk)
                if (i < chunkSections.length - 1) {
                  console.log(`⏱️ CHUNK DELAY: Waiting 1 second before showing CHUNK_${chunkNumber + 1}`);
                  await new Promise(resolve => setTimeout(resolve, 1000));
                }
              }
            } else {
              // 🎯 SINGLE-CHUNK RESPONSE: Display as single bubble (existing logic)
              console.log(`🎯 SINGLE MESSAGE: No chunk markers detected, displaying as single bubble`);
              
              // 🎛️ FIX (Jan 29, 2026): Sync backend state from context_data BEFORE checking sliders
              // Background responses include backend_state in context_data (added to main.py)
              // We need to sync this to chatState.backendState so slider detection works
              let contextBackendState = null;
              if (response.context?.backend_state) {
                contextBackendState = response.context.backend_state;
                console.log('🔄 BACKGROUND: Syncing backend state from context_data:', contextBackendState);
                
                // Sync to chatState (this updates onboardingStep, chatName, etc.)
                transition({
                  type: 'SYNC_BACKEND_STATE',
                  payload: {
                    backendState: contextBackendState
                  }
                });
              }
              
              // 🎛️ FIX (Jan 29, 2026): Use fresh contextBackendState OR fallback to chatState
              // Since transition() is async, chatState.backendState won't be updated yet
              // Use the fresh contextBackendState we just extracted
              const backendStateToUse = contextBackendState || chatState.backendState;
              const shouldTriggerSliders = backendStateToUse?.personality_slider_action === true;
              if (shouldTriggerSliders) {
                console.log('🎛️ BACKGROUND: Backend says show personality sliders');
                console.log('   Using backend state:', backendStateToUse);
              }
              
              // 🚨 FIX #3: Ensure sender is always 'cedar' for background responses
              // Prevent incorrect 'user' sender type that causes blank duplicate messages
              const cedarMessage: ChatMessage = {
                id: responseMessageId,
                sender: 'cedar', // 🚨 FIX #3: Always cedar, never user
                text: rawText.trim(),
                timestamp: new Date(response.timestamp || response.completed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                status: 'delivered',
                isAnimated: isFreshResponse,
                wasAnimated: !isFreshResponse,
                metadata: {
                  // 🎉 FIX: Spread backend metadata so special_header, plan_id, actions, etc. flow through
                  ...(response.metadata || {}),
                  triggerPersonalitySliders: shouldTriggerSliders, // 🎛️ FIX: Trigger sliders for Step 6
                  chunk_info: {
                    sequence: 1,
                    total_parts: 1,
                    type: 'background_processed'
                  },
                  isAutoGenerated: true,
                  stepTransition: `background_task_${response.task_id}`,
                  optionC: false,
                  discoveryStart: false,
                  responseAge: ageInSeconds
                } as any
              };
              
              // 🔧 FEB 10/11, 2026: Parse action buttons from background responses (+ auto-fire service connectors)
              // Background polling may receive messages with raw [label](action:type) markdown
              if (cedarMessage.text && /\[([^\]]+)\]\(action:([^)]+)\)/.test(cedarMessage.text)) {
                const { cleanText, actions, serviceAction } = parseMessageActions(cedarMessage.text);
                if (actions && actions.length > 0) {
                  cedarMessage.text = cleanText;
                  (cedarMessage as any).actions = actions;
                  console.log(`🔧 ACTION PARSE: Parsed ${actions.length} action(s) from background response ${responseMessageId}`);
                }
                if (serviceAction) {
                  cedarMessage.text = cleanText;
                  (cedarMessage as any).serviceAction = serviceAction;
                  console.log(`📱 ACTION PARSE: Auto-attached serviceAction from background response ${responseMessageId}`);
                }
              }

              // 🚨 FIX #3: Validate message has content before adding
              if (!cedarMessage.text || cedarMessage.text.trim().length === 0) {
                console.log(`⏭️  SKIP: Background response ${responseMessageId} has no text content - skipping blank message`);
                processedMessageIdsRef.current.add(responseMessageId); // Track to prevent retry
                continue; // Skip this response
              }

              // M35B FIX: Guard against raw JSON from intelligence engine leaks (matches native guard)
              const _trimmedBg = cedarMessage.text.trim();
              if (_trimmedBg.startsWith('{') || _trimmedBg.startsWith('[')) {
                console.warn(`⏭️  SKIP: Background response ${responseMessageId} appears to be raw JSON — suppressing`);
                processedMessageIdsRef.current.add(responseMessageId);
                continue;
              }
              
              // 🏋️ POST-WORKOUT DEBRIEF: Inject ContextualLaunchPill before debrief message
              // This gives debriefs the same visual polish as Mission 29 "Chat with Cedar" deep links
              if (response.context?.type === 'post_workout_debrief') {
                const workoutTitle = response.context?.workout_title || 'Workout';
                const activityType = response.context?.activity_type || 'workout';
                const durationMin = response.context?.duration_minutes;
                const completionType = response.context?.completion_type || 'completed';
                const pillId = `debrief-pill-${responseMessageId}`;
                
                if (!processedMessageIdsRef.current.has(pillId)) {
                  const debriefPill: ChatMessage = {
                    id: pillId,
                    sender: 'system',
                    text: '',
                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    status: 'delivered',
                    contextualPill: {
                      type: 'workout',
                      title: `✅ ${workoutTitle}`,
                      subtitle: durationMin ? `${durationMin} min • ${completionType}` : completionType,
                      metadata: {
                        from: 'Workout Complete',
                        status: 'completed'
                      }
                    }
                  };
                  
                  processedMessageIdsRef.current.add(pillId);
                  console.log(`🏋️ DEBRIEF PILL: Injecting ContextualLaunchPill for workout "${workoutTitle}"`);
                  
                  transition({
                    type: 'ADD_MESSAGE',
                    payload: { message: debriefPill }
                  });
                }
              }
              
              // 🔒 Add to persistent tracker BEFORE dispatching
              processedMessageIdsRef.current.add(cedarMessage.id);
              console.log(`🔒 PERSISTENT TRACKER: Added ${cedarMessage.id} (total: ${processedMessageIdsRef.current.size})`);
              
              transition({
                type: 'ADD_MESSAGE',
                payload: { message: cedarMessage }
              });
              
              // 🎛️ TRIGGER PERSONALITY SLIDERS if flagged
              if (cedarMessage.metadata?.triggerPersonalitySliders) {
                console.log('🎛️ BACKGROUND: Triggering inline personality sliders after message added');
                
                // 🔒 DISABLE AUTOSCROLL: Turn off autoscroll when Step 6 sliders appear
                transition({
                  type: 'UPDATE_UI',
                  payload: {
                    scrollState: {
                      shouldAutoScroll: false,
                      isUserAtBottom: true
                    }
                  }
                });
                
                // 🎛️ SHOW INLINE SLIDERS: Trigger the personality slider component
                personalitySliderActions.showInline([]);
              }
            }
          }
        };
        
        // Execute async processing
        processResponsesSequentially();
          
          // 🔔 Trigger notification event for in-app notification (Mission 11 integration)
          // Only notify for NEW responses (not ones already loaded during init)
          if (newResponses.length > 0) {
            const firstResponse = newResponses[0];
            const responsePreview = firstResponse.text || firstResponse.response?.text || 'Your response is ready';
            const truncatedPreview = responsePreview.length > 100 
              ? responsePreview.substring(0, 100) + '...' 
              : responsePreview;
            
            // Dispatch custom event for App.tsx to catch
            const notificationEvent = new CustomEvent('chatResponseReady', {
              detail: {
                message: newResponses.length === 1 
                  ? 'Cedar responded!' 
                  : `Cedar sent ${newResponses.length} responses!`,
                responsePreview: truncatedPreview
              }
            });
            
            if (Platform.OS === 'web') {
              window.dispatchEvent(notificationEvent);
              console.log(`🔔 BACKGROUND: Dispatched notification event for ${newResponses.length} NEW responses`);
            }
            // Note: On native, notification handled by expo-notifications
            
            // 🔒 MARK AS RETRIEVED: Moved to top of checkBackgroundResponses() (BUG #75 FIX)
            // REMOVED OLD DUPLICATE: Was here after processing, now at function start (line ~1877)
          } else if (completedResponses.length > 0 && newResponses.length === 0) {
            console.log(`⏭️  BACKGROUND: No notification sent - all responses were loaded during init`);
          }
          
        // Clear typing state and persistence (only if there were new responses)
        if (newResponses.length > 0) {
          console.log('🔄 BACKGROUND: Clearing typing state after displaying NEW completed responses');
          transition({
            type: 'UPDATE_UI',
            payload: { 
              isTyping: false,
              typingMessageId: null
            }
          });
          setCurrentUserMessage(''); // Clear stored message when responses are complete
          
          // 🔄 PERSISTENCE: Clear pending request from storage
          await storage.removeItem(`pending_response_${currentUser}`);
          console.log('🔄 PERSISTENCE: Cleared pending request from storage');
        } else if (completedResponses.length > 0 && newResponses.length === 0) {
          console.log('⏭️  BACKGROUND: Not clearing typing state - no new responses added (already in chat)');
        }
        }
      } catch (error) {
        console.error('❌ BACKGROUND: Error processing completed responses:', error);
      } finally {
        checkingBackgroundResponsesRef.current = false;
      }
  };

  // 🔄 BACKGROUND RESPONSE CHECKING: Set up polling when component mounts
  useEffect(() => {
    // Check immediately when component mounts or when returning to chat
    checkBackgroundResponses();

    // Apr 28, 2026 (Recal Hardening Sprint, PR 7, G20): exponential backoff
    // on consecutive failures. Pre-fix the bare setInterval(poll, 5000) kept
    // hammering at 5s during a partial outage (5xx, network blip), amplifying
    // upstream load and slowing recovery. Implementation keeps the
    // setInterval shape (so the existing clearInterval cleanup at line 3012
    // works unchanged) but the poll callback self-throttles based on a
    // consecutive-failure counter — when in backoff, the tick exits early
    // until the backoff window has elapsed. Effective rate degrades from
    // 5s → 10s → 20s → 40s → 60s (cap) on consecutive failures and snaps
    // back to 5s on the next success. Tick cost when backed off is ~0
    // (one comparison) so leaving the underlying setInterval at 5s is fine.
    let _consecutiveFailures = 0;
    let _currentBackoff = 5000;
    const BASE_INTERVAL = 5000;
    const MAX_BACKOFF = 60_000;
    let _nextEligibleAt = Date.now();
    const checkBackgroundResponsesWithBackoff = async () => {
      if (Date.now() < _nextEligibleAt) {
        return; // skip this tick; still in backoff window
      }
      try {
        await checkBackgroundResponses();
        // checkBackgroundResponses swallows errors internally — getting here
        // means it completed without throwing. Reset backoff.
        if (_consecutiveFailures > 0) {
          _consecutiveFailures = 0;
          _currentBackoff = BASE_INTERVAL;
        }
        _nextEligibleAt = Date.now() + BASE_INTERVAL;
      } catch (err) {
        _consecutiveFailures += 1;
        _currentBackoff = Math.min(_currentBackoff * 2, MAX_BACKOFF);
        _nextEligibleAt = Date.now() + _currentBackoff;
        // eslint-disable-next-line no-console
        console.warn(
          `🔄 BACKGROUND POLL DEGRADED: failures=${_consecutiveFailures}, ` +
          `next_backoff=${_currentBackoff}ms, err=${(err as Error)?.message}`,
        );
      }
    };

    // Set up interval to check periodically (every 5 seconds for faster recovery).
    // The underlying tick rate stays at 5s; backoff is enforced inside the callback.
    const interval = setInterval(checkBackgroundResponsesWithBackoff, BASE_INTERVAL);
    
    // 🔄 VISIBILITY CHANGE: web equivalent of native focus/blur.
    //
    // 'visible':  tab regained focus — poll for responses that arrived
    //             while hidden, AND fire a mark-read (Pattern B read
    //             receipt; advances chat_last_read_at on the backend).
    //
    // 'hidden':   tab lost focus — fire a second mark-read so messages
    //             the user received via SSE during the active session
    //             (but before switching tabs) are reflected as read.
    //             Without this, a user who has chat open, receives 5
    //             Cedar replies live, then switches tabs will see a
    //             stale unread-5 on return. Web analogue of native
    //             blur read-receipt.
    const handleVisibilityChange = Platform.OS === 'web' ? () => {
      if (document.visibilityState === 'visible') {
        console.log('🔄 BACKGROUND: Page became visible, checking for responses');
        checkBackgroundResponses();
        markChatAsRead();
      } else if (document.visibilityState === 'hidden') {
        markChatAsRead();
      }
    } : null;
    
    if (Platform.OS === 'web' && handleVisibilityChange) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }
    
    return () => {
      clearInterval(interval);
      if (Platform.OS === 'web' && handleVisibilityChange) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, []); // 🔧 FIX (Jan 9, 2026): Empty deps - only run once on mount, not on every state change

  // 🏋️ MISSION 29 PENDING DEBRIEF: Poll every 1.5s while waiting for debrief so message appears quickly
  useEffect(() => {
    if (!pendingDebriefTaskId) return;
    const fastInterval = setInterval(checkBackgroundResponses, 1500);
    return () => clearInterval(fastInterval);
  }, [pendingDebriefTaskId]);

  // 🎯 TOUR COMPLETION POLL: Immediate background poll when tour completes
  // 🚨 FIX (Jan 28, 2026): Fetch prewarmed "what name?" message immediately after tour
  useEffect(() => {
    // Watch for phase transition from 'tour' to 'onboarding'
    // This happens when COMPLETE_TOUR transition fires (line 1049-1058)
    if (chatState.phase === 'onboarding') {
      // Check if we just transitioned from tour
      const prevPhase = chatState.ui.chatPaused ? 'tour' : 'unknown';
      
      console.log('🎯 TOUR COMPLETE: Phase is now onboarding, triggering immediate background poll');
      console.log('   This will fetch the prewarmed "what name?" message that was blocked during tour');
      
      // Trigger immediate background poll to fetch waiting messages
      // No delay - we want the name question to appear right away after tour
      setTimeout(() => {
        checkBackgroundResponses();
      }, 100); // Small 100ms delay to let phase transition settle
    }
  }, [chatState.phase]); // Watch for phase changes

  // 🔔 NOTIFICATION POLLING - Poll for new notifications every 5 seconds
  useEffect(() => {
    /**
     * Trigger in-app notification banner
     * Dispatches custom event for InAppNotificationBannerManager to handle
     * 
     * 📥 JAN 31, 2026: Mission 11 in-app banner integration
     */
    const triggerInAppBanner = (notification: {
      id: string;
      title: string;
      message: string;
      icon: string | React.ReactNode;
      conversationId?: string;
    }) => {
      if (Platform.OS !== 'web' || typeof window === 'undefined') return;
      console.log('📥 BANNER: Triggering in-app banner', notification);
      const event = new CustomEvent('coach-cedar-notification', {
        detail: {
          id: notification.id,
          title: notification.title,
          message: notification.message,
          icon: notification.icon,
          conversationId: notification.conversationId,
          timestamp: Date.now()
        }
      });
      window.dispatchEvent(event);
    };

    // L1 FIX (Mission 35B): Gate verbose polling logs behind __DEV__
    const _debugNotif = typeof __DEV__ !== 'undefined' && __DEV__;

    const pollNotifications = async () => {
      try {
        const userId = await getCurrentUserId();

        const { getAuthHeaders } = await import('../utils/auth');
        const authHeaders = await getAuthHeaders();

        if (!authHeaders.Authorization || !userId) {
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/notifications/chat-pills/${userId}`, {
          headers: authHeaders
        });

        if (response.ok) {
          // Success - reset failure counter
          consecutiveNotificationFailures.current = 0;

          const data = await response.json();
          if (data.notifications && data.notifications.length > 0) {
            if (_debugNotif) console.log('POLL: New notifications:', data.notifications.length);

            const isOnChat = Platform.OS === 'web' ? (typeof window !== 'undefined' && window.location?.pathname === '/chat') : true;

            for (const notification of data.notifications) {
              const notifType = notification.type ?? notification.notification_type;
              const behavior = getNotificationBehavior(notifType);

              if (_debugNotif) console.log(`NOTIF: ${notifType} -> ${behavior}`);

              if (shouldAutoNavigate(notifType, isOnChat)) {
                router.push(`/chat?from=auto-notification&notificationId=${notification.id}&notificationType=${notifType}&timestamp=${Date.now()}`);
                return;
              }

              if (shouldShowBanner(notifType, isOnChat)) {
                triggerInAppBanner({
                  id: notification.id,
                  title: getNotificationTitle(notifType),
                  message: getBannerMessage(notification.message || notification.body || '', 100),
                  icon: getNotificationIcon(notifType),
                  conversationId: notification.conversation_id
                });
              }
            }
            
            // 🔄 FEB 4, 2026: Convert notifications to message objects for inline display
            // This ensures they stay in place chronologically instead of jumping to bottom
            for (const notification of data.notifications) {
              // Check if notification already exists as a message (avoid duplicates)
              const existingMessage = chatState.messages.find(
                m => m.metadata?.notificationId === notification.id
              );
              
              if (!existingMessage) {
                // 🏋️ SKIP: Don't create AsyncProcessNotificationPill for debriefs -
                // ContextualLaunchPill + Cedar message are injected via checkBackgroundResponses
                const pillType = notification.type ?? notification.notification_type;
                if (pillType === 'post_workout_debrief') {
                  console.log(`🏋️ DEBRIEF SKIP: Suppressing AsyncProcessNotificationPill for debrief (handled by ContextualLaunchPill)`);
                  continue;
                }

                // Skip pills with missing/fallback content — misconfigured scheduled notifications
                if (!notification.title || notification.title === 'Coach Cedar Update') {
                  console.log(`⚠️ SKIP: Notification ${notification.id} has no title or fallback title`);
                  continue;
                }
                
                const notificationMessage: ChatMessage = {
                  id: `notification_${notification.id}`,
                  sender: 'system' as const,
                  text: '', // Empty text, rendered via special component
                  timestamp: notification.created_at,
                  status: 'delivered' as const,
                  metadata: {
                    notificationPill: true,
                    notificationId: notification.id,
                    notificationData: notification
                  }
                };
                
                console.log(`📩 NOTIFICATION: Converting to message object (ID: ${notification.id})`);
                
                transition({
                  type: 'ADD_MESSAGE',
                  payload: { message: notificationMessage }
                });
              }
            }
            
            setLastNotificationCheck(Date.now());
            
            // 🏷️ JAN 31, 2026 FIX: Mark notifications as viewed when on chat
            // Prevents re-fetching same notifications on every poll
            if (isOnChat && data.notifications.length > 0) {
              console.log('🏷️ MARKING AS VIEWED: Acknowledging', data.notifications.length, 'notifications');
              
              // Mark each notification as acknowledged
              for (const notification of data.notifications) {
                try {
                  await fetch(`${API_BASE_URL}/api/notifications/acknowledge`, {
                    method: 'POST',
                    headers: {
                      ...authHeaders,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      user_id: userId,
                      notification_id: notification.id,
                      action: 'viewed'
                    })
                  });
                  console.log('✅ ACKNOWLEDGED:', (notification.type ?? notification.notification_type), notification.id);
                } catch (error) {
                  console.warn('⚠️ Failed to acknowledge notification:', notification.id, error);
                }
              }
            }
          } else {
            console.log('🔔 POLL: No notifications in response');
          }
        } else {
          // 🛡️ Non-OK response (4xx/5xx) - track but don't spam console
          consecutiveNotificationFailures.current++;
          
          if (consecutiveNotificationFailures.current <= 2) {
            console.log(`⚠️ Notification poll: ${response.status} (likely deployment), will retry...`);
          } else if (consecutiveNotificationFailures.current >= 5) {
            console.error(`❌ Notification polling: 5+ failures with status ${response.status}`);
            const errorText = await response.text().catch(() => 'Unable to read error');
            console.error(`❌ Error response:`, errorText);
          }
          // Else: Silent retry for transient infrastructure errors (502 during deployment)
        }
      } catch (error) {
        // 🛡️ RESILIENT ERROR HANDLING (Jan 30, 2026):
        // Intermittent 502 errors during deployment are EXPECTED (infrastructure-level)
        // Don't spam console, just track consecutive failures
        consecutiveNotificationFailures.current++;
        
        if (consecutiveNotificationFailures.current === 1) {
          console.log('⚠️ Notification poll failed (likely deployment in progress), will retry...');
        } else if (consecutiveNotificationFailures.current >= 5) {
          console.error('❌ Notification polling: 5+ consecutive failures - endpoint may be down');
          console.error('❌ Last error:', error);
        }
        // Else: Silent retry (don't spam console for every transient network error)
      }
    };

    console.log('🔔 POLL: Setting up notification polling interval...');
    // Poll immediately and then every 5 seconds
    pollNotifications();
    const interval = setInterval(pollNotifications, 5000);

    return () => {
      console.log('🔔 POLL: Cleaning up notification polling interval');
      clearInterval(interval);
    };
  }, []); // Poll independently of user changes
  
  // 🔇 REMOVED (Jan 5, 2026): Processing pill not needed with interim message system
  // 🔍 DEBUG: Log processing state changes
  // useEffect(() => {
  //   if (processingFiles) {
  //     console.log('🔍 SHIMMER DEBUG: processingFiles is true, shimmer should be visible');
  //   }
  // }, [processingFiles]);

  // 📅 CHAT HISTORY: Load chat history on component mount
  useEffect(() => {
    const loadChatHistory = async () => {
      if (loadingHistory) return;
      
      setLoadingHistory(true);
      try {
        console.log('📅 HISTORY: Loading chat history...');

        // Load chat history from backend (server-side storage is authoritative;
        // the legacy localStorage migration shim was removed in Phase 6 since
        // the server builds history from chat_messages automatically).
        const history = await ChatHistoryProcessor.getChatHistory();
        setChatHistory(history);
        console.log('✅ HISTORY: Chat history loaded successfully');
      } catch (error) {
        console.error('❌ HISTORY: Failed to load chat history:', error);
      } finally {
        setLoadingHistory(false);
      }
    };

    loadChatHistory();

    // Pattern B read receipt on chat-page mount — advances
    // chat_last_read_at and (on native) clears the badge + dismisses
    // delivered notifications.
    markChatAsRead();

    // Cleanup: fire another mark-read on unmount, i.e. when the user
    // navigates away from the Chat page entirely. Web analogue of
    // native's useFocusEffect blur cleanup. Without this, Cedar replies
    // received live via SSE during the Chat-page session are not
    // reflected as read until the user returns — same class of stale-
    // unread bug as the native long-session case.
    return () => {
      markChatAsRead();
    };
  }, []);

  // 📅 CHAT HISTORY VISIBILITY: Show when user scrolls up (only in discovery Step 7+)
  useEffect(() => {
    const scrollElement = scrollFrameRef.current;
    if (!scrollElement) return;

    const handleScroll = () => {
      // Only enable history if user is in discovery (Step 7+)
      const currentStep = chatState.backendState?.onboardingStep ?? 0;
      if (currentStep < 7) {
        return; // Don't show history during onboarding
      }

      // Show history when user scrolls up (within 500px of top)
      const scrollTop = scrollElement.scrollTop;
      const showThreshold = 500;

      if (scrollTop < showThreshold && !showChatHistory) {
        setShowChatHistory(true);
        console.log('📅 HISTORY: Activated history browser (user scrolled up in discovery)');
      }
    };

    scrollElement.addEventListener('scroll', handleScroll);
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, [showChatHistory, chatState.backendState?.onboardingStep]);
  
  // 🔇 REMOVED (Jan 5, 2026): Processing pill not needed with interim message system
  // 🎯 SHIMMER MINIMUM DISPLAY TIME: Ensure shimmer shows for at least 2 seconds
  // const setProcessingFilesWithMinimumTime = (value: boolean) => {
  //   if (value === false && processingStartTime) {
  //     const elapsed = Date.now() - processingStartTime;
  //     const minimumDisplayTime = 2000; // 2 seconds
  //     
  //     if (elapsed < minimumDisplayTime) {
  //       const remainingTime = minimumDisplayTime - elapsed;
  //       console.log(`🔍 SHIMMER: Delaying hide by ${remainingTime}ms to ensure visibility`);
  //       setTimeout(() => {
  //         setProcessingFiles(false);
  //         setProcessingStartTime(null);
  //       }, remainingTime);
  //       return;
  //     }
  //   }
  //   
  //   setProcessingFiles(value);
  //   if (value === false) {
  //     setProcessingStartTime(null);
  //   }
  // };
  
  // 🚀 SERVICE CONNECTION HANDLERS - Clean implementation
  // Strava OAuth flow removed - Strava integration disabled from frontend (Feb 2026)
  const handleServiceConnect = async (service: ServiceType) => {
    console.log('🎯 SERVICE CONNECT:', service);
    
    try {
      console.log('🔍 SERVICE: Continuing with inline flow for', service);
    } catch (error) {
      console.error('❌ SERVICE CONNECTION ERROR:', error);
      throw error;
    }
  };

  const handleServiceCancel = async (messageId: string) => {
    console.log('❌ SERVICE CANCELLED:', messageId);
    
    try {
      // Remove the service connection message
      transition({
        type: 'REMOVE_MESSAGE',
        payload: { messageId }
      });
      
      // Call backend to record decline and get smart continuation
      const jwtToken = await storage.getItem('jwt_token') || await storage.getItem('token');
      
      // Type definition for backend response
      type DeclineResponse = {
        success: boolean;
        message: string;
        continue_flow: string;
        next_question?: string;
        reason: string;
      };
      
      let response_data: DeclineResponse | null = null;
      try {
        const response = await fetch(`${API_BASE_URL}/device/decline`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(jwtToken ? { 'Authorization': `Bearer ${jwtToken}` } : {})
          },
          body: JSON.stringify({
            reason: 'cancel_button'
          })
        });
        
        if (response.ok) {
          response_data = await response.json() as DeclineResponse;
          console.log('✅ DEVICE DECLINE: Backend response:', response_data);
        } else {
          console.warn('⚠️ DEVICE DECLINE: Backend call failed, using fallback message');
        }
      } catch (error) {
        console.error('❌ DEVICE DECLINE ERROR:', error);
        // Continue with fallback - don't block UX
      }
      
      // Use backend message if available, otherwise fallback
      const cancelText = response_data?.message || 
        "No worries at all! We can connect your fitness data whenever you're ready. In the meantime, I can still provide great coaching based on what you share with me.";
      
      const cancelMessage: ChatMessage = {
        id: `cedar_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        text: cancelText,
        sender: 'cedar',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        status: 'delivered'
      };
      
      transition({ type: 'ADD_MESSAGE', payload: { message: cancelMessage } });
      
      // If there's a next question (Context Gate), add it as a follow-up
      if (response_data?.next_question) {
        console.log('🎯 DEVICE DECLINE: Context Gate has next question, will be asked naturally in next turn');
        // The backend has updated the Context Gate state - next user message will trigger it
      }
      
      
    } catch (error) {
      console.error('Error handling service cancellation:', error);
    }
  };
  const [hasAskedForName, setHasAskedForName] = useState(false);
  
  // 🚨 AUTO-TRIGGER PROTECTION: Prevent multiple rapid auto-triggers
  const [autoTriggerSent, setAutoTriggerSent] = useState(false);
  
  // 🚀 AUTO-MESSAGE POLLING: For Option C auto-follow-up system
  const [lastAutoMessagePoll, setLastAutoMessagePoll] = useState<string>('');
  const autoMessageIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 🎬 INITIAL MESSAGE ANIMATION: Callback for when typing animation completes
  const handleInitialAnimationComplete = useCallback(() => {
    console.log('✅ Initial message: Animation completed naturally');
    
    // 🔧 FIX: Use getCurrentUserId directly instead of relying on chatState.messages
    // This prevents callback recreation on every message update (which was breaking animation)
    const currentUser = getCurrentUserId();
    if (!currentUser) return;
    
    // Mark message as delivered by creating a simple update
    // No need to find the message - we know it's id='initial'
    const completedMessage = {
      id: 'initial',
      sender: 'cedar' as const,
      text: "Hi, I'm Coach Cedar!",
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      status: 'delivered' as const,
      isStreaming: false,
      isAnimated: false,
      wasAnimated: true  // 🎬 Mark as animated so it doesn't re-animate
    };
    transition({ type: 'ADD_MESSAGE', payload: { message: completedMessage } });
    
    // Async tour check
    const checkTourStatus = async () => {
      // 🔧 FIX (Feb 2026): Use 'onboarding_tour_seen' — this is the key written by
      // OnboardingTour.tsx (line 118) and App.tsx (line 156). The old 'tour_completed'
      // key was never written, so this always returned false (tour always re-fired) for
      // any user who had gone through the tour. Now consistent with initializeChatSystem.
      const tourCompleted = await getUserStorage('onboarding_tour_seen', currentUser) === 'true';
      
      if (!tourCompleted) {
        // Wait 1 second after animation, then start tour
        setTimeout(() => {
          console.log('🎯 Starting tour after 1s pause');
          transition({ type: 'START_TOUR' });
          setUserStorage('chat_paused', 'true', currentUser);
          
          if (Platform.OS === 'web') {
            const event = new CustomEvent('startOnboardingTour');
            window.dispatchEvent(event);
          }
          // Note: On native, tour events handled by state management
        }, 1000);  // 1s pause before tour starts
      } else {
        // Ready for onboarding
        transition({ type: 'COMPLETE_TOUR' });
      }
    };
    
    // Call the async function
    checkTourStatus();
  }, [transition]); // 🚨 CRITICAL: Removed chatState.messages - it was causing callback recreation on every frame!

  // Refs remain the same
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollFrameRef = useRef<HTMLDivElement>(null);
  const composerBottomRef = useRef<HTMLDivElement>(null);
  
  // 🚨 CRITICAL FIX: Refs for scroll state to prevent stale closures
  const isUserAtBottomRef = useRef<boolean>(true);
  const shouldAutoScrollRef = useRef<boolean>(true);

  // Scroll FAB visibility (up/down navigation arrows)
  const showScrollDownFabRef = useRef<boolean>(false);
  const showScrollUpFabRef = useRef<boolean>(false);
  const [showScrollDownFab, setShowScrollDownFab] = useState(false);
  const [showScrollUpFab, setShowScrollUpFab] = useState(false);
  const scrollDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Derived state from state machine
  const messages = chatState.messages;
  const isTyping = chatState.ui.isTyping;
  const typingMessageId = chatState.ui.typingMessageId;
  const chatPaused = chatState.ui.chatPaused;
  const isUserAtBottom = chatState.ui.scrollState.isUserAtBottom;
  const shouldAutoScroll = chatState.ui.scrollState.shouldAutoScroll;
  const isInitialLoad = chatState.ui.scrollState.isInitialLoad;
  
  // 🚨 CRITICAL FIX: Update refs to prevent stale closures
  isUserAtBottomRef.current = isUserAtBottom;
  shouldAutoScrollRef.current = shouldAutoScroll;

  // 📱 iOS KEYBOARD FIX (web only): Adjust composer position when keyboard appears
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || !window.visualViewport) return;
    const composerEl = composerBottomRef.current;
    if (!composerEl || window.innerWidth > 768) return;

    const handleKeyboardAdjust = () => {
      if (!composerEl || !window.visualViewport) return;
      const viewport = window.visualViewport;
      const layoutHeight = window.innerHeight;
      const visualHeight = viewport.height;
      const keyboardHeight = layoutHeight - visualHeight;
      if (keyboardHeight > 30) {
        composerEl.style.bottom = `0px`;
        console.log(`📱 COMPOSER: Keyboard open (${keyboardHeight}px), bottom = 0px (on keyboard)`);
      } else {
        composerEl.style.bottom = `calc(82px + env(safe-area-inset-bottom, 0px))`;
        console.log(`📱 COMPOSER: Keyboard closed, bottom = 82px + safe-area (above nav)`);
      }
    };
    window.visualViewport.addEventListener('resize', handleKeyboardAdjust);
    window.visualViewport.addEventListener('scroll', handleKeyboardAdjust);
    handleKeyboardAdjust();
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleKeyboardAdjust);
        window.visualViewport.removeEventListener('scroll', handleKeyboardAdjust);
      }
    };
  }, []);

  // State machine initialization - SINGLE useEffect replaces all racing hooks
  useEffect(() => {
    console.log('🚀 STATE MACHINE: Initializing chat system...');
    
    const initializeChatSystem = async () => {
      try {
        // Step 1: Get current user
        const currentUser = getCurrentUserId();
        if (!currentUser) {
          console.error('❌ STATE MACHINE: No user ID available');
          return;
        }

        // 🐛 DIAGNOSTIC (Jan 21, 2026): Debug chat history persistence
        console.log('🔍 INIT: Starting chat initialization');
        console.log(`   Current user ID: ${currentUser}`);
        console.log(`   Storage key will be: ${currentUser}_chat_messages`);
        
        // Check if localStorage has any data at all
        if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
          const allKeys = Object.keys(localStorage);
          const chatKeys = allKeys.filter(k => k.includes('chat_messages'));
          console.log(`   Total localStorage keys: ${allKeys.length}`);
          console.log(`   Chat message keys found: ${chatKeys.length}`);
          if (chatKeys.length > 0) {
            console.log(`   Chat keys:`, chatKeys.slice(0, 5));
          }
        }

        // Step 2: Load user state from storage
        const tourCompleted = await getUserStorage('onboarding_tour_seen', currentUser) === 'true';
        const chatInitialized = await getUserStorage('chat_initialized', currentUser) === 'true';

        // 🔧 CRITICAL FIX (Jan 31, 2026): Declare persistedBackendState BEFORE message parsing
        // Problem: Line 3013 accesses persistedBackendState inside filter operation, but variable
        // was declared at line 3175 (AFTER parsing). This caused ReferenceError when returning to chat.
        // Result: Parse crash → empty array → database fallback → educational message lost
        // Solution: Move declaration to TOP of function, before any message filtering
        let persistedBackendState: any = null;
        const persistedBackendStateStr = await getUserStorage('backend_state', currentUser);
        if (persistedBackendStateStr) {
          try {
            persistedBackendState = JSON.parse(persistedBackendStateStr);
            console.log('🔄 STATE MACHINE: Loaded backend state BEFORE message parsing:', persistedBackendState);
          } catch (error) {
            console.error('❌ STATE MACHINE: Failed to parse persisted backend state:', error);
          }
        }

        // Step 3: Load persisted messages
        let persistedMessages: ChatMessage[] = [];
        const persistedMessagesStr = await getUserStorage('chat_messages', currentUser);
        console.log(`🔍 INIT: getUserStorage returned:`, persistedMessagesStr ? `${persistedMessagesStr.length} chars` : 'null');
        if (persistedMessagesStr) {
          try {
            const rawMessages = JSON.parse(persistedMessagesStr);
            
            // 🔧 FIX Issue #1: Deduplicate persisted messages by ID
            // Messages can be saved multiple times due to rapid re-renders or navigation
            // 🔧 FIX MELISSA BUG #2 (Nov 17, 2025): Filter out stale onboarding messages
            // Problem: Old CHUNK 1/CHUNK 2 messages from onboarding steps 4/5 persist in localStorage
            // and get re-inserted when user returns to chat after navigating away.
            // Solution: Filter out messages older than 2 hours that contain onboarding chunk markers
            const twoHoursAgo = Date.now() - (2 * 60 * 60 * 1000);
            const seen = new Set<string>();
            // 🚀 INSTANT MESSAGE DISPLAY (Jan 22, 2026): Detect complete messages on load
            // Complete messages (delivered with text) → wasAnimated: true (show instantly)
            // Incomplete messages (streaming or no text) → wasAnimated: false (will animate or be replaced)
            persistedMessages = rawMessages
              .map((msg: any) => {
                let wasAnimated = false;
                
                if (msg.sender === 'cedar') {
                  const isComplete = msg.status === 'delivered' && 
                                    msg.text && 
                                    msg.text.trim().length > 0;
                  
                  // Special handling for initial message
                  if (msg.id === 'initial') {
                    wasAnimated = msg.wasAnimated || false; // Preserve existing state
                  } else {
                    wasAnimated = isComplete; // Complete = show instantly, incomplete = will animate
                  }
                  
                  console.log(`🔍 INIT: Message ${msg.id} - status: ${msg.status}, text length: ${msg.text?.length || 0}, wasAnimated: ${wasAnimated}`);
                } else {
                  // User messages always animated
                  wasAnimated = true;
                }
                
                return {
                  ...msg,
                  isAnimated: false,
                  wasAnimated,
                  // Ensure status is set correctly
                  status: msg.status || (msg.sender === 'cedar' ? 'streaming' : 'delivered')
                };
              })
              .filter((msg: ChatMessage) => {
                // Deduplicate check
                if (seen.has(msg.id)) {
                  console.log(`🔧 FIX Issue #1: Skipping duplicate persisted message: ${msg.id}`);
                  return false;
                }
                seen.add(msg.id);
                
                // 🔧 MELISSA BUG #2 FIX: Filter out stale onboarding chunk messages
                // These are onboarding messages that should be ephemeral but got persisted
                const isStaleOnboardingChunk = 
                  msg.sender === 'cedar' && 
                  msg.id && 
                  msg.id.includes('chunk') &&
                  msg.text && (
                    msg.text.includes('CHUNK_1') || 
                    msg.text.includes('CHUNK_2') ||
                    msg.text.includes('CHUNK 1:') ||
                    msg.text.includes('CHUNK 2:')
                  );
                
                // Parse timestamp to check age
                let messageAge = 0;
                try {
                  // Timestamp format: "9:30 PM" or "14:30"
                  // Can't determine exact age without date, so be conservative
                  // Only filter if message has clear chunk markers AND is from onboarding context
                  if (isStaleOnboardingChunk) {
                    console.log(`🔧 MELISSA BUG #2: Filtering out stale onboarding chunk message: ${msg.id}`);
                    return false;
                  }
                } catch (error) {
                  // If we can't parse timestamp, keep the message (safe default)
                }
                
                // 🔧 MALFORMED ID FILTER (JAN 30, 2026): Remove Cedar messages with invalid ID format
                // Purpose: Prevent blank bubbles from corrupted/old messages in localStorage
                // Valid Cedar IDs: cedar_{timestamp}_{random}, cedar-{type}-{timestamp}, {timestamp}-chunk-{i}
                // Invalid: Plain numeric IDs like "769746094100" (these should never exist)
                if (msg.sender === 'cedar') {
                  const id = msg.id || '';
                  
                  // Check if ID is malformed (plain numeric without proper prefix/format)
                  const isPlainNumeric = /^\d+$/.test(id); // Just digits, no prefixes
                  const isMalformedChunk = /^\d+-chunk-\d+$/.test(id); // Old chunk format without cedar_ prefix
                  
                  if (isPlainNumeric || isMalformedChunk) {
                    console.log(`🚨 MALFORMED ID FILTER: Removing Cedar message with invalid ID: ${id} (plain numeric or malformed chunk)`);
                    return false;
                  }
                }
                
                // 🔧 STEP 3: STEP-AWARENESS FILTER (JAN 30, 2026)
                // Purpose: Prevent old onboarding UI (personality sliders, etc.) from reappearing after Step 6
                // This fixes the "personality sliders reappearing in Step 7" bug
                if (msg.sender === 'cedar' && msg.metadata) {
                  // 🔧 FIX (Feb 22, 2026): Guard against null persistedBackendState for new users
                  // New users have no stored backend_state, so persistedBackendState is null.
                  // Accessing .onboardingStep on null throws TypeError, crashing initializeChatSystem
                  // and causing the blank screen / tour not firing bug.
                  const currentStep = persistedBackendState?.onboardingStep || 99;
                  const onboardingCompleted = persistedBackendState?.onboardingCompleted || false;
                  
                  // Filter personality sliders (should only appear in Step 6)
                  if (msg.metadata.triggerPersonalitySliders === true) {
                    const shouldKeep = !(currentStep >= 7 || onboardingCompleted);
                    if (!shouldKeep) {
                      console.log(`🔧 STEP FILTER: Removing old personality slider message (current step: ${currentStep}, completed: ${onboardingCompleted})`);
                    }
                    return shouldKeep;
                  }
                  
                  // Filter messages from old onboarding steps
                  if (msg.metadata.onboardingStep !== undefined) {
                    const messageStep = msg.metadata.onboardingStep;
                    const shouldKeep = !(currentStep > messageStep && (currentStep >= 7 || onboardingCompleted));
                    if (!shouldKeep) {
                      console.log(`🔧 STEP FILTER: Removing old Step ${messageStep} message (current step: ${currentStep})`);
                    }
                    return shouldKeep;
                  }
                }
                
                return true;
              });
            
            if (rawMessages.length !== persistedMessages.length) {
              console.log(`🔧 FIX: Removed ${rawMessages.length - persistedMessages.length} duplicate/stale messages from persisted storage`);
            }
          } catch (error) {
            console.error('❌ STATE MACHINE: Failed to parse persisted messages:', error);
          }
        }
        
        // 🆕 STEP 7: INTERRUPTED MESSAGE RECOVERY (JAN 30, 2026)
        // Purpose: Re-fetch complete versions of messages that were interrupted during streaming
        // This handles the case where user navigates away mid-stream
        const interruptedMessages = persistedMessages.filter(msg => msg.metadata?.interrupted === true);
        
        if (interruptedMessages.length > 0) {
          console.log(`🔄 INTERRUPTED: Found ${interruptedMessages.length} interrupted messages, fetching complete versions from database`);
          
          try {
            const jwtToken = await storage.getItem('jwt_token');
            
            const response = await fetch(`${API_BASE_URL}/api/chat/messages/recent?limit=50`, {
              headers: {
                'Authorization': `Bearer ${jwtToken}`
              }
            });
            
            if (response.ok) {
              const data = await response.json();
              
              // Replace interrupted messages with complete versions from database
              const dbMessageMap = new Map((data.messages as ChatMessage[]).map((m: ChatMessage) => [m.id, m]));
              
              persistedMessages = persistedMessages.map(msg => {
                if (msg.metadata?.interrupted && dbMessageMap.has(msg.id)) {
                  const completeMessage = dbMessageMap.get(msg.id) as ChatMessage;
                  console.log(`✅ INTERRUPTED: Replaced ${msg.id} with complete version (${completeMessage.text?.length || 0} chars)`);
                  return completeMessage;
                }
                return msg;
              });
            }
          } catch (error) {
            console.error('❌ INTERRUPTED: Failed to fetch complete versions:', error);
            console.log('   Continuing with interrupted messages as-is');
          }
        }
        
        // Step 3.2: ENHANCED DATABASE FALLBACK (Jan 25, 2026, Enhanced Jan 30, 2026)
        // Fetch from database if:
        // 1. localStorage is empty or has very few messages (<3)
        // 2. localStorage is STALE (last message > 5 minutes old)
        // This handles: localStorage cleared, new device, incognito mode, storage failures, stale data
        
        // 🆕 STEP 5: STALE DETECTION (JAN 30, 2026)
        const isLocalStorageStale = (messages: ChatMessage[]): boolean => {
          if (messages.length === 0) return true;
          
          const lastMessage = messages[messages.length - 1];
          // Try to parse timestamp - messages use time format like "9:30 PM"
          // Since we don't have dates, use created_at if available, otherwise check message age heuristically
          
          // Simple heuristic: If we have messages but no new ones came from background polling,
          // and user was away for a while, localStorage might be stale
          // We'll use a more conservative check: if localStorage has messages but database fallback wasn't triggered before
          // Just check if < 3 messages OR if we detect it's been a while
          
          // For now, we'll rely on the <3 message check as primary signal
          // Additional stale detection can be added if we track lastUpdated timestamp
          return false; // Placeholder - can enhance with timestamp tracking if needed
        };
        
        // 🔧 FIX (Feb 2026): Skip database fallback for brand new users (!chatInitialized)
        // New users have no prior conversation - fetching from DB would pull prewarmed onboarding
        // messages, populating persistedMessages and preventing the animated greeting + tour flow.
        const shouldFetchFromDatabase = chatInitialized && (persistedMessages.length < 3 || isLocalStorageStale(persistedMessages));
        
        if (shouldFetchFromDatabase) {
          const staleReason = persistedMessages.length < 3 
            ? `localStorage has only ${persistedMessages.length} messages` 
            : 'localStorage may be stale';
          console.log(`🔄 DATABASE FALLBACK: ${staleReason}, fetching from database...`);
          
          try {
            const jwtToken = await storage.getItem('jwt_token');
            
            const response = await fetch(`${API_BASE_URL}/api/chat/messages/recent?limit=50`, {
              headers: {
                'Authorization': `Bearer ${jwtToken}`
              }
            });
            
            if (response.ok) {
              const data = await response.json();
              console.log(`✅ DATABASE FALLBACK: Fetched ${data.count} messages from database`);
              console.log(`   Range: ${data.oldest} to ${data.newest}`);
              
              // Merge with any messages from localStorage (deduplicate by ID)
              const localStorageIds = new Set(persistedMessages.map(m => m.id));
              const dbMessages = data.messages.filter((m: ChatMessage) => !localStorageIds.has(m.id));
              
              persistedMessages = [...persistedMessages, ...dbMessages];
              console.log(`✅ MERGED: ${persistedMessages.length} total messages (${dbMessages.length} from database)`);
              
              // Save to localStorage for future
              await setUserStorage('chat_messages', JSON.stringify(persistedMessages), currentUser);
              console.log(`💾 DATABASE FALLBACK: Saved ${persistedMessages.length} messages to localStorage for next time`);
            } else {
              console.warn(`⚠️ DATABASE FALLBACK: Failed to fetch (${response.status}), continuing with localStorage only`);
            }
          } catch (dbError) {
            console.error('❌ DATABASE FALLBACK: Error fetching from database:', dbError);
            console.log('   Continuing with localStorage messages only');
          }
        } else {
          console.log(`✅ INIT: localStorage has ${persistedMessages.length} messages, skipping database fallback`);
        }

        // Step 3.5: Check for completed background responses and merge with persisted messages
        // This ensures responses that finished while user was away are ALREADY present
        
        // 🚨 FIX (Jan 22, 2026): Helper function for text similarity calculation
        // Uses Jaccard similarity (intersection over union of word sets)
        const calculateTextSimilarity = (text1: string, text2: string): number => {
          const words1 = new Set(text1.split(/\s+/).filter(w => w.length > 0));
          const words2 = new Set(text2.split(/\s+/).filter(w => w.length > 0));
          
          if (words1.size === 0 && words2.size === 0) return 1.0;
          if (words1.size === 0 || words2.size === 0) return 0.0;
          
          const intersection = new Set([...words1].filter(w => words2.has(w)));
          const union = new Set([...words1, ...words2]);
          
          return intersection.size / union.size;
        };
        
        // 🔧 FIX (Jan 31, 2026): persistedBackendState now declared at TOP of function (line ~2908)
        // This prevents "Cannot access 'bs' before initialization" error during message filtering
        // Old location was here (line 3175), but filter at line 3013 needs it earlier
        
        // 🔧 FIX (Feb 2026): Skip background response check for brand new users (!chatInitialized)
        // New users have no prior conversation history - checking would pull prewarmed onboarding
        // messages and prematurely populate persistedMessages, breaking the animated greeting + tour.
        if (!chatInitialized) {
          console.log('🆕 INIT: New user detected - skipping background response check to preserve greeting + tour flow');
        }
        
        console.log('🔄 INIT: Checking for completed background responses during initialization');
        
        try {
          const completedResponses = chatInitialized ? await checkForCompletedBackgroundResponses(currentUser) : [];
          
          if (completedResponses.length > 0) {
            console.log(`🔄 INIT: Found ${completedResponses.length} completed responses - merging with persisted messages`);
            
            // 🚨 CRITICAL FIX (Nov 4, 2025): Deduplicate background responses against persisted messages
            // The persistence commit enabled saving for ALL users, so we must dedupe during init
            // Otherwise same message loads from BOTH localStorage AND background responses
            const persistedIds = new Set(persistedMessages.map(msg => msg.id));
            
            // 🐛 DIAGNOSTIC LOGGING (Jan 21, 2026): Debug persisted messages
            console.log(`🔍 DEDUP SETUP: Persisted messages analysis`);
            console.log(`   Total persisted messages: ${persistedMessages.length}`);
            console.log(`   Cedar messages: ${persistedMessages.filter(m => m.sender === 'cedar').length}`);
            console.log(`   Persisted IDs (first 10):`, Array.from(persistedIds).slice(0, 10));
            persistedMessages.filter(m => m.sender === 'cedar').slice(0, 5).forEach(msg => {
              console.log(`   Cedar message: ID=${msg.id}, text="${msg.text.substring(0, 60)}..."`);
            });
            
            // 🔧 FIX (Jan 5, 2026): Also dedupe by text content to catch fallback IDs
            // Issue: Same message can have different IDs (cedar_xxx vs cedar_fallback_xxx)
            // causing duplicates even though text is identical
            // 🚨 FIX (Jan 22, 2026): Use normalized text for better matching (whitespace, case)
            const normalizeText = (text: string): string => {
              return text.trim().replace(/\s+/g, ' ').toLowerCase();
            };
            
            const persistedTexts = new Set(
              persistedMessages
                .filter(m => m.sender === 'cedar')
                .map(m => normalizeText(m.text).substring(0, 200)) // First 200 chars, normalized
            );
            
            // 🚨 FIX (Jan 22, 2026): Also check FULL normalized text for exact matches
            const persistedFullTexts = new Set(
              persistedMessages
                .filter(m => m.sender === 'cedar')
                .map(m => normalizeText(m.text)) // Full text, normalized
            );
            
            completedResponses.forEach((response) => {
              try {
                const messageId = response.message_id || `cedar-bg-${response.task_id}`;
                const responseText = (response.response_text || response.text || response.response?.text || '').trim();
                const normalizedResponseText = normalizeText(responseText);
                const textFingerprint = normalizedResponseText.substring(0, 200);
                
                // 🐛 DIAGNOSTIC LOGGING (Jan 21, 2026): Debug onboarding message duplication
                console.log(`🔍 DEDUP CHECK: Background response analysis`);
                console.log(`   Message ID from backend: ${messageId}`);
                console.log(`   response.message_id: ${response.message_id || 'undefined'}`);
                console.log(`   response.task_id: ${response.task_id || 'undefined'}`);
                console.log(`   Text fingerprint (normalized): "${textFingerprint}"`);
                console.log(`   Persisted IDs count: ${persistedIds.size}`);
                console.log(`   Persisted texts count: ${persistedTexts.size}`);
                console.log(`   ID exists in persisted: ${persistedIds.has(messageId)}`);
                console.log(`   Text fingerprint exists: ${persistedTexts.has(textFingerprint)}`);
                console.log(`   Full text exists: ${persistedFullTexts.has(normalizedResponseText)}`);
              
              // Skip if already in persisted messages (by ID)
              if (persistedIds.has(messageId)) {
                console.log(`⏭️ INIT: Skipping background response ${messageId} - already in persisted messages (ID match)`);
                return;
              }
              
              // 🚨 FIX (Jan 22, 2026): Check FULL normalized text first (most reliable)
              if (persistedFullTexts.has(normalizedResponseText)) {
                console.log(`⏭️ INIT: Skipping background response ${messageId} - exact duplicate text (full normalized match)`);
                return;
              }
              
              // 🔧 FIX (Jan 5, 2026): Skip if text fingerprint matches existing message
              if (persistedTexts.has(textFingerprint)) {
                console.log(`⏭️ INIT: Skipping background response ${messageId} - duplicate text content detected (fingerprint match)`);
                return;
              }
              
              // 🚨 FIX (Jan 22, 2026): Also check against current chatState.messages if available
              // This catches messages that were added during initialization but not yet saved to localStorage
              // Note: chatState might not be available during initialization, so this is a safety check
              try {
                const currentMessages = (chatState as any)?.messages || [];
                if (currentMessages.length > 0) {
                  const currentMessageTexts = new Set(
                    currentMessages
                      .filter((m: ChatMessage) => m.sender === 'cedar')
                      .map((m: ChatMessage) => normalizeText(m.text))
                  );
                  
                  if (currentMessageTexts.has(normalizedResponseText)) {
                    console.log(`⏭️ INIT: Skipping background response ${messageId} - already in current chatState (full normalized match)`);
                    return;
                  }
                }
              } catch (e) {
                // chatState not available yet - that's OK, we'll rely on persisted messages check
              }
              
              // 🚨 FIX (Jan 22, 2026): Similarity check for near-duplicates (>95% match)
              // Catches messages with minor formatting differences (extra spaces, line breaks, etc.)
              let isNearDuplicate = false;
              for (const persistedText of persistedFullTexts) {
                const similarity = calculateTextSimilarity(normalizedResponseText, persistedText);
                if (similarity > 0.95) {
                  console.log(`⏭️ INIT: Skipping background response ${messageId} - near-duplicate detected (${(similarity * 100).toFixed(1)}% similarity)`);
                  isNearDuplicate = true;
                  break;
                }
              }
              if (isNearDuplicate) {
                return;
              }
              
              // 🆕 SMART ANIMATION LOGIC (Dec 29, 2025):
              // - If response is COMPLETE (older than 30s) → Instant reveal (no animation)
              // - If response is PARTIAL/fresh (< 30s) → Animate as if just received
              const completedAt = new Date(response.completed_at || response.timestamp);
              const ageInSeconds = (Date.now() - completedAt.getTime()) / 1000;
              const isFreshResponse = ageInSeconds < 30;
              
              // 🔧 FIX (Jan 5, 2026): Skip responses older than 24 hours (likely from chat history)
              if (ageInSeconds > 86400) {
                console.log(`⏭️ INIT: Skipping old background response ${messageId} (age: ${(ageInSeconds/3600).toFixed(1)}h)`);
                return;
              }
              
              // 🐛 FIX (Jan 21, 2026): Skip old onboarding messages if user has progressed to discovery
              // PROBLEM: Step 4 health motivation question stored in background_chat_responses
              //          User progresses to Step 7, navigates away, returns
              //          Old Step 4 message retrieved and duplicated at current position
              // SOLUTION: If user is in discovery/completed (Step 7+) AND response is >5min old,
              //          skip it - these are cached onboarding messages that user already saw
              const currentStep = persistedBackendState?.onboardingStep || 0;
              const onboardingCompleted = persistedBackendState?.onboardingCompleted || false;
              const isInDiscoveryOrBeyond = currentStep >= 7 || onboardingCompleted;
              const isOldResponse = ageInSeconds > 300; // 5 minutes
              
              if (isInDiscoveryOrBeyond && isOldResponse) {
                console.log(`⏭️ INIT: Skipping old onboarding cache ${messageId} (age: ${ageInSeconds.toFixed(0)}s, current step: ${currentStep})`);
                console.log(`   Reason: User is in discovery (step ${currentStep}), this is stale onboarding message`);
                console.log(`   Text preview: "${textFingerprint}"`);
                return;
              }
              
              console.log(`🔄 INIT: Response age: ${ageInSeconds.toFixed(1)}s, will ${isFreshResponse ? 'ANIMATE (fresh/partial)' : 'SHOW INSTANTLY (complete)'}`);
              
              // 🔧 FIX: Strip chunk markers from background response text before displaying
              // Prevents CHUNK_1:, CHUNK_2: markers from appearing in UI
              const rawText = response.response_text || response.text || response.response?.text || 'Response completed';
              const cleanText = rawText
                .replace(/CHUNK_\d+:\s*/gi, '')  // Strip CHUNK_1:, CHUNK_2:, etc.
                .replace(/\[CHUNK\s+\d+\]\s*/gi, '')  // Strip [CHUNK 1], etc.
                .replace(/---\s*CHUNK\s+\d+\s*---\s*/gi, '')  // Strip --- CHUNK 1 ---, etc.
                .trim();
              
              const cedarMessage: ChatMessage = {
                id: messageId,
                sender: 'cedar',
                text: cleanText,
                timestamp: new Date(response.timestamp || response.completed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                status: 'delivered',
              // 🆕 SMART ANIMATION: Fresh responses animate, complete responses show instantly
              isAnimated: false,
              wasAnimated: !isFreshResponse,  // Only mark as already shown if it's a complete response
              metadata: {
                  chunk_info: {
                    sequence: 1,
                    total_parts: 1,
                    type: 'background_processed'
                  },
                  isAutoGenerated: true,
                  stepTransition: `background_task_${response.task_id}`,
                  optionC: false,
                  discoveryStart: false,
                  responseAge: ageInSeconds,
                  loadedDuringInit: true  // Flag to distinguish from post-mount recovery
                } as any
              };
              
              // Add to persisted messages array (only if not already present)
              persistedMessages.push(cedarMessage);
              persistedIds.add(messageId); // Track for subsequent iterations
              } catch (forEachError) {
                console.error(`❌ INIT: Error processing background response in forEach:`, forEachError);
                console.error(`   Response data:`, response);
                // Continue with next response
              }
            });
            
            console.log(`✅ INIT: Merged ${completedResponses.length} background responses with ${persistedMessages.length - completedResponses.length} persisted messages`);
          } else {
            console.log('🔄 INIT: No completed background responses found');
          }
        } catch (error) {
          console.error('❌ INIT: Error checking background responses during initialization:', error);
          // Non-blocking - continue with persisted messages only
        }
        
        // 🔧 FEB 10, 2026: Parse action buttons from ALL loaded messages
        // Messages saved before the action parsing fix contain raw [label](action:type) markdown
        // which browsers render as "[blocked]" text. Parse these into structured actions.
        persistedMessages = persistedMessages.map(msg => {
          if (msg.sender === 'cedar' && msg.text && /\[([^\]]+)\]\(action:([^)]+)\)/.test(msg.text)) {
            const { cleanText, actions, serviceAction } = parseMessageActions(msg.text);
            if ((actions && actions.length > 0) || serviceAction) {
              console.log(`🔧 ACTION PARSE: Fixed action(s) in persisted message ${msg.id}${serviceAction ? ' (+ serviceAction for inline connector)' : ''}`);
              return { ...msg, text: cleanText, actions, ...(serviceAction ? { serviceAction } : {}) };
            }
          }
          return msg;
        });

        // 🔒 POPULATE PERSISTENT TRACKER: Add all loaded message IDs to prevent re-processing
        persistedMessages.forEach(msg => {
          if (msg.sender === 'cedar') {
            processedMessageIdsRef.current.add(msg.id);
          }
        });
        console.log(`🔒 INIT: Populated persistent tracker with ${processedMessageIdsRef.current.size} message IDs`);

        const user: ChatUser = {
          id: currentUser,
          name: await storage.getItem('userName') || 'User',
          tourCompleted,
          onboardingCompleted: false
        };

        // Step 4: Determine initial phase based on state
        let initialPhase: ChatPhase = 'initializing';
        let hasStartedInitialMessage = false;

        console.log(`🔍 PHASE CALC: Starting phase calculation`);
        console.log(`   persistedMessages.length = ${persistedMessages.length}`);
        console.log(`   tourCompleted = ${tourCompleted}`);
        console.log(`   searchParams.from = ${searchParams.from}`);

        if ((searchParams.from === 'move-section' || searchParams.from === 'settings') && searchParams.contextData) {
          // Contextual entry - go straight to active
          console.log(`   ✅ Branch: Contextual entry - setting phase to 'active'`);
          initialPhase = 'active';
          // 🔧 FIX (Mar 19, 2026): searchParams are strings after Expo Router migration — JSON.parse
          let parsedContextData: any;
          try {
            parsedContextData = typeof searchParams.contextData === 'string' ? JSON.parse(searchParams.contextData as string) : searchParams.contextData;
          } catch { parsedContextData = {}; }
          transition({
            type: 'HANDLE_CONTEXTUAL_ENTRY',
            payload: {
              from: searchParams.from,
              contextData: parsedContextData,
              initialMessage: searchParams.initialMessage
            }
          });
        } else if (searchParams.from === 'post_workout_debrief' && searchParams.conversation_id) {
          // User story #3: Just finished workout, navigated here to see debrief – go to active and use same conversation
          console.log(`   ✅ Branch: Post-workout debrief entry - setting phase to 'active'`);
          initialPhase = 'active';
        } else if (searchParams.post_workout_debrief_pending && searchParams.task_id) {
          // Mission 29 in-app flow: Debrief pending – user on Chat waiting for pill + thinking → typing → delivered
          console.log(`   ✅ Branch: Post-workout debrief PENDING - setting phase to 'active', will show pill + thinking then poll`);
          initialPhase = 'active';
        } else if (persistedMessages.length > 0) {
          // User has existing conversation
          console.log(`   ✅ Branch: Has ${persistedMessages.length} messages - setting phase to 'active'`);
          initialPhase = 'active';
          hasStartedInitialMessage = true;
        } else if (persistedBackendState?.onboardingStep > 0) {
          // 🔧 FIX (Mar 2026): Backend says onboarding already started but localStorage is empty
          // (e.g., cleared storage, different browser, incognito). Go to active and let DB fetch
          // restore messages instead of re-showing the welcome greeting.
          console.log(`   ✅ Branch: No local messages but backend onboardingStep=${persistedBackendState.onboardingStep} - setting phase to 'active'`);
          initialPhase = 'active';
          hasStartedInitialMessage = true;
        } else if (!tourCompleted) {
          // New user - needs tour
          console.log(`   ✅ Branch: Tour not completed - setting phase to 'initializing'`);
          initialPhase = 'initializing';
        } else {
          // Tour completed but no messages - ready for onboarding
          console.log(`   ✅ Branch: Tour completed, no messages - setting phase to 'onboarding'`);
          initialPhase = 'onboarding';
        }

        // Step 5: Initialize state
        console.log(`🚨 DEBUG: Initializing with ${persistedMessages.length} persisted messages`);
        // 🔧 FIX: Log actual message IDs to track persistence
        if (persistedMessages.length > 0) {
          const lastMessage = persistedMessages[persistedMessages.length - 1];
          console.log(`📝 INIT: Last persisted message - ID: ${lastMessage.id}, sender: ${lastMessage.sender}, text length: ${lastMessage.text?.length || 0}, status: ${lastMessage.status}`);
        }
        
        console.log(`🔍 INIT: Calculated initialPhase = "${initialPhase}"`);
        console.log(`   persistedMessages.length = ${persistedMessages.length}`);
        console.log(`   tourCompleted = ${tourCompleted}`);
        console.log(`   hasStartedInitialMessage = ${hasStartedInitialMessage}`);
        
        transition({
          type: 'INITIALIZE',
          payload: {
            user,
            messages: persistedMessages,
            phase: initialPhase,
            hasStartedInitialMessage,
            backendState: persistedBackendState
          }
        });

        // Step 6: Handle initial message creation when there are no messages
        // Show the animated greeting ONLY for genuinely new users.
        // 🔧 FIX (Mar 2026): Also check backend state — if onboarding already started
        // (step > 0), skip the welcome message even if localStorage is empty.
        const backendAlreadyStarted = persistedBackendState?.onboardingStep > 0;
        if (persistedMessages.length === 0 && !backendAlreadyStarted) {
          console.log('🎯 STATE MACHINE: Creating initial message (no persisted messages found)');
          
          // Mark as initialized to prevent future re-initialization
          setUserStorage('chat_initialized', 'true', currentUser);
          
          // Create initial message with typing animation
          // 🔧 FIX (Feb 2026): Reduced from 500ms to 0ms — CmpCedarbubble now renders TypingText
          // immediately (avatar CSS animation plays independently), so no blank period needed.
          // setTimeout(0) still lets React commit the INITIALIZE render before adding the message.
          setTimeout(() => {
            const messageText = "Hi, I'm Coach Cedar!";
            const initialMessage: ChatMessage = {
              id: 'initial',
              sender: 'cedar',
              text: messageText,
              timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              status: 'typing',  // Start as typing to trigger animation
              isStreaming: true,  // Enable TypingText animation
              isAnimated: true
            };

            transition({ type: 'ADD_MESSAGE', payload: { message: initialMessage } });
            console.log(`⌨️ Initial message: Starting typing animation (${messageText.length} chars)`);
          }, 0);
        }

        // Step 7: Tour completion handling moved to separate useEffect
        // (This prevents race conditions and ensures proper detection)

        console.log('✅ STATE MACHINE: Initialization complete');
        
        // User story #3: Post-workout debrief entry – store session so replies use same conversation, then fetch debrief immediately
        // Mission 29 in-app: Pending debrief – show pill + thinking, poll by task_id; when response arrives → typing then delivered
        if (searchParams.post_workout_debrief_pending && searchParams.task_id) {
          const task_id = searchParams.task_id as string;
          pendingDebriefTaskIdRef.current = task_id;
          setPendingDebriefTaskId(task_id);
          await storage.setItem('contextual_session', JSON.stringify({
            type: 'post_workout_debrief',
            conversation_id: searchParams.conversation_id,
            title: searchParams.workout_title || 'Workout',
            pending_task_id: task_id
          }));
          console.log('🔄 POST-WORKOUT DEBRIEF PENDING: Stored session + task_id, adding pill + thinking, then polling');
          setTimeout(() => {
            const pillId = `debrief-pill-${task_id}`;
            if (!processedMessageIdsRef.current.has(pillId)) {
              const workoutTitle = (searchParams as { workout_title?: string })?.workout_title || 'Workout';
              const debriefPill: ChatMessage = {
                id: pillId,
                sender: 'system',
                text: '',
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                status: 'delivered',
                contextualPill: {
                  type: 'workout',
                  title: `✅ ${workoutTitle}`,
                  subtitle: 'Cedar is writing your debrief…',
                  metadata: { from: 'Workout Complete', status: 'completed' }
                }
              };
              processedMessageIdsRef.current.add(pillId);
              transition({ type: 'ADD_MESSAGE', payload: { message: debriefPill } });
            }
            const thinkingId = `pending-debrief-${task_id}`;
            const thinkingMessage: ChatMessage = {
              id: thinkingId,
              sender: 'cedar',
              text: '',
              timestamp: '',
              status: 'thinking',
              isStreaming: false,
              isAnimated: true
            };
            transition({ type: 'ADD_MESSAGE', payload: { message: thinkingMessage } });
            checkBackgroundResponses();
          }, 100);
        } else if (searchParams.from === 'post_workout_debrief' && searchParams.conversation_id) {
          await storage.setItem('contextual_session', JSON.stringify({
            type: 'post_workout_debrief',
            conversation_id: searchParams.conversation_id,
            title: searchParams.workout_title || 'Workout'
          }));
          console.log('🔄 POST-WORKOUT DEBRIEF: Stored contextual_session, triggering immediate background poll');
          setTimeout(() => checkBackgroundResponses(), 300); // 300ms so debrief message appears quickly
        } else {
          // 🔧 FIX (Jan 22, 2026): Immediately check for background responses after initialization
          setTimeout(() => {
            console.log('🔄 INIT: Triggering immediate background poll check after initialization');
            checkBackgroundResponses();
          }, 1000);
        }
        
      } catch (error) {
        console.error('❌ STATE MACHINE: Initialization failed:', error);
        // Fallback to safe state
        transition({ type: 'ACTIVATE_CHAT', payload: {} });
      }
    };

    initializeChatSystem();
  }, []); // Run once only - no dependencies to avoid race conditions

  // 🔄 RECOVERY: Check for pending requests on mount and restore typing indicator
  useEffect(() => {
    const currentUser = getCurrentUserId();
    if (!currentUser) return;
    
    (async () => {
      const pendingRequestStr = await storage.getItem(`pending_response_${currentUser}`);
      if (pendingRequestStr) {
        try {
          const pendingRequest = JSON.parse(pendingRequestStr);
          const timeElapsed = Date.now() - pendingRequest.timestamp;
          
          // Only restore if request is less than 5 minutes old (prevent stale state)
          if (timeElapsed < 5 * 60 * 1000) {
            console.log('🔄 RECOVERY: Found pending request, restoring typing indicator');
            
            // 🔧 FIX (Feb 2, 2026): Use stored cedarMessageId for proper message matching
            const storedCedarId = pendingRequest.cedarMessageId;
            
            // Check if thinking message exists with the CORRECT ID
            const existingThinkingMessage = storedCedarId 
              ? chatState.messages.find(msg => msg.id === storedCedarId)
              : chatState.messages.find(msg => 
                  msg.sender === 'cedar' && 
                  (msg.status === 'thinking' || msg.status === 'typing') &&
                  (!msg.text || msg.text.trim() === '')
                );
            
            if (!existingThinkingMessage && storedCedarId) {
              console.log(`🔄 RECOVERY: Thinking message missing, recreating with original ID: ${storedCedarId}`);
              const thinkingMessage: ChatMessage = {
                id: storedCedarId,  // Use ORIGINAL cedarMessageId
                sender: 'cedar',
                text: '',
                timestamp: '',
                status: 'thinking',
                isStreaming: false,
                isAnimated: true
              };
              transition({ type: 'ADD_MESSAGE', payload: { message: thinkingMessage } });
            } else if (existingThinkingMessage) {
              console.log(`🔄 RECOVERY: Thinking message exists with ID: ${existingThinkingMessage.id}`);
            } else {
              console.log('🔄 RECOVERY: No cedarMessageId stored, creating fallback thinking message');
              const thinkingMessage: ChatMessage = {
                id: `cedar_recovery_${Date.now()}`,
                sender: 'cedar',
                text: '',
                timestamp: '',
                status: 'thinking',
                isStreaming: false,
                isAnimated: true
              };
              transition({ type: 'ADD_MESSAGE', payload: { message: thinkingMessage } });
            }
            
            transition({
              type: 'UPDATE_UI',
              payload: { isTyping: true, typingMessageId: 'recovering' }
            });
            
            // 🆕 FIX (Dec 30, 2025): RETRY mechanism for background response with exponential backoff
            // Problem: Backend parallel task might still be writing to background_chat_responses table
            // Solution: Retry up to 5 times with delays (0s, 1s, 2s, 4s, 8s = 15s total)
            console.log('🔄 RECOVERY: Starting retry loop for background response (max 5 attempts over 15s)');
            
            const maxRetries = 5;
            const retryDelays = [0, 1000, 2000, 4000, 8000]; // Exponential backoff
            let attempt = 0;
            let found = false;
            
            const retryCheck = async () => {
              while (attempt < maxRetries && !found) {
                const delay = retryDelays[attempt];
                if (delay > 0) {
                  console.log(`🔄 RECOVERY: Waiting ${delay}ms before attempt ${attempt + 1}/${maxRetries}...`);
                  await new Promise(resolve => setTimeout(resolve, delay));
                }
                
                attempt++;
                console.log(`🔄 RECOVERY: Attempt ${attempt}/${maxRetries} - checking for background response...`);
                
                const responses = await checkForCompletedBackgroundResponses(currentUser);
                
                if (responses.length > 0) {
                  console.log(`✅ RECOVERY: Found ${responses.length} completed responses on attempt ${attempt}!`);
                  found = true;
                  
                  // Clear typing indicator and let background poller process the responses
                  transition({
                    type: 'UPDATE_UI',
                    payload: { isTyping: false, typingMessageId: null }
                  });
                  
                  // Trigger background poller to process the responses immediately
                  checkBackgroundResponses();
                } else {
                  console.log(`⏳ RECOVERY: No responses yet on attempt ${attempt}/${maxRetries}`);
                }
              }
              
              if (!found) {
                console.log('❌ RECOVERY: No background response found after 5 attempts (15s) - giving up');
                transition({
                  type: 'UPDATE_UI',
                  payload: { isTyping: false, typingMessageId: null }
                });
              }
            };
            
            // Start retry loop (non-blocking)
            retryCheck().catch(error => {
              console.error('❌ RECOVERY: Error in retry loop:', error);
              transition({
                type: 'UPDATE_UI',
                payload: { isTyping: false, typingMessageId: null }
              });
            });
          } else {
            // Clear stale pending request
            console.log('🔄 RECOVERY: Clearing stale pending request (>5min old)');
            await storage.removeItem(`pending_response_${currentUser}`);
          }
        } catch (error) {
          console.error('❌ RECOVERY: Failed to parse pending request:', error);
          await storage.removeItem(`pending_response_${currentUser}`);
        }
      }
    })();
  }, []); // Run once on mount
  
  // 🔧 FIX Issue #5: Clean up stale Cedar messages after initialization completes
  // Runs when messages change (after INITIALIZE loads them)
  useEffect(() => {
    const currentUser = getCurrentUserId();
    if (!currentUser || chatState.phase === 'initializing' || chatState.messages.length === 0) {
      return;
    }
    
    // 🔧 FIX (Feb 2, 2026): Check for pending response BEFORE cleaning up thinking dots
    // If user navigated away during processing and comes back, don't remove the thinking message!
    (async () => {
      const pendingRequestStr = await storage.getItem(`pending_response_${currentUser}`);
      const hasPendingResponse = !!pendingRequestStr;
      
      // 🔧 FIX Issue #3: AGGRESSIVE cleanup of stale Cedar messages (>30 seconds with thinking/typing/sending status)
      // These can block the send button after timeouts or failed requests
      // 🔧 FIX NAVIGATION BUG (Nov 7, 2025): NEVER clean 'delivered' messages (they're complete)
      // 🔧 FIX (Feb 2, 2026): Don't clean thinking dots if there's a pending response (user navigated away during processing)
      const now = Date.now();
      const staleMessages = chatState.messages.filter(msg => {
        if (msg.sender !== 'cedar') return false;
        
        // 🚨 CRITICAL: Never clean delivered messages (even if old)
        if (msg.status === 'delivered') {
          console.log(`⏭️ CLEANUP: Skipping ${msg.id.slice(-12)} - already delivered (status=${msg.status})`);
          return false;
        }
        
        // 🔧 FIX (Feb 2, 2026): Don't clean thinking dots if pending response exists
        if (msg.status === 'thinking' && hasPendingResponse) {
          console.log(`⏭️ CLEANUP: Skipping ${msg.id.slice(-12)} - thinking dots preserved (pending response exists)`);
          return false;
        }
        
      // 🔧 FIX (Feb 4, 2026): Fix completed messages stuck in 'typing' status
      // Problem: Messages with full content but status='typing' block send button
      // Solution: Mark as delivered if they have substantial content (>100 chars)
      // 🔧 FIX P1-10: Skip promotion if message is currently being streamed
      if (msg.text && msg.text.trim().length > 100 &&
          (msg.status === 'typing' || msg.status === 'thinking')) {
        // Guard: don't promote if this message is actively streaming
        if (chatState.ui.isTyping && chatState.ui.typingMessageId && msg.id.includes(chatState.ui.typingMessageId)) {
          console.log(`⏭️ CLEANUP: Skipping ${msg.id.slice(-12)} - currently streaming (typingMessageId match)`);
          return false;
        }
        if (msg.isStreaming) {
          console.log(`⏭️ CLEANUP: Skipping ${msg.id.slice(-12)} - isStreaming flag is true`);
          return false;
        }
        console.log(`🔧 CLEANUP FIX: Message ${msg.id.slice(-12)} has ${msg.text.length} chars but status=${msg.status} - marking as delivered`);
        // Don't filter it out - let it get marked as delivered below
        return true;
      }
      
      // Skip messages with moderate content (10-100 chars) - might still be streaming
      if (msg.text && msg.text.trim().length > 10 && msg.text.trim().length <= 100) {
        console.log(`⏭️ CLEANUP: Skipping ${msg.id.slice(-12)} - has content (${msg.text.length} chars, might be streaming)`);
        return false;
      }
      
      if (msg.status !== 'thinking' && msg.status !== 'typing' && msg.status !== 'sending' && msg.status !== 'processing') return false;
        
        // Extract timestamp from message ID (format: cedar_1762375232621_...)
        const idParts = msg.id.split('_');
        if (idParts.length < 2) return false;
        const msgTimestamp = parseInt(idParts[1]);
        if (isNaN(msgTimestamp)) return false;
        
        const ageInMs = now - msgTimestamp;
        return ageInMs > 30 * 1000; // 🔧 FIX: Reduced from 5 minutes to 30 seconds (more aggressive cleanup)
      });
      
      if (staleMessages.length > 0) {
        console.log(`🔧 FIX Issue #3: Cleaning up ${staleMessages.length} stale Cedar messages (age > 30s, unblocking send button)`);
        staleMessages.forEach(msg => {
          console.log(`  - Cleaning stale message: ${msg.id}, status: ${msg.status}, age: ${Math.round((now - parseInt(msg.id.split('_')[1])) / 1000)}s`);
          transition({
            type: 'ADD_MESSAGE',
            payload: {
              message: {
                ...msg,
                status: 'delivered' // Unblock by marking as delivered
              }
            }
          });
        });
        
        // 🔧 FIX (Feb 4, 2026): Clear isTyping flag after cleanup
        // If we cleaned up typing/thinking messages, the isTyping flag should be cleared too
        if (chatState.ui.isTyping) {
          console.log('🔧 CLEANUP: Clearing isTyping flag after message cleanup');
          transition({
            type: 'UPDATE_UI',
            payload: { isTyping: false, typingMessageId: null }
          });
        }
      }
      
      // 🔧 FIX (Feb 4, 2026): Aggressive isTyping clear for stuck states
      // If isTyping is true but NO messages are actually processing, clear it
      const hasActiveProcessing = chatState.messages.some(msg =>
        msg.sender === 'cedar' &&
        (msg.status === 'thinking' || msg.status === 'typing') &&
        (!msg.text || msg.text.trim().length < 100)
      );
      
      if (chatState.ui.isTyping && !hasActiveProcessing && !hasPendingResponse) {
        console.log('🔧 STUCK STATE FIX: isTyping=true but no active processing - clearing to unblock send');
        transition({
          type: 'UPDATE_UI',
          payload: { isTyping: false, typingMessageId: null }
        });
      }
    })();
  }, [chatState.messages.length, chatState.phase]); // Run when messages load or phase changes

  // 🔧 MISSION 30 FIX (Jan 26, 2026): Clean scroll restoration - zero visible animation
  // ROOT CAUSE #1: Save not happening reliably before navigation
  // ROOT CAUSE #2: Position set after content renders (visible scroll)
  // CLEAN SOLUTION: Aggressive save + synchronous useLayoutEffect positioning
  // 🚨 MISSION 20: Web-only scroll restoration (DOM manipulation)
  useLayoutEffect(() => {
    // Platform guard: scroll restoration is web-only (uses DOM APIs)
    if (Platform.OS !== 'web') return;
    
    const scrollContainer = scrollFrameRef.current;
    if (!scrollContainer) return;

    const currentUser = getCurrentUserId();
    if (!currentUser) return;

    // Only run ONCE after initialization completes
    // 🔧 FIX (Jan 31, 2026): Removed phase check - triggers as soon as messages load
    // Problem: Phase could stay stuck in 'initializing', blocking restoration forever
    // Solution: Restore scroll immediately when messages available (simpler condition)
    if (chatState.messages.length > 0 && 
        chatState.ui.scrollState.isInitialLoad) {
      
      // 🔧 FIX (Feb 2, 2026): Make scroll restoration async for Mission 20 compliance + opacity fade
      (async () => {
        // STEP 1: Get saved position using async storage (Mission 20 pattern)
        const savedScrollPosition = await getUserStorage('scroll_position', currentUser);
        const wasAtBottom = savedScrollPosition === 'bottom';
        
        // STEP 2: Clear flag IMMEDIATELY (prevents cascade)
        // 🔧 CRITICAL FIX (Jan 31, 2026): Set shouldAutoScroll based on WHERE user was, not always true
        // Problem: Always setting shouldAutoScroll: true triggers auto-scroll effect → scrolls to bottom
        // Solution: Only enable auto-scroll if user was at bottom when they left
        transition({
          type: 'UPDATE_UI',
          payload: {
            scrollState: {
              isUserAtBottom: wasAtBottom,
              shouldAutoScroll: wasAtBottom,  // Only auto-scroll if they were at bottom
              isInitialLoad: false
            }
          }
        });
        
        console.log(`📜 SCROLL RESTORE: Retrieved saved position: "${savedScrollPosition}" (messages: ${chatState.messages.length}, wasAtBottom: ${wasAtBottom})`);
        
        // STEP 3: Detect new messages (async storage)
        const lastMessageId = chatState.messages[chatState.messages.length - 1]?.id;
        const lastSeenMessageId = await getUserStorage('last_seen_message', currentUser);
        const newMessagesArrived = lastMessageId !== lastSeenMessageId;
        
        console.log(`📜 NEW MESSAGE CHECK: lastSeen="${lastSeenMessageId}", current="${lastMessageId}", newArrived=${newMessagesArrived}`);
        
        // STEP 4: Calculate target position
        let targetScrollTop: number;

        // 🆕 ISSUE 3c: Detect debrief / contextual-launch entry → always go to bottom
        const isDebriefEntry = !!(
          (searchParams as Record<string, unknown>)?.post_workout_debrief_pending ||
          (searchParams as Record<string, unknown>)?.from === 'post_workout_debrief' ||
          (searchParams as Record<string, unknown>)?.from === 'contextual-launch'
        );

        if (isDebriefEntry) {
          // 🎯 ISSUE 3c: Debrief / contextual-launch → scroll to bottom, no new-messages divider
          targetScrollTop = scrollContainer.scrollHeight;
          console.log('📜 SCROLL RESTORE: Debrief/contextual-launch entry → forcing scroll to bottom');
        } else if (savedScrollPosition === 'bottom') {
          if (newMessagesArrived) {
            // 🎯 USE CASE 2 & 3: New messages since last visit but user was at bottom last time.
            // Scroll to start of the first new message so they land right above it.
            const newMessageElement = Platform.OS === 'web' ? document.getElementById(lastMessageId) : null;
            targetScrollTop = newMessageElement
              ? newMessageElement.offsetTop - 20
              : scrollContainer.scrollHeight;
          } else {
            // 🎯 USE CASE 1: Stay at bottom
            targetScrollTop = scrollContainer.scrollHeight;
          }
        } else if (savedScrollPosition && !isNaN(parseInt(savedScrollPosition))) {
          // 🎯 Restore exact position (user was mid-conversation)
          targetScrollTop = parseInt(savedScrollPosition);
        } else {
          // Default to bottom
          targetScrollTop = scrollContainer.scrollHeight;
        }

        // 🆕 ISSUE 3b: Show "New Messages" divider only when:
        //   • NOT a debrief / contextual-launch entry
        //   • New messages arrived
        //   • User was NOT already at the bottom (they have to scroll down to see them)
        if (!isDebriefEntry && newMessagesArrived && !wasAtBottom && lastSeenMessageId) {
          // Find the first message after lastSeenMessageId
          const seenIndex = chatState.messages.findIndex(m => m.id === lastSeenMessageId);
          const firstNew = seenIndex >= 0
            ? chatState.messages[seenIndex + 1]
            : null;
          if (firstNew) {
            firstNewMessageIdRef.current = firstNew.id;
            setShowNewMessagesDivider(true);
            showNewMessagesDividerRef.current = true;
            console.log(`📜 NEW MESSAGES DIVIDER: Show before message "${firstNew.id}"`);
          }
        }
        
        console.log(`📜 SCROLL RESTORE: Target=${targetScrollTop}px, scrollHeight=${scrollContainer.scrollHeight}px`);
        
        // 🔧 FIX (Feb 2, 2026): INSTANT FROZEN RESTORATION (no visible scroll)
        // User expectation: Navigate away = "frozen", navigate back = "unfrozen" at exact position
        // Problem: Browser resets scrollTop to 0 before content renders → visible scroll from 0→target
        // Solution: Hide container, wait for DOM + position to fully stabilize, THEN fade in
        
        // STEP 5: Hide container BEFORE setting position
        scrollContainer.style.opacity = '0';
        console.log(`📜 SCROLL RESTORE: Container hidden (opacity: 0) - FROZEN`);
        
        // STEP 6: Wait for content to render (longer delay for complete stabilization)
        setTimeout(() => {
          // Set position when DOM is stable
          scrollContainer.scrollTop = targetScrollTop;
          console.log(`📜 SCROLL RESTORE: Set position to ${targetScrollTop}px`);
          
          // CRITICAL: Verify position stuck MULTIPLE times before fading in
          // This ensures NO correction happens after fade-in starts
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const currentScrollTop = scrollContainer.scrollTop;
              const currentScrollHeight = scrollContainer.scrollHeight;
              
              // If scrollHeight grew or position drifted, correct it NOW (while hidden)
              if (savedScrollPosition === 'bottom' && currentScrollHeight > targetScrollTop + 100) {
                scrollContainer.scrollTop = currentScrollHeight;
                console.log(`📜 SCROLL CORRECT: scrollHeight grew to ${currentScrollHeight}px, corrected to bottom (WHILE HIDDEN)`);
              } else if (Math.abs(currentScrollTop - targetScrollTop) > 10) {
                scrollContainer.scrollTop = targetScrollTop;
                console.log(`📜 SCROLL CORRECT: Position drifted to ${currentScrollTop}px, corrected to ${targetScrollTop}px (WHILE HIDDEN)`);
              }
              
              // FINAL VERIFICATION: Wait ONE MORE rAF to ensure position is 100% stable
              requestAnimationFrame(() => {
                const finalScrollTop = scrollContainer.scrollTop;
                console.log(`📜 SCROLL VERIFY: Final position confirmed at ${finalScrollTop}px`);
                
                // NOW fade in - position is guaranteed stable
                scrollContainer.style.transition = 'opacity 0.15s ease-in';
                scrollContainer.style.opacity = '1';
                console.log(`✅ SCROLL RESTORE: UNFROZEN - Fade-in complete at stable position`);
              });
            });
          });
        }, 400);
        
        // STEP 8: Update last seen message (async storage)
        if (lastMessageId) {
          await setUserStorage('last_seen_message', lastMessageId, currentUser);
        }
        
        console.log(`✅ SCROLL RESTORE: Restoration initiated with ${savedScrollPosition === 'bottom' ? 'bottom' : targetScrollTop + 'px'} target`);
      })();
    }
  }, [chatState.messages.length, chatState.ui.scrollState.isInitialLoad]);  // 🔧 FIX: Changed from phase to messages.length

  // 🔧 FIX (Jan 4, 2026): Save scroll position proactively using Expo Router navigation detection
  // Problem: visibilitychange doesn't fire for SPA route changes, cleanup gets zeros
  // Solution: Use Expo Router's usePathname to detect navigation + periodic saves
  const saveScrollPositionRef = useRef<() => void>(null);
  const lastSavedPositionRef = useRef<string>('bottom');
  
  useEffect(() => {
    const scrollContainer = scrollFrameRef.current;
    const currentUser = getCurrentUserId();
    
    const saveScrollPosition = () => {
      if (!scrollContainer || !currentUser) return;
      
      const scrollTop = scrollContainer.scrollTop;
      const scrollHeight = scrollContainer.scrollHeight;
      const clientHeight = scrollContainer.clientHeight;
      const maxScrollTop = scrollHeight - clientHeight;
      
      // Skip if we're getting zeros (DOM is unmounting)
      if (scrollHeight === 0 || clientHeight === 0) {
        console.log(`📜 SCROLL SAVE: Skipping - DOM unmounting (scrollHeight=${scrollHeight}, clientHeight=${clientHeight})`);
        return;
      }
      
      // 🔧 FIX (Dec 29, 2025): More accurate "at bottom" detection
      const hasScrollbar = maxScrollTop > 0;
      const isWithin50pxOfBottom = hasScrollbar && Math.abs(maxScrollTop - scrollTop) < 50;
      const isActuallyAtBottom = !hasScrollbar || isWithin50pxOfBottom;
      
      const scrollPosToSave = isActuallyAtBottom ? 'bottom' : scrollTop.toString();
      
      // Only save if position actually changed
      if (scrollPosToSave !== lastSavedPositionRef.current) {
        setUserStorage('scroll_position', scrollPosToSave, currentUser);
        lastSavedPositionRef.current = scrollPosToSave;
        console.log(`📜 SCROLL SAVE: Saved scroll position: ${scrollPosToSave} (scrollTop=${scrollTop}px, maxScroll=${maxScrollTop}px, hasScrollbar=${hasScrollbar}, isAtBottom=${isActuallyAtBottom})`);
      }
    };
    
    // Store in ref so it can be called from event handlers
    saveScrollPositionRef.current = saveScrollPosition;
    
    // ✅ TRIGGER 1: On scroll (fast debounce - 150ms)
    let scrollTimeout: ReturnType<typeof setTimeout>;
    const handleScrollSave = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(saveScrollPosition, 150);
    };
    
    // ✅ TRIGGER 2: Periodic backup every 500ms (very aggressive - always fresh)
    const intervalId = setInterval(saveScrollPosition, 500);
    
    // ✅ TRIGGER 3: Visibility change (user switching tabs/apps) - web only
    const handleVisibilitySave = Platform.OS === 'web' ? () => {
      if (document.hidden) {
        saveScrollPosition(); // Immediate save when leaving
        console.log('📜 VISIBILITY: User leaving, saved immediately');
      }
    } : null;
    
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScrollSave);
    }
    
    if (Platform.OS === 'web' && handleVisibilitySave) {
      document.addEventListener('visibilitychange', handleVisibilitySave);
    }
    
    return () => {
      // ✅ FINAL SAVE: On cleanup (before component unmounts)
      saveScrollPosition();
      console.log('📜 CLEANUP: Final save before unmount');
      
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScrollSave);
      }
      
      if (Platform.OS === 'web' && handleVisibilitySave) {
        document.removeEventListener('visibilitychange', handleVisibilitySave);
      }
      
      clearInterval(intervalId);
      clearTimeout(scrollTimeout);
    };
  }, []);
  
  // ✅ TRIGGER 4: Expo Router navigation (when pathname changes)
  const locationRef = useRef(pathname);
  useEffect(() => {
    if (pathname !== locationRef.current) {
      console.log(`📜 NAVIGATION: Pathname changed ${locationRef.current} → ${pathname}`);

      // If navigating AWAY from /chat, save immediately
      if (locationRef.current === '/chat' && pathname !== '/chat') {
        if (saveScrollPositionRef.current) {
          saveScrollPositionRef.current();
          console.log('📜 NAVIGATION: Leaving /chat, saved position');
        }
      }
      
      locationRef.current = pathname;
    }
  }, [pathname]);

  // 🚨 CRITICAL FIX: Tour completion detection with proper dependency management
  // Use refs to avoid infinite loops while still accessing current state
  const onboardingTriggeredRef = useRef(false);
  const autoTriggerSentRef = useRef(false);
  
  useEffect(() => {
    // Update refs when state changes (no re-render trigger)
    onboardingTriggeredRef.current = chatState.ui.onboardingTriggered;
    autoTriggerSentRef.current = autoTriggerSent;
  }, [chatState.ui.onboardingTriggered, autoTriggerSent]);

  // 🚨 BACKGROUND POLLING REMOVED - Using synchronous processing only

  // 🚀 GARMIN OAUTH RETURN DETECTION WITH SYNC PROGRESS
  // Strava OAuth handling removed from frontend (Feb 2026) - backend preserved for future use
  useEffect(() => {
    const handleOAuthReturn = async () => {
      if (Platform.OS !== 'web') return; // OAuth only on web
      
      const urlParams = new URLSearchParams(window.location.search);
      
      // Check for query param redirects from callback pages
      const garminSync = urlParams.get('garmin_sync');
      
      const garminConnectionInProgress = await storage.getItem('garmin_connection_in_progress');
      
      // Determine which service just connected (Garmin only)
      const serviceConnected = garminSync === 'initial' ? 'garmin' :
                              garminConnectionInProgress === 'true' ? 'garmin' : null;
      
      // Handle query param redirects (from callback pages)
      if (garminSync === 'initial' && serviceConnected) {
        console.log(`🎉 GARMIN OAUTH: Query param redirect detected`);
        
        // Clean up URL parameters immediately
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
        
        // Add initial connection message from Settings flow
        const connectingMessage: ChatMessage = {
          id: `sync-connecting-${Date.now()}`,
          sender: 'cedar',
          text: `We're doing an initial sync of information, and I'll let you know when it's done and we can discuss it.`,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          status: 'delivered'
        };
        
        transition({ type: 'ADD_MESSAGE', payload: { message: connectingMessage } });
        
        // 🔄 SHOW SYNC PROGRESS COMPONENT
        setShowSyncProgress(true);
        setSyncService(serviceConnected);
        setSyncFromSettings(true);  // Track that this came from Settings
        
        // Scroll to bottom to show the connecting message
        setTimeout(() => {
          const chatContainer = document.querySelector('.chat-messages');
          if (chatContainer) {
            chatContainer.scrollTop = chatContainer.scrollHeight;
          }
        }, 100);
        
        return;  // Exit early after handling query param
      }
    };
    
    handleOAuthReturn();
  }, [transition]);

  // 🚨 NAVIGATION CLEANUP SIMPLIFIED - No background processing to manage
  
  useEffect(() => {
    if (Platform.OS !== 'web') return; // Web-only navigation logic
    
    // Only handle tour completion from navigation state - and only once per navigation
    if (searchParams.tourCompleted || searchParams.shouldAskForName) {
      console.log('🎯 AUTO-TRIGGER: Tour completion detected from navigation state');
      console.log('   Search params:', searchParams);
      console.log('   Current onboarding triggered:', onboardingTriggeredRef.current);
      
      // 🚨 CRITICAL: Prevent multiple auto-triggers using refs (no circular dependency)
      if (onboardingTriggeredRef.current || autoTriggerSentRef.current) {
        console.log('🛡️ AUTO-TRIGGER: Already triggered, skipping to prevent loops');
        console.log('   onboardingTriggered:', onboardingTriggeredRef.current, 'autoTriggerSent:', autoTriggerSentRef.current);
        // Clear state immediately without calling navigate again
        window.history.replaceState(null, '', '/chat');
        return;
      }
      
      const shouldAutoTrigger = (
        (searchParams.tourCompleted || searchParams.shouldAskForName) && 
        !searchParams.fromOnboarding &&  // Don't trigger if coming back from onboarding
        // 🔧 FIX (Jan 29, 2026): Allow auto-trigger for NEW users (backendState undefined)
        // Problem: Jan 27 fix blocked new users by requiring backendState to exist
        // Solution: Allow if backendState undefined OR if step < 6 and not completed
        (!chatState.backendState ||  // NEW user (no backend state yet) ✅
         (!chatState.backendState.onboardingCompleted &&  // Not completed
          (chatState.backendState.onboardingStep || 0) < 6))  // And step < 6
      );
      
      if (shouldAutoTrigger) {
        // 🚨 ADDITIONAL SAFETY: Check messages using current state snapshot
        const currentMessages = messages;
        if (currentMessages.length > 1) {
          console.log('🛡️ AUTO-TRIGGER: Skipping - user already has conversation history');
          // Clear state immediately without calling navigate again
          window.history.replaceState(null, '', '/chat');
          return;
        }
        
        console.log('🎯 AUTO-TRIGGER: Starting onboarding conversation automatically');
        
        // Run async check then proceed (useEffect callback cannot be async)
        (async () => {
          // 🚨 CRITICAL FIX (Feb 12, 2026): Check for prewarmed message BEFORE triggering
          // PROBLEM: Prewarmed message completes during tour → Background poll adds it
          //          → Auto-trigger ALSO fires → Reuses same cedar_message_id
          //          → Overwrites delivered message with "thinking" placeholder
          //          → Streams again → Duplicate typing animation
          // SOLUTION: Check if background response exists first
          //          → If YES, skip auto-trigger (background poll handles it)
          //          → If NO, proceed with auto-trigger
          try {
            const currentUser = getCurrentUserId();
            if (currentUser) {
              const completedResponses = await checkForCompletedBackgroundResponses(currentUser);
              if (completedResponses.length > 0) {
                console.log('🛡️ AUTO-TRIGGER: Prewarmed message exists - skipping auto-trigger');
                console.log(`   Found ${completedResponses.length} background response(s) - background poll will display automatically`);
                // Clear state and return - background poll will handle display
                window.history.replaceState(null, '', '/chat');
                return;
              }
              console.log('✅ AUTO-TRIGGER: No prewarmed messages found - proceeding with auto-trigger');
            }
          } catch (error) {
            console.warn('⚠️ AUTO-TRIGGER: Failed to check background responses, proceeding with auto-trigger anyway', error);
            // If check fails, proceed with auto-trigger (safe fallback)
          }
          
          // Set onboarding triggered immediately to prevent race conditions
          transition({ type: 'START_ONBOARDING' });
          setAutoTriggerSent(true); // 🛡️ Mark auto-trigger as sent
          
          // Clear state immediately to prevent re-triggering
          window.history.replaceState(null, '', '/chat');
          
          // Start onboarding conversation immediately
          setTimeout(async () => {
          try {
            console.log('🎯 AUTO-TRIGGER: Sending __START_ONBOARDING__ message via streaming');
            
            // Get JWT token for onboarding auto-trigger
            let jwtToken = await storage.getItem('jwt_token');
            if (!jwtToken) {
              let inviteToken: string | null = await storage.getItem('invite_token');
              if (!inviteToken) {
                const urlParams = new URLSearchParams(window.location.search);
                inviteToken = urlParams.get('invite_token');
                if (inviteToken) {
                  await storage.setItem('invite_token', inviteToken);
                }
              }
              
              if (inviteToken && typeof inviteToken === 'string') {
                const inviteResponse = await fetch(`${API_BASE_URL}/api/validate-invite/${encodeURIComponent(inviteToken)}`, {
                  method: 'GET',
                  headers: { 'Content-Type': 'application/json' }
                });
                
                if (inviteResponse.ok) {
                  const inviteData = await inviteResponse.json();
                  jwtToken = inviteData.jwt_token;
                  if (jwtToken) {
                    await storage.setItem('jwt_token', jwtToken);
                  }
                }
              }
            }
            
            if (jwtToken && ENABLE_STREAMING) {
              // 🔧 FIX: Use streaming for onboarding auto-trigger too
              console.log('🎯 AUTO-TRIGGER: Using streaming for onboarding message');
              
              // 🎯 CRITICAL FIX (Nov 24, 2025): Reuse the cedar_message_id from pre-warm
              // This prevents duplicate messages (one from pre-warm, one from streaming)
              const preWarmCedarId = await storage.getItem('prewarm_cedar_id');
              const cedarMessageId = preWarmCedarId || `cedar_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
              
              // Show thinking dots immediately (will stay visible during cache check/regeneration)
              const thinkingMessage: ChatMessage = {
                id: cedarMessageId,
                sender: 'cedar',
                text: '',
                timestamp: '',  // 🎯 No timestamp yet - thinking dots aren't a message!
                status: 'thinking',
                isStreaming: false,
                isAnimated: true  // 🎬 FIX (Dec 4, 2025): Enable animation when content arrives
              };
              transition({ type: 'ADD_MESSAGE', payload: { message: thinkingMessage } });
              
              // 🔄 OPTION 1/2 HYBRID (Jan 22, 2026): Check cache status and regenerate if expired
              try {
                const cacheStatusResponse = await fetch(`${API_BASE_URL}/api/chat/prewarm-status`, {
                  method: 'GET',
                  headers: {
                    'Authorization': `Bearer ${jwtToken}`,
                    'Content-Type': 'application/json'
                  }
                });
                
                if (cacheStatusResponse.ok) {
                  const cacheStatus = await cacheStatusResponse.json();
                  console.log(`🔍 PRE-WARM STATUS: exists=${cacheStatus.exists}, expired=${cacheStatus.expired}, age=${cacheStatus.age_seconds || 0}s`);
                  
                  if (cacheStatus.expired || !cacheStatus.exists) {
                    console.log(`🔄 PRE-WARM: Cache expired or missing (age: ${cacheStatus.age_seconds || 'N/A'}s), regenerating...`);
                    
                    // Regenerate cache (thinking dots already showing)
                    const regenerateResponse = await fetch(`${API_BASE_URL}/api/chat/prewarm-regenerate`, {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${jwtToken}`,
                        'Content-Type': 'application/json'
                      }
                    });
                    
                    if (regenerateResponse.ok) {
                      const regenerateData = await regenerateResponse.json();
                      console.log(`✅ PRE-WARM: Cache regenerated successfully (expires at: ${regenerateData.expires_at})`);
                    } else {
                      console.log(`⚠️ PRE-WARM: Cache regeneration failed, continuing anyway`);
                    }
                  } else {
                    console.log(`✅ PRE-WARM: Cache is valid (age: ${cacheStatus.age_seconds}s), using existing`);
                  }
                } else {
                  console.log(`⚠️ PRE-WARM: Failed to check cache status, continuing anyway`);
                }
              } catch (cacheError) {
                console.log(`⚠️ PRE-WARM: Error checking/regenerating cache (non-critical): ${cacheError}`);
                // Continue anyway - backend will handle cache check
              }
              
              // 🔍 DIAGNOSTIC: Log localStorage state for debugging
              // 🚀 PRE-WARM: Backend will check app.state.prewarm_cache automatically
              // The streaming orchestrator has the correct logic to deliver instant responses
              // when a pre-warmed response is available. No frontend check needed!
              console.log(`🚀 PRE-WARM: Sending streaming request - backend will check cache automatically`);
          
          await sendStreamingMessage(
                '__START_ONBOARDING__',
                [], // No attachments for auto-trigger
                transition,
                (state: any) => {
                  transition({ type: 'SYNC_BACKEND_STATE', payload: { backendState: state } });
                },
                (streaming: boolean) => {
                  transition({ type: 'UPDATE_UI', payload: { isTyping: streaming } });
                },
                cedarMessageId,  // 🎯 Pass the cedar ID
                chatState.messages,  // 🚀 ISSUE #2: Pass messages for goal detection
                processedMessageIdsRef,  // 🔒 FIX: Pass deduplication tracker
                undefined,  // No pipeline progress for onboarding
                undefined,
                undefined,
                undefined,  // No pipeline goal ID for onboarding
                chatState.backendState?.chatName,  // 🐛 FIX (Jan 31, 2026): Pass chatName
                logExtraction  // 🩺 MISSION 24
              );
            } else {
              // Fallback to regular POST if no JWT or streaming disabled
              console.log('🎯 AUTO-TRIGGER: Using regular POST for onboarding message');
              transition({ type: 'UPDATE_UI', payload: { isTyping: true } });
              
              const response = await getCedarResponse('__START_ONBOARDING__', []);
              
              // 🚨 SYNC: Update frontend state with backend state
              if (response.backendState) {
                transition({ 
                  type: 'SYNC_BACKEND_STATE', 
                  payload: { backendState: response.backendState } 
                });
              }
              
              const onboardingMessage: ChatMessage = {
                id: `cedar_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                sender: 'cedar',
                text: response.text,
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                status: 'delivered',
                isAnimated: true
              };
              
              console.log('🎯 AUTO-TRIGGER: Adding onboarding message:', onboardingMessage.text.substring(0, 50) + '...');
              transition({ type: 'ADD_MESSAGE', payload: { message: onboardingMessage } });
              transition({ type: 'UPDATE_UI', payload: { isTyping: false, typingMessageId: onboardingMessage.id } });
              
              setTimeout(() => {
                transition({ type: 'UPDATE_UI', payload: { typingMessageId: null } });
              }, 2000);
            }
          } catch (error) {
            console.error('❌ AUTO-TRIGGER: Error starting onboarding:', error);
            transition({ type: 'UPDATE_UI', payload: { isTyping: false } });
          }
        }, 800); // 🎯 FIX (Jan 31, 2026): Delay for tour modal to close completely before message appears
        })();
      }
    }
  }, [pathname]); // 🚨 FIXED: Remove messages dependency to prevent auto-trigger loops

  // 🎯 FALLBACK: Trigger Step 1 when in early onboarding with only greeting (no tour state)
  // Covers: refresh on /chat, direct link, or any path where search params were lost.
  // Step 1 ("What name would you like me to call you?") and early onboarding flow must still run.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (autoTriggerSentRef.current) return;
    const phase = chatState.phase;
    const messages = chatState.messages;
    const inEarlyOnboarding = phase === 'onboarding' || phase === 'initializing';
    const onlyGreeting = messages.length === 1 && messages[0]?.sender === 'cedar';
    if (!inEarlyOnboarding || !onlyGreeting) return;

    (async () => {
      try {
        const currentUser = getCurrentUserId();
        if (currentUser) {
          const completedResponses = await checkForCompletedBackgroundResponses(currentUser);
          if (completedResponses.length > 0) {
            return;
          }
        }
      } catch {
        // Proceed with trigger on check failure
      }

      transition({ type: 'START_ONBOARDING' });
      setAutoTriggerSent(true);

      setTimeout(async () => {
        try {
          let jwtToken = await storage.getItem('jwt_token');
          if (!jwtToken) {
            let inviteToken: string | null = await storage.getItem('invite_token');
            if (!inviteToken) {
              const urlParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
              inviteToken = urlParams.get('invite_token');
              if (inviteToken) await storage.setItem('invite_token', inviteToken);
            }
            if (inviteToken && typeof inviteToken === 'string') {
              const inviteResponse = await fetch(`${API_BASE_URL}/api/validate-invite/${encodeURIComponent(inviteToken)}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
              });
              if (inviteResponse.ok) {
                const inviteData = await inviteResponse.json();
                jwtToken = inviteData.jwt_token;
                if (jwtToken) await storage.setItem('jwt_token', jwtToken);
              }
            }
          }

          if (jwtToken && ENABLE_STREAMING) {
            const preWarmCedarId = await storage.getItem('prewarm_cedar_id');
            const cedarMessageId = preWarmCedarId || `cedar_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const thinkingMessage: ChatMessage = {
              id: cedarMessageId,
              sender: 'cedar',
              text: '',
              timestamp: '',
              status: 'thinking',
              isStreaming: false,
              isAnimated: false
            };
            transition({ type: 'ADD_MESSAGE', payload: { message: thinkingMessage } });
            await sendStreamingMessage(
              '__START_ONBOARDING__',
              [],
              transition,
              (state: any) => {
                transition({ type: 'SYNC_BACKEND_STATE', payload: { backendState: state } });
              },
              (streaming: boolean) => {
                transition({ type: 'UPDATE_UI', payload: { isTyping: streaming } });
              },
              cedarMessageId,
              chatState.messages,
              processedMessageIdsRef,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,  // chatName
              logExtraction
            );
          } else if (jwtToken) {
            const resp = await getCedarResponse('__START_ONBOARDING__', []);
            if (resp?.text) {
              const cedarMessage: ChatMessage = {
                id: `cedar_${Date.now()}`,
                sender: 'cedar',
                text: resp.text,
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                status: 'delivered',
                isStreaming: false,
                isAnimated: false
              };
              transition({ type: 'ADD_MESSAGE', payload: { message: cedarMessage } });
            }
          }
        } catch (err) {
          console.warn('Fallback Step 1 trigger failed', err);
        }
      }, 100);
    })();
  }, [chatState.phase, chatState.messages.length]); // Run when phase or message count changes in early onboarding

  // 🚀 AUTO-MESSAGE POLLING: Monitor for Option C auto-follow-up messages
  useEffect(() => {
    const userId = getCurrentUserId();
    if (!userId || chatState.phase === 'initializing') {
      return;
    }
    
    const startAutoMessagePolling = () => {
      // Clear any existing interval
      if (autoMessageIntervalRef.current) {
        clearInterval(autoMessageIntervalRef.current);
      }
      
      // Start polling every 2 seconds
      autoMessageIntervalRef.current = setInterval(async () => {
        try {
          const autoMessages = await pollAutoMessages(userId, lastAutoMessagePoll);
          
          if (autoMessages.length > 0) {
            console.log(`🚀 AUTO-MESSAGES: Found ${autoMessages.length} new auto-messages`);
            
            autoMessages.forEach((autoMsg, index) => {
              // 🔧 FEB 10/11, 2026: Parse action buttons from auto-messages (+ auto-fire service connectors)
              let autoMsgText = autoMsg.content;
              let autoMsgActions: any[] | undefined;
              let autoMsgServiceAction: ChatMessage['serviceAction'] | undefined;
              if (autoMsgText && /\[([^\]]+)\]\(action:([^)]+)\)/.test(autoMsgText)) {
                const parsed = parseMessageActions(autoMsgText);
                if (parsed.actions && parsed.actions.length > 0) {
                  autoMsgText = parsed.cleanText;
                  autoMsgActions = parsed.actions;
                  console.log(`🔧 ACTION PARSE: Parsed ${parsed.actions.length} action(s) from auto-message`);
                }
                if (parsed.serviceAction) {
                  autoMsgText = parsed.cleanText;
                  autoMsgServiceAction = parsed.serviceAction;
                  console.log(`📱 ACTION PARSE: Auto-attached serviceAction from auto-message`);
                }
              }

              const autoMessage: ChatMessage = {
                id: autoMsg.id || `auto-${Date.now()}-${index}`,
                sender: 'cedar',
                text: autoMsgText,
                timestamp: new Date(autoMsg.created_at).toLocaleTimeString([], { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                }),
                status: 'delivered',
                isAnimated: true,
                actions: autoMsgActions,
                ...(autoMsgServiceAction ? { serviceAction: autoMsgServiceAction } : {}),
                metadata: {
                  isAutoGenerated: true,
                  autoFollowUp: true,
                  stepTransition: autoMsg.metadata?.step_transition,
                  optionC: true
                }
              };
              
              console.log(`🚀 AUTO-MESSAGE: Adding auto-follow-up message: ${autoMessage.text.substring(0, 50)}...`);
              
              // Add message with small delay for natural feel
              setTimeout(() => {
                transition({
                  type: 'ADD_MESSAGE',
                  payload: { message: autoMessage }
                });
              }, index * 500); // Stagger multiple messages
            });
            
            // Update last poll timestamp
            const latestTimestamp = autoMessages[autoMessages.length - 1]?.created_at;
            if (latestTimestamp) {
              setLastAutoMessagePoll(latestTimestamp);
            }
          }
        } catch (error) {
          console.error('❌ AUTO-MESSAGE polling failed:', error);
        }
      }, 2000); // Poll every 2 seconds
    };
    
    // Start polling
    startAutoMessagePolling();
    
    // Cleanup on unmount
    return () => {
      if (autoMessageIntervalRef.current) {
        clearInterval(autoMessageIntervalRef.current);
      }
    };
  }, []); // 🔧 FIX (Jan 9, 2026): Empty deps - only run once on mount. lastAutoMessagePoll is read from state, no need as dependency

  // 🔧 FIX: Hide browser progress bar and prevent blue bar issues (web only)
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    
    // Hide any browser progress bars
    const meta = document.createElement('meta');
    meta.name = 'theme-color';
    meta.content = '#ffffff';
    document.head.appendChild(meta);

    // Prevent blue bar from browser progress
    const style = document.createElement('style');
    style.textContent = `
      body { margin: 0; padding: 0; background: #ffffff !important; }
      html { margin: 0; padding: 0; background: #ffffff !important; }
      #root { background: #ffffff !important; }
      .clean-chat-container { background: #ffffff !important; }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(meta);
      document.head.removeChild(style);
    };
  }, []);

  // Function to scroll to bottom with mobile-safe scrolling
  const scrollToBottom = useCallback((instant: boolean = false) => {
    // Use container.scrollTo instead of scrollIntoView to prevent page shifting
    if (scrollFrameRef.current) {
      const container = scrollFrameRef.current;
      
      // 🎯 FIX #2: Respect slider mode - don't auto-scroll when sliders are active
      if (container.getAttribute('data-slider-mode') === 'true') {
        console.log('🔒 SCROLL: Auto-scroll blocked - sliders are active');
        return;
      }
      
      // 🔧 FIX: Add extra offset to ensure last line is fully visible
      // scrollHeight alone can leave the last bubble cut off at bottom
      // Add 100px buffer to ensure complete visibility
      // 🔧 FIX (Jan 5, 2026): Use 'auto' (instant) when restoring, 'smooth' for normal scrolling
      container.scrollTo({
        top: container.scrollHeight + 100,
        behavior: instant ? 'auto' : 'smooth'
      });
    }
  }, []);

  // 📅 HISTORY SCROLL: Scroll to history section (button click handler)
  const scrollToHistory = useCallback(() => {
    if (Platform.OS !== 'web') return; // Web-only scroll logic
    
    // Check if user is in discovery (Step 7+)
    const currentStep = chatState.backendState?.onboardingStep ?? 0;
    if (currentStep < 7) {
      console.log('📅 HISTORY: History not available during onboarding');
      return;
    }

    // Always show history when user clicks the button
    if (!showChatHistory) {
      setShowChatHistory(true);
      console.log('📅 HISTORY: Activated history browser (user clicked button)');
    }
    
    const historyStart = document.getElementById('history-start');
    if (historyStart) {
      historyStart.scrollIntoView({ behavior: 'smooth' });
      console.log('📅 HISTORY: Scrolled to history section');
    } else {
      console.log('📅 HISTORY: History section not found, scrolling to top');
      // Fallback: scroll to top of chat
      if (scrollFrameRef.current) {
        scrollFrameRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [showChatHistory, chatState.backendState?.onboardingStep]);

  // 📅 CHAT HISTORY HANDLERS
  const handleMonthToggle = useCallback((monthId: string) => {
    setExpandedMonths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(monthId)) {
        newSet.delete(monthId);
      } else {
        newSet.add(monthId);
      }
      return newSet;
    });
  }, []);

  const handleWeekExpand = useCallback(async (weekId: string) => {
    try {
      console.log(`📅 HISTORY: Loading messages for week ${weekId}...`);
      
      // Get week messages from backend
      const weekMessages = await ChatHistoryProcessor.getWeekMessages(weekId);
      
      if (weekMessages.length > 0) {
        // Add divider message
        const dividerMessage: ChatMessage = {
          id: `divider-${weekId}`,
          sender: 'system',
          text: '',
          timestamp: new Date().toISOString(),
          status: 'delivered',
          metadata: {
            isDivider: true,
            dividerText: 'This Week'
          } as any
        };

        // Messages are already in frontend format from ChatHistoryProcessor
        const frontendMessages: ChatMessage[] = weekMessages;

        // Add historical messages to current chat
        frontendMessages.forEach(msg => {
          transition({ type: 'ADD_MESSAGE', payload: { message: msg } });
        });
        
        // Add divider message
        transition({ type: 'ADD_MESSAGE', payload: { message: dividerMessage } });
        
        // Expand the week in UI
        setExpandedWeeks(prev => new Set(prev).add(weekId));
        
        console.log(`✅ HISTORY: Loaded ${weekMessages.length} messages for week`);
      }
    } catch (error) {
      console.error('❌ HISTORY: Failed to load week messages:', error);
    }
  }, []);

  // Check if user is at the bottom of the scroll container
  const checkIfAtBottom = useCallback(() => {
    if (!scrollFrameRef.current) return false;
    
    const { scrollTop, scrollHeight, clientHeight } = scrollFrameRef.current;
    const threshold = 100; // 100px threshold - consider "at bottom" if within 100px
    const atBottom = scrollHeight - scrollTop - clientHeight <= threshold;
    
    // 🚨 CRITICAL FIX: Use ref to prevent infinite loops - don't trigger state updates in scroll handlers
    return atBottom;
  }, []); // 🚨 No dependencies - pure scroll position calculation

  // Handle scroll events to detect user manual scrolling
  const handleScroll = useCallback(() => {
    // 🚨 CRITICAL FIX: Use throttling to prevent excessive scroll event processing
    const atBottom = checkIfAtBottom();
    
    // 🚨 CRITICAL FIX: Use refs to avoid stale closures and infinite loops
    const currentIsUserAtBottom = isUserAtBottomRef.current;
    const currentShouldAutoScroll = shouldAutoScrollRef.current;
    
    // 🆕 ISSUE 3b: Hide "New Messages" divider when user scrolls past it or reaches the bottom
    if (showNewMessagesDividerRef.current && Platform.OS === 'web') {
      const scrollContainer = scrollFrameRef.current as unknown as HTMLElement | null;
      if (scrollContainer) {
        const firstNewId = firstNewMessageIdRef.current;
        if (firstNewId) {
          const firstNewEl = document.getElementById(firstNewId);
          if (firstNewEl) {
            // Divider is "passed" when the top of the first-new-message element has scrolled above viewport
            const elementTop = firstNewEl.offsetTop;
            const viewportBottom = scrollContainer.scrollTop + scrollContainer.clientHeight;
            if (viewportBottom > elementTop || atBottom) {
              // User has scrolled past the divider → hide it
              showNewMessagesDividerRef.current = false;
              setShowNewMessagesDivider(false);
              console.log('📜 NEW MESSAGES DIVIDER: Hidden (user scrolled past)');
            }
          } else if (atBottom) {
            // Element no longer in DOM, but user is at bottom → hide
            showNewMessagesDividerRef.current = false;
            setShowNewMessagesDivider(false);
          }
        } else if (atBottom) {
          showNewMessagesDividerRef.current = false;
          setShowNewMessagesDivider(false);
        }
      }
    }

    // Scroll FAB visibility: show arrows when 500px+ away from top/bottom
    if (scrollFrameRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollFrameRef.current;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const distanceFromTop = scrollTop;
      const fabThreshold = 500;

      // Down arrow: show when scrolled far from bottom
      const shouldShowDown = distanceFromBottom > fabThreshold;
      if (shouldShowDown !== showScrollDownFabRef.current) {
        showScrollDownFabRef.current = shouldShowDown;
        setShowScrollDownFab(shouldShowDown);
      }

      // Up arrow: show when user has scrolled up 3+ screen heights from bottom
      // This means they're deep in history browsing and may want to jump to top
      const scrolledUpThreshold = clientHeight * 3;
      const shouldShowUp = distanceFromTop > fabThreshold && distanceFromBottom > scrolledUpThreshold;
      if (shouldShowUp !== showScrollUpFabRef.current) {
        showScrollUpFabRef.current = shouldShowUp;
        setShowScrollUpFab(shouldShowUp);
      }
    }

    // Update scroll state only if it actually changed, with throttling
    if (atBottom !== currentIsUserAtBottom) {
      // Use setTimeout to debounce scroll state updates (tracked via ref for cleanup)
      if (scrollDebounceTimerRef.current) clearTimeout(scrollDebounceTimerRef.current);
      scrollDebounceTimerRef.current = setTimeout(() => {
        // 🔧 FIX (Dec 7, 2025): Check refs again before dispatching to prevent race conditions
        // Multiple scroll events can schedule multiple dispatches - only dispatch if still needed
        const stillAtBottom = checkIfAtBottom();
        const refIsUserAtBottom = isUserAtBottomRef.current;
        
        if (stillAtBottom !== refIsUserAtBottom) {
          console.log(`📜 SCROLL STATE UPDATE: ${refIsUserAtBottom} → ${stillAtBottom}`);
          transition({ 
            type: 'UPDATE_UI', 
            payload: { 
              scrollState: { 
                isUserAtBottom: stillAtBottom,
                shouldAutoScroll: stillAtBottom ? true : shouldAutoScrollRef.current
              }
            }
          });
        } else {
          console.log(`📜 SCROLL STATE SKIP: Already at correct state (${stillAtBottom})`);
        }
      }, 50); // 50ms debounce
    }
  }, []); // 🚨 CRITICAL FIX: No dependencies to prevent infinite recreation

  // 🔧 PHASE 4: Optimized message saving - only triggers on length changes
  const messagesLengthRef = useRef(messages.length);
  const lastMessageIdRef = useRef(messages[messages.length - 1]?.id);
  
  useEffect(() => {
    const currentLength = messages.length;
    const currentLastId = messages[messages.length - 1]?.id;
    
    // 🔍 DIAGNOSTIC (Jan 24, 2026): Always log to debug chat persistence across navigation
    console.log(`📝 SAVE EFFECT: Triggered - Length: ${currentLength} (prev: ${messagesLengthRef.current}), Last ID: ${currentLastId} (prev: ${lastMessageIdRef.current})`);
    
    // Only save if there are actual new messages or message changes
    if (currentLength !== messagesLengthRef.current || currentLastId !== lastMessageIdRef.current) {
      console.log('📝 Messages changed:', currentLength, `Last: ${currentLastId}`);
      console.log('💾 Saving messages to storage:', currentLength);
      
      // 🔧 FIX: Log if messages decreased (potential issue)
      if (currentLength < messagesLengthRef.current) {
        console.warn(`⚠️ MESSAGE COUNT DECREASED: ${messagesLengthRef.current} → ${currentLength}. This might indicate messages being removed unexpectedly.`);
        console.warn(`⚠️ Previous last ID: ${lastMessageIdRef.current}, Current last ID: ${currentLastId}`);
      }

      const currentUser = getCurrentUserId();
      
      // 🐛 DIAGNOSTIC (Jan 21, 2026): Debug save operation
      console.log(`🔍 SAVE TRIGGER: Attempting to save messages`);
      console.log(`   Current user ID: ${currentUser}`);
      console.log(`   Messages count: ${messages.length}`);
      console.log(`   Will save to key: ${currentUser}_chat_messages`);
      
      if (currentUser && messages.length > 0) {
        // 🔧 FIX NAVIGATION PERSISTENCE BUG (Nov 9, 2025): Don't save incomplete messages
        // If user navigates away during streaming, frontend has thinking bubble with no text
        // Background poll has complete response but frontend skips it (ID already exists)
        // Filter out empty Cedar messages BEFORE saving to localStorage
        const messagesToSave = messages
          .filter(msg => {
            // Keep all user messages
            if (msg.sender === 'user') return true;
            
            // For Cedar messages, only save if they have text content OR are currently processing
            if (msg.sender === 'cedar') {
              const hasText = msg.text && msg.text.trim().length > 0;
              const isProcessing = msg.status === 'thinking' || msg.status === 'typing' || msg.isStreaming;
              const shouldKeep = hasText || isProcessing;
              
              if (!shouldKeep) {
                console.log(`⏭️  SAVE FILTER: Skipping empty Cedar message ${msg.id} (no text and not processing)`);
              } else if (msg.status === 'thinking') {
                console.log(`💾 SAVE FILTER: Keeping thinking dots message ${msg.id} for recovery`);
              }
              return shouldKeep;
            }
            
            return true;
          })
          .map(msg => {
            // 🚀 INSTANT MESSAGE DISPLAY (Jan 22, 2026): Set wasAnimated based on message completeness
            // Complete messages (delivered with text) → wasAnimated: true (show instantly)
            // Incomplete messages (streaming or no text) → wasAnimated: false (will animate or be replaced)
            let wasAnimated = false;
            
            if (msg.sender === 'cedar') {
              const isComplete = msg.status === 'delivered' && 
                                msg.text && 
                                msg.text.trim().length > 0;
              
              // Special handling for initial message
              if (msg.id === 'initial') {
                wasAnimated = msg.wasAnimated || false; // Preserve existing state
              } else {
                wasAnimated = !!isComplete; // Complete = show instantly, incomplete = will animate
              }
              
              console.log(`💾 SAVE: Message ${msg.id} - status: ${msg.status}, text length: ${msg.text?.length || 0}, wasAnimated: ${wasAnimated}`);
            } else {
              // User messages always animated
              wasAnimated = true;
            }
            
            return {
              ...msg,
              isAnimated: false,
              wasAnimated
            };
          });
        
        // 🔧 BUILD FIX (Jan 21, 2026): setUserStorage is async, but useEffect callback cannot be async
        // Solution: Use .then() instead of await
        setUserStorage('chat_messages', JSON.stringify(messagesToSave), currentUser)
          .then(() => {
            console.log(`💾 SAVE: Saved ${messagesToSave.length}/${messages.length} messages (filtered ${messages.length - messagesToSave.length} incomplete)`);
            
            // 🔧 FIX: Log last saved message to ensure it's being persisted
            if (messagesToSave.length > 0) {
              const lastSaved = messagesToSave[messagesToSave.length - 1];
              console.log(`💾 SAVE: Last saved message - ID: ${lastSaved.id}, sender: ${lastSaved.sender}, text length: ${lastSaved.text?.length || 0}, status: ${lastSaved.status}`);
            }
            
            // 🔧 SAVE VERIFICATION (Jan 25, 2026): Verify save succeeded, retry if failed
            // 🚨 MISSION 20: Web-only verification (checks localStorage implementation)
            if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
              const storageKey = `${currentUser}_chat_messages`;
              const verifyData = localStorage.getItem(storageKey);
              
              if (verifyData) {
                // Verify message count matches
                try {
                  const savedMessages = JSON.parse(verifyData);
                  if (savedMessages.length === messagesToSave.length) {
                    console.log(`✅ SAVE VERIFIED: ${savedMessages.length} messages in localStorage`);
                  } else {
                    console.warn(`⚠️ SAVE MISMATCH: Tried to save ${messagesToSave.length}, localStorage has ${savedMessages.length}`);
                    // Retry save
                    console.log('🔄 RETRYING SAVE...');
                    localStorage.setItem(storageKey, JSON.stringify(messagesToSave));
                    console.log('✅ RETRY COMPLETE');
                  }
                } catch (parseError) {
                  console.error('❌ SAVE VERIFICATION FAILED: Could not parse saved data');
                  // Retry save
                  console.log('🔄 RETRYING SAVE due to parse error...');
                  localStorage.setItem(storageKey, JSON.stringify(messagesToSave));
                  console.log('✅ RETRY COMPLETE');
                }
                
                console.log(`   Data length: ${verifyData.length} chars`);
                console.log(`   Preview: ${verifyData.substring(0, 100)}...`);
              } else {
                console.error(`❌ SAVE FAILED: No data found at key "${storageKey}" after setUserStorage call`);
              }
            }
          })
          .catch((error) => {
            console.error('❌ SAVE ERROR: Failed to save messages to storage:', error);
          });
      }
      
      messagesLengthRef.current = currentLength;
      lastMessageIdRef.current = currentLastId;
    }
  }, [
    messages.length, 
    messages[messages.length - 1]?.id,
    // 🚀 INSTANT MESSAGE DISPLAY (Jan 22, 2026): Also watch message content to trigger saves during streaming
    // When message text updates during streaming, this dependency changes, triggering save
    messages[messages.length - 1]?.text?.length,
    messages[messages.length - 1]?.status
  ]); // 🔧 PHASE 4: Specific dependencies + streaming content tracking

  // 🚪 STEP 1: IMMEDIATE SAVE ON NAVIGATION (JAN 30, 2026)
  // Purpose: Prevent message loss when user navigates away during streaming or mid-conversation
  // Saves both messages AND scroll position immediately on beforeunload and unmount
  useEffect(() => {
    const saveEverythingImmediately = async () => {
      const currentUser = getCurrentUserId();
      if (!currentUser || messages.length === 0) return;
      
      console.log('🚪 NAVIGATION SAVE: Saving messages and scroll position before unmount/navigation');
      
      // Save messages (with interrupted flag for streaming messages)
      const messagesToSave = messages
        .filter(msg => {
          // Keep all user messages
          if (msg.sender === 'user') return true;
          
          // For Cedar messages, only save if they have text content OR are currently streaming
          if (msg.sender === 'cedar') {
            const hasText = msg.text && msg.text.trim().length > 0;
            const isStreaming = msg.status === 'typing' || msg.isStreaming;
            return hasText || isStreaming;
          }
          
          return true;
        })
        .map(msg => {
          // Mark interrupted if still streaming/thinking
          if (msg.status === 'typing' || msg.status === 'thinking') {
            return {
              ...msg,
              metadata: {
                ...msg.metadata,
                interrupted: true // Flag for re-fetch on next mount
              }
            };
          }
          return msg;
        });
      
      await setUserStorage('chat_messages', JSON.stringify(messagesToSave), currentUser);
      console.log(`✅ NAVIGATION SAVE: Saved ${messagesToSave.length} messages`);
      
      // Save scroll position
      const scrollContainer = scrollFrameRef.current;
      if (scrollContainer) {
        const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
        const maxScrollTop = scrollHeight - clientHeight;
        const hasScrollbar = maxScrollTop > 0;
        const isAtBottom = !hasScrollbar || Math.abs(maxScrollTop - scrollTop) < 50;
        
        const scrollPosToSave = isAtBottom ? 'bottom' : scrollTop.toString();
        await setUserStorage('scroll_position', scrollPosToSave, currentUser);
        console.log(`✅ NAVIGATION SAVE: Saved scroll position: ${scrollPosToSave}`);
      }
      
      // Save dedup tracker to unified storage
      const dedupArray = Array.from(processedMessageIdsRef.current);
      await storage.setItem(`${currentUser}_processedMessageIds`, JSON.stringify(dedupArray));
      console.log(`✅ NAVIGATION SAVE: Saved ${dedupArray.length} IDs to dedup tracker`);
    };

    const handleBeforeUnload = () => {
      // 🔧 M35B FIX: Abort any active SSE stream on navigation so the backend
      // releases the per-user streaming lock immediately.
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      saveEverythingImmediately();
    };

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      }
      // 🔧 M35B FIX: Also abort on component unmount (SPA route change).
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      saveEverythingImmediately();
    };
  }, [messages]); // Re-run if messages change (ensures we always save latest)

  // Auto-scroll effect - handles scrolling behavior
  // 🔧 PHASE 4: Optimized auto-scroll - triggers on new messages AND growing messages during streaming
  // 🆕 STEP 4: ENHANCED WITH SMART AUTO-SCROLL (JAN 30, 2026)
  // Purpose: Only auto-scroll when user was at bottom (preserve reading position otherwise)
  const lastScrollTriggerRef = useRef(messages.length);
  const lastMessageLengthRef = useRef(0);
  const wasAtBottomBeforeNewMessageRef = useRef(true); // Track if user was at bottom before new message
  
  // 🆕 Track scroll position before new messages arrive
  useEffect(() => {
    const scrollContainer = scrollFrameRef.current;
    if (!scrollContainer) return;
    
    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    const maxScrollTop = scrollHeight - clientHeight;
    const isCurrentlyAtBottom = maxScrollTop > 0 ? Math.abs(maxScrollTop - scrollTop) < 100 : true;
    
    wasAtBottomBeforeNewMessageRef.current = isCurrentlyAtBottom;
  }, [messages.length]); // Run BEFORE messages.length changes in the auto-scroll effect
  
  useEffect(() => {
    const currentLength = messages.length;
    const lastMessage = messages[messages.length - 1];
    const lastMessageLength = lastMessage?.text?.length || 0;
    
    // 🎯 STREAMING FIX: Auto-scroll when message grows during streaming OR when new message arrives
    const isNewMessage = currentLength > 0 && currentLength !== lastScrollTriggerRef.current;
    const isGrowingMessage = lastMessageLength > lastMessageLengthRef.current && isTyping;
    
    if (!isNewMessage && !isGrowingMessage) return;
    
    // 🆕 STEP 4: SMART AUTO-SCROLL DECISION (JAN 30, 2026)
    // Check if user was at bottom BEFORE the new message arrived
    const userWasAtBottom = wasAtBottomBeforeNewMessageRef.current;
    
    console.log(`📜 AUTO-SCROLL CHECK: shouldAutoScroll=${shouldAutoScroll}, wasAtBottom=${userWasAtBottom}, isTyping=${isTyping}, growing=${isGrowingMessage}`);
    
    // 🎛️ SMART SCROLL: Allow auto-scroll for all messages including sliders
    // Removed the slider message scroll disable logic per user requirements
    
    // 🚨 ENHANCED LOGIC: Auto-scroll if:
    // 1. shouldAutoScroll is enabled (not disabled by slider mode)
    // 2. User was at bottom before new message (respects reading position)
    // 3. OR message is growing during streaming (follow typing animation)
    const shouldScroll = shouldAutoScroll && scrollFrameRef.current && (userWasAtBottom || isTyping);
    
    if (shouldScroll) {
      if (userWasAtBottom) {
        console.log('✅ AUTO-SCROLL: User was at bottom, scrolling to show new message');
      } else if (isTyping) {
        console.log('✅ AUTO-SCROLL: Message growing during streaming, following animation');
      }
      // Longer delay for mobile to ensure DOM and layout are stable
      const delay = /Mobi|Android/i.test(navigator.userAgent) ? 150 : 50;
      setTimeout(() => {
        scrollToBottom();
      }, delay);
    } else {
      // Log why we're not scrolling
      if (!shouldAutoScroll) {
        console.log('❌ AUTO-SCROLL: shouldAutoScroll is FALSE (slider mode or user disabled)');
      } else if (!userWasAtBottom && !isTyping) {
        console.log('📜 AUTO-SCROLL: User was reading history (not at bottom), keeping scroll position');
      } else if (!scrollFrameRef.current) {
        console.log('❌ AUTO-SCROLL: scrollFrameRef.current is NULL');
      }
    }
    
    lastScrollTriggerRef.current = currentLength;
    lastMessageLengthRef.current = lastMessageLength;
  }, [messages.length, messages[messages.length - 1]?.text?.length, shouldAutoScroll, isUserAtBottom, isTyping]); // 🔧 PHASE 4: Specific scroll dependencies including text length for streaming

  // Add scroll event listener to the scroll container
  useEffect(() => {
    const scrollContainer = scrollFrameRef.current;
    if (!scrollContainer) return;

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      if (scrollDebounceTimerRef.current) clearTimeout(scrollDebounceTimerRef.current);
    };
  }, []); // 🚨 CRITICAL FIX: Remove handleScroll dependency to prevent listener recreation

  // 🔧 FIX COMPOSER PERSISTENCE (Nov 9, 2025): Save draft to localStorage on input change
  useEffect(() => {
    const currentUser = getCurrentUserId();
    if (currentUser && input.trim()) {
      setUserStorage('composer_draft', input, currentUser);
    } else if (currentUser && !input.trim()) {
      // Clear draft if input is empty
      setUserStorage('composer_draft', '', currentUser);
    }
  }, [input]);

  // 🚨 CRITICAL FIX: Remove problematic scroll state management useEffect
  // This was causing infinite loops - scroll state is now managed in the debounced handleScroll

  // 🎯 AUTO-SCROLL FOR THINKING DOTS: Scroll when typing starts
  useEffect(() => {
    if (isTyping && shouldAutoScroll && isUserAtBottom && scrollFrameRef.current) {
      // Small delay to ensure thinking dots are rendered
      setTimeout(() => {
        scrollToBottom();
        console.log('📜 AUTO-SCROLL: Scrolled to show thinking dots');
      }, 100);
    }
  }, [isTyping, shouldAutoScroll, isUserAtBottom]);
  // Auto-scroll behavior is handled directly in the scroll effect above

  // Note: All initialization logic is now handled by the state machine

  // Check for notification permissions - but only if tour has been completed
  useEffect(() => {
    const currentUser = getCurrentUserId();
    let timer: NodeJS.Timeout | undefined;
    (async () => {
      const hasSeenTour = await getUserStorage('onboarding_tour_seen', currentUser);
      if (hasSeenTour === 'true' && !chatPaused) {
        timer = setTimeout(() => {
          checkAndRequestNotifications();
        }, 60000);
      }
    })();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [chatPaused]); // Depend on chatPaused to re-trigger when tour completes

  // Handle research source context for "Chat with Cedar about this" deep linking
  useEffect(() => {
    if (Platform.OS !== 'web') return; // Web-only routing
    
    if (searchParams.from === 'insights-research-source' && searchParams.context) {
      // 🔧 FIX (Mar 19, 2026): searchParams are strings after Expo Router migration — JSON.parse
      let contextObj: any;
      try {
        contextObj = typeof searchParams.context === 'string' ? JSON.parse(searchParams.context as string) : searchParams.context;
      } catch { contextObj = {}; }
      const {
        sourceId,
        goalId,
        workoutId,
        initialMessage,
        // 🆕 JAN 2025: Enriched application context
        applicationSummary,
        keyInfluence,
        citedFinding,
        appliedTo
      } = contextObj;
      
      // 🆕 FIX #3: Store contextual launch data for backend API request
      const contextualLaunchData = {
        from: 'insights-research-source',
        type: 'research',
        id: sourceId,
        goalId: goalId,
        workoutId: workoutId,
        title: 'research source',
        timestamp: Date.now()
      };
      
      // Store and fetch research context
      const fetchResearchContext = async () => {
        // Store contextual launch data first
        await storage.setItem('contextual_launch', JSON.stringify(contextualLaunchData));
        console.log('🎯 CONTEXTUAL LAUNCH: Stored research launch data for backend:', contextualLaunchData);
        try {
          const jwtToken = await storage.getItem('jwt_token');
          const params = new URLSearchParams();
          if (goalId) params.append('goal_id', goalId);
          if (workoutId) params.append('workout_id', workoutId);
          
          const response = await fetch(
            `${API_BASE_URL}/api/research/source/${sourceId}/context?${params.toString()}`,
            {
              headers: {
                'Authorization': `Bearer ${jwtToken}`
              }
            }
          );
          
          if (!response.ok) {
            throw new Error('Failed to fetch research context');
          }
          
          const researchContext = await response.json();
          
          // 🆕 JAN 2025: Store enriched research context for backend orchestration
          const enrichedContext = {
            ...researchContext,
            // Add enriched fields from navigation state
            applicationSummary,
            keyInfluence,
            citedFinding,
            appliedTo
          };
          
          await storage.setItem('research_context', JSON.stringify(enrichedContext));
          
          // Pre-fill the composer with initial message
          if (initialMessage) {
            setInput(initialMessage);
            
            // Focus textarea after a brief delay
            setTimeout(() => {
              if (textareaRef.current) {
                textareaRef.current.focus();
                const textLength = initialMessage.length;
                textareaRef.current.setSelectionRange(textLength, textLength);
              }
            }, 100);
          }
          
          // 🆕 FIX #2: Add instant greeting for research sources too
          const greetingText = `I see you want to learn about "${researchContext.source_title}". ${researchContext.usage_connections} What would you like to know?`;
          
          const contextualMessage: ChatMessage = {
            id: `research-context-${Date.now()}`,
            sender: 'cedar',
            text: greetingText,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            status: 'delivered',
            isAnimated: true
          };
          
          transition({ type: 'ADD_MESSAGE', payload: { message: contextualMessage } });
          console.log('✅ INSTANT GREETING: Added research greeting message');
          
        } catch (error) {
          console.error('Error fetching research context:', error);
          
          // Fallback: still pre-fill the message and show generic greeting
          if (initialMessage) {
            setInput(initialMessage);
          }
          
          // Add fallback greeting
          const fallbackGreeting: ChatMessage = {
            id: `research-fallback-${Date.now()}`,
            sender: 'cedar',
            text: 'I see you want to discuss this research. What would you like to know?',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            status: 'delivered',
            isAnimated: true
          };
          
          transition({ type: 'ADD_MESSAGE', payload: { message: fallbackGreeting } });
        }
      };
      
      fetchResearchContext();
      
      // Clear navigation state to prevent re-triggering
      window.history.replaceState({}, '', '/chat');
    }
  }, [searchParams, messages.length, transition]);

  // 🆕 OCT 2025: Handle research event context for "Chat with Cedar about this" from events
  useEffect(() => {
    if (Platform.OS !== 'web') return; // Web-only routing
    
    if (searchParams.from === 'research-event' && searchParams.context) {
      // 🔧 FIX (Mar 19, 2026): searchParams are strings after Expo Router migration — JSON.parse
      let contextObj: any;
      try {
        contextObj = typeof searchParams.context === 'string' ? JSON.parse(searchParams.context as string) : searchParams.context;
      } catch { contextObj = {}; }
      const {
        eventId,
        eventType,
        eventTitle,
        eventDescription,
        intelligenceEngine,
        userQuery,
        initialMessage,
        goalId,
        planId,
        workoutId
      } = contextObj;
      
      // Fetch research event context from backend
      const fetchEventContext = async () => {
        try {
          const jwtToken = await storage.getItem('jwt_token');
          
          const response = await fetch(
            `${API_BASE_URL}/api/research/events/${eventId}/detail`,
            {
              headers: {
                'Authorization': `Bearer ${jwtToken}`
              }
            }
          );
          
          if (!response.ok) {
            throw new Error('Failed to fetch research event context');
          }
          
          const eventDetail = await response.json();
          
          // Store research event context for backend orchestration
          const eventContext = {
            event_id: eventId,
            event_type: eventType,
            event_title: eventTitle,
            event_description: eventDescription,
            intelligence_engine: intelligenceEngine,
            original_user_query: userQuery,
            event_sources: eventDetail.research_sources,
            goal_id: goalId,
            plan_id: planId,
            workout_id: workoutId
          };
          
          await storage.setItem('research_event_context', JSON.stringify(eventContext));
          
          // Pre-fill the composer with initial message
          if (initialMessage) {
            setInput(initialMessage);
            
            // Focus textarea after a brief delay
            setTimeout(() => {
              if (textareaRef.current) {
                textareaRef.current.focus();
                const textLength = initialMessage.length;
                textareaRef.current.setSelectionRange(textLength, textLength);
              }
            }, 100);
          }
          
        } catch (error) {
          console.error('Failed to fetch research event context:', error);
        }
      };
      
      fetchEventContext();
      
      // Clear navigation state to prevent re-triggering
      window.history.replaceState({}, '', '/chat');
    }
  }, [searchParams]);

  // 🎯 PROACTIVE CONTEXTUAL CHAT LAUNCH HANDLER
  // Handles all "Chat with Cedar" button clicks with green pill + proactive message
  useEffect(() => {
    if (Platform.OS !== 'web') return; // Web-only: searchParams and window.history
    if (searchParams.from === 'contextual-launch' && searchParams.contextData) {
      // 🔧 FIX (Mar 19, 2026): searchParams are strings after Expo Router migration — JSON.parse
      let contextData: any;
      try {
        contextData = typeof searchParams.contextData === 'string' ? JSON.parse(searchParams.contextData as string) : searchParams.contextData;
      } catch { contextData = {}; }
      
      console.log('🎯 CONTEXTUAL LAUNCH: User clicked Chat with Cedar button');
      console.log('   Type:', contextData.type);
      console.log('   Title:', contextData.title);
      
      // STEP 1: Display green pill immediately (optimistic UI)
      const pillMessage: ChatMessage = {
        id: `pill-${Date.now()}`,
        sender: 'system',
        text: '',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        status: 'delivered',
        contextualPill: {
          type: contextData.type,
          title: contextData.title,
          subtitle: contextData.description,
          metadata: {
            from: (contextData.type === 'research' || contextData.type === 'research_event') ? 'Insights' : 'Move',
            ...contextData.metadata
          }
        }
      };
      
      transition({ type: 'ADD_MESSAGE', payload: { message: pillMessage } });
      console.log('✅ CONTEXTUAL PILL: Displayed immediately');
      
      // STEP 2: Auto-trigger proactive backend call
      const triggerProactiveMessage = async () => {
        try {
          const jwtToken = await storage.getItem('jwt_token');
          
          // Prepare contextual launch data
          const contextualLaunchData = {
            from: 'contextual-launch',
            type: contextData.type,
            id: contextData.id,
            title: contextData.title,
            description: contextData.description,
            timestamp: Date.now(),
            metadata: contextData.metadata || {}
          };
          
          console.log('🚀 PROACTIVE: Calling backend for automatic message...');
          
          const response = await fetch(`${API_BASE_URL}/api/contextual-launch-proactive`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${jwtToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ 
              contextual_launch: contextualLaunchData 
            })
          });
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          
          const data = await response.json();
          console.log('✅ PROACTIVE: Backend responded successfully');
          
          // STEP 3: Update pill with enriched server data (if different)
          if (data.pill_data && JSON.stringify(data.pill_data) !== JSON.stringify(pillMessage.contextualPill)) {
            pillMessage.contextualPill = data.pill_data;
            transition({ type: 'ADD_MESSAGE', payload: { message: pillMessage } });
            console.log('✅ CONTEXTUAL PILL: Updated with server data');
          }
          
          // STEP 4: Display Cedar's proactive message
          const cedarMessage: ChatMessage = {
            id: data.cedar_message_id || `cedar-proactive-${Date.now()}`,
            sender: 'cedar',
            text: data.proactive_message,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            createdAt: new Date().toISOString(),
            status: 'delivered',
            isAnimated: true,
            metadata: {
              conversation_type: 'deep_link',
              contextual_launch: contextualLaunchData,
              intelligence_context: data.intelligence_context
            }
          };
          
          transition({ type: 'ADD_MESSAGE', payload: { message: cedarMessage } });
          console.log('✅ MISSION 29: Cedar sent proactive opening automatically');
          console.log('📨 Message displayed in chat with animation');
          
          // STEP 5: Store enriched context as SESSION for follow-up messages
          // 🆕 UPDATED (Jan 25, 2026): Store as 'contextual_session' to indicate it persists
          // This ensures subsequent messages maintain DEEP_LINK context + conversation_id
          await storage.setItem('contextual_session', JSON.stringify({
            ...data.enriched_context,  // Spread the enriched context at top level
            conversation_id: data.conversation_id,  // ← CRITICAL for session tracking
            intelligence_context: data.intelligence_context,
            can_trigger_recalibration: data.can_trigger_recalibration,
            from_: 'contextual-launch',  // Preserve the from_ field
            contextual_conversation: true,  // Flag for backend
            started_at: Date.now()  // Track when session started
          }));
          console.log('✅ SESSION STORED: Will persist for entire conversation (24h)');
          console.log('   conversation_id:', data.conversation_id);
          console.log('   This enables multi-turn follow-ups with full context');
          
        } catch (error) {
          console.error('❌ PROACTIVE: Failed to fetch proactive message:', error);
          
          // Fallback: Show generic Cedar greeting
          const fallbackMessage: ChatMessage = {
            id: `cedar-fallback-${Date.now()}`,
            sender: 'cedar',
            text: `What would you like to discuss about this ${contextData.type}?`,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            createdAt: new Date().toISOString(),
            status: 'delivered'
          };
          transition({ type: 'ADD_MESSAGE', payload: { message: fallbackMessage } });
          console.log('⚠️ FALLBACK: Showed generic greeting due to error');
        }
      };
      
      // Trigger proactive call
      triggerProactiveMessage();
      
      // Clear navigation state
      window.history.replaceState({}, '', '/chat');
    }
    
    // Handle legacy modal context (keep for backward compatibility)
    else if (searchParams.from === 'modal') {
      const context = searchParams.context;
      const initialMessage = searchParams.initialMessage;
      
      if (context && messages.length > 0) {
        const contextMessage: ChatMessage = {
          id: `context-${Date.now()}`,
          sender: 'user',
          text: `📍 Context: ${context}`,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          status: 'delivered'
        };
        
        // Only add context if we have existing messages and don't already have a context message
        const hasContextMessage = messages.some(m => m.text.startsWith('📍 Context:'));
        if (!hasContextMessage) {
          transition({ type: 'ADD_MESSAGE', payload: { message: contextMessage } });
        }
      }
      
      if (initialMessage) {
        setInput(initialMessage);
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.focus();
            // ⌨️ CURSOR POSITIONING: Place cursor at end of prefilled text for natural editing
            const textLength = initialMessage.length;
            textareaRef.current.setSelectionRange(textLength, textLength);
          }
        }, 100);
      }
    }
  }, [searchParams]); // Remove messages dependency to prevent loops

  // Helper function to determine file type and generate preview
  const getFileType = (file: File): 'image' | 'document' | 'other' => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.includes('pdf') || file.type.includes('document') || file.type.includes('text')) return 'document';
    return 'other';
  };

  const generatePreview = async (file: File): Promise<string | undefined> => {
    if (file.type.startsWith('image/')) {
      return URL.createObjectURL(file);
    }
    // For other files, could generate thumbnails or use file icons
    return undefined;
  };

  // Helper function to format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Real LLM integration with backend
  const getCedarResponse = async (
    userMessage: string, 
    attachments: FileAttachment[]
  ): Promise<{ text: string; reaction?: MessageReaction; backendState?: any }> => {
    try {
      console.log('🚀 Making LLM API call to /api/chat with message:', userMessage.substring(0, 50) + '...');
      console.log('📎 Attachments to send:', attachments.length);
      
      // 🔧 PHASE 5: Simplified authentication - use tokens directly 
      let authToken = await storage.getItem('jwt_token');
      
      // If no JWT, use invite token directly (backend supports both)
      if (!authToken) {
        let inviteToken: string | null = await storage.getItem('invite_token');
        if (!inviteToken && Platform.OS === 'web') {
          const urlParams = new URLSearchParams(window.location.search);
          inviteToken = urlParams.get('token');
          if (inviteToken) {
            await storage.setItem('invite_token', inviteToken);
          }
        }
        
        // Use invite token directly - no conversion needed
        if (inviteToken && typeof inviteToken === 'string') {
          authToken = inviteToken;
          console.log('✅ PHASE 5: Using invite token directly (no JWT conversion)');
        }
      }

      // 🔧 CRITICAL FIX: Handle files properly with multipart/form-data
      let requestBody: any;
      let headers: Record<string, string> = {};
      
      if (attachments.length > 0) {
        // 📎 FILES PRESENT: Use multipart/form-data
        console.log('📎 FIXED: Sending files via multipart/form-data');
        
        const formData = new FormData();
        formData.append('message', userMessage);
        
        // Add each file — base64-encode XML files to bypass AWS WAF body inspection
        const XML_FILE_EXTS = ['tcx', 'gpx', 'xml'];
        for (let i = 0; i < attachments.length; i++) {
          const fname = attachments[i].file.name || '';
          const fext = (fname.split('.').pop() || '').toLowerCase();
          if (XML_FILE_EXTS.includes(fext)) {
            const b64 = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve((reader.result as string).split(',')[1]);
              reader.onerror = reject;
              reader.readAsDataURL(attachments[i].file);
            });
            formData.append(`file_b64_${i}`, b64);
            formData.append(`file_b64_${i}_name`, fname);
            console.log(`📎 Base64-encoded file_b64_${i}: ${fname}`);
          } else {
            formData.append(`file${i}`, attachments[i].file);
            console.log(`📎 Added file${i}: ${fname}`);
          }
        }
        
        requestBody = formData;
        // Don't set Content-Type header - let browser set it with boundary
        
      } else {
        // 💬 NO FILES: Use JSON (existing behavior)
        console.log('💬 No files, using JSON request');
        headers['Content-Type'] = 'application/json';
        
        // 🚨 CRITICAL: Include frontend state for synchronization
        // 🚨 CRITICAL FIX (Dec 22, 2025): Validate frontend state before sending
        // Prevents Step 3/4/5 from being skipped due to incorrect state advancement
        const backendStep = chatState.backendState?.onboardingStep || 0;
        const backendName = chatState.backendState?.chatName;
        const backendCompleted = chatState.backendState?.onboardingCompleted || false;
        
        // DETECT INCONSISTENCY: Frontend thinks we're at a step that hasn't been properly processed
        // Example: Step 2→3 transition where backend says step=3 but user never answered motivation
        let validatedStep = backendStep;
        let validatedName = backendName;
        
        if (backendStep === 3 && !backendName) {
          // SUSPICIOUS: At Step 3 (motivation) but no name stored - likely incorrect state
          console.warn('⚠️ FRONTEND VALIDATION: Step 3 with no name - likely incorrect state');
          console.warn('   This suggests Step 2→3 transition was incorrectly advanced');
          console.warn('   Correcting: Staying at Step 2 to ensure proper flow');
          validatedStep = 2;
        }
        
        if (backendStep === 4 && !backendName) {
          // CRITICAL: At Step 4 (discovery) but no name - definitely incorrect state
          console.error('🚨 FRONTEND VALIDATION: Step 4 with no name - CRITICAL STATE ERROR');
          console.error('   Steps 2 and 3 were likely skipped');
          console.error('   Correcting: Reverting to Step 2');
          validatedStep = 2;
        }
        
        // Construct validated frontend state
        const frontendState = {
          onboarding_step: validatedStep,
          onboarding_completed: backendCompleted,
          detected_scenario: chatState.backendState?.detectedScenario,
          chat_name: validatedName,
          personality_sliders_submitted: personalitySliderState.hasSubmitted
        };
        
        console.log('🔄 SYNC: getCedarResponse sending frontend state:', JSON.stringify(frontendState, null, 2));
        if (validatedStep !== backendStep) {
          console.log('🚨 FRONTEND STATE CORRECTED:');
          console.log('   Backend said step=' + backendStep + ', frontend sending step=' + validatedStep);
        }
        console.log('🔄 SYNC: getCedarResponse message:', userMessage);
        
        requestBody = JSON.stringify({
          message: userMessage,
          frontend_state: frontendState
        });
      }
      
      // Add authentication header
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
        console.log('✅ PHASE 5: Using authentication token');
      } else {
        console.error('❌ PHASE 5: No authentication token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers,
        body: requestBody,
        signal: AbortSignal.timeout(CHAT_REQUEST_TIMEOUT) // 120 second timeout (matches CloudFront OriginReadTimeout)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ LLM API error:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}, details: ${errorText}`);
      }

      const data = await response.json();
      console.log('✅ LLM API call successful');
      console.log('🔍 SYNC: Backend response data:', { 
        hasText: !!data.text, 
        hasMetadata: !!data.metadata,
        dataKeys: Object.keys(data)
      });
      
      // 🔍 ENHANCED DIAGNOSTICS: Validate response structure
      if (!data) {
        console.error('❌ Backend returned null/undefined data');
        throw new Error('Backend returned null response');
      }
      
      const responseText = data.text || data.response || '';
      if (!responseText) {
        console.error('❌ Backend response missing text content:', {
          hasText: !!data.text,
          hasResponse: !!data.response,
          dataKeys: Object.keys(data),
          dataStructure: data
        });
      }
      
      // 🚨 NEW: Extract backend state from metadata for synchronization
      let backendState: any = null;
      if (data.metadata) {
        console.log('🔄 SYNC: getCedarResponse - Raw metadata from backend:', JSON.stringify(data.metadata, null, 2));
        
        // Extract onboarding state information
        backendState = {
          onboardingStep: data.metadata.onboarding_step || chatState.backendState?.onboardingStep || 0,
          onboardingCompleted: data.metadata.onboarding_completed || false,
          chatName: data.metadata.chat_name || chatState.backendState?.chatName,
          detectedScenario: data.metadata.detected_scenario || data.metadata.scenario || null
        };
        
        console.log('🔄 SYNC: getCedarResponse - Extracted backend state:', JSON.stringify(backendState, null, 2));
      } else {
        console.log('🔄 SYNC: getCedarResponse - No metadata in response');
      }
      
      // 🚨 REMOVED PROBLEMATIC NAME PREPENDING: Backend already handles names correctly
      // The following code was causing "Clean Test" contamination by using administrative names
      // instead of preferred chat names. Backend manages name usage properly.
      const enhancedResponse = responseText;
      
      // 😊 SOCIALLY INTELLIGENT EMOJI REACTIONS: Context-aware coaching responses
      let reaction: MessageReaction | undefined;
      
      // Get user's personality preferences (default to balanced if not set)
      const userWarmth = personalitySliderState.values.warmth || 5; // 1-10 scale
      const userHumor = personalitySliderState.values.humor || 5;   // 1-10 scale
      
      // Analyze message context and emotional significance
      const messageContext = analyzeMessageContext(userMessage);
      const shouldReact = shouldCedarReact(messageContext, userWarmth, userHumor);
      
      if (shouldReact) {
        const contextualEmoji = getContextualEmoji(messageContext, userWarmth, userHumor);
        if (contextualEmoji) {
          reaction = { 
            emoji: contextualEmoji,
            source: 'cedar' as const,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          };
          console.log(`😊 SOCIAL INTELLIGENCE: Cedar reacted with ${contextualEmoji} (${messageContext.primaryEmotion}, warmth:${userWarmth}, humor:${userHumor})`);
        }
      }

      return { 
        text: enhancedResponse,
        reaction,
        backendState
      };

    } catch (error) {
      console.error('❌ Error calling LLM backend:', error);
      
      // 🚨 CRITICAL: Prevent infinite render loops after failures
      const isRenderLoopError = error instanceof Error && error.message.includes('Failed to fetch');
      if (isRenderLoopError) {
        console.log('🛡️ RENDER LOOP PREVENTION: API failure detected, implementing safeguards');
        // Force a brief delay to prevent rapid re-renders
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      // 🔧 ENHANCED ERROR HANDLING: Specific file upload error detection
      const isTimeoutAbort = error instanceof DOMException && error.name === 'AbortError';

      const isAuthError = error instanceof Error && (
        error.message.includes('401') ||
        error.message.includes('authentication') ||
        error.message.includes('token')
      );

      const isFileError = !isTimeoutAbort && error instanceof Error && (
        error.message.includes('413') ||  // Payload too large
        error.message.includes('415') ||  // Unsupported media type
        error.message.includes('multipart') ||
        error.message.includes('file')
      );

      const isCorsError = !isTimeoutAbort && error instanceof Error && (
        error.message.includes('CORS') ||
        error.message.includes('Failed to fetch') ||
        error.message.includes('Access-Control-Allow-Origin')
      );

      if (isAuthError) {
        console.error('🔒 Authentication issue detected - user needs to re-authenticate');
        await storage.removeItem('jwt_token');
        await storage.removeItem('invite_token');
      }

      let errorMessage = 'I apologize, but I\'m experiencing technical difficulties. Please try again in a moment.';

      if (isTimeoutAbort && uploadedAttachments?.length > 0) {
        errorMessage = "I had trouble processing your file — it may have taken too long. Please try uploading again, or describe what it shows and I can help from there.";
      } else if (isTimeoutAbort) {
        errorMessage = "That took longer than expected. Let me check if your message was processed...";
      } else if (isCorsError) {
        errorMessage = 'The service is temporarily unavailable. Please wait a moment and try again, or refresh the page if the issue persists.';
      } else if (isFileError) {
        errorMessage = 'I had trouble processing your file. Please check the file type and size (max 5MB), then try again.';
      } else if (isAuthError) {
        errorMessage = 'Authentication expired. Please refresh the page and try again.';
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      const errorResponse: ChatMessage = {
        id: `cedar_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        sender: 'cedar',
        text: errorMessage,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        status: 'error'
      };

      transition({
        type: 'ADD_MESSAGE',
        payload: { message: errorResponse }
      });
      
      // 🚨 CRITICAL FIX: Return error response to satisfy function signature
      return {
        text: errorMessage,
        reaction: undefined,
        backendState: null
      };
    }
  };

  // 🔒 Track if chunk processing is active to prevent background polling race condition
  const isProcessingChunksRef = useRef(false);
  
  // 🎯 SPRINT 3: Handle conversational chunks for sequential delivery
  const processConversationalChunks = useCallback(async (
    response: any, 
    newMessage: ChatMessage | null, 
    actions?: ChatMessage['actions'] | null
  ) => {
    try {
      // 🔒 NOTE: Flag is now set by handleSend BEFORE calling this function
      // This function no longer manages the flag - caller handles it
      // 🎯 ONBOARDING CHUNK HANDLING: Steps 4-5 NEED chunks (Option C system)
      // Only skip chunk processing for steps 0-3 (name, greeting, privacy, ready)
      // Steps 4-5 use chunks for motivation acknowledgment + personalization
      if (chatState.backendState && 
          chatState.backendState.onboardingStep !== undefined && 
          chatState.backendState.onboardingStep <= 3) {
        console.log('⚠️ ONBOARDING: Skipping chunk processing for step', chatState.backendState.onboardingStep, '(steps 0-3 only)');
        return false; // Don't process chunks during name/greeting/privacy/ready
      }

      // Check if response has conversational chunks (handle multiple formats)
      console.log('🔍 CHUNK DEBUG: processConversationalChunks called with:', response);
      console.log('🔍 CHUNK DEBUG: response.chunks:', response.chunks);
      console.log('🔍 CHUNK DEBUG: response.optimization_applied:', response.optimization_applied);
      
      const chunks = response.chunks || response?.chunks;
      const optimizationApplied = response.optimization_applied || response?.optimization_applied;
      
      console.log('🔍 CHUNK DEBUG: extracted chunks:', chunks);
      console.log('🔍 CHUNK DEBUG: extracted optimizationApplied:', optimizationApplied);
      
      if (chunks && optimizationApplied) {
        console.log('🎯 Processing conversational chunks:', chunks.length);
        
        // Process each chunk sequentially with perfect timing
        for (let i = 0; i < chunks.length; i++) {
          const chunk = chunks[i];
          const isFirstChunk = i === 0;
          const isLastChunk = i === chunks.length - 1;
          
          // 🎯 PHASE 4: Handle intelligent chunking format
          let chunkContent = chunk.content || chunk.text || chunk;
          
          // 🔧 FIX: Strip chunk markers like "CHUNK_1:", "CHUNK_2:", etc. if present
          // Backend might send these markers which should not be displayed to users
          if (typeof chunkContent === 'string') {
            chunkContent = chunkContent.replace(/^CHUNK_\d+:\s*/i, '').trim();
            chunkContent = chunkContent.replace(/^\[CHUNK\s+\d+\]\s*/i, '').trim();
            chunkContent = chunkContent.replace(/^---\s*CHUNK\s+\d+\s*---\s*/i, '').trim();
          }
          
          const chunkDelay = chunk.delay_ms || chunk.delay || (isFirstChunk ? 0 : 1500);
          const chunkType = chunk.type || 'default';
          
          console.log(`🎯 Chunk ${i + 1}/${chunks.length}: "${chunkContent.slice(0, 30)}..." (type: ${chunkType}, delay: ${chunkDelay}ms)`);
          
          // 🎯 PHASE 1: Show thinking dots for subsequent chunks with intelligent timing
          if (!isFirstChunk) {
            // Use intelligent delay from backend or fallback to 500ms
            const thinkingDelay = Math.min(chunkDelay, 2000); // Cap at 2 seconds
            console.log(`🤔 Showing thinking dots for chunk ${i + 1} (${thinkingDelay}ms)`);
            
            transition({
              type: 'UPDATE_UI',
              payload: { typingMessageId: 'thinking' }
            });
            
            await new Promise(resolve => setTimeout(resolve, thinkingDelay));
            
            // Stop thinking dots
            transition({
              type: 'UPDATE_UI',
              payload: { typingMessageId: null }
            });
          }
          
          // 🎯 PHASE 2: Add the chunk message with intelligent content
          // 🔧 FIX (Jan 30, 2026): Use proper cedar_ prefix to prevent malformed IDs
          const chunkMessage: ChatMessage = {
            id: `cedar_${Date.now()}_chunk${i}_${Math.random().toString(36).substr(2, 5)}`,
            sender: 'cedar',
            text: chunkContent,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            actions: isLastChunk && actions ? actions : undefined, // Only add actions to last chunk
            status: 'delivered',
            isAnimated: true, // 🎯 Enable typing animation for each chunk
            metadata: {
              chunk_info: {
                sequence: i + 1,
                total_parts: chunks.length,
                type: chunkType
              }
            } as any
          };
          
          // Add chunk message
          transition({
            type: 'ADD_MESSAGE',
            payload: { message: chunkMessage }
          });

          // Add reaction to user message if Cedar reacted (only for first chunk)
          if (response.reaction && i === 0) {
            // Update the user message with reaction using transition
            transition({
              type: 'UPDATE_UI', 
              payload: {
                // We'll handle the reaction update differently - this is a placeholder
              }
            });
          }
          
          // 🎯 PHASE 3: Start typing animation for this chunk
          transition({
            type: 'UPDATE_UI',
            payload: { typingMessageId: chunkMessage.id }
          });
          
          // Calculate typing duration for chunk
          // Match the actual typing speed from TypingText component (40ms per character)
          const typingSpeed = 40; // ms per character
          // 🔧 FIX: Use chunkContent (cleaned string) instead of chunk.content which may be undefined
          const typingTime = chunkContent.length * typingSpeed;
          
          console.log(`⌨️ Chunk ${i + 1} typing animation: ${typingTime}ms`);
          
          // 🎯 PHASE 4: Wait for typing animation to complete
          await new Promise(resolve => setTimeout(resolve, typingTime));
          transition({
            type: 'UPDATE_UI',
            payload: { typingMessageId: null }
          });
          
          console.log(`✅ Chunk ${i + 1} typing complete`);
          
          // Play notification sound only for last chunk
          if (isLastChunk) {
            playNotificationSound();
          console.log(`🔔 Final chunk complete - notification played`);
        }
      }
      
      // 🔒 NOTE: Flag is cleared by handleSend AFTER this function returns
      return true; // Indicate chunks were processed
    }
    
    // 🔒 NOTE: Flag is cleared by handleSend AFTER this function returns
    return false; // No chunks to process
  } catch (error) {
    console.error('❌ Error processing conversational chunks:', error);
    // 🔒 NOTE: Flag is cleared by handleSend AFTER this function returns
    return false;
  }
}, [playNotificationSound]);

  // 🚨 PHASE 4: Wait for uploads to complete function
  const waitForUploadsToComplete = async (): Promise<void> => {
    return new Promise((resolve) => {
      const checkUploads = () => {
        const stillUploading = attachments.filter(att => att.uploadStatus === 'uploading');
        if (stillUploading.length === 0) {
          resolve();
        } else {
          setTimeout(checkUploads, 500); // Check every 500ms
        }
      };
      checkUploads();
    });
  };


  // 🚨 DEPRECATED: Old EventSource-based streaming removed (replaced with fetch() + ReadableStream at lines 222-403)

  // 🚨 BACKGROUND PROCESSING LOGIC REMOVED
  // Background processing has been disabled to allow measurement of actual processing times
  // and manage user expectations through other UX patterns (loading states, typing indicators, etc.)

  const handleSend = async () => {
    if ((!input.trim() && attachments.length === 0) || isTyping) return;
    
    // 🔧 FIX: Declare currentUser once at function scope (used at lines ~3120 and ~3774)
    const currentUser = getCurrentUserId();
    
    // Check if there's already a pending Cedar message to prevent duplicates
    // 🚨 FIX (Jan 27, 2026): Check ui.isTyping FIRST - more reliable than message status
    // Problem: React batches state updates. When stream completes, message status updates to 'delivered'
    // and isTyping updates to false, but these updates are queued (not synchronous). If user clicks send
    // immediately, the check runs on OLD state where message still shows 'typing', causing false blocking.
    // Solution: Check chatState.ui.isTyping first - this flag is updated via UPDATE_UI and is the source
    // of truth. Only do expensive message array scan if isTyping is true (defensive check).
    
    if (chatState.ui.isTyping) {
      console.log('🛡️ BLOCKING: User input blocked - isTyping flag is true');
      return;
    }
    
    // Defensive check: Scan messages for any still-processing (backup for edge cases)
    const hasPendingCedarMessage = chatState.messages.some(msg => 
      msg.sender === 'cedar' && 
      (msg.status === 'thinking' || msg.status === 'typing' || msg.status === 'sending') &&
      (msg.text && msg.text.trim().length > 0 || msg.isStreaming === true)
    );
    
    if (hasPendingCedarMessage) {
      console.log('🛡️ BLOCKING: User input blocked - Cedar message still processing (message status check)');
      return;
    }
    
    // Note: Removed concurrent processing block - both onboarding and user input now use streaming
    
    // Allow send anytime: include only attachments that are fully uploaded
    const stillUploading = attachments.filter(att => att.uploadStatus === 'uploading');
    const failedUploads = attachments.filter(att => att.uploadStatus === 'error');

    if (failedUploads.length > 0) {
      console.log(`❌ Cannot send: ${failedUploads.length} files failed to upload`);
      // Show error message but allow sending without failed files
      const errorMessage: ChatMessage = {
        id: `error_${Date.now()}`,
        sender: 'system', 
        text: `⚠️ ${failedUploads.length} file${failedUploads.length > 1 ? 's' : ''} failed to upload and won't be included. You can retry uploading them or send your message without them.`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        status: 'delivered'
      };
      transition({ type: 'ADD_MESSAGE', payload: { message: errorMessage } });
      
      // Remove failed uploads from attachments
      setAttachments(prev => prev.filter(att => att.uploadStatus !== 'error'));
      // Continue sending message without failed files
    }
    
    // 🚨 RATE LIMITING: Prevent rapid message sending that could cause loops
    const lastMessageTime = await storage.getItem('lastMessageTime');
    const rateLimitNow = Date.now();
    if (lastMessageTime && (rateLimitNow - parseInt(lastMessageTime)) < 1000) {
      console.log('🛡️ RATE LIMIT: Preventing rapid message sending');
      return;
    }
    await storage.setItem('lastMessageTime', rateLimitNow.toString());

    // Include all attachments with a valid file object — the 'uploaded' status is a cosmetic
    // client-side simulation (files are actually sent once via /api/chat FormData).
    // Filtering to 'uploaded' only created a race condition: if the user sends before the
    // 2-3s animation completes, uploadedAttachments is empty, forcing the streaming path
    // which never includes file data. Filter out only genuinely errored attachments.
    const uploadedAttachments = attachments.filter(att => att.uploadStatus !== 'error' && att.file);

    // Create user message
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      sender: 'user',
      text: input.trim(),
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      status: 'delivered',
      attachments: uploadedAttachments.length > 0 ? uploadedAttachments : undefined
    };

    transition({ type: 'ADD_MESSAGE', payload: { message: userMessage } });
    setAttachments([]); // Clear preview immediately, batched with ADD_MESSAGE to prevent flash

    // 🛡️ DEFENSIVE PERSIST (Mar 19, 2026): Save user message to localStorage IMMEDIATELY
    // before the async API call begins. The normal save runs asynchronously via useEffect .then()
    // which creates a window where a component remount loses the message (BUG: Step 4 evaporation).
    // This synchronous write ensures the message survives even if the 35s streaming wait causes issues.
    if (currentUser) {
      try {
        const existingMessagesRaw = await storage.getItem(`${currentUser}_chat_messages`);
        const existingMessages = existingMessagesRaw ? JSON.parse(existingMessagesRaw) : [];
        const alreadySaved = existingMessages.some((m: any) => m.id === userMessage.id);
        if (!alreadySaved) {
          existingMessages.push({ ...userMessage, wasAnimated: true });
          await storage.setItem(`${currentUser}_chat_messages`, JSON.stringify(existingMessages));
          console.log(`🛡️ DEFENSIVE PERSIST: User message ${userMessage.id} saved to localStorage before API call`);
        }
      } catch (persistError) {
        console.warn('⚠️ DEFENSIVE PERSIST: Failed (non-critical):', persistError);
      }
    }

    console.log('🔄 TYPING DEBUG: Setting typing state to true');
    console.log('🔄 TYPING DEBUG: About to call backend API');

    // 🎭 PROGRESSIVE EMOJI REACTION: Cedar "reads" and reacts after 1 second
    setTimeout(async () => {
      const userWarmth = personalitySliderState.values.warmth || 5;
      const userHumor = personalitySliderState.values.humor || 5;
      const messageContext = analyzeMessageContext(input.trim());
      const shouldReact = shouldCedarReact(messageContext, userWarmth, userHumor);

      if (shouldReact) {
        const contextualEmoji = getContextualEmoji(messageContext, userWarmth, userHumor);
        if (contextualEmoji) {
          console.log(`🎭 Cedar reacting with ${contextualEmoji} to "${input.trim().substring(0, 30)}..."`);
          
          // Add reaction to the user message after 1-second delay
          const reaction: MessageReaction = {
            emoji: contextualEmoji,
            source: 'cedar',
            timestamp: new Date().toISOString()
          };
          
          transition({ 
            type: 'ADD_REACTION', 
            payload: { 
              messageId: userMessage.id, 
              reaction: reaction 
            } 
          });
        }
      }
    }, 1000); // 1-second delay for "Cedar reads and reacts" effect
    
    // 🎛️ PERSONALITY SLIDER TEXT CONFIRMATION DETECTION
    // Check if user is confirming personality slider setup
    const lastCedarMessage = messages.filter(m => m.sender === 'cedar').slice(-1)[0];
    const isSliderConfirmation = lastCedarMessage?.sliderAction && 
      (input.toLowerCase().includes('yes') || 
       input.toLowerCase().includes('sounds good') || 
       input.toLowerCase().includes('perfect') ||
       input.toLowerCase().includes('that works') ||
       input.toLowerCase().includes('okay') ||
       input.toLowerCase().includes('ok'));

    if (isSliderConfirmation) {
      console.log('🎛️ User confirmed personality sliders, saving preferences...');
      // Get slider values and save them
      const sliderValues = lastCedarMessage.sliderData;
      if (sliderValues) {
        await savePersonalityPreferences(sliderValues);
        console.log('✅ Personality preferences saved successfully');
      }
    }
    
    // 🔧 FIX Issue #3: IMMEDIATE cleanup of stale Cedar messages before sending
    // This ensures send button isn't blocked by stale messages from previous timeouts
    // 🔧 FIX NAVIGATION BUG (Nov 7, 2025): NEVER clean 'delivered' messages or messages with content
    const cleanupNow = Date.now();
    const staleMessagesBeforeSend = chatState.messages.filter(msg => {
      if (msg.sender !== 'cedar') return false;
      
      // 🚨 CRITICAL: Never clean delivered messages
      if (msg.status === 'delivered') return false;
      
      // 🔧 FIX NAVIGATION BUG #2: Also skip messages with actual text content
      if (msg.text && msg.text.trim().length > 10) return false;
      
      if (msg.status !== 'thinking' && msg.status !== 'typing' && msg.status !== 'sending' && msg.status !== 'processing') return false;
      
      const idParts = msg.id.split('_');
      if (idParts.length < 2) return false;
      const msgTimestamp = parseInt(idParts[1]);
      if (isNaN(msgTimestamp)) return false;
      
      const ageInMs = cleanupNow - msgTimestamp;
      return ageInMs > 10 * 1000; // Clean up anything older than 10 seconds before sending
    });
    
    if (staleMessagesBeforeSend.length > 0) {
      console.log(`🔧 FIX Issue #3: Force-cleaning ${staleMessagesBeforeSend.length} stale Cedar messages BEFORE send attempt`);
      staleMessagesBeforeSend.forEach(msg => {
        transition({
          type: 'ADD_MESSAGE',
          payload: {
            message: {
              ...msg,
              status: 'delivered'
            }
          }
        });
      });
    }
    
    // Store current message for Stream of Consciousness before clearing input
    setCurrentUserMessage(input.trim());
    
    // Clear input (attachments already cleared above, batched with ADD_MESSAGE)
    setInput('');
    
    // 🔧 FIX Issue #2: Use ONLY typingMessageId to prevent double typing dots
    // 🎯 FIX: Generate Cedar message ID EARLY so thinking dots use the SAME bubble
    const cedarMessageId = `cedar_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    console.log(`🎯 THINKING DOTS: Created placeholder message with ID ${cedarMessageId}`);
    
    // Create the Cedar message bubble immediately with "thinking" status
    // This bubble will be reused when streaming starts (no second bubble!)
    const thinkingMessage: ChatMessage = {
      id: cedarMessageId,
      sender: 'cedar',
      text: '',
      timestamp: '',  // 🎯 No timestamp yet - thinking dots aren't a message!
      status: 'thinking',  // Shows thinking dots
      isStreaming: false,
      isAnimated: true  // 🎬 FIX (Dec 4, 2025): Enable animation when content arrives
    };
    
    transition({ type: 'ADD_MESSAGE', payload: { message: thinkingMessage } });
    console.log(`🎯 THINKING DOTS: Added placeholder bubble - will be populated when stream arrives`);
    
    // Set typing state (for send button and stream visibility)
    transition({
      type: 'UPDATE_UI',
      payload: { 
        isTyping: true,  // Still set for send button logic
        typingMessageId: cedarMessageId  // Use actual cedar ID, not 'thinking'
      }
    });
    
    // 🔄 PERSISTENCE: Track pending request in localStorage for recovery after navigation
    if (currentUser) {
      await storage.setItem(`pending_response_${currentUser}`, JSON.stringify({
        messageId: `user-${Date.now()}`,
        cedarMessageId: cedarMessageId,  // 🔧 FIX (Feb 2, 2026): Store Cedar message ID for recovery
        message: input.trim(),
        timestamp: Date.now()
      }));
      console.log(`🔄 PERSISTENCE: Saved pending request (cedarId: ${cedarMessageId})`);
    }
    
    // 🕐 TIMEOUT PROTECTION: Declare timeout ID in proper scope for cleanup
    let timeoutId: NodeJS.Timeout | null = null;
    
    try {
      console.log('🚀 Making LLM API call with message:', input.substring(0, 50) + '...');
      console.log('📎 Files to upload:', attachments.length);
      
      // Get JWT token for authentication
      let jwtToken = await storage.getItem('jwt_token');
      
      // If no JWT, get invite token and convert to JWT
      if (!jwtToken) {
        let inviteToken: string | null = await storage.getItem('invite_token');
        if (!inviteToken && Platform.OS === 'web') {
          const urlParams = new URLSearchParams(window.location.search);
          inviteToken = urlParams.get('invite_token');
          if (inviteToken) {
            await storage.setItem('invite_token', inviteToken);
          }
        }
        
         if (inviteToken && typeof inviteToken === 'string') {
           const inviteResponse = await fetch(`${API_BASE_URL}/api/validate-invite/${encodeURIComponent(inviteToken)}`, {
             method: 'GET',
             headers: { 'Content-Type': 'application/json' }
           });
          
          if (inviteResponse.ok) {
            const inviteData = await inviteResponse.json();
            jwtToken = inviteData.jwt_token;
            if (jwtToken) {
              await storage.setItem('jwt_token', jwtToken);
            }
          }
        }
      }
      
      if (!jwtToken) {
        throw new Error('Authentication required. Please check your invite token.');
      }

      // 🔄 REQUEST HANDLING: Setup abort controller for navigation handling
      abortControllerRef.current = new AbortController();
      
      // 🕐 TIMEOUT PROTECTION: Dynamic timeout based on request type (text vs file upload)
      const hasFileAttachments = attachments.length > 0;
      const requestTimeout = hasFileAttachments ? FILE_UPLOAD_TIMEOUT : CHAT_REQUEST_TIMEOUT;
      const requestType = hasFileAttachments ? 'file upload' : 'text message';
      
      console.log(`⏱️ REQUEST TIMEOUT: Set ${requestTimeout/1000}s timeout for ${requestType}`);
      
      timeoutId = setTimeout(() => {
        if (abortControllerRef.current) {
          console.log(`⏰ TIMEOUT: Aborting ${requestType} request after ${requestTimeout/1000} seconds`);

          // 🔧 TIMEOUT RECOVERY (Dec 1, 2025): Trigger immediate background check
          if (cedarMessageId) {
            console.log(`🔒 TIMEOUT RECOVERY: Triggering immediate background check for ${cedarMessageId}`);
            checkBackgroundResponses();
          }

          abortControllerRef.current.abort();

          // 🛡️ HARD CLEANUP: If this was a file upload, clear thinking dots immediately
          // and show error. File processing can hang if the backend dies mid-processing.
          if (hasFileAttachments && cedarMessageId) {
            console.log(`🛡️ FILE TIMEOUT: Clearing thinking dots for ${cedarMessageId}`);
            transition({ type: 'UPDATE_UI', payload: { isTyping: false, typingMessageId: null } });
            setIsStreaming(false);
            transition({
              type: 'ADD_MESSAGE',
              payload: {
                message: {
                  id: cedarMessageId,
                  sender: 'cedar',
                  text: "I had trouble processing your file — it may have taken too long. Please try uploading again, or describe what it shows and I can help from there.",
                  timestamp: new Date().toISOString(),
                  status: 'delivered',
                  isStreaming: false,
                }
              }
            });
          }
        }
      }, requestTimeout);
      
      const messageId = `user-${Date.now()}`;
      
      // 🌊 PHASE 3: TRY STREAMING FIRST (if enabled and no file attachments)
      let streamingMessageId: string | null = null;
      if (ENABLE_STREAMING && uploadedAttachments.length === 0) {
        console.log('🌊 STREAMING: Attempting streaming for message without attachments');
        // 🔧 FIX: Set streamingMessageId so fallback can clean up properly
        streamingMessageId = cedarMessageId;
        try {
          // 🔧 FIX Issue #2: Don't set isTyping again - already set above (line 3353-3359)
          // Redundant state update causes double typing dots
          
          // Start Stream of Consciousness (will receive thoughts in metadata)
          setCurrentUserMessage(input);
          
          // Call new streaming function (pass cedar ID for placeholder bubble reuse)
          await sendStreamingMessage(
            input,
            uploadedAttachments,
            transition,
            (state: any) => {
              transition({ type: 'SYNC_BACKEND_STATE', payload: { backendState: state } });
            },
            (streaming: boolean) => {
              transition({ type: 'UPDATE_UI', payload: { isTyping: streaming } });
            },
            cedarMessageId,  // 🎯 FIX: Pass the cedar ID so stream reuses the thinking dots bubble
            chatState.messages,  // 🚀 ISSUE #2: Pass messages for goal detection
            processedMessageIdsRef,  // 🔒 FIX: Pass deduplication tracker
            setShowPipelineProgress,  // 🚀 ISSUE #2: Pipeline progress state
            setPipelineGoalText,
            setPipelineGoalDetails,
            setPipelineGoalId,  // 🚀 ISSUE #2 FIX: Pass goal ID setter
            chatState.backendState?.chatName,  // 🐛 FIX (Jan 31, 2026): Pass chatName for progress indicator
            logExtraction  // 🩺 MISSION 24: Extraction health monitoring
          );
          
          console.log('✅ STREAMING: Message sent successfully via streaming');
          return; // Exit early - streaming handled everything
        } catch (streamingError: any) {
          // 🔧 M35B FIX: If the fetch was aborted (user navigated away / component
          // unmounted), don't attempt fallback or show errors — the response will be
          // picked up by background polling when the user returns.
          if (streamingError.name === 'AbortError') {
            console.log('🔧 M35B: SSE fetch aborted (user navigated away) — skipping fallback');
            return;
          }

          console.log('⚠️ STREAMING: Failed, falling back to regular chat:', streamingError.message);

          // 🔧 FIX: Clear typing state immediately when streaming fails
          transition({
            type: 'UPDATE_UI',
            payload: { isTyping: false, typingMessageId: null }
          });

          if (streamingError.message === 'STREAMING_TIMEOUT') {
            console.log('⏰ STREAMING: Timeout - using regular endpoint');
          } else if (streamingError.message === 'STREAMING_CONNECTION_FAILED') {
            console.log('🔌 STREAMING: Connection failed - using regular endpoint');
          } else if (streamingError.message?.includes('ERR_HTTP2_PROTOCOL_ERROR') || streamingError.message?.includes('HTTP2')) {
            console.log('🔌 STREAMING: HTTP2 protocol error - network issue, will retry with regular endpoint');
          } else {
            console.log('❌ STREAMING: Other error - using regular endpoint');
          }
          
          // 🎯 THINKING DOTS FIX (Dec 19, 2025): KEEP the thinking message to show dots while waiting for background response
          // When streaming fails, background polling will deliver the response within a few seconds
          // Keeping the thinking message shows the user that Cedar is still working on the response
          // The background response will replace this thinking message when it arrives
          if (streamingMessageId) {
            console.log('🎯 FALLBACK: Keeping thinking message to show dots during background poll:', streamingMessageId);
            // Update the message to 'thinking' status to show dots (it may be in 'typing' status)
            const existingMessage = chatState.messages.find(m => m.id === streamingMessageId);
            if (existingMessage) {
              transition({
                type: 'ADD_MESSAGE',
                payload: {
                  message: {
                    ...existingMessage,
                    status: 'thinking',  // Show thinking dots while background polls
                    text: '',  // Clear any partial text
                    isStreaming: false
                  }
                }
              });
              console.log('🎯 FALLBACK: Updated message to thinking status for background poll');
            }
          } else {
            console.log('🎯 FALLBACK: No streamingMessageId - background poll will add response directly');
            // No cleanup needed - background poll handles response delivery
          }
          
          // 🎯 THINKING DOTS FIX (Dec 19, 2025): REMOVED aggressive cleanup
          // Previous cleanup removed ALL empty thinking messages after 500ms, which broke thinking dots
          // Now we keep thinking messages to show dots while background polling delivers response
          // Only clean up messages that are truly stuck (>30 seconds old) via the regular cleanup interval
          
          // 🛡️ FIX (Mar 19, 2026): Re-set isTyping BEFORE fallback POST
          // The catch block above cleared isTyping (line ~7001), which unlocks the input.
          // This allows the user to send a duplicate message during the fallback attempt.
          // Re-lock the input until the fallback POST completes or errors.
          transition({
            type: 'UPDATE_UI',
            payload: { isTyping: true, typingMessageId: cedarMessageId }
          });
          console.log('🛡️ FALLBACK: Re-locked input (isTyping=true) before fallback POST');

          // Continue to regular chat implementation below
          console.log('🔄 FALLBACK: Continuing with traditional POST /api/chat');
        }
      } else if (uploadedAttachments.length > 0) {
        console.log('📎 FILES DETECTED: Using traditional POST /api/chat for file uploads');
      } else {
        console.log('🔄 STREAMING DISABLED: Using traditional POST /api/chat');
      }
      
      // 🚨 CRITICAL: Include current frontend state for backend synchronization
      // 🎛️ FIX: Check if we're at step 6 and sliders should be processed
      const isStep6 = chatState.backendState?.onboardingStep === 6;
      const shouldProcessSliders = isStep6 && personalitySliderState.hasSubmitted;
      
      // 🚨 CRITICAL FIX (Dec 22, 2025): Validate frontend state before sending (file upload path)
      // Prevents Step 3/4/5 from being skipped due to incorrect state advancement
      const backendStep = chatState.backendState?.onboardingStep || 0;
      const backendName = chatState.backendState?.chatName;
      const backendCompleted = chatState.backendState?.onboardingCompleted || false;
      
      // DETECT INCONSISTENCY: Frontend thinks we're at a step that hasn't been properly processed
      let validatedStep = backendStep;
      let validatedName = backendName;
      
      if (backendStep === 3 && !backendName) {
        console.warn('⚠️ FRONTEND VALIDATION (file upload): Step 3 with no name - likely incorrect state');
        validatedStep = 2;
      }
      
      if (backendStep === 4 && !backendName) {
        console.error('🚨 FRONTEND VALIDATION (file upload): Step 4 with no name - CRITICAL STATE ERROR');
        validatedStep = 2;
      }
      
      const frontendState = {
        onboarding_step: validatedStep,
        onboarding_completed: backendCompleted,
        detected_scenario: chatState.backendState?.detectedScenario,
        chat_name: validatedName,
        personality_sliders_submitted: shouldProcessSliders,
        personality_preferences: shouldProcessSliders ? personalitySliderState.values : undefined
      };

      console.log('🔄 SYNC: Sending frontend state to backend:', JSON.stringify(frontendState, null, 2));
      if (validatedStep !== backendStep) {
        console.log('🚨 FRONTEND STATE CORRECTED (file upload):');
        console.log('   Backend said step=' + backendStep + ', frontend sending step=' + validatedStep);
      }
      console.log('🔄 SYNC: Current chatState.backendState:', JSON.stringify(chatState.backendState, null, 2));

      // 🎯 FIXED: Use FormData for file uploads instead of JSON
      const formData = new FormData();
      formData.append('message', input);
      formData.append('frontend_state', JSON.stringify(frontendState));
      
      // 🆕 FIX #3 (UPDATED Jan 25, 2026): Include contextual SESSION data (don't clear)
      // 🚨 CRITICAL: Don't clear after first message - this is a SESSION, not a one-off
      console.log('🔍 CONTEXTUAL SESSION: Attempting to retrieve from storage...');
      const contextualSessionJson = await storage.getItem('contextual_session');
      console.log('🔍 CONTEXTUAL SESSION: Retrieved from storage:', contextualSessionJson ? 'YES' : 'NO');
      
      if (contextualSessionJson) {
        try {
          const contextualSession = JSON.parse(contextualSessionJson);
          console.log('✅ CONTEXTUAL SESSION: Parsed successfully');
          console.log('   Type:', contextualSession.type);
          console.log('   Title:', contextualSession.title);
          console.log('   Conversation ID:', contextualSession.conversation_id);
          
          // Include full session data for backend detection
          formData.append('contextual_launch', JSON.stringify(contextualSession));
          console.log('✅ CONTEXTUAL SESSION: Appended to formData as contextual_launch');
          
          // ✅ CRITICAL: Include conversation_id for session tracking
          if (contextualSession.conversation_id) {
            formData.append('conversation_id', contextualSession.conversation_id);
            console.log('🎯 CONTEXTUAL SESSION: Including conversation_id:', contextualSession.conversation_id);
          }
          
          console.log('🎯 CONTEXTUAL SESSION: Session data will persist (NOT clearing)');
          console.log('   Session will persist for entire conversation (24h expiry)');
          
          // ❌ DON'T CLEAR - session persists for multi-turn conversation
          // sessionStorage.removeItem('contextual_launch');  // REMOVED - this was the bug!
        } catch (parseError) {
          console.error('❌ CONTEXTUAL SESSION: Failed to parse JSON:', parseError);
          console.error('   Raw data:', contextualSessionJson);
        }
      } else {
        console.log('ℹ️ CONTEXTUAL SESSION: No session found in storage (normal for non-contextual messages)');
      }
      
      // Add files to FormData for backend processing.
      // XML-based files (TCX/GPX) are base64-encoded to bypass AWS WAF body inspection
      // which blocks raw <?xml content as potential XXE attacks.
      const XML_EXTS = ['tcx', 'gpx', 'xml'];
      let webFileIdx = 0;
      for (const attachment of uploadedAttachments) {
        const ext = (attachment.filename?.split('.').pop() || '').toLowerCase();
        if (XML_EXTS.includes(ext)) {
          const b64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve((reader.result as string).split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(attachment.file);
          });
          formData.append(`file_b64_${webFileIdx}`, b64);
          formData.append(`file_b64_${webFileIdx}_name`, attachment.filename);
          console.log(`📎 Base64-encoding XML file_b64_${webFileIdx}: ${attachment.filename} (${b64.length} chars)`);
        } else {
          formData.append(`file${webFileIdx}`, attachment.file);
          console.log(`📎 Adding file${webFileIdx}: ${attachment.filename} (${attachment.size_bytes} bytes)`);
        }
        webFileIdx++;
      }

      console.log('🔄 SYNC: Complete request with files:', uploadedAttachments.length);

      // 🔇 REMOVED (Jan 5, 2026): Processing pill not needed with interim message system
      // Set processing state if files are present
      // if (uploadedAttachments.length > 0) {
      //   console.log('🔍 SHIMMER: Setting processingFiles to true');
      //   setProcessingFiles(true);
      //   setProcessingStartTime(Date.now());
      // }

      // 🚫 TEMPORARILY DISABLED: Goal processing detection and UI
      // 🎯 GOAL PROCESSING: Set processing state if goal creation is detected
      // const hasGoalIntent = detectGoalCreationIntent(input);
      // if (hasGoalIntent && !uploadedAttachments.length) {
      //   console.log('🎯 GOAL PROCESSING: Setting processingGoals to true');
      //   setProcessingGoals(true);
      // }

      // 🚀 BACKGROUND PROCESSING: Only enable after onboarding completion
      // This enables the goal creation pipeline that Coach Cedar promises to users
      // 🚀 BACKGROUND PROCESSING: Enable only after onboarding completion
      // During onboarding, we need immediate chunking and state management
      const isOnboardingCompleted = chatState.backendState?.onboardingCompleted || false;
      const shouldAllowBackground = isOnboardingCompleted; // Only enable after onboarding
      formData.append('allow_background_processing', shouldAllowBackground.toString());
      formData.append('frontend_message_id', messageId);
      
      // Background processing is enabled for goal/plan creation

      // 🔄 SEND REQUEST TO BACKEND (multipart/form-data for file processing)
      // 🔧 FIX: Explicitly set Accept header to application/json for fallback requests
      // This prevents backend from returning SSE when streaming failed
      const response = await fetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'Accept': 'application/json'  // 🔧 FIX: Explicitly request JSON, not SSE
          // 🎯 IMPORTANT: Don't set Content-Type header - let browser set it with boundary for multipart
        },
        body: formData,
        signal: abortControllerRef.current?.signal
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Chat API error response:', errorText);
        
        if (response.status === 401) {
          throw new Error('Authentication failed. Please refresh and try again.');
        } else if (response.status === 413) {
          throw new Error('File too large. Please try a smaller file.');
        } else if (response.status === 415) {
          throw new Error('Unsupported file type. Please try a different format.');
        } else {
          throw new Error(`Request failed: ${response.status} ${response.statusText}`);
        }
      }

      // 🔧 FIX: Check content-type before parsing - backend may return SSE even in fallback
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('text/event-stream')) {
        console.error('❌ FALLBACK ERROR: Backend returned SSE format but fallback expects JSON');
        console.error('   This means streaming failed but backend still sent SSE response');
        throw new Error('Streaming response received in fallback mode - please retry');
      }

      // Traditional JSON response
      const data = await response.json();
      console.log('✅ Received response from backend:', data);

      // 🛡️ FIX (Mar 19, 2026): Detect 202 "request_in_progress" from backend dedup
      // When streaming fails and fallback POST hits duplicate detection, backend returns 202
      // with {"error": "request_in_progress"}. This is NOT a real response — the original
      // streaming request is still being processed. Keep thinking dots and let background
      // polling deliver the real response instead of showing a fake error message.
      if (response.status === 202 || data.error === 'request_in_progress' || data.error === 'duplicate_request') {
        console.log(`🛡️ DEDUP DETECTED: Backend returned ${response.status} — request already in progress`);
        console.log('   Keeping thinking dots active — background poll will deliver real response');
        console.log(`   Original request ID: ${data.request_id || 'unknown'}`);

        // Keep typing state active so thinking dots stay visible
        transition({
          type: 'UPDATE_UI',
          payload: { isTyping: true, typingMessageId: cedarMessageId }
        });

        // 🕐 CLEANUP: Clear timeout
        if (timeoutId) clearTimeout(timeoutId);

        // 🕐 SAFETY TIMEOUT: If background poll doesn't deliver within 60s, clear thinking dots
        setTimeout(() => {
          console.log('🕐 DEDUP SAFETY: 60s since 202 — checking if response arrived');
          // Only clear if still showing thinking dots for this message
          const msg = chatState.messages.find(m => m.id === cedarMessageId);
          if (msg && (msg.status === 'thinking' || msg.status === 'typing') && (!msg.text || msg.text.trim() === '')) {
            console.log('🕐 DEDUP SAFETY: Response never arrived — clearing thinking dots');
            transition({
              type: 'ADD_MESSAGE',
              payload: {
                message: {
                  ...msg,
                  text: 'I\'m still working on your response. It should appear shortly — please wait a moment.',
                  status: 'delivered',
                  isStreaming: false
                }
              }
            });
            transition({ type: 'UPDATE_UI', payload: { isTyping: false, typingMessageId: null } });
          }
        }, 60000);

        return; // Exit early — background poll handles delivery
      }

      // 🔒 RACE CONDITION PROTECTION: Set flag immediately when response arrives
      // This prevents background polling from duplicating the message while we process it
      // Flag will be cleared after BOTH chunked AND single message paths complete
      isProcessingChunksRef.current = true;
      console.log('🔒 IMMEDIATE: Set isProcessingChunksRef=true to prevent background duplication');

      // 🕐 CLEANUP: Clear timeout on successful response
      if (timeoutId) clearTimeout(timeoutId);

      // 🚀 REVOLUTIONARY: CHECK FOR SERVICE ACTION RESPONSE (like personality sliders)
      if (data.service_action) {
        console.log('🎯 SERVICE ACTION DETECTED:', data.service_action);
        
        // 🚀 CRITICAL FIX: Don't create duplicate service message if we already have one from direct action
        // Check if we already have a recent service connection message to prevent duplicates
        const hasRecentServiceMessage = chatState.messages.some(msg => 
          msg.serviceAction && 
          msg.serviceAction.service === data.service_action.service &&
          Date.now() - parseInt(msg.id.split('-').pop() || '0') < 5000 // Within last 5 seconds
        );
        
        if (!hasRecentServiceMessage) {
          // Create a message with service action (similar to personality sliders)
          const serviceMessage: ChatMessage = {
            id: `service-backend-${data.service_action.service}-${Date.now()}`,
            text: data.service_action.suggested_message || data.response || "Let me help you connect your fitness data!",
            sender: 'cedar',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            status: 'delivered',
            serviceAction: {
              type: 'service_connection',
              service: data.service_action.service,
              platform: data.service_action.platform
            }
          };
          
          console.log('🚀 BACKEND SERVICE: Adding service message from backend response');
          transition({ type: 'ADD_MESSAGE', payload: { message: serviceMessage } });
        } else {
          console.log('🚀 BACKEND SERVICE: Skipping duplicate service message - already exists');
        }
        
        // 🚀 CRITICAL FIX: Clear typing state IMMEDIATELY to remove thinking dots below modal
        console.log('🔄 CLEARING: Removing thinking dots after service action - IMMEDIATE');
        transition({
          type: 'UPDATE_UI',
          payload: { 
            isTyping: false,
            typingMessageId: null
          }
        });
        
        // 🚀 ADDITIONAL FIX: Also clear with delay to ensure state persistence
        setTimeout(() => {
          console.log('🔄 CLEARING: Removing thinking dots after service action - DELAYED');
          transition({
            type: 'UPDATE_UI',
            payload: { 
              isTyping: false,
              typingMessageId: null
            }
          });
        }, 100);
        
        // 🔇 REMOVED (Jan 5, 2026): Processing pill not needed with interim message system
        // Reset file processing state and return early
        // console.log('🔍 SHIMMER: Setting processingFiles to false (service action)');
        // setProcessingFilesWithMinimumTime(false);
        return;
      }

      // 🔇 REMOVED (Jan 5, 2026): Processing pill not needed with interim message system
      // Reset file processing state
      // console.log('🔍 SHIMMER: Setting processingFiles to false (normal completion)');
      // setProcessingFilesWithMinimumTime(false);

      // 🚫 TEMPORARILY DISABLED: Goal processing state reset
      // 🎯 GOAL PROCESSING: Reset goal processing state
      // if (processingGoals) {
      //   console.log('🎯 GOAL PROCESSING: Setting processingGoals to false (normal completion)');
      //   setProcessingGoals(false);
      // }

      // 🔄 BACKGROUND PROCESSING: Check if response indicates background processing
      console.log('🔄 BACKGROUND DEBUG: Checking response for background processing');
      console.log('🔄 BACKGROUND DEBUG: data.status =', data.status);
      console.log('🔄 BACKGROUND DEBUG: data.metadata =', data.metadata);
      console.log('🔄 BACKGROUND DEBUG: background_processing =', data.metadata?.background_processing);
      
      if (data.status === 'processing' && data.metadata?.background_processing) {
        console.log('🔄 BACKGROUND: Detected background processing response');
        console.log('🔄 BACKGROUND: Task ID:', data.metadata.task_id);
        console.log('🔄 BACKGROUND: Estimated completion:', data.metadata.estimated_completion);
        
        // Create background processing message
        const backgroundMessage: ChatMessage = {
          id: data.id || `cedar-bg-${Date.now()}`,
          sender: 'cedar',
          text: data.text || "I'm processing your message in the background. Feel free to navigate away - I'll have your response ready when you return!",
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          status: 'processing',
          metadata: {
            background_processing: true,
            task_id: data.metadata.task_id,
            frontend_message_id: data.metadata.frontend_message_id,
            estimated_completion: data.metadata.estimated_completion
          }
        };
        
        // During onboarding, don't show the background processing message - just keep thinking dots
        // For post-onboarding, we might want to show the background message for navigation away
        const isOnboarding = chatState.phase === 'onboarding';
        
        if (!isOnboarding) {
          // Add background processing message for non-onboarding phases
          transition({ type: 'ADD_MESSAGE', payload: { message: backgroundMessage } });
          console.log('🔄 BACKGROUND: Added background processing message for non-onboarding phase');
        } else {
          console.log('🔄 BACKGROUND: Skipping background processing message during onboarding - keeping clean UX');
        }
        
        // DON'T clear typing state - let it continue showing thinking dots and Stream of Consciousness
        // The typing state will be cleared when the background response is retrieved and displayed
        console.log('🔄 BACKGROUND: Keeping typing state active for thinking dots and Stream of Consciousness');
        
        setCurrentUserMessage(''); // Clear stored message when background processing starts
        console.log('🔄 BACKGROUND: Background processing message added, polling will handle completion');
        return; // Exit early - background polling will handle the completed response
      }

      // 🚨 MATT FIX #4: Clear isTyping immediately when response is received
      // This ensures send button is enabled even if response processing fails
      console.log('🔧 MATT FIX: Clearing isTyping immediately upon response receipt');
      transition({
        type: 'UPDATE_UI',
        payload: { isTyping: false, typingMessageId: null }
      });
      
      // 🚨 MATT FIX #5: Find thinking dots bubble to reuse (prevents double messages)
      // When file uploads return immediately, we should reuse the thinking dots bubble instead of creating a new one
      // Store it in a variable for later use in message creation
      const thinkingDotsMessage = chatState.messages.find(msg => 
        msg.sender === 'cedar' && 
        (msg.status === 'thinking' || msg.status === 'typing') &&
        (!msg.text || msg.text.trim() === '')
      );
      
      if (thinkingDotsMessage) {
        console.log('🔧 MATT FIX: Found thinking dots bubble to reuse - will update instead of creating new message');
      }
      
      // 🚨 CRITICAL: Extract and sync backend state from response
      if (data.metadata) {
        console.log('🔄 SYNC: handleSend - Raw metadata from backend:', JSON.stringify(data.metadata, null, 2));
        
        // 🚀 FIX (Dec 25, 2025): Backend sends backend_state (not backendStateCamel)
        const backendStateCamel = data.metadata.backend_state || {};
        const backendState = {
          onboardingStep: backendStateCamel.onboardingStep || data.metadata.onboarding_step || chatState.backendState?.onboardingStep || 0,
          onboardingCompleted: backendStateCamel.onboardingCompleted || data.metadata.onboarding_completed || false,
          chatName: backendStateCamel.chatName || data.metadata.chat_name || chatState.backendState?.chatName || '',
          detectedScenario: backendStateCamel.detectedScenario || data.metadata.detected_scenario || data.metadata.scenario || ''
        };
        
        console.log('🔄 SYNC: handleSend - Extracted backend state:', JSON.stringify(backendState, null, 2));
        console.log('🔄 SYNC: handleSend - Triggering state sync');
        transition({ 
          type: 'SYNC_BACKEND_STATE', 
          payload: { backendState } 
        });
        
        // 🚨 CRITICAL: Clear personality_sliders_submitted flag if backend tells us to
        if (data?.metadata.clear_personality_sliders_submitted) {
          console.log('🎛️ CLEARING: Backend requested to clear personality_sliders_submitted flag');
          // 🎛️ FIX: Only reset sliders if we're past step 6 to avoid interfering with synthesis
          if (backendState.onboardingStep > 6) {
            console.log('🎛️ CLEARING: Safe to reset - past step 6');
            personalitySliderActions.reset();
          } else {
            console.log('🎛️ CLEARING: Skipping reset - still at step 6, synthesis may be pending');
          }
          transition({
            type: 'CLEAR_PERSONALITY_SLIDERS_SUBMITTED',
            payload: {}
          });
        }
      } else {
        console.log('🔄 SYNC: handleSend - No metadata in backend response');
      }

      // 🔍 ENHANCED DIAGNOSTICS: Validate response structure
      if (!data) {
        console.error('❌ Backend returned null/undefined data');
        throw new Error('Invalid response from server');
      }

      const responseText = data.text || data.response || '';
      if (!responseText) {
        console.error('❌ Backend response missing text content:', {
          hasText: !!data.text,
          hasResponse: !!data.response,
          dataKeys: Object.keys(data),
          dataStructure: data
        });
        
        // 🚨 MATT FIX #4: Clear isTyping even if response is unexpected
        // This prevents send button from being stuck disabled
        console.log('🔧 MATT FIX: Clearing isTyping due to unexpected response structure');
        transition({
          type: 'UPDATE_UI',
          payload: { isTyping: false, typingMessageId: null }
        });
      }

      // Parse and clean the response text to remove action markup (+ auto-fire service connectors)
      const { cleanText: rawCleanText, actions, serviceAction: fallbackServiceAction } = parseMessageActions(responseText || '');
      // 🔧 FIX (Mar 26, 2026): Apply paragraph break formatting to JSON responses.
      // SSE streaming path already applies addParagraphBreaks during streaming; the JSON
      // response path (personality synthesis, file uploads, fallbacks) was skipping it,
      // resulting in wall-of-text messages without proper paragraph breaks.
      const cleanText = addParagraphBreaks(rawCleanText);

      // 🎛️ AUTO-TRIGGER SLIDERS: Check if personality sliders should be triggered
      const hasSliderAction = actions?.some(action => action.type === 'personality_sliders');
      
      // 🎛️ FIX (Jan 30, 2026): Trust backend personality_slider_action flag
      // Backend has sophisticated logic in should_show_personality_sliders_clean()
      const backendStateFromMetadata = data.metadata?.backend_state;
      const shouldTriggerSliders = hasSliderAction || 
                                  (backendStateFromMetadata?.personality_slider_action === true);
      
      console.log('🔍 SLIDER DEBUG: responseText:', responseText);
      console.log('🔍 SLIDER DEBUG: actions found:', actions);
      console.log('🔍 SLIDER DEBUG: hasSliderAction:', hasSliderAction);
      console.log('🔍 SLIDER DEBUG: metadata personality_slider_action:', data.metadata?.personality_slider_action);
      console.log('🔍 SLIDER DEBUG: backend_state from metadata:', backendStateFromMetadata);
      console.log('🔍 SLIDER DEBUG: shouldTriggerSliders (combined):', shouldTriggerSliders);
      
      if (shouldTriggerSliders) {
        console.log('🎛️ SLIDER AUTO-TRIGGER: Personality sliders detected, will trigger after message');
      }
      
      // 🎯 CHECK FOR CONVERSATIONAL CHUNKS: Use chunking if available
      // Check multiple possible locations where chunks might be placed by backend
      let chunksData: any = null;
      let actualChunks: any[] | null = null;
      let optimizationApplied = false;
      
      // Priority 1: Direct chunks array (includes intelligent chunking and Option C chunks)
      if (data.chunks && Array.isArray(data.chunks) && data.chunks.length >= 1) {
        actualChunks = data.chunks;
        optimizationApplied = data.metadata?.intelligent_chunking?.applied || data.metadata?.option_c_triggered || true;
        chunksData = { chunks: actualChunks, optimization_applied: optimizationApplied };
        console.log('🚀 CHUNKING DETECTED: Found chunks from POST endpoint', actualChunks?.length || 0, 'Option C:', !!data.metadata?.option_c_triggered);
      }
      // Priority 2: Metadata chunks (including Option C chunks)
      else if (data.metadata?.chunks && Array.isArray(data.metadata.chunks) && data.metadata.chunks.length >= 1) {
        actualChunks = data.metadata.chunks;
        optimizationApplied = data.metadata.optimization_applied || data.metadata.option_c_triggered || true;
        chunksData = { chunks: actualChunks, optimization_applied: optimizationApplied };
        console.log('🚀 OPTION C CHUNKING: Detected Option C chunks from metadata', actualChunks?.length || 0);
      }
      // Priority 3: Conversational optimization structure
      else if (data.metadata?.conversational_optimization?.chunks && Array.isArray(data.metadata.conversational_optimization.chunks) && data.metadata.conversational_optimization.chunks.length >= 2) {
        actualChunks = data.metadata.conversational_optimization.chunks;
        optimizationApplied = data.metadata.conversational_optimization.optimization_applied || true;
        chunksData = data.metadata.conversational_optimization;
      }
      
      const hasChunks = actualChunks && actualChunks.length >= 1;
      
      if (hasChunks && optimizationApplied && actualChunks) {
        console.log('🎯 CHUNKED RESPONSE: Processing', actualChunks.length, 'chunks');
        console.log('🎯 CHUNKS DEBUG: chunks data structure:', chunksData);
        console.log('🎯 CHUNKS DEBUG: chunksData keys:', Object.keys(chunksData || {}));
        
        // Use the chunking system instead of single message
        // Turn off global typing indicator before processing chunks
        transition({
          type: 'UPDATE_UI',
          payload: { isTyping: false }
        });
        
        await processConversationalChunks(
          chunksData,
          null, // No base message needed
          null  // No actions for now
        );
        
        // 🔒 RACE CONDITION PROTECTION: Clear flag after chunked response completes
        isProcessingChunksRef.current = false;
        console.log('🔒 CHUNKED PATH: Cleared isProcessingChunksRef - background polling can resume');
      } else {
        // 🎯 SINGLE MESSAGE: Use traditional single message display
        console.log('🎯 SINGLE RESPONSE: No chunks detected, using single message');
        console.log('🎯 CHUNKS DEBUG: chunks data found:', !!chunksData);
        console.log('🎯 CHUNKS DEBUG: has chunks array:', !!actualChunks);
        console.log('🎯 CHUNKS DEBUG: chunks length:', actualChunks?.length);
        console.log('🎯 CHUNKS DEBUG: optimization applied:', optimizationApplied);
        console.log('🎯 CHUNKS DEBUG: full data keys:', Object.keys(data));
        
        // 🔧 FIX: Clean up any remaining thinking/typing bubbles before creating response
        // This ensures the three dots disappear when fallback response arrives
        const remainingThinkingBubbles = chatState.messages.filter(msg => 
          msg.sender === 'cedar' && 
          (!msg.text || msg.text.trim() === '') &&
          (msg.status === 'thinking' || msg.status === 'typing' || msg.status === 'sending')
        );
        
        if (remainingThinkingBubbles.length > 0) {
          console.log(`🗑️ FALLBACK CLEANUP: Found ${remainingThinkingBubbles.length} remaining thinking/typing bubbles, marking as delivered`);
          remainingThinkingBubbles.forEach(msg => {
            // Mark as delivered instead of removing - more reliable and ensures state updates
            transition({
              type: 'ADD_MESSAGE',
              payload: {
                message: {
                  ...msg,
                  status: 'delivered',
                  text: msg.text || '', // Ensure text is set (even if empty)
                  isStreaming: false
                }
              }
            });
            console.log(`🗑️ FALLBACK CLEANUP: Marked message ${msg.id} as delivered`);
          });
          
          // 🔧 SAFEGUARD: Ensure isTyping is cleared after cleanup
          // This prevents send button from staying disabled
          transition({
            type: 'UPDATE_UI',
            payload: { isTyping: false, typingMessageId: null }
          });
          console.log('🔧 FALLBACK CLEANUP: Cleared isTyping state after bubble cleanup');
        }
        
        // 🔧 FIX: Update existing streaming message or create new one
        if (streamingMessageId) {
          // 🚨 FIX #4: Use backend-provided message ID if available, but keep streaming ID for updates
          const backendMessageId = data.id || null;
          
          // Update existing streaming message instead of creating new one
          console.log('🔄 FALLBACK: Updating existing streaming message:', streamingMessageId);
          const cedarResponse: ChatMessage = {
            id: streamingMessageId, // 🚨 FIX #2: Keep original ID to trigger UPDATE in ADD_MESSAGE
            sender: 'cedar',
            text: cleanText || 'I received your message but encountered an issue processing it. Could you try again?',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            status: 'delivered',
            isAnimated: true,  // Enable typing animation for fallback responses
            actions: actions, // 🔧 FEB 10, 2026: Include parsed action buttons (connect_garmin, etc.)
            ...(fallbackServiceAction ? { serviceAction: fallbackServiceAction } : {}), // 🔧 FEB 11, 2026: Auto-fire service connectors
            // 🚨 FIX #5: Add metadata to identify file upload interim responses
            metadata: (data.metadata?.type === 'file_upload_interim_response' || shouldTriggerSliders) ? {
              ...(data.metadata?.type === 'file_upload_interim_response' ? {
                type: 'file_upload_interim_response' as const,
                content_type: data.metadata?.content_type,
                filename: data.metadata?.filename
              } : {}),
              ...(shouldTriggerSliders ? { triggerPersonalitySliders: shouldTriggerSliders } : {})
            } : undefined
          };

          // 🚨 FIX #2: ADD_MESSAGE automatically updates if ID matches existing message
          transition({
            type: 'ADD_MESSAGE',
            payload: { message: cedarResponse }
          });
          
          // 🔧 FIX (Dec 2025 - File Upload Duplicate): Add to persistent tracker with BOTH IDs
          processedMessageIdsRef.current.add(cedarResponse.id);
          if (backendMessageId && backendMessageId !== streamingMessageId) {
            processedMessageIdsRef.current.add(backendMessageId); // Track backend ID too
            console.log(`🔒 FILE UPLOAD: Also tracking backend ID ${backendMessageId} to prevent duplicates`);
          }
          console.log(`🔒 FILE UPLOAD: Added ${cedarResponse.id} to persistent tracker (prevents duplicate from background poller)`);
          } else {
            // 🚨 FIX #4: Use backend-provided message ID if available (prevents ID mismatch)
            // Backend returns "id": "cedar-{conversation_id}" which should be used consistently
            const backendMessageId = data.id || null;
            
            // 🚨 MATT FIX #5: Reuse thinking dots bubble if it exists (prevents double messages)
            // The thinkingDotsMessage was found earlier (line 4901-4909) and is in outer scope
            if (thinkingDotsMessage) {
              // 🚨 FIX #2: Keep same ID and update content (ADD_MESSAGE handles updates automatically)
              // This prevents failed speech bubble from REMOVE + ADD pattern
              console.log('🔧 MATT FIX: Updating thinking dots bubble (keeping same ID)');
              const cedarResponse: ChatMessage = {
                ...thinkingDotsMessage,
                id: thinkingDotsMessage.id, // 🚨 FIX #2: Keep original ID to trigger UPDATE in ADD_MESSAGE
                text: cleanText || 'I received your message but encountered an issue processing it. Could you try again?',
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                status: 'delivered',
                isAnimated: true,
                actions: actions,
                ...(fallbackServiceAction ? { serviceAction: fallbackServiceAction } : {}), // 🔧 FEB 11, 2026: Auto-fire service connectors
                // 🚨 FIX #5: Add metadata to identify file upload interim responses
                metadata: (data.metadata?.type === 'file_upload_interim_response' || shouldTriggerSliders) ? {
                  ...(data.metadata?.type === 'file_upload_interim_response' ? {
                    type: 'file_upload_interim_response' as const,
                    content_type: data.metadata?.content_type,
                    filename: data.metadata?.filename
                  } : {}),
                  ...(shouldTriggerSliders ? { triggerPersonalitySliders: shouldTriggerSliders } : {})
                } : undefined
              };
              
              // 🚨 FIX #2: ADD_MESSAGE automatically updates if ID matches existing message
              transition({ 
                type: 'ADD_MESSAGE', 
                payload: { message: cedarResponse }
              });
              
              // 🔧 FIX (Dec 2025 - File Upload Duplicate): Add to persistent tracker
              // Track both thinking dots ID and backend ID (if different) to prevent duplicates
              processedMessageIdsRef.current.add(cedarResponse.id);
              if (backendMessageId && backendMessageId !== thinkingDotsMessage.id) {
                processedMessageIdsRef.current.add(backendMessageId);
                console.log(`🔒 FILE UPLOAD: Also tracking backend ID ${backendMessageId} to prevent duplicates`);
              }
              console.log(`🔒 FILE UPLOAD: Added ${cedarResponse.id} to persistent tracker (prevents duplicate from background poller)`);
            } else {
              // Create new cedar response message (normal flow - no thinking dots to reuse)
              const cedarResponse: ChatMessage = {
                id: backendMessageId || (Date.now() + 1).toString(), // 🚨 FIX #4: Use backend ID if available
                sender: 'cedar',
                text: cleanText || 'I received your message but encountered an issue processing it. Could you try again?',
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                status: 'delivered',
                isAnimated: true,  // 🚨 CRITICAL FIX: Enable typing animation for all responses
                actions: actions,  // 🚨 CRITICAL FIX: Add parsed actions to message
                ...(fallbackServiceAction ? { serviceAction: fallbackServiceAction } : {}), // 🔧 FEB 11, 2026: Auto-fire service connectors
                // 🚨 FIX #5: Add metadata to identify file upload interim responses
                metadata: (data.metadata?.type === 'file_upload_interim_response' || shouldTriggerSliders) ? {
                  ...(data.metadata?.type === 'file_upload_interim_response' ? {
                    type: 'file_upload_interim_response' as const,
                    content_type: data.metadata?.content_type,
                    filename: data.metadata?.filename
                  } : {}),
                  ...(shouldTriggerSliders ? { triggerPersonalitySliders: shouldTriggerSliders } : {})
                } : undefined
              };

              transition({
                type: 'ADD_MESSAGE',
                payload: { message: cedarResponse }
              });
              
              // 🔧 FIX (Dec 2025 - File Upload Duplicate): Add to persistent tracker
              // Same bug as dual response system - file upload responses need tracker entry
              processedMessageIdsRef.current.add(cedarResponse.id);
              console.log(`🔒 FILE UPLOAD: Added ${cedarResponse.id} to persistent tracker (prevents duplicate from background poller)`);
            }
          }
        
        // 🔒 RACE CONDITION PROTECTION: Clear flag after single response completes
        isProcessingChunksRef.current = false;
        console.log('🔒 SINGLE PATH: Cleared isProcessingChunksRef - background polling can resume');
      }

      // 🎛️ PERSONALITY SLIDER AUTO-TRIGGER: Use the shouldTriggerSliders we calculated earlier
      // (Already defined at line ~6058 with Step 6 detection + action markup detection)
      if (shouldTriggerSliders) {
        console.log('🎛️ AUTO-TRIGGER: Creating inline personality sliders from metadata');
        
        // 🔒 DISABLE AUTOSCROLL: Turn off autoscroll when Step 6 sliders appear
        transition({
          type: 'UPDATE_UI',
          payload: {
            scrollState: {
              shouldAutoScroll: false,
              isUserAtBottom: true
            }
          }
        });
        console.log('🔒 SCROLL: Autoscroll disabled for Step 6 personality sliders');
        
        // 🎯 FIX #2: IMMEDIATE SCROLL FREEZE - Do this FIRST before any delays
        const scrollContainer = scrollFrameRef.current;
        if (scrollContainer) {
          const currentScrollTop = scrollContainer.scrollTop;
          scrollContainer.setAttribute('data-slider-mode', 'true');
          scrollContainer.style.scrollBehavior = 'auto';
          scrollContainer.style.overflow = 'hidden'; // Prevent any scrolling
          scrollContainer.scrollTop = currentScrollTop;
          console.log('🔒 SCROLL: Chat scroll IMMEDIATELY FROZEN for slider interaction');
        }
        
        // 🎯 FIX #1: TIMING - Extended delay to let message animation complete
        setTimeout(() => {
          console.log('🎛️ DELAYED TRIGGER: Now showing sliders after message animation completed');
          
          // Re-enable scroll but keep slider mode
          if (scrollContainer) {
            scrollContainer.style.overflow = 'auto';
            scrollContainer.setAttribute('data-slider-mode', 'true'); // Keep freeze flag
            console.log('🔓 SCROLL: Re-enabled scroll but maintaining slider mode');
          }
          
          // Create system message with inline sliders
          const inlineSliderMessage: ChatMessage = {
            id: `sliders-${Date.now()}`,
            sender: 'system',
            text: '',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            status: 'delivered',
            sliderAction: {
              type: 'personality_sliders',
              sliders: [
                { id: 'energy', label: 'Energy & Warmth', value: 3, min: 1, max: 5 },
                { id: 'data_depth', label: 'Data Depth', value: 3, min: 1, max: 5 },
                { id: 'challenge', label: 'Challenge', value: 3, min: 1, max: 5 },
                { id: 'humor', label: 'Humor', value: 3, min: 1, max: 5 }
              ]
            }
          };
          
          transition({
            type: 'ADD_MESSAGE',
            payload: { message: inlineSliderMessage }
          });
          
          console.log('✅ AUTO-TRIGGER: Inline personality sliders created successfully with proper timing');
        }, 1500); // 1500ms delay to let message animation complete first
      }
      
      // Auto-scroll to bottom for new messages
      if (scrollFrameRef.current) {
        setTimeout(() => {
          if (scrollFrameRef.current) {
            // 🎯 FIX #2: Respect slider mode - don't auto-scroll when sliders are active
            if (scrollFrameRef.current.getAttribute('data-slider-mode') === 'true') {
              console.log('🔒 SCROLL: Manual auto-scroll blocked - sliders are active');
              return;
            }
            scrollFrameRef.current.scrollTop = scrollFrameRef.current.scrollHeight;
          }
        }, 100);
      }

    } catch (error) {
      console.error('❌ Error calling LLM backend:', error);
      
      // 🕐 CLEANUP: Clear timeout on error
      if (timeoutId) clearTimeout(timeoutId);
      
      // 🚨 MATT FIX #4: Clear isTyping on error to prevent stuck send button
      console.log('🔧 MATT FIX: Clearing isTyping due to error');
      transition({
        type: 'UPDATE_UI',
        payload: { isTyping: false, typingMessageId: null }
      });
      
      // 🔇 REMOVED (Jan 5, 2026): Processing pill not needed with interim message system
      // Reset file processing state on error
      // console.log('🔍 SHIMMER: Setting processingFiles to false (error)');
      // setProcessingFilesWithMinimumTime(false);
      
      // 🚫 TEMPORARILY DISABLED: Goal processing state reset on error
      // 🎯 GOAL PROCESSING: Reset goal processing state on error
      // if (processingGoals) {
      //   console.log('🎯 GOAL PROCESSING: Setting processingGoals to false (error)');
      //   setProcessingGoals(false);
      // }
      
      // 🚨 CRITICAL: Prevent infinite render loops after failures
      const isRenderLoopError = error instanceof Error && error.message.includes('Failed to fetch');
      if (isRenderLoopError) {
        console.log('🛡️ RENDER LOOP PREVENTION: API failure detected, implementing safeguards');
        // Force a brief delay to prevent rapid re-renders
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      // 🔧 ENHANCED ERROR HANDLING: Specific file upload error detection
      const isTimeoutAbort = error instanceof DOMException && error.name === 'AbortError';

      const isAuthError = error instanceof Error && (
        error.message.includes('401') ||
        error.message.includes('authentication') ||
        error.message.includes('token')
      );

      const isFileError = !isTimeoutAbort && error instanceof Error && (
        error.message.includes('413') ||  // Payload too large
        error.message.includes('415') ||  // Unsupported media type
        error.message.includes('multipart') ||
        error.message.includes('file')
      );

      const isCorsError = !isTimeoutAbort && error instanceof Error && (
        error.message.includes('CORS') ||
        error.message.includes('Failed to fetch') ||
        error.message.includes('Access-Control-Allow-Origin')
      );

      if (isAuthError) {
        console.error('🔒 Authentication issue detected - user needs to re-authenticate');
        await storage.removeItem('jwt_token');
        await storage.removeItem('invite_token');
      }

      let errorMessage = 'I apologize, but I\'m experiencing technical difficulties. Please try again in a moment.';

      if (isTimeoutAbort && uploadedAttachments?.length > 0) {
        errorMessage = "I had trouble processing your file — it may have taken too long. Please try uploading again, or describe what it shows and I can help from there.";
      } else if (isTimeoutAbort) {
        errorMessage = "That took longer than expected. Let me check if your message was processed...";
      } else if (isCorsError) {
        errorMessage = 'The service is temporarily unavailable. Please wait a moment and try again, or refresh the page if the issue persists.';
      } else if (isFileError) {
        errorMessage = 'I had trouble processing your file. Please check the file type and size (max 5MB), then try again.';
      } else if (isAuthError) {
        errorMessage = 'Authentication expired. Please refresh the page and try again.';
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      const errorResponse: ChatMessage = {
        id: `cedar_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        sender: 'cedar',
        text: errorMessage,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        status: 'error'
      };

      transition({
        type: 'ADD_MESSAGE',
        payload: { message: errorResponse }
      });
      
    }
    
    // 🗑️ POST THINKING CLEANUP: Clear isTyping state when response arrives
    transition({
      type: 'UPDATE_UI',
      payload: { isTyping: false, typingMessageId: null }
    });
    setCurrentUserMessage(''); // Clear stored message when typing is done
    
    // 🔄 PERSISTENCE: Clear pending request from localStorage
    if (currentUser) {
      await storage.removeItem(`pending_response_${currentUser}`);
      console.log('🔄 PERSISTENCE: Cleared pending request from localStorage after response');
    }
  };

  const handleAttachment = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // 1-file limit: only ever process the first file selected
    const file = files[0];

    // Declared here because the size cap below depends on it. It used to be declared
    // further down, next to the extension allow-list.
    const fileExtension = file.name.includes('.') ? ('.' + file.name.split('.').pop()!.toLowerCase()) : '';

    // P1-1: File size validation — per format. The caps live in
    // web/src/constants/intakeFormats.ts, which mirrors services/intake_format_registry.py
    // and is bound to it by tests/gate/test_intake_client_registry_lockstep.py.
    // The SERVER is the source of truth: a client cap below the server's is a false
    // refusal the user cannot appeal (a phone clip was once refused at about three
    // seconds while the prompt invited "a short video of a movement", and a 40MB Apple
    // Health export was refused at 25).
    const MAX_FILE_SIZE_MB = intakeCapMb(fileExtension);
    const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      console.warn(`📎 REJECTED: File "${file.name}" is ${sizeMB} MB (max ${MAX_FILE_SIZE_MB} MB)`);
      void trackIntakeRefusal('size_refused', {
        extension: fileExtension, sizeMb: Number(sizeMB), capMb: MAX_FILE_SIZE_MB,
      });
      const errorMsg: ChatMessage = {
        id: `cedar_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        sender: 'cedar',
        text: `That file is ${sizeMB} MB, which exceeds the ${MAX_FILE_SIZE_MB} MB limit. Please choose a smaller file or compress it first.`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        status: 'delivered'
      };
      transition({ type: 'ADD_MESSAGE', payload: { message: errorMsg } });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // P1-2: File type validation — allowed extensions only.
    //
    // Accepting a file and then doing nothing useful with it is worse than an honest
    // refusal, because the user believes their history landed.
    //
    // The list lives in web/src/constants/intakeFormats.ts (mirroring the registry's
    // accepted_extensions()) and the picker's accept= attribute is rendered from the
    // SAME module, so the two can no longer disagree — they did until 2026-08-20, when
    // accept= still carried `image/*,video/*` and therefore OFFERED the .heic that this
    // check refuses. Edit the registry, not either list.
    // (fileExtension is declared above, where the size cap needs it.)
    //
    // The HELPER, not a raw `.includes()`: it normalises the leading dot and the case, so
    // it behaves identically whichever way a caller derived `ext`. Web keeps the dot and
    // native strips it, and that difference has no business being a behavioural one when
    // native adopts this module.
    if (!isIntakeExtensionAllowed(fileExtension)) {
      console.warn(`📎 REJECTED: File "${file.name}" has unsupported type "${fileExtension}"`);
      // The server can never see this — the file is stopped on-device, so its demand
      // ledger under-counts precisely the formats people try most.
      void trackIntakeRefusal('format_refused', { extension: fileExtension });
      const errorMsg: ChatMessage = {
        id: `cedar_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        sender: 'cedar',
        text: `I can't read ${fileExtension || 'that kind of file'} yet. ${intakeSummary}`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        status: 'delivered'
      };
      transition({ type: 'ADD_MESSAGE', payload: { message: errorMsg } });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    console.log('📎 PHASE 4: Starting immediate file upload for 1 file (limit enforced)');

    const attachment: FileAttachment = {
      id: Date.now().toString() + Math.random(),
      file,
      url: URL.createObjectURL(file),
      type: getFileType(file),
      preview: await generatePreview(file),
      filename: file.name,
      size_bytes: file.size,
      mime_type: file.type,
      // 🚨 PHASE 4: Immediate upload functionality
      uploadStatus: 'pending',
      uploadProgress: 0,
      uploadError: undefined,
      uploadId: `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
    
    // 🚨 PHASE 4: Add attachment to UI immediately
    setAttachments(prev => [...prev, attachment]);
    
    // 🚨 PHASE 4: Start immediate upload
    startImmediateUpload(attachment);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 🚨 PHASE 4: Immediate upload function with progress tracking
  const startImmediateUpload = async (attachment: FileAttachment) => {
    console.log(`📤 Starting immediate upload for: ${attachment.filename}`);
    
    try {
      // Update status to uploading
      setAttachments(prev => prev.map(att => 
        att.id === attachment.id 
          ? { ...att, uploadStatus: 'uploading', uploadProgress: 0 }
          : att
      ));

      // 🚨 FIX: Simulate upload progress (files are actually sent via /api/chat)
      // No separate upload needed - just show progress simulation
      const progressInterval = setInterval(() => {
        setAttachments(prev => prev.map(att => {
          if (att.id === attachment.id && att.uploadStatus === 'uploading') {
            const newProgress = Math.min(att.uploadProgress + 15, 95); // Slower progress
            return { ...att, uploadProgress: newProgress };
          }
          return att;
        }));
      }, 300); // Update every 300ms for smooth progress

      // 🚨 FIX: Simulate upload completion after a realistic time
      setTimeout(() => {
        clearInterval(progressInterval);
        
        console.log(`✅ Upload simulated complete for: ${attachment.filename}`);
        
        // Mark as uploaded with 100% progress
        setAttachments(prev => prev.map(att => 
          att.id === attachment.id 
            ? { 
                ...att, 
                uploadStatus: 'uploaded', 
                uploadProgress: 100
              }
            : att
        ));
      }, 2000 + Math.random() * 1000); // 2-3 seconds to complete

    } catch (error) {
      console.error(`❌ Upload failed for: ${attachment.filename}`, error);
      
      // Mark as error
      setAttachments(prev => prev.map(att => 
        att.id === attachment.id 
          ? { 
              ...att, 
              uploadStatus: 'error', 
              uploadProgress: 0,
              uploadError: error instanceof Error ? error.message : 'Upload failed'
            }
          : att
      ));
    }
  };

  const handlePaperclipClick = () => {
    fileInputRef.current?.click();
  };

  const removeAttachment = (id: string) => {
    setAttachments(prev => {
      const updated = prev.filter(att => att.id !== id);
      const removedAttachment = prev.find(att => att.id === id);
      if (removedAttachment) {
        URL.revokeObjectURL(removedAttachment.url);
      }
      return updated;
    });
  };




  const getSendButtonState = () => {
    const hasText = input.trim().length > 0;
    const hasAttachments = attachments.length > 0;
    
    // 🎛️ DISABLE SEND BUTTON: When personality sliders are showing
    const hasActiveSliders = messages.some(message => 
      message.sliderAction && (message.sender === 'cedar' || message.sender === 'system')
    );
    
    if (hasActiveSliders) {
      return 'disabled';
    }
    
    // 🎯 FIXED STATE LOGIC: Proper state transitions for conversation flow
    if (isTyping) {
      // During thinking dots and Cedar typing: show inactive (grey) state
      console.log('🔘 SEND BUTTON: isTyping=true, returning inactive state');
      return 'inactive';
    } else if (hasText || hasAttachments) {
      // When user has typed content: show default (green) state - ready to send
      console.log('🔘 SEND BUTTON: hasContent=true, returning default state');
      return 'default';
    } else {
      // When idle with no content: show default (green) state - ready to be used
      console.log('🔘 SEND BUTTON: idle state, returning default state');
      return 'default';
    }
  };

  const getAttachButtonState = () => {
    // Gate attach button for Essentials / Trial users (mirrors native Step 40).
    // Trial boost: Essentials/deferred-trial users in their 7-day trial window
    // get Chat+ feature parity, including image uploads. Flips off the moment
    // trial_expires_at passes, at which point the Essentials hard gate kicks in.
    const bs = chatState.backendState as any;
    const sub = bs?.subscription || {};
    const tier = sub?.tier || bs?.subscription_tier || '';
    const isBeta = bs?.is_beta || false;
    const trialBoostActive = sub?.trial_boost_active === true;
    if (!isBeta && (tier === 'basic' || tier === 'trial') && !trialBoostActive) {
      return 'disabled';
    }
    if (attachments.length > 0) {
      return 'disabled';
    }
    return 'default';
  };

  const handleSendClick = () => {
    const hasContent = input.trim() || attachments.length > 0;
    if (!isTyping && hasContent) {
      handleSend();
    }
  };

  // Parse message text for action buttons
  // 🔧 FEB 11, 2026: Delegates to module-level parseMessageActionsModule (defined before sendStreamingMessage)
  // so that both component-level code AND module-level sendStreamingMessage can use the same logic.
  // 🔧 FEB 11, 2026: Also returns serviceAction when connect_garmin detected,
  // so ServiceConnector auto-renders inline instead of showing a clickable button.
  // Strava handling removed from frontend (Feb 2026) - backend preserved for future use
  const parseMessageActions = (text: string): { cleanText: string; actions?: ChatMessage['actions']; serviceAction?: ChatMessage['serviceAction'] } => {
    console.log('🔍 Parsing message actions for text:', text);
    const result = parseMessageActionsModule(text);
    let serviceAction: ChatMessage['serviceAction'] | undefined;
    
    if (result.actions) {
      // Check for service connection actions → auto-attach serviceAction for inline rendering (Garmin only)
      const serviceConnectionAction = result.actions.find(a => a.type === 'connect_garmin');
      if (serviceConnectionAction) {
        serviceAction = {
          type: 'service_connection',
          service: 'garmin' as ServiceType,
          platform: (userPlatform || 'auto') as PlatformType
        };
        // Remove service actions from actions array (ServiceConnector renders them, not buttons)
        result.actions = result.actions.filter(a => a.type !== 'connect_garmin');
        if (result.actions.length === 0) {
          result.actions = undefined;
        }
        console.log(`📱 AUTO-FIRE: Converted garmin action → serviceAction (ServiceConnector will render inline)`);
      }
      
      result.actions?.forEach(a => {
        if (a.type === 'personality_sliders') {
          console.log('🎛️ DETECTED: Personality sliders trigger - adding to actions list');
        }
        console.log('🎯 Found action:', { label: a.label, type: a.type });
      });
    }
    return { cleanText: result.cleanText, actions: result.actions as ChatMessage['actions'], serviceAction };
  };

  // Handle action button clicks
  // Strava connect_strava handler removed from frontend (Feb 2026) - backend preserved for future use
  const handleActionClick = async (action: NonNullable<ChatMessage['actions']>[0]) => {
    if (action.type === 'connect_garmin') {
      // Garmin native integration message (Updated January 2025)
      const garminMessage: ChatMessage = {
        id: `cedar_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        sender: 'user',
        text: "I'd like to connect my Garmin",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        status: 'delivered'
      };

      transition({
        type: 'ADD_MESSAGE',
        payload: { message: garminMessage }
      });
      
      // Trigger Cedar's response about Garmin integration
      transition({
        type: 'UPDATE_UI',
        payload: { 
          isTyping: true,
          typingMessageId: garminMessage.id 
        }
      });
      
      // Create service connection message for Garmin
      requestAnimationFrame(() => {
        setTimeout(() => {
          const serviceConnectionMessage: ChatMessage = {
            id: `garmin-connection-${Date.now()}`,
            sender: 'cedar',
            text: "Ready to connect to Garmin Connect? I'll sync your activities, training metrics, and workout history to provide personalized coaching based on your device's data.",
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            status: 'delivered',
            serviceAction: {
              type: 'service_connection',
              service: 'garmin' as ServiceType,
              platform: userPlatform || 'desktop' as PlatformType
            }
          };
          
          console.log('🏃 GARMIN: Adding service connection message after user message');
          transition({
            type: 'ADD_MESSAGE',
            payload: { message: serviceConnectionMessage }
          });
          
          transition({
            type: 'UPDATE_UI',
            payload: { 
              isTyping: false,
              typingMessageId: null
            }
          });
        }, 100); // Longer delay to ensure user message is fully rendered
      });
    } else if (action.type === 'request_notification_permission') {
      // Trigger notification permission modal immediately
      setShowNotificationPermission(true);
    } else if (action.type === 'personality_sliders') {
      console.log('🎛️ PERSONALITY SLIDERS: User clicked to set preferences', action);
      
      // 🎛️ CONFIRMATION FLOW: Create inline sliders when user confirms
      const inlineSliderMessage: ChatMessage = {
        id: `sliders-${Date.now()}`,
        sender: 'system',
        text: '', // No text needed
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        status: 'delivered',
        sliderAction: {
          type: 'personality_sliders',
          sliders: action.sliders || [
            { id: 'energy', label: 'Energy & Cheer', value: 3, min: 1, max: 5 },
            { id: 'data_depth', label: 'Data Depth', value: 3, min: 1, max: 5 },
            { id: 'challenge', label: 'Challenge Intensity', value: 3, min: 1, max: 5 },
            { id: 'humor', label: 'Humor Style', value: 3, min: 1, max: 5 },
            { id: 'warmth', label: 'Warmth & Heart', value: 3, min: 1, max: 5 }
          ]
        }
      };
      
      transition({
        type: 'ADD_MESSAGE',
        payload: { message: inlineSliderMessage }
      });
      
      console.log('✅ CONFIRMATION FLOW: Inline sliders created after user confirmation');
    } else if (action.type === 'view_source') {
      // Navigate to insights with specific source selected
      router.push(`/insights?filter=sources-resources&selectedSource=${action.sourceId}&from=chat`);
    } else if (action.type === 'revert_modality_4') {
      // Apr 2026 — LOAD_AWARE_ADAPTIVE_COACHING Phase 0
      // Revert an autonomous plan modification while still within its
      // can_undo_until window. The FYI message from dispatch_system_
      // initiated_action embeds a subtle revert button; tapping here
      // calls DELETE /api/plan-modifications/:action_id.
      const actionIdToRevert = (action as any).ref || (action as any).actionId;
      if (!actionIdToRevert) {
        console.warn('revert_modality_4 action missing ref/actionId');
        return;
      }
      try {
        const token = await storage.getItem('jwt_token');
        const baseUrl = Platform.OS === 'web' ? '' : getApiBaseUrl();
        const res = await fetch(
          `${baseUrl}/api/plan-modifications/${encodeURIComponent(actionIdToRevert)}`,
          {
            method: 'DELETE',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );
        if (res.status === 409) {
          // Too late — undo window expired.
          console.log('Revert declined: undo window expired');
        } else if (!res.ok) {
          console.warn('Revert failed:', await res.text());
        }
        // The backend posts a confirmation chat message regardless of
        // success/failure, so no extra UI work needed here.
      } catch (e) {
        console.warn('Revert request errored', e);
      }
    } else if (
      action.type === 'missed_workout_accept'
      || action.type === 'missed_workout_did_it'
      || action.type === 'missed_workout_chat'
      || action.type === 'missed_workout_absorb'
      || action.type === 'modality_2b_accept'
      || action.type === 'modality_2b_chat'
      || action.type === 'modality_2b_dismiss'
    ) {
      // Apr 2026 — Missed-Workout Sprint Phase 1
      // POST to deterministic banner action handler. On success, append
      // an inline confirmation. On feature_flag_disabled, fall back to
      // the legacy synthetic-chat path (gated rollout).
      const recalRowId = (action as any).ref;
      const actionLabel = (action as any).label || action.type;
      const fallbackToSyntheticChat = () => {
        try {
          if (typeof (window as any).__cedarSendMessage === 'function') {
            (window as any).__cedarSendMessage(actionLabel);
          }
        } catch (e) {
          console.warn('Proactive banner action fallback failed', e);
        }
      };

      if (!recalRowId) {
        console.warn('Banner action missing ref; using synthetic-chat fallback');
        fallbackToSyntheticChat();
        return;
      }

      try {
        const token = await storage.getItem('jwt_token');
        const baseUrl = Platform.OS === 'web' ? '' : getApiBaseUrl();
        const res = await fetch(
          `${baseUrl}/api/banner-actions/${encodeURIComponent(action.type)}/${encodeURIComponent(recalRowId)}`,
          {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );

        if (!res.ok) {
          console.warn(
            `Banner action ${action.type} returned ${res.status}; falling back to synthetic chat`,
          );
          fallbackToSyntheticChat();
          return;
        }

        const body = await res.json();

        if (body && body.error === 'feature_flag_disabled') {
          // Flag is OFF — fall back to legacy path so the user still
          // gets a response. Production-safe staged rollout.
          fallbackToSyntheticChat();
          return;
        }

        if (body && body.success) {
          // Append inline confirmation. Accept paths produce a real
          // chat message via the orchestrator's dual-delivery — but
          // for did_it / chat / dismiss the dispatcher doesn't post
          // anything to chat_messages, so we render the confirmation
          // here.
          const isAcceptAction =
            action.type === 'missed_workout_accept'
            || action.type === 'modality_2b_accept';
          if (!isAcceptAction && body.message) {
            const confirmationMsg: ChatMessage = {
              id: `banner-action-confirm-${Date.now()}`,
              sender: 'cedar',
              text: body.message,
              timestamp: new Date().toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              }),
              status: 'delivered',
            };
            transition({
              type: 'ADD_MESSAGE',
              payload: { message: confirmationMsg },
            });
          }
        } else if (body && body.message) {
          // success=false but with a message — show the failure to user.
          console.warn('Banner action returned non-success:', body);
        }
      } catch (e) {
        console.warn('Banner action network error; falling back', e);
        fallbackToSyntheticChat();
      }
    }
  };

  // 💰 TRIAL GATE: Fetch subscription status; show gate when user is not beta and not active (trial not started)
  const shouldShowTrialGate = useCallback(async (): Promise<boolean> => {
    try {
      const token = await storage.getItem('jwt_token');
      const url = Platform.OS === 'web'
        ? '/api/user/subscription-status'
        : `${getApiBaseUrl()}/api/user/subscription-status`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) return false; // On error, don't block (proceed to Step 7)
      const data = await res.json();
      return !data.is_beta_tester && !data.is_active;
    } catch (e) {
      console.warn('Trial gate check failed', e);
      return false;
    }
  }, []);

  // 🎛️ UNIFIED PERSONALITY MODAL SUBMISSION: Use clean hook-based approach
  const handlePersonalityModalSubmit = async (values: { [key: string]: number }) => {
    // 🔓 UNFREEZE SCROLL: Resume normal scrolling after slider submission
    const scrollContainer = scrollFrameRef.current;
    if (scrollContainer) {
      scrollContainer.removeAttribute('data-slider-mode');
      scrollContainer.style.scrollBehavior = 'smooth';
      scrollContainer.style.overflow = 'auto';
      console.log('🔓 SCROLL: Normal chat scroll behavior restored');
    }
    
    // 🔓 RESTORE AUTOSCROLL STATE: Update state machine to re-enable autoscroll
    console.log('🔓 MODAL SUBMIT: Restoring auto-scroll');
    transition({
      type: 'UPDATE_UI',
      payload: { 
        scrollState: {
          shouldAutoScroll: true,
          isUserAtBottom: true 
        }
      }
    });
    
    // 🎯 IMMEDIATE SCROLL: Force scroll to bottom right after modal submission
    setTimeout(() => {
      if (scrollFrameRef.current) {
        console.log('🎯 MODAL SUBMIT: Force scrolling to bottom immediately');
        scrollToBottom();
      }
    }, 100);
    
    // 🔓 RESTORE AUTOSCROLL STATE: Update state machine to re-enable autoscroll
    transition({
      type: 'UPDATE_UI',
      payload: { 
        scrollState: {
          shouldAutoScroll: true,
          isUserAtBottom: true 
        }
      }
    });
    
    // 🎯 Create placeholder thinking message (same as regular chat flow)
    const cedarMessageId = `cedar_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const thinkingMessage: ChatMessage = {
      id: cedarMessageId,
      sender: 'cedar',
      text: '',
      timestamp: '',  // 🎯 No timestamp yet - thinking dots aren't a message!
      status: 'thinking',
      isStreaming: false,
      isAnimated: true  // 🎬 FIX (Dec 4, 2025): Enable animation when content arrives
    };
    transition({ type: 'ADD_MESSAGE', payload: { message: thinkingMessage } });
    
    transition({
      type: 'UPDATE_UI',
      payload: { isTyping: true, typingMessageId: cedarMessageId }
    });

    try {
      // 🎛️ Update slider values and submit using unified hook
      personalitySliderActions.updateValues(values);
      const data = await personalitySliderActions.submit('modal');
      console.log('🎯 AUTO-CONTINUE DISCOVERY: Backend response received', data);

      // Parse and clean the response text to remove action markup
      const { cleanText } = parseMessageActions(data?.text || data?.response || '');

      // 🎛️ PERSONALITY SYNTHESIS: Backend generates the actual response
      // CRITICAL: No fallback! Use backend response directly
      const cedarResponse: ChatMessage = {
        id: cedarMessageId,  // 🎯 Reuse the placeholder message ID!
        sender: 'cedar',
        text: cleanText || "Perfect — I've got your style dialed in. Let's get into what you're working toward.",  // Conversational fallback for empty backend response
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        status: 'delivered',
      };

      if (!cleanText) {
        console.error('❌ PERSONALITY SYNTHESIS: Backend returned no text! Using fallback.');
      }

      transition({
        type: 'ADD_MESSAGE',
        payload: { message: cedarResponse }
      });

      // 🔄 SYNC: Update backend state from metadata (or show trial gate)
      if (data.metadata) {
        const backendState = {
          onboardingStep: data.metadata.onboarding_step || 7,
          onboardingCompleted: data.metadata.onboarding_completed,
          chatName: data.metadata.chat_name,
          detectedScenario: data.metadata.detected_scenario || 'discovery_phase',
          lastSyncedAt: new Date().toISOString()
        };

        const gate = await shouldShowTrialGate();
        if (gate) {
          setShowTrialGate(true);
          setPendingStep7BackendState(backendState);
          return;
        }

        transition({
          type: 'SYNC_BACKEND_STATE',
          payload: { backendState }
        });

        console.log('🔄 DISCOVERY PHASE: State synced, discovery questions should be active');
        
        // 🚀 AUTO-START DISCOVERY: DISABLED - Backend will ask discovery questions after slider acknowledgment
        // The backend's Step 7 guidance will automatically trigger age/injuries question
        if (backendState.onboardingStep === 7 && backendState.onboardingCompleted && backendState.detectedScenario === 'discovery_phase') {
          console.log('🚀 AUTO-DISCOVERY: Step 7 reached, backend will handle discovery questions');
        }
      }

    } catch (error) {
      console.error('❌ PERSONALITY SLIDER SUBMISSION ERROR:', error);
      
      // Fallback response on error
      const fallbackResponse: ChatMessage = {
        id: `cedar_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        sender: 'cedar',
        text: 'Thanks for setting your preferences! Now, let me learn more about you. How old are you? Any injuries or areas that need special attention?',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        status: 'delivered',
      };

      transition({
        type: 'ADD_MESSAGE',
        payload: { message: fallbackResponse }
      });
    } finally {
      transition({
        type: 'UPDATE_UI',
        payload: { isTyping: false }
      });
    }
  };

  // Handle slider value changes
  const handleSliderChange = (sliderId: string, value: number) => {
      personalitySliderActions.updateValue(sliderId, value);
  };

  // Handle slider submission
  const handleSliderSubmit = async (values: Record<string, number>) => {
    // 🔓 UNFREEZE SCROLL: Resume normal scrolling after slider submission
    const scrollContainer = scrollFrameRef.current;
    if (scrollContainer) {
      scrollContainer.removeAttribute('data-slider-mode');
      scrollContainer.style.scrollBehavior = 'smooth';
      scrollContainer.style.overflow = 'auto'; // Ensure overflow is restored
      console.log('🔓 SCROLL: Normal chat scroll behavior restored');
    }
    
    // 🔓 RESTORE AUTOSCROLL STATE: Update state machine to re-enable autoscroll
    console.log('🔓 RESTORING AUTO-SCROLL: Setting shouldAutoScroll=true, isUserAtBottom=true');
    transition({
      type: 'UPDATE_UI',
      payload: { 
        scrollState: {
          shouldAutoScroll: true,
          isUserAtBottom: true 
        }
      }
    });
    
    // 🎯 IMMEDIATE SCROLL: Force scroll to bottom right after slider submission
    setTimeout(() => {
      if (scrollFrameRef.current) {
        console.log('🎯 SLIDER SUBMIT: Force scrolling to bottom immediately');
        scrollToBottom();
      }
    }, 100);

    // Modal visibility is handled by unified hook

    // 🎯 Create placeholder thinking message (same as regular chat flow)
    const cedarMessageId = `cedar_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const thinkingMessage: ChatMessage = {
      id: cedarMessageId,
      sender: 'cedar',
      text: '',
      timestamp: '',  // 🎯 No timestamp yet - thinking dots aren't a message!
      status: 'thinking',
      isStreaming: false,
      isAnimated: true  // 🎬 FIX (Dec 4, 2025): Enable animation when content arrives
    };
    transition({ type: 'ADD_MESSAGE', payload: { message: thinkingMessage } });
    
    transition({
      type: 'UPDATE_UI',
      payload: { isTyping: true, typingMessageId: cedarMessageId }
    });

    try {
      // 🎯 CRITICAL FIX: Use same approach as handlePersonalityModalSubmit
      // This ensures proper frontend_state with personality_sliders_submitted flag
      
      // 🎛️ Update slider values and submit using unified hook
      // Pass values directly to avoid React state race condition
      personalitySliderActions.updateValues(values);
      const data = await personalitySliderActions.submit('inline', values);
      console.log('🎯 INLINE SLIDER SUBMISSION: Backend response received', data);

      // Parse and clean the response text to remove action markup
      const { cleanText } = parseMessageActions(data?.text || data?.response || '');
      
      // 🎛️ PERSONALITY SYNTHESIS: Backend generates the actual response
      // CRITICAL: No fallback! Use backend response directly
      const cedarMessage: ChatMessage = {
        id: cedarMessageId,  // 🎯 Reuse the placeholder message ID!
        sender: 'cedar',
        text: cleanText || 'Got it! Your preferences have been updated.',  // 🔧 FIX P1-9: Fallback for empty backend response
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        status: 'delivered',
        isAnimated: true  // ✅ ENABLE TYPING ANIMATION
      };

      if (!cleanText) {
        console.error('❌ PERSONALITY SYNTHESIS: Backend returned no text! Using fallback.');
      }

      transition({
        type: 'ADD_MESSAGE',
        payload: { message: cedarMessage }
      });

      // 🔄 SYNC: Update backend state from metadata (or show trial gate)
      if (data.metadata) {
        const backendState = {
          onboardingStep: data.metadata.onboarding_step || 7,
          onboardingCompleted: data.metadata.onboarding_completed,
          chatName: data.metadata.chat_name,
          detectedScenario: data.metadata.detected_scenario || 'discovery_phase',
          lastSyncedAt: new Date().toISOString()
        };

        const gate = await shouldShowTrialGate();
        if (gate) {
          setShowTrialGate(true);
          setPendingStep7BackendState(backendState);
          return;
        }

        transition({
          type: 'SYNC_BACKEND_STATE',
          payload: { backendState }
        });

        console.log('🔄 DISCOVERY PHASE: State synced, discovery questions should be active');
      }

      // 🚀 AUTO-TRIGGER DISCOVERY INTRODUCTION: REMOVED - Let proper LLM guidance handle discovery
      if (false) {
        console.log('🚀 DISCOVERY INTRO: Auto-triggering discovery introduction after synthesis');
        
        // Wait for synthesis animation to complete, then send discovery intro
        setTimeout(async () => {
          try {
            const jwtToken = await storage.getItem('jwt_token');
            if (!jwtToken) return;

            const discoveryIntroResponse = await fetch('/api/chat', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${jwtToken}`
              },
              body: JSON.stringify({
                message: "I'm ready for the discovery phase",
                file_insights: [],
                frontend_state: {
                  auto_discovery_trigger: true,
                  onboarding_step: 7,
                  detected_scenario: "discovery_phase"
                }
              })
            });

            if (discoveryIntroResponse.ok) {
              const discoveryData = await discoveryIntroResponse.json();
              console.log('🚀 DISCOVERY INTRO: Received discovery introduction', discoveryData);
              
              const discoveryMessage: ChatMessage = {
                id: `cedar_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                sender: 'cedar',
                text: discoveryData?.text || discoveryData?.response || 'To create a plan that actually works for you, I need to understand where you\'re starting from and where you want to go. I\'ll ask about your current fitness level, any limitations I should know about, and what data sources can help me track your progress. Sound good?',
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                status: 'delivered',
                isAnimated: true
              };

              transition({
                type: 'ADD_MESSAGE',
                payload: { message: discoveryMessage }
              });
            }
          } catch (error) {
            console.error('❌ DISCOVERY INTRO ERROR:', error);
          }
        }, Math.max(3000, cleanText.length * 40 + 1000)); // Wait for synthesis animation + 1s
      }
      
      // ✅ TRIGGER TYPING ANIMATION: Set the typing message ID
      transition({
        type: 'UPDATE_UI',
        payload: { typingMessageId: cedarMessage.id }
      });
      
      // ✅ COMPLETE ANIMATION: Clear typing ID after animation completes
      setTimeout(() => {
        transition({
          type: 'UPDATE_UI',
          payload: { typingMessageId: null }
        });
      }, Math.max(2000, cleanText.length * 40)); // Dynamic duration based on text length

    } catch (error) {
      console.error('❌ INLINE SLIDER SUBMISSION ERROR:', error);
      
      // Fallback response on error
      const fallbackResponse: ChatMessage = {
        id: `cedar_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        sender: 'cedar',
        text: 'Thanks for setting your preferences! Now, let me learn more about you. How old are you? Any injuries or areas that need special attention?',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        status: 'delivered',
      };

      transition({
        type: 'ADD_MESSAGE',
        payload: { message: fallbackResponse }
      });
    } finally {
      transition({
        type: 'UPDATE_UI',
        payload: { 
          isTyping: false,
          typingMessageId: null
        }
      });
    }
  };

  // Notification permission handling
  const checkAndRequestNotifications = async () => {
    const notificationService = NotificationService.getInstance();
    
    // Check if we should show the permission modal
    if (!notificationService.isSupported()) {
      console.log('Notifications not supported');
      return;
    }
    
    const permission = notificationService.getPermissionStatus();
    const hasAsked = await storage.getItem('notification_permission_asked') === 'true';
    
    // Show modal if permission is default and we haven't asked before
    if (permission === 'default' && !hasAsked) {
      setShowNotificationPermission(true);
    }

    // M14 FIX (Mission 35B): If permission was previously denied in the browser,
    // show a recovery prompt guiding the user to browser settings. Only show once
    // per session to avoid being annoying.
    if (permission === 'denied' && !hasAsked) {
      const recoveryShown = await storage.getItem('notification_recovery_shown_session');
      if (!recoveryShown) {
        await storage.setItem('notification_recovery_shown_session', 'true');
        setShowNotificationPermission(true);
      }
    }
  };

  const handleAllowNotifications = async () => {
    try {
      // Mark as asked so we don't prompt again
      await storage.setItem('notification_permission_asked', 'true');

      const notificationService = NotificationService.getInstance();
      const granted = await notificationService.requestPermissionAndSubscribe();
      
      if (granted) {
        // Update backend setting to enable notifications
        const userId = getCurrentUserId();
        if (userId) {
          // (The per-user notification-settings placeholder route this used to PUT
          // to was an unauthenticated in-memory stub; the Access-Control Airtight
          // sprint deleted it. The browser subscription above is the real state.)

          // Fire welcome notification now that permissions are enabled
          fetch('/api/notifications/send-async-process', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: userId,
              process_type: 'welcome_notification',
              title: 'Notifications on',
              description: 'Cedar can now reach out when something is worth your attention.',
              deep_link_url: '/(tabs)/chat',
            })
          }).catch(err => console.error('Failed to send welcome notification:', err));
        }
      }
      
      setShowNotificationPermission(false);
    } catch (error) {
      console.error('Failed to enable notifications:', error);
      setShowNotificationPermission(false);
    }
  };

  const handleDenyNotifications = async () => {
    try {
      // Mark as asked so we don't prompt again after an explicit denial
      await storage.setItem('notification_permission_asked', 'true');

      // (No backend write: the per-user notification-settings placeholder route
      // was deleted by the Access-Control Airtight sprint and never persisted.)
      setShowNotificationPermission(false);
    } catch (error) {
      console.error('Failed to disable notifications:', error);
      setShowNotificationPermission(false);
    }
  };

  const handleNotificationLater = () => {
    setShowNotificationPermission(false);
  };

  // Add debug panel at top of chat component for troubleshooting
  const [showDebugPanel, setShowDebugPanel] = useState(false);

  // Debug information (synchronous only for component-level constant)
  const debugInfo = {
    urlToken: Platform.OS === 'web' && new URLSearchParams(window.location.search).get('token') ? 'Present' : 'Missing',
    onboardingTriggered: chatState.ui.onboardingTriggered,
    messagesLength: messages.length,
    tourCompleted: searchParams.tourCompleted,
    shouldAskForName: searchParams.shouldAskForName,
    // 🚨 NEW: Backend state synchronization info
    backendState: chatState.backendState ? {
      onboardingStep: chatState.backendState.onboardingStep,
      onboardingCompleted: chatState.backendState.onboardingCompleted,
      chatName: chatState.backendState.chatName,
      detectedScenario: chatState.backendState.detectedScenario,
      lastSyncedAt: chatState.backendState.lastSyncedAt
    } : 'Not synced',
    phase: chatState.phase
  };

  // 🎯 SPRINT 2: Plan notification engagement tracking
  const handlePlanNotificationEngagement = useCallback(async (actionType: string, planId?: string) => {
    console.log('🎯 Plan notification engagement:', { actionType, planId });
    
    // Track engagement analytics
    try {
      const engagementData = {
        action_type: actionType,
        plan_id: planId,
        timestamp: new Date().toISOString(),
        user_id: getCurrentUserId(),
        session_id: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        page_url: Platform.OS === 'web' ? window.location.href : 'mobile-app'
      };
      
      // Store engagement locally for immediate tracking
      const userId = getCurrentUserId();
      const currentEngagements = userId ? await getUserStorage('plan_notification_engagements', userId) || '[]' : '[]';
      const engagements = JSON.parse(currentEngagements);
      engagements.push(engagementData);
      
      // Keep only last 50 engagements to prevent storage bloat
      if (engagements.length > 50) {
        engagements.splice(0, engagements.length - 50);
      }
      
      if (userId) {
        setUserStorage('plan_notification_engagements', JSON.stringify(engagements), userId);
      }
      
      // Send to backend for aggregation and analytics
      try {
        await fetch('/api/analytics/plan-notification-engagement', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(engagementData)
        });
        
        console.log('✅ Plan notification engagement tracked locally and sent to backend');
      } catch (backendError) {
        console.warn('⚠️ Failed to send engagement to backend, but stored locally:', backendError);
      }
      
    } catch (error) {
      console.error('❌ Failed to track plan notification engagement:', error);
    }
  }, []);

  // 🎯 REMOVED: Local file processing logic - now handled by backend
  // Files are sent directly to backend via multipart/form-data

  return (
    <div className="clean-chat-container">
      {/* Main Chat Area */}
      <div 
        className="chat-content" 
        ref={scrollFrameRef}
      >
        {/* 📅 CHAT HISTORY: Show only when first Cedar message is above viewport */}
        <div id="history-start">
          {showChatHistory && chatHistory && (
            <ChatHistory
              history={chatHistory}
              onWeekExpand={handleWeekExpand}
              onMonthToggle={handleMonthToggle}
            />
          )}
          {showChatHistory && loadingHistory && (
            <div className="loading-history">
              <div className="loading-spinner"></div>
              <span>Loading chat history...</span>
            </div>
          )}
        </div>

        {/* 🔧 FIX Issue #4: Removed debug message that flashed on initial load */}

        {/* 🔧 FIX: Filter out empty messages with typing/sending status to prevent empty bubbles */}
        {/* 🎯 THINKING DOTS FIX: Allow empty 'thinking' messages to show (they display thinking dots) */}
        {/* 🗓️ DAY DIVIDER: Insert divider above first message of each calendar day (Mission 20) */}
        {(() => {
          const toLocalDateKey = (d: Date): string => {
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          };
          const getMessageDateKey = (m: ChatMessage): string | null => {
            const raw = (m as ChatMessage & { createdAt?: string }).createdAt ?? m.timestamp;
            if (!raw) return null;
            const d = new Date(raw);
            if (isNaN(d.getTime())) return null;
            return toLocalDateKey(d);
          };
          const filtered = messages.filter((msg: ChatMessage) => {
            if (msg.metadata?.type === 'web_search_result') {
              console.log(`🔍 FILTERING: Excluding internal web search message (ID: ${msg.id})`);
              return false;
            }
            if (msg.text && msg.text.trim().length > 0) return true;
            if (msg.status === 'delivered') return true;
            if (msg.status === 'thinking' && (!msg.text || msg.text.trim().length === 0)) return true;
            if ((msg.status === 'typing' || msg.status === 'sending') && (!msg.text || msg.text.trim().length === 0)) return false;
            return true;
          });
          type DayDividerItem = { type: 'divider'; dateKey: string };
          type MessageItem = { type: 'message'; message: ChatMessage };
          const items: (DayDividerItem | MessageItem)[] = [];
          let lastKey: string | null = null;
          for (const message of filtered) {
            const dateKey = getMessageDateKey(message);
            if (dateKey !== null && dateKey !== lastKey) {
              items.push({ type: 'divider', dateKey });
              lastKey = dateKey;
            }
            items.push({ type: 'message', message });
          }
          return items.map((item, index) => {
            if (item.type === 'divider') {
              return <DayDivider key={`day-divider-${item.dateKey}`} dateKey={item.dateKey} />;
            }
            const message = item.message;
            // 🆕 ISSUE 3b: Render "New Messages" divider immediately before the first new message
            const isFirstNewMessage = showNewMessagesDivider && firstNewMessageIdRef.current === message.id;
          // 🔧 FIX: Log last message being rendered to diagnose disappearing messages
          if (index === items.length - 1 && message.sender === 'cedar') {
            console.log(`🎯 RENDERING: Last Cedar message - ID: ${message.id}, text length: ${message.text?.length || 0}, status: ${message.status}`);
          }
          // 🚨 CRITICAL FIX: Never animate messages that were already animated (loaded from storage)
          const shouldAnimate = message.isAnimated && !message.wasAnimated;
          
          // 🎯 CONTEXTUAL LAUNCH PILL: Display green pill for contextual chat launches
          if (message.contextualPill) {
            const pillWorkoutId = message.contextualPill.metadata?.workout_id;
            const pillOnClick = pillWorkoutId
              ? () => router.push(`/workout/${pillWorkoutId}`)
              : undefined;
            return (
              <div key={message.id} className="message-wrapper system">
                <ContextualLaunchPill
                  type={message.contextualPill.type}
                  title={message.contextualPill.title}
                  subtitle={message.contextualPill.subtitle}
                  metadata={message.contextualPill.metadata}
                  onClick={pillOnClick}
                />
              </div>
            );
          }
          
          // 🎯 SPRINT 2: Handle system messages with plan notifications
          if (message.sender === 'system' && message.planNotification) {
            return (
              <div key={message.id} className="message-wrapper system">
                <PlanNotificationComponent
                  message={message.text}
                  notificationType={message.planNotification.notificationType}
                  planId={message.planNotification.planId}
                  actions={message.planNotification.actions}
                  metadata={message.planNotification.metadata}
                  onEngagement={handlePlanNotificationEngagement}
                />
              </div>
            );
          }
          
          // 🎛️ ENHANCED: Handle messages with inline personality sliders (cedar or system)
          if ((message.sender === 'cedar' || message.sender === 'system') && message.sliderAction) {
            return (
              <div key={message.id} className="message-wrapper system">
                <InlinePersonalitySliders
                  sliders={message.sliderAction.sliders || []}
                  onSubmit={async (values) => {
                    console.log('🎛️ SLIDERS: User submitted values:', values);
                    try {
                      // 🎛️ FIXED: Remove the slider message after submission
                      transition({
                        type: 'REMOVE_MESSAGE',
                        payload: { messageId: message.id }
                      });
                      console.log('🗑️ SLIDERS: Removed slider message after submission:', message.id);
                      
                      // 🎯 NEW: Add confirmation badge immediately
                      // 🔧 FIX: Convert slider values from 0-100 range (backend scale) back to 1-5 range for display
                      // Backend receives 0-100: 0→1, 25→2, 50→3, 75→4, 100→5
                      // Display expects 1-5 for human-readable descriptions
                      const convertBackendToDisplay = (backendValue: number): number => {
                        // Map 0-100 back to 1-5: 0→1, 1-24→1, 25-49→2, 50-74→3, 75-99→4, 100→5
                        if (backendValue <= 0) return 1;
                        if (backendValue <= 24) return 1;
                        if (backendValue <= 49) return 2;
                        if (backendValue <= 74) return 3;
                        if (backendValue <= 99) return 4;
                        return 5;
                      };
                      
                      const displayValues = Object.entries(values).reduce((acc, [key, value]) => ({
                        ...acc,
                        [key]: convertBackendToDisplay(value as number)
                      }), {} as Record<string, number>);
                      
                      console.log('🎛️ SLIDER DISPLAY: Original values (0-100):', values);
                      console.log('🎛️ SLIDER DISPLAY: Converted for display (1-5):', displayValues);
                      
                      const confirmationMessage: ChatMessage = {
                        id: `coaching-style-saved-${Date.now()}`,
                        sender: 'system',
                        text: '', // Empty text since we'll render the badge component
                        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                        status: 'delivered',
                        coachingStyleSaved: {
                          sliderValues: displayValues, // Use converted values (1-5 range)
                          sliderLabels: message.sliderAction?.sliders?.reduce((acc, slider) => ({
                            ...acc,
                            [slider.id]: slider.label
                          }), {}) || {}
                        }
                      };
                      
                      transition({
                        type: 'ADD_MESSAGE',
                        payload: { message: confirmationMessage }
                      });
                      console.log('✅ SLIDERS: Added coaching style confirmation badge');
                      
                      // Process the slider submission in background
                      await handleSliderSubmit(values);
                      
                      // 🔓 RE-ENABLE AUTOSCROLL: Turn autoscroll back on forever after slider submission
                      transition({
                        type: 'UPDATE_UI',
                        payload: {
                          scrollState: {
                            shouldAutoScroll: true,
                            isUserAtBottom: true
                          }
                        }
                      });
                      console.log('🔓 SCROLL: Autoscroll re-enabled forever after slider submission');
                      
                      // 🎯 AUTO-SCROLL: Scroll down to show thinking dots animation
                      setTimeout(() => {
                        scrollToBottom();
                        console.log('📜 AUTO-SCROLL: Scrolled to bottom after slider submission');
                      }, 100); // Small delay to ensure DOM updates
                    } catch (error) {
                      console.error('❌ SLIDERS: Error submitting:', error);
                      // Don't crash - just log the error and continue
                    }
                  }}
                />
              </div>
            );
          }

          // 🎯 NEW: Handle coaching style saved confirmation badge
          if (message.sender === 'system' && message.coachingStyleSaved) {
            return (
              <div key={message.id} className="message-wrapper system">
                <CoachingStyleSaved
                  sliderValues={message.coachingStyleSaved.sliderValues}
                  sliderLabels={message.coachingStyleSaved.sliderLabels}
                />
              </div>
            );
          }

          // 🎉 JAN 5, 2026: Handle Plan Ready messages with special header (Option A)
          // 🔇 MAR 15, 2026: Commented out — pipeline progress strip now handles
          // the "Plan created" CTA with "Explore the plan" button. This was causing
          // duplicate "plan ready" messages in chat. Re-enable if needed with user input.
          // if (message.metadata?.special_header) {
          //   return (
          //     <PlanReadyMessage
          //       key={message.id}
          //       message={message.text || ''}
          //       planId={message.metadata.plan_id}
          //       actions={message.metadata.actions || []}
          //     />
          //   );
          // }

          // 🔬 JAN 25, 2026: Handle Source Attribution messages with deep link button
          if (message.metadata?.attribution_query && message.metadata?.research_event_id) {
            const researchEventId = message.metadata.research_event_id;
            
            return (
              <div key={message.id} className="message-wrapper cedar">
                <div className="attribution-message-container" style={{ maxWidth: '380px', marginLeft: '16px' }}>
                  {/* Cedar's attribution message */}
                  <CmpChatmessage
                    className="chat-message cedar"
                    sender="cedar"
                    cmpCedarbubbleDivClassName={undefined}
                    cmpUserbubbleDivClassName={undefined}
                    cmpErrorbubbleClassName={undefined}
                    text={message.text || ''}
                    timestamp={message.timestamp}
                    onAnimationComplete={undefined}
                  />

                  {/* Deep link button to research event */}
                  {researchEventId && researchEventId !== 'no_event' && (
                    <button
                      onClick={() => {
                        console.log(`🔬 DEEP LINK: Navigating to research event ${researchEventId}`);
                        router.push(`/insights?tab=cedar-insights&event_id=${researchEventId}&from=chat`);
                      }}
                      className="mt-3 w-full px-4 py-3 bg-[var(--cedar-primary)] text-white rounded-lg font-medium hover:bg-[var(--cedar-dark)] transition-colors flex items-center justify-center gap-2"
                      style={{
                        backgroundColor: 'var(--cedar-primary)',
                        color: 'white'
                      }}
                    >
                      <ExternalLink size={16} />
                      View Research Event
                    </button>
                  )}
                </div>
              </div>
            );
          }

          // In-chat workout/plan/goal card: driven by message.metadata.contextualItem (e.g. type 'workout'),
          // rendered by ContextualItemBadge. Web only — no equivalent in ChatScreen.native.tsx.
          // 🎯 NEW: Handle contextual chat launches with badge
          if (message.sender === 'cedar' && message.metadata?.contextualItem) {
            const contextItem = message.metadata.contextualItem;
            return (
              <div key={message.id} className="message-wrapper cedar">
                <div className="contextual-message-container" style={{ maxWidth: '380px', marginLeft: '16px' }}>
                  {/* Contextual item badge */}
                  <ContextualItemBadge
                    type={contextItem.type}
                    title={contextItem.title}
                    description={contextItem.description}
                  />
                  
                  {/* Cedar's greeting message */}
                  <CmpChatmessage
                    className="chat-message cedar"
                    sender="cedar"
                    cmpCedarbubbleDivClassName={undefined}
                    cmpUserbubbleDivClassName={undefined}
                    cmpErrorbubbleClassName={undefined}
                    text={message.text || ''}
                    timestamp={message.timestamp}
                    onAnimationComplete={undefined}
                  />
                </div>
              </div>
            );
          }

          // Post-workout debrief: the ContextualLaunchPill is injected as a separate system message
          // by checkBackgroundResponses; the Cedar chat bubble renders normally here (no badge inside).

          // 🚀 REVOLUTIONARY: Handle messages with inline service connections
          if ((message.sender === 'cedar' || message.sender === 'system') && message.serviceAction) {
            return (
              <div key={message.id} className="message-wrapper system">
                {/* Show the Cedar message first */}
                <CmpChatmessage
                  className="chat-message cedar"
                  sender="cedar"
                  cmpCedarbubbleDivClassName={undefined}
                  cmpUserbubbleDivClassName={undefined}
                  cmpErrorbubbleClassName={undefined}
                  text={message.text || ''}
                  timestamp={message.timestamp}
                  onAnimationComplete={undefined}
                />

                {/* Then show the service connector */}
                <ServiceConnector
                  service={message.serviceAction.service}
                  platform={message.serviceAction.platform}
                  onConnect={handleServiceConnect}
                  onCancel={() => handleServiceCancel(message.id)}
                />
              </div>
            );
          }
          
          // 🎯 MAR 11, 2026 FIX: Progress indicator is now rendered as a fixed
          // strip above the composer (see pipelineGoalId-driven render below).
          // Skip rendering it inline — return null so it takes no space in the
          // message flow. The inline system message remains as a historical marker.
          if (message.sender === 'system' && message.metadata?.progressIndicator) {
            return null;
          }

          // 🟢 Recalibration indicator rendered as fixed strip above composer.
          // Skip inline — return null so it takes no space in message flow.
          if (message.sender === 'system' && message.metadata?.recalibrationIndicator) {
            return null;
          }

          // 🔔 FEB 4, 2026: Notification pills rendered inline as messages
          // Stays in chronological position instead of jumping to bottom
          if (message.sender === 'system' && message.metadata?.notificationPill) {
            const notification = message.metadata.notificationData;
            
            if (!notification) {
              console.warn('⚠️ NOTIFICATION: No notification data in metadata, skipping render');
              return null;
            }
            
            return (
              <div key={message.id} className="message-wrapper system">
                <AsyncProcessNotificationPill
                  notification={notification}
                  onDeepLink={(url) => {
                    console.log('🔗 Deep link clicked:', url);
                  }}
                  onDiscuss={(prompt, notificationId) => {
                    console.log('💬 Discussion prompt:', prompt);
                    setInput(prompt);
                    // Remove notification message from messages array
                    transition({
                      type: 'REMOVE_MESSAGE',
                      payload: { messageId: message.id }
                    });
                  }}
                  onAcknowledge={(notificationId, action) => {
                    console.log('✅ Notification acknowledged:', notificationId, action);
                    // Remove notification message from messages array
                    transition({
                      type: 'REMOVE_MESSAGE',
                      payload: { messageId: message.id }
                    });
                  }}
                />
              </div>
            );
          }

          // 🎯 GOAL CREATION SUCCESS BADGE: Celebratory badge when pipeline completes
          if (message.sender === 'system' && message.metadata?.goalCreationSuccess) {
            return (
              <div key={message.id} className="message-wrapper system">
                <GoalCreationSuccessBadge
                  goalText={message.metadata.goalTitle || ''}
                  goalDetails={message.metadata.goalDetails}
                />
              </div>
            );
          }
          
          // 🌲 CEDAR REPORT CARD: Visual card when Cedar publishes a weekly report
          if (message.sender === 'system' && (message.metadata as any)?.type === 'cedar_report_card') {
            const reportMeta = message.metadata as any;
            return (
              <div key={message.id} className="message-wrapper system" style={{ padding: '8px 16px' }}>
                <div
                  onClick={() => router.push('/(tabs)/insights')}
                  style={{
                    background: 'linear-gradient(135deg, var(--cedar-prime, #1F6C4F) 0%, var(--cedar-dark, #113C2B) 100%)',
                    borderRadius: '14px',
                    padding: '20px',
                    color: 'white',
                    cursor: 'pointer',
                    transition: 'transform 0.15s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.01)')}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '20px' }}>📊</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '16px' }}>Your Cedar Report</div>
                      {reportMeta.reportWeekLabel && (
                        <div style={{ fontSize: '13px', opacity: 0.8 }}>{reportMeta.reportWeekLabel}</div>
                      )}
                    </div>
                    <span style={{ marginLeft: 'auto', opacity: 0.6 }}>›</span>
                  </div>
                  {reportMeta.reportSummary && (
                    <div style={{ fontSize: '14px', opacity: 0.9, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {reportMeta.reportSummary}
                    </div>
                  )}
                  {reportMeta.insights?.training && (
                    <div style={{ marginTop: '10px', fontSize: '13px', opacity: 0.8, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>🏃</span> {reportMeta.insights.training}
                    </div>
                  )}
                  <div style={{ marginTop: '14px', fontSize: '13px', opacity: 0.7 }}>Tap to read your full report</div>
                </div>
              </div>
            );
          }

          // 💬 WEEKLY CHECK-IN CARD: Button to start weekly check-in discussion
          if (message.sender === 'system' && (message.metadata as any)?.type === 'weekly_checkin_prompt') {
            const checkInMeta = message.metadata as any;
            const isActive = (chatState.backendState as any)?.touchpointType === 'weekly_checkin';
            return (
              <div key={message.id} className="message-wrapper system" style={{ padding: '4px 16px 8px' }}>
                <div
                  onClick={async () => {
                    if (isActive) return;
                    try {
                      const jwtToken = await storage.getItem('jwt_token');
                      const res = await fetch(`${API_BASE_URL}/api/touchpoints/start`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${jwtToken}` },
                        body: JSON.stringify({ touchpoint_type: 'weekly_checkin' })
                      });
                      if (res.ok) {
                        const data = await res.json();
                        console.log('✅ Weekly check-in started:', data);
                      }
                    } catch (err) {
                      console.error('❌ Failed to start weekly check-in:', err);
                    }
                  }}
                  style={{
                    background: isActive ? 'var(--cedar-subtle, #e8f2ed)' : 'var(--cedar-light, #5FA287)',
                    borderRadius: '14px',
                    padding: '16px 20px',
                    cursor: isActive ? 'default' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    transition: 'transform 0.15s',
                    color: isActive ? 'var(--cedar-prime, #1F6C4F)' : 'white',
                  }}
                >
                  <span style={{ fontSize: '18px' }}>{isActive ? '✓' : '💬'}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '15px' }}>
                      {isActive ? 'Weekly Check-In In Progress' : 'Begin Weekly Check-In'}
                    </div>
                    {checkInMeta.reportWeekLabel && (
                      <div style={{ fontSize: '13px', opacity: 0.8, marginTop: '2px' }}>
                        Discuss your {checkInMeta.reportWeekLabel} report with Cedar
                      </div>
                    )}
                  </div>
                  {!isActive && <span style={{ opacity: 0.6 }}>›</span>}
                </div>
              </div>
            );
          }

          // 🆕 ISSUE 3b: Wrap with Fragment so we can prepend the "New Messages" divider
          return (
          <React.Fragment key={message.id}>
            {/* 🆕 ISSUE 3b: Render divider immediately before the first new message */}
            {isFirstNewMessage && <NewMessagesDivider />}
            <div
              className={`message-wrapper ${message.sender}`}
            >
              {/* @ts-ignore - CmpChatmessage is a JS component; prop types are inferred as any */}
              <CmpChatmessage
                className={`message-bubble ${message.sender}`}
                sender={message.sender}
                cmpCedarbubbleDivClassName={message.metadata?.subscriptionDegraded ? 'cedar-bubble-degraded' : undefined}
                cmpUserbubbleDivClassName={undefined}
                cmpErrorbubbleClassName={undefined}
                text={message.text || ''}
                timestamp={message.timestamp}
                attachments={message.attachments as any}
                reactions={message.reactions as any}
                isTyping={typingMessageId === message.id || shouldAnimate}
                isFirstMessage={message.id === 'initial' && message.text === "Hi, I'm Coach Cedar!" && shouldAnimate}
                status={message.status || 'delivered'}
                isStreaming={message.isStreaming || false}
                onAnimationComplete={message.id === 'initial' ? handleInitialAnimationComplete : undefined}
              />
              
              {/* Action buttons for Cedar messages */}
              {message.actions && message.actions.length > 0 && (() => {
                console.log('🎯 Rendering actions for message:', message.id, message.actions);
                return (
                  <div className="message-actions-container">
                    {message.actions.map((action) => {
                      // Apr 2026 — LOAD_AWARE_ADAPTIVE_COACHING P0: "subtle"
                      // style is used for the modality-4 revert affordance
                      // (white background, green text) so it doesn't
                      // dominate the FYI message; user only taps if they
                      // want to undo.
                      const a = action as any;
                      const isSubtle = a.style === 'subtle';
                      const baseClass =
                        action.type === 'personality_sliders'
                          ? 'personality-action'
                          : 'service-action';
                      const subtleInline = isSubtle
                        ? {
                            backgroundColor: '#ffffff',
                            color: '#2E7D5B',
                            borderColor: '#2E7D5B',
                            borderWidth: 1,
                            borderStyle: 'solid' as const,
                            fontWeight: 500,
                          }
                        : undefined;
                      return (
                        <button
                          key={action.id}
                          className={`action-button ${baseClass}${isSubtle ? ' subtle-action' : ''}`}
                          style={subtleInline}
                          onClick={() => {
                            console.log('🔥 Action clicked:', action);
                            handleActionClick(action as any);
                          }}
                        >
                          {action.label}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </React.Fragment>
        );
          });
        })()}
        
        {/* 🔇 REMOVED (Jan 5, 2026): Processing pill not needed with interim message system */}
        {/* FILE PROCESSING SHIMMER: Use same structure as thinking dots but with analyzing indicator */}
        {/* {processingFiles && (
          <div className="message-wrapper cedar">
            <div className="message-bubble cedar" style={{ 
              background: 'var(--colorsurface-100, #ffffff)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              borderRadius: '16px',
              padding: '12px 16px',
              maxWidth: '280px',
              marginLeft: '16px',
              display: 'flex',
              alignItems: 'center'
            }}>
              <AnalyzingIndicator className="cedar-analyzing-indicator" />
            </div>
          </div>
        )} */}
        
        {/* 🚨 PERMANENTLY DISABLED (Jan 28, 2026): PIPELINE PROGRESS INDICATOR
            After 5+ fix attempts, component consistently causes React error #310
            Crash happens at React hook level (useMemo hook), not catchable with try-catch
            Fundamental architectural issue with this component
            
            SIMPLER ALTERNATIVE: Just show static "Creating your goal..." message
            - No polling
            - No real-time updates
            - Success badge after 30s
            - Doesn't crash!
            
            TODO (future): Rebuild from scratch with simpler architecture
            - No useMemo complexity
            - Static phases
            - Poll only for completion (not phase-by-phase)
        */}
        
        {/* 🎯 REMOVED (Jan 28, 2026): Progress indicator now inserted as inline message */}
        {/* Progress indicator stays in chat history where it appeared, doesn't jump to bottom */}
        
        {/* 🎉 REMOVED (Jan 28, 2026): Success badge also inserted as inline message */}
        
        {/* 🔄 DEVICE SYNC PROGRESS INDICATOR (Garmin) */}
        {showSyncProgress && syncService && (
          <div className="message-wrapper cedar">
            <DeviceSyncProgressIndicator
              service={syncService}
              onComplete={async () => {
                console.log(`🔄 DEVICE SYNC: ${syncService} sync completed`);
                
                // Hide progress indicator
                setShowSyncProgress(false);
                const completedService = syncService;
                const fromSettings = syncFromSettings;
                setSyncService(null);
                setSyncFromSettings(false);
                
                // Tier D-6 (2026-05-15) — HRZoneEducationModal first-Garmin-
                // sync trigger removed. Replacement path is a single in-chat
                // banner via the existing notification surface (handled
                // backend-side) → user taps → drawer opens → user discovers
                // the "What are heart rate zones?" link for deeper learning.
                // See ~/Desktop/hr-zones-drawer-redesign.md (RETIRE section).


                // 🎯 Trigger post-sync summary generation
                try {
                  const jwtToken = await storage.getItem('jwt_token');
                  
                  if (jwtToken) {
                    // Call backend to generate sync summary
                    const apiBaseUrl = getApiBaseUrl();
                    const summaryResponse = await fetch(`${apiBaseUrl}/api/device/sync-summary`, {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${jwtToken}`,
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify({
                        service: completedService,
                        source: fromSettings ? 'settings' : 'onboarding'
                      })
                    });
                    
                    if (summaryResponse.ok) {
                      const summary = await summaryResponse.json();
                      
                      // Add Cedar's summary message to chat
                      const summaryMessage: ChatMessage = {
                        id: `sync-summary-${Date.now()}`,
                        sender: 'cedar',
                        text: summary.cedar_message,
                        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                        status: 'delivered'
                      };
                      
                      transition({ type: 'ADD_MESSAGE', payload: { message: summaryMessage } });
                      
                      // Scroll to show summary (web only)
                      setTimeout(() => {
                        if (Platform.OS === 'web') {
                          const chatContainer = document.querySelector('.chat-messages');
                          if (chatContainer) {
                            chatContainer.scrollTop = chatContainer.scrollHeight;
                          }
                        }
                      }, 100);
                    }
                  }
                } catch (error) {
                  console.error('❌ SYNC SUMMARY: Failed to generate summary:', error);
                  // Non-blocking - sync still succeeded
                }
              }}
            />
          </div>
        )}
        
        {/* 🚫 REMOVED (Feb 4, 2026): Separate notification rendering moved inline to messages.map() */}
        {/* Notifications now rendered as message objects at lines 8275-8312 */}
        {/* This ensures they stay in chronological position instead of jumping to bottom */}
        
        {/* Invisible div for scroll target */}
        <div ref={messagesEndRef} style={{ height: '1px' }} />
      </div>

      {/* Scroll navigation FABs */}
      <button
        className={`scroll-fab scroll-fab-up${showScrollUpFab ? ' visible' : ''}`}
        onClick={() => {
          if (scrollFrameRef.current) {
            scrollFrameRef.current.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }}
        aria-label="Scroll to top"
        aria-hidden={!showScrollUpFab}
        tabIndex={showScrollUpFab ? 0 : -1}
      >
        <ArrowUp size={20} />
      </button>
      <button
        className={`scroll-fab scroll-fab-down${showScrollDownFab ? ' visible' : ''}`}
        onClick={() => scrollToBottom(false)}
        aria-label="Scroll to bottom"
        aria-hidden={!showScrollDownFab}
        tabIndex={showScrollDownFab ? 0 : -1}
      >
        <ArrowDown size={20} />
      </button>

      {/* Composer - OUTSIDE scrollable container, truly fixed */}
      <div className="chat-bottom" ref={composerBottomRef}>

        {/* 💰 Sprint 7: Hard block overlay replaces entire composer area */}
        {chatState.backendState?.subscription_blocked ? (
          <SubscriptionBlockOverlay
            blockType={chatState.backendState?.block_type}
            tier={chatState.backendState?.subscription?.tier}
            onUpgradeClick={() => {
              router.push('/settings?openSubscription=true');
            }}
          />
        ) : (
        <>
        {/* Attachment Preview */}
        {attachments.length > 0 && (
          <div className="attachment-preview-simple">
            {attachments.map((attachment) => (
              <div 
                key={attachment.id} 
                className="attachment-item-simple"
              >
                {/* File Icon with Overlay */}
                <div className="file-icon-container">
                  {attachment.type === 'image' ? (
                    <img 
                      src={attachment.preview || attachment.url} 
                      alt={attachment.file.name}
                      className="file-thumbnail"
                    />
                  ) : (
                    <div className="file-icon">
                      📄
                    </div>
                  )}
                  
                  {/* 🚨 PHASE 4: Circular Progress Overlay */}
                  {attachment.uploadStatus === 'uploading' && (
                    <div className="progress-overlay">
                      <svg className="progress-circle" viewBox="0 0 36 36">
                        <path
                          className="progress-bg"
                          d="m18,2.0845 a 15.9155,15.9155 0 0,1 0,31.831 a 15.9155,15.9155 0 0,1 0,-31.831"
                        />
                        <path
                          className="progress-fill"
                          strokeDasharray={`${attachment.uploadProgress}, 100`}
                          d="m18,2.0845 a 15.9155,15.9155 0 0,1 0,31.831 a 15.9155,15.9155 0 0,1 0,-31.831"
                        />
                      </svg>
                    </div>
                  )}

                  {/* Upload Status Indicators */}
                  {attachment.uploadStatus === 'uploaded' && (
                    <div className="status-overlay success">
                      ✓
                    </div>
                  )}

                  {attachment.uploadStatus === 'error' && (
                    <div className="status-overlay error">
                      ✕
                    </div>
                  )}
                </div>

                {/* File Name (truncated) */}
                <div className="file-name">
                  {attachment.file.name.length > 12 
                    ? attachment.file.name.substring(0, 12) + '...' 
                    : attachment.file.name
                  }
                </div>

                {/* Remove Button */}
                <button 
                  className="remove-btn"
                  onClick={() => removeAttachment(attachment.id)}
                  title="Remove file"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* 💰 Sprint 7: Subscription limit warning banner
            Shows ONLY for non-paid tiers (trial, post-trial selling/lite).
            Paid users (full/extended) get Cedar speech bubbles instead of banners.
            The banner itself returns null when warningLevel is null. */}
        {!['full', 'extended'].includes(chatState.backendState?.subscription?.tier || '') && (
          <SubscriptionWarningBanner
            tier={chatState.backendState?.subscription?.tier}
            remaining={chatState.backendState?.subscription?.remaining}
            limit={chatState.backendState?.subscription?.limit}
            warningLevel={chatState.backendState?.subscription?.warning_level}
            postTrialPhase={chatState.backendState?.subscription?.post_trial_phase}
            degradedMode={chatState.backendState?.subscription?.degraded_mode}
            weekEndDate={chatState.backendState?.subscription?.week_end_date}
            debriefsUsed={chatState.backendState?.subscription?.debriefs_used}
            debriefsLimit={chatState.backendState?.subscription?.debriefs_limit}
            onUpgradeClick={() => {
              // Navigate to subscription settings
              router.push('/settings?openSubscription=true');
            }}
          />
        )}

        {/* 🎯 MAR 11, 2026: Persistent pipeline progress strip above composer.
            Renders when pipelineGoalId is set (goal creation in progress).
            Fixed at bottom of chat — won't scroll out of view. */}
        {pipelineGoalId && (
          <div style={{
            padding: '0 16px 8px 16px',
            background: 'var(--background, #F5F5F5)',
          }}>
            <GoalPipelineProgress
              goalId={pipelineGoalId}
              goalText={pipelineGoalText}
              onDismiss={() => {
                console.log('✅ PIPELINE: Dismissing pipeline strip');
                setPipelineGoalId(null);
                // Also remove the inline system message marker
                const progressMsg = chatState.messages.find(
                  m => m.sender === 'system' && m.metadata?.progressIndicator &&
                       (m.metadata?.goalId === pipelineGoalId || m.metadata?.goal_id === pipelineGoalId)
                );
                if (progressMsg) {
                  transition({ type: 'REMOVE_MESSAGE', payload: { messageId: progressMsg.id } });
                }
              }}
            />
          </div>
        )}

        {/* 🟢 Persistent recalibration progress strip above composer.
            Renders when recalTrackingId is set (recalibration in progress).
            Fixed at bottom of chat — won't scroll out of view. */}
        {recalTrackingId && (
          <div style={{
            padding: '0 16px 8px 16px',
            background: 'var(--background, #F5F5F5)',
          }}>
            <RecalibrationProgress
              trackingId={recalTrackingId}
              recalibrationType={recalType || undefined}
              onDismiss={() => {
                console.log('✅ RECALIBRATION: Strip dismissed, clearing state');
                setRecalTrackingId(null);
                setRecalType(null);
                // Remove the inline system message marker
                const recalMsg = chatState.messages.find(
                  m => m.sender === 'system' && m.metadata?.recalibrationIndicator &&
                       m.metadata?.recalibrationTrackingId === recalTrackingId
                );
                if (recalMsg) {
                  transition({ type: 'REMOVE_MESSAGE', payload: { messageId: recalMsg.id } });
                }
              }}
            />
          </div>
        )}

        {/* Composer — tier-gated states: Essentials deactivated, weekly/monthly depleted, or normal */}
        {(() => {
          const bs = chatState.backendState as any;
          const sub = bs?.subscription || {};
          const tier = sub?.tier || bs?.subscription_tier || '';
          const isBeta = bs?.is_beta || false;
          const composerDeactivated = sub?.composer_deactivated;
          const activeTouchpointId = sub?.active_touchpoint_id;
          const degradedMode = sub?.degraded_mode;
          const weeklyRemaining = sub?.remaining ?? 999;
          const essentialsGracePeriod = sub?.essentials_grace_period ?? false;
          const isOnboarding = !bs?.onboardingCompleted;
          // 🩺 MAY 13 2026 — pdrtest22 audit: dropped the OR-clause
          // `|| !activeTouchpointId`. See ChatScreen.native.tsx:1620 for
          // full rationale. Gate now requires explicit backend signal.
          const isEssentialsDeactivated = tier === 'basic' && !isBeta && !isOnboarding && bs !== null && !essentialsGracePeriod && composerDeactivated === true;
          const isChatPlusPro = (tier === 'full' || tier === 'extended') && !isBeta;
          const isWeeklyDepleted = isChatPlusPro && degradedMode === true && weeklyRemaining <= 0;
          const isMonthlyDepleted = isChatPlusPro && monthlyDepleted;

          // 🔧 ESSENTIALS DEACTIVATED: Show "Make a change" button instead of composer
          if (isEssentialsDeactivated) {
            return (
              <div style={{ padding: '16px 20px', borderTop: '1px solid var(--cedar-subtle, #e8f2ed)', background: 'var(--cedar-subtle, #e8f2ed)' }}>
                <button
                  onClick={async () => {
                    try {
                      const jwtToken = await storage.getItem('jwt_token');
                      const res = await fetch(`${API_BASE_URL}/api/touchpoints/start`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${jwtToken}` },
                        body: JSON.stringify({ touchpoint_type: 'recalibration' })
                      });
                      if (res.ok) console.log('✅ Recalibration touchpoint started');
                    } catch (err) {
                      console.error('❌ Recalibration start failed:', err);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '14px',
                    backgroundColor: 'var(--cedar-prime, #1F6C4F)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    minHeight: '48px',
                  }}
                >
                  Make a change
                </button>
                <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '13px', color: 'var(--cedar-text-secondary, #6B7280)' }}>
                  <span>Want more time with Cedar? </span>
                  <a href="/settings?openSubscription=true" style={{ color: 'var(--cedar-prime, #1F6C4F)', fontWeight: 500 }}>Explore plans</a>
                </div>
              </div>
            );
          }

          // 💰 WEEKLY DEPLETED: Show borrow/upgrade options
          if (isWeeklyDepleted) {
            return (
              <div style={{ padding: '16px 20px', borderTop: '1px solid var(--cedar-subtle, #e8f2ed)', background: 'var(--cedar-subtle, #e8f2ed)' }}>
                <div style={{ fontSize: '14px', color: 'var(--cedar-text-secondary, #6B7280)', marginBottom: '12px', textAlign: 'center' }}>
                  Cedar's availability for chat this week has been used up
                </div>
                <button
                  onClick={async () => {
                    try {
                      const jwtToken = await storage.getItem('jwt_token');
                      const res = await fetch(`${API_BASE_URL}/api/touchpoints/borrow-week`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${jwtToken}` }
                      });
                      if (res.ok) {
                        const data = await res.json();
                        if (data.borrowed) {
                          console.log('✅ Borrowed from next week');
                          // Refresh subscription state so the composer re-renders
                          if (data.subscription) {
                            transition({ type: 'SYNC_BACKEND_STATE', payload: { backendState: { subscription: data.subscription } } });
                          } else {
                            // Fallback: reload the page to pick up new state
                            window.location.reload();
                          }
                        } else if (data.reason === 'monthly_depleted') {
                          console.log('⚠️ Monthly allotment exhausted');
                          setMonthlyDepleted(true);
                        }
                      } else {
                        // Non-ok response — treat as monthly depleted
                        setMonthlyDepleted(true);
                      }
                    } catch (err) {
                      console.error('❌ Borrow failed:', err);
                      setMonthlyDepleted(true);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '14px',
                    backgroundColor: 'var(--cedar-prime, #1F6C4F)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    minHeight: '48px',
                    marginBottom: '8px',
                  }}
                >
                  Continue Chat
                </button>
                {tier === 'full' && (
                  <button
                    onClick={() => router.push('/settings?openSubscription=true')}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: 'transparent',
                      color: 'var(--cedar-prime, #1F6C4F)',
                      border: '1px solid var(--cedar-prime, #1F6C4F)',
                      borderRadius: '10px',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    Explore CC Pro
                  </button>
                )}
              </div>
            );
          }

          // 💰 MONTHLY DEPLETED: Pool fully exhausted, no more borrowing
          if (isMonthlyDepleted) {
            return (
              <div style={{ padding: '16px 20px', borderTop: '1px solid var(--cedar-subtle, #e8f2ed)', background: 'var(--cedar-subtle, #e8f2ed)' }}>
                <div style={{ fontSize: '14px', color: 'var(--cedar-text-secondary, #6B7280)', marginBottom: '12px', textAlign: 'center' }}>
                  Cedar's availability for the month has been used up
                </div>
                <button
                  onClick={async () => {
                    try {
                      const jwtToken = await storage.getItem('jwt_token');
                      await fetch(`${API_BASE_URL}/api/touchpoints/start`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${jwtToken}` },
                        body: JSON.stringify({ touchpoint_type: 'recalibration' })
                      });
                    } catch (err) {
                      console.error('❌ Recalibration start failed:', err);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '14px',
                    backgroundColor: 'var(--cedar-prime, #1F6C4F)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    minHeight: '48px',
                    marginBottom: '8px',
                  }}
                >
                  Make a change to this week's plan
                </button>
                {tier === 'full' && (
                  <button
                    onClick={() => router.push('/settings?openSubscription=true')}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: 'transparent',
                      color: 'var(--cedar-prime, #1F6C4F)',
                      border: '1px solid var(--cedar-prime, #1F6C4F)',
                      borderRadius: '10px',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    Explore CC Pro
                  </button>
                )}
              </div>
            );
          }

          // 🟢 NORMAL COMPOSER — cedar bark brown tint in degraded or post-trial lite mode
          return (
            <div className={`composer-wrapper${
              (degradedMode || sub.post_trial_phase === 'lite')
                ? ' composer-degraded' : ''
            }`}>
              <CmpComposer
                className={`composer ${isTyping ? 'cedar-typing' : ''}`}
                value={input}
                onChange={setInput}
                onSend={handleSend}
                onPaperclip={handlePaperclipClick}
                onHistoryClick={scrollToHistory}
                sendButtonState={getSendButtonState() as any}
                attachButtonState={getAttachButtonState()}
                onSendClick={handleSendClick}
                attachments={[]}
                onRemoveAttachment={removeAttachment as any}
                textareaRef={textareaRef}
                placeholder={messages.length > 0 ? `Reply to ${messages[messages.length - 1]?.sender === 'cedar' ? 'Cedar' : 'yourself'}...` : undefined}
                isTyping={isTyping}
              />
            </div>
          );
        })()}

        </>
        )}
      </div>
      
      {/* Navigation is handled by PersistentNavigation in App.tsx - DO NOT duplicate here */}
      
      {/* Hidden file input */}
      <input
        type="file"
        multiple
        accept={INTAKE_ACCEPT_ATTRIBUTE}
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleAttachment}
      />

      {/* 🎛️ UNIFIED PERSONALITY SLIDER MODAL */}
      {personalitySliderState.isVisible && personalitySliderState.mode === 'modal' && (
        <PersonalitySliderModal
          isOpen={true}
          onClose={() => personalitySliderActions.hide()}
          onSubmit={handlePersonalityModalSubmit}
          sliders={personalitySliderState.configuration}
        />
      )}

      {/* 💰 TRIAL GATE: After Step 6, before Step 7 — only path forward is Start trial (or Maybe later if added) */}
      {showTrialGate && (
        <PaywallModal
          visible={true}
          onClose={() => {}}
          showCloseButton={false}
          mode="trial"
          isBetaTester={isBetaTester}
          expiresAt={expiresAt}
          subscriptionStatus={subscriptionStatus}
          onPurchaseSuccess={async () => {
            await refreshSubscription();
            if (pendingStep7BackendState) {
              transition({ type: 'SYNC_BACKEND_STATE', payload: { backendState: pendingStep7BackendState } });
              setPendingStep7BackendState(null);
            }
            setShowTrialGate(false);
            // Request notification permission after trial starts and user returns to chat
            setTimeout(() => {
              checkAndRequestNotifications();
            }, 1500);
          }}
        />
      )}

      {/* Notification Permission Modal - temporarily disabled for build */}
      {showNotificationPermission && (
        <NotificationPermissionModal
          onAllow={handleAllowNotifications}
          onDeny={handleDenyNotifications}
          onLater={handleNotificationLater}
          mode={Platform.OS === 'web' && typeof Notification !== 'undefined' && Notification.permission === 'denied' ? 'recovery' : 'request'}
        />
      )}

      {/* Plan Notification Component - temporarily disabled for build */}
      {/* {messages.some(msg => msg.text.includes('plan_notification_metadata')) && (
        <PlanNotificationComponent />
      )} */}

      <style>{`
        .clean-chat-container {
          display: flex;
          flex-direction: column;
          /* 📱 VISUAL VIEWPORT FIX: NO explicit height - use top+bottom anchor */
          /* Container stretched between top: 0 and bottom: 0 of layout viewport */
          /* This prevents upward slide when visual viewport shrinks (keyboard) */
          background: #ffffff !important;
          /* NO position: relative - breaks position: fixed children! */
          overflow: hidden;
        }

        .chat-content {
          flex: 1;
          overflow-y: auto;
          padding: 20px 16px 100px 16px;
          display: flex;
          flex-direction: column;
          gap: 16px;
          /* 📱 SAFE AREA FIX: Content scrolls INSIDE safe area boundaries */
          /* No max-height needed - parent container is anchored */
          -webkit-overflow-scrolling: touch;
          scroll-behavior: smooth;
        }

        .message-wrapper {
          display: flex;
          flex-direction: column;
          max-width: 100%;
        }

        .message-wrapper.user {
          align-items: flex-end;
        }

        .message-wrapper.cedar {
          align-items: flex-start;
        }

        .message-wrapper.system {
          align-items: center;
          margin: 12px 0;
        }

        .message-bubble {
          max-width: 80%;
          margin-bottom: 8px;
        }

        .message-actions-container {
          display: flex;
          gap: 8px;
          margin-top: 8px;
          margin-left: 16px;
          flex-wrap: wrap;
        }

        .action-button {
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          border: none;
          border-radius: 20px;
          padding: 8px 16px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
        }

        .action-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }

        .personality-action {
          background: linear-gradient(135deg, #1F6C4F 0%, #2d5238 100%);
          color: white;
          border: none;
          border-radius: 20px;
          padding: 8px 16px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 2px 8px rgba(74, 124, 89, 0.3);
        }

        .personality-action:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(74, 124, 89, 0.4);
          background: linear-gradient(135deg, #5a8c69 0%, #3d6248 100%);
        }

        .cedar-reactions {
          display: flex;
          gap: 8px;
          margin-top: 4px;
          margin-left: 16px;
        }

        .cedar-reaction {
          display: flex;
          align-items: center;
          gap: 4px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 4px 8px;
          font-size: 12px;
        }



        .typing-wrapper {
          display: flex;
          justify-content: flex-start;
          align-items: flex-start;
          margin: 0;
          padding: 0;
          /* Ensure proper alignment with Cedar messages */
          margin-bottom: 16px;
        }

        /* Composer area - positioned ABOVE PersistentNavigation */
        .chat-bottom {
          position: fixed;
          /* Bottom value set dynamically by JS keyboard handler */
          bottom: calc(82px + env(safe-area-inset-bottom, 0px));
          left: 50%;
          transform: translateX(-50%);
          max-width: 390px;
          width: 100%;
          background: white;
          border-top: 1px solid rgba(0, 0, 0, 0.05);
          z-index: 100; /* Below PersistentNavigation (9999) */
          transition: bottom 0.25s cubic-bezier(0.4, 0.0, 0.2, 1); /* Smooth keyboard transitions */
        }

        .composer-wrapper {
          padding: 12px 16px;
        }

        .attachment-preview-simple {
          padding: 12px 16px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          gap: 8px;
          overflow-x: auto;
        }

        .attachment-item-simple {
          position: relative;
          background: white;
          border-radius: 8px;
          padding: 8px;
          min-width: 100px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .file-icon-container {
          position: relative;
          width: 60px;
          height: 60px;
          border-radius: 4px;
          overflow: hidden;
        }

        .file-thumbnail {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .file-icon {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          color: white;
        }

        .file-name {
          font-size: 12px;
          font-weight: 500;
          color: #1e293b;
          margin-top: 4px;
        }

        .remove-btn {
          position: absolute;
          top: -8px;
          right: -8px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          border: none;
          background: #ef4444;
          color: white;
          font-size: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        @media (max-width: 640px) {
          /* Mobile responsive styles handled by InlinePersonalitySliders.css */
        }

        /* 🚨 PHASE 4: Immediate Upload Progress Styling - Brand Aligned */
        .attachment-item-simple {
          position: relative;
          transition: all 0.3s ease;
        }

        .attachment-item-simple.uploading {
          background: rgba(76, 175, 80, 0.1);
          border: 1px solid rgba(76, 175, 80, 0.3);
        }

        .attachment-item-simple.uploaded {
          background: rgba(76, 175, 80, 0.15);
          border: 1px solid rgba(76, 175, 80, 0.4);
        }

        .attachment-item-simple.error {
          background: rgba(244, 67, 54, 0.1);
          border: 1px solid rgba(244, 67, 54, 0.3);
        }

        .progress-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
        }

        .progress-circle {
          width: 24px;
          height: 24px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid #4CAF50;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .status-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
        }

        .status-overlay.success {
          background: rgba(76, 175, 80, 0.1);
          border: 1px solid rgba(76, 175, 80, 0.3);
        }

        .status-overlay.error {
          background: rgba(244, 67, 54, 0.1);
          border: 1px solid rgba(244, 67, 54, 0.3);
        }

        .status-overlay .status-icon {
          font-size: 12px;
        }

        .status-overlay .status-text {
          font-size: 12px;
          color: #4CAF50;
          font-weight: 500;
        }

        .status-overlay .status-text.error {
          color: #F44336;
        }

        .status-overlay .status-text.success {
          color: #4CAF50;
        }

        .status-overlay .status-text.uploading {
          color: #FFA500;
        }

        .status-overlay .status-text.error.uploading {
          color: #FF4500;
        }

        .status-overlay .status-text.success.uploading {
          color: #7FFFD4;
        }

        .status-overlay .status-text.uploading::after {
          content: '';
          animation: blink 1.5s ease-in-out infinite;
        }

        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }

        .reading-indicator-container {
          position: absolute;
          top: -35px;
          left: 20px;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(76, 175, 80, 0.2);
          border-radius: 16px;
          padding: 6px 12px;
          box-shadow: 0 4px 12px rgba(76, 175, 80, 0.15);
          z-index: 15;
          animation: fadeInUp 0.3s ease-out;
        }

        .reading-shimmer {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .reading-text {
          font-size: 12px;
          font-weight: 500;
          color: #4CAF50;
          letter-spacing: 0.3px;
        }

        .shimmer-dots {
          display: flex;
          gap: 3px;
          align-items: center;
        }

        .shimmer-dot {
          width: 3px;
          height: 3px;
          border-radius: 50%;
          background: #4CAF50;
          animation: shimmerPulse 1.5s ease-in-out infinite;
        }

        .shimmer-dot:nth-child(1) {
          animation-delay: 0s;
        }

        .shimmer-dot:nth-child(2) {
          animation-delay: 0.15s;
        }

        .shimmer-dot:nth-child(3) {
          animation-delay: 0.3s;
        }

        @keyframes shimmerPulse {
          0%, 60%, 100% {
            opacity: 0.3;
            transform: scale(0.8);
          }
          30% {
            opacity: 1;
            transform: scale(1.2);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Enhanced typing wrapper to accommodate reading indicator */
        .typing-wrapper {
          position: relative;
        }

        /* 🚨 PHASE 4: Simple Attachment Preview - Clean UX */
        .attachment-preview-simple {
          display: flex;
          gap: 12px;
          padding: 8px 16px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 8px;
          margin-bottom: 8px;
          overflow-x: auto;
        }

        .attachment-item-simple {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          min-width: 70px;
          position: relative;
        }

        .file-icon-container {
          position: relative;
          width: 48px;
          height: 48px;
          border-radius: 8px;
          overflow: hidden;
          background: #f0f0f0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .file-thumbnail {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .file-icon {
          font-size: 20px;
          color: #666;
        }

        .progress-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.3);
          border-radius: 8px;
        }

        .progress-circle {
          width: 24px;
          height: 24px;
          transform: rotate(-90deg);
        }

        .progress-bg {
          fill: none;
          stroke: rgba(255, 255, 255, 0.3);
          stroke-width: 2;
        }

        .progress-fill {
          fill: none;
          stroke: #4CAF50;
          stroke-width: 2;
          stroke-linecap: round;
          transition: stroke-dasharray 0.3s ease;
        }

        .status-overlay {
          position: absolute;
          top: -4px;
          right: -4px;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: bold;
          color: white;
        }

        .status-overlay.success {
          background: #4CAF50;
        }

        .status-overlay.error {
          background: #F44336;
        }

        .file-name {
          font-size: 10px;
          color: #666;
          text-align: center;
          max-width: 60px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .remove-btn {
          position: absolute;
          top: -6px;
          right: -6px;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: rgba(244, 67, 54, 0.9);
          color: white;
          border: none;
          font-size: 12px;
          font-weight: bold;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0.8;
          transition: all 0.2s ease;
        }

        .remove-btn:hover {
          opacity: 1;
          transform: scale(1.1);
        }
      `}</style>
      
      {/* Tier D-6 (2026-05-15) — HRZoneEducationModal render block retired
          alongside its state/import/trigger. See
          ~/Desktop/hr-zones-drawer-redesign.md (RETIRE section). */}
    </div>
  );
};

export default () => (
  <ErrorBoundary 
    resetOnPropsChange={true}
    onError={(error, errorInfo) => {
      console.error('🚨 PHASE 2 FIX: Chat component error caught by boundary:', { error, errorInfo });
    }}
  >
    <Chat />
  </ErrorBoundary>
); /* Force deployment trigger Thu Jun 26 18:42:52 EDT 2025 */
